/*
 * https://github.com/5orenso
 *
 * Copyright (c) 2020 Øistein Sørensen
 * Licensed under the MIT license.
 */

// Partner visits API (2026-10-06): see docs/superpowers/specs/2026-10-06-partner-visits-api-design.md

import { h, Fragment } from 'preact';
import util from 'preact-util';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';

import localUtil from './util';
import reloadAppHelper from './reloadApp';
import versionStore from '../stores/version';
import { DEFAULT_ACCOUNT_MAP } from './accountMap';
import { extractOrgNumber } from './orgNumber';
import { NO_SHOW_REASON_DATALIST } from './noShowReasons';
import { formatLookupAddress } from './lookupAddress';
import { packagingCallback } from './productPackaging';
import { normalizeContrastPreference } from './contrastPreference';

import Feedback from './feedback';

import fieldsOther from '../product/fields-other';
import { applyFieldExtensions } from './fieldExtensions';

// The C registry may carry `fieldExtensions`: additions to sections that live
// in this shared file (extra product fields for a product module, extra
// customer toggles, ...). They are applied below so the definitions themselves
// stay out of this file. A product export that stubs the registry to `{}` gets
// no extensions and therefore never sees those fields.
const { fieldExtensions = [], ...fieldsOtherEntities } = fieldsOther;
import { COLOR_PICKER_LIST, findInvalidEmailChars, apiServer, postnummerLookup, brregLookup, renderDateTimeShortcuts } from './fieldsHelpers';
import { CAT_SPECIES_ID as NRR_CAT_SPECIES_ID, changedFields as nrrChangedFields } from './nrrAnimal';
import { appConfirm } from './dialogs';
import { beginDialogAction } from './dialogAction';
import { animalAge } from './animalAge';

// Re-export for existing consumers that import these from lib/fields.
export { COLOR_PICKER_LIST } from './fieldsHelpers';

// Default searchKeys mapping for common field names (Norwegian + English)
// Used by fieldsCompact.js for field search functionality
export const defaultFieldSearchKeys = {
    // Identity fields
    id: ['id', 'identifikator', 'nummer'],
    uuidv4: ['uuid', 'unik id', 'unique id'],

    // Common fields
    name: ['navn', 'name', 'tittel', 'title'],
    title: ['tittel', 'title', 'navn', 'name', 'overskrift'],
    description: ['beskrivelse', 'description', 'info', 'informasjon'],
    body: ['innhold', 'body', 'tekst', 'text', 'brødtekst'],
    ingress: ['ingress', 'intro', 'sammendrag', 'summary'],
    comment: ['kommentar', 'comment', 'merknad', 'note', 'notat'],

    // Person fields
    firstname: ['fornavn', 'first name', 'navn', 'name'],
    lastname: ['etternavn', 'last name', 'navn', 'name'],
    email: ['epost', 'e-post', 'email', 'mail'],
    cellphone: ['mobil', 'telefon', 'cellphone', 'phone', 'tlf', 'mob'],
    address: ['adresse', 'address', 'gate', 'street'],
    postalcode: ['postnummer', 'postal code', 'zip', 'postnr'],
    postalplace: ['poststed', 'postal place', 'sted', 'place'],
    place: ['sted', 'place', 'by', 'city', 'plass'],
    country: ['land', 'country', 'nasjon'],
    birth: ['fødsel', 'birth', 'fødselsdag', 'birthday', 'født', 'alder', 'age'],

    // Business fields
    customer: ['kunde', 'customer', 'klient', 'client'],
    user: ['bruker', 'user', 'person'],
    price: ['pris', 'price', 'kostnad', 'cost', 'beløp', 'amount'],
    vat: ['mva', 'vat', 'moms', 'avgift', 'tax'],
    qty: ['antall', 'qty', 'quantity', 'mengde', 'stk', 'pieces'],

    // Product fields
    category: ['kategori', 'category', 'type', 'gruppe', 'group'],
    type: ['type', 'kategori', 'category', 'art', 'kind'],
    sku: ['sku', 'varenummer', 'product number', 'produktnummer'],
    gtin: ['gtin', 'ean', 'strekkode', 'barcode'],
    stock: ['lager', 'stock', 'beholdning', 'inventory'],

    // Date fields
    date: ['dato', 'date', 'dag', 'day'],
    startDate: ['startdato', 'start date', 'fra', 'from', 'begynner'],
    endDate: ['sluttdato', 'end date', 'til', 'to', 'slutter'],
    end: ['slutt', 'end', 'til', 'to'],

    // Status fields
    offline: ['offline', 'inaktiv', 'inactive', 'skjult', 'hidden'],
    active: ['aktiv', 'active', 'enabled', 'på', 'on'],

    // Media fields
    images: ['bilder', 'images', 'foto', 'photos', 'bilde', 'image'],
    files: ['filer', 'files', 'dokumenter', 'documents', 'vedlegg', 'attachments'],
    url: ['url', 'lenke', 'link', 'nettadresse', 'web'],
    links: ['lenker', 'links', 'url', 'nettadresser'],

    // Social fields
    facebook: ['facebook', 'fb'],
    instagram: ['instagram', 'insta', 'ig'],

    // Tags and categories
    tags: ['tags', 'tagger', 'emneord', 'keywords', 'merkelapper', 'stikkord'],

    // Animal fields
    species: ['art', 'species', 'dyreart'],
    speciesId: ['art', 'species', 'dyreart'],
    breed: ['rase', 'breed'],
    breedId: ['rase', 'breed'],
    gender: ['kjønn', 'gender', 'hann', 'hunn', 'sex'],
    chipId: ['chip', 'chipid', 'microchip', 'id-brikke', 'brikke'],
    weight: ['vekt', 'weight', 'kg', 'kilo'],

    // Race/event fields
    race: ['løp', 'race', 'konkurranse', 'competition'],
    raceClass: ['klasse', 'race class', 'løpsklasse'],

    // Medical/journal fields
    journal: ['journal', 'pasientjournal', 'record'],
    diagnose: ['diagnose', 'diagnosis'],
    prescription: ['resept', 'prescription'],
    drug: ['medisin', 'drug', 'medikament', 'medicine'],

    // Calendar fields
    calendarId: ['kalender', 'calendar'],

    // Organization fields
    sort: ['sortering', 'sort', 'rekkefølge', 'order'],
    color: ['farge', 'color', 'colour'],
    members: ['medlemmer', 'members', 'deltakere', 'participants'],

    // Other common fields
    sale: ['salg', 'sale', 'ordre', 'order'],
    sponsors: ['sponsorer', 'sponsors'],
    attribution: ['kreditering', 'attribution', 'credits'],
    qualifying: ['kvalifisering', 'qualifying'],
};


const reloadApp = () => {
    reloadAppHelper();
}



const buildBrregMapForPrimary = (brregData, { phoneField = 'phone' } = {}) => ({
    name: brregData.name || '',
    address: brregData.address || '',
    postalcode: brregData.postalcode || '',
    place: brregData.place || '',
    country: brregData.country || '',
    url: brregData.url || '',
    email: brregData.email || '',
    [phoneField]: brregData.phone || '',
});

const buildBrregMapForSecondary = (brregData) => ({
    name2: brregData.name || '',
});

const applyBrregToForm = async ({
    field, targetFieldMap, brregData, obj, updateValue, holdingStore, calledFrom,
}) => {
    const recordKeys = Object.keys(holdingStore).filter(key => holdingStore[key] === obj);
    const dialogAction = beginDialogAction(holdingStore, 'applyBrregToForm', () => [
        obj.id,
        obj[field],
        ...Object.keys(targetFieldMap).map(key => obj[key]),
        ...recordKeys.map(key => holdingStore[key]?.id),
    ]);
    if (!dialogAction) return;
    const { id } = obj;
    const orgNumber = obj[field];
    try {
        const toApply = [];
        const conflicts = [];

        Object.entries(targetFieldMap).forEach(([targetField, newValue]) => {
            if (!newValue) return;
            const current = obj[targetField];
            if (!current || String(current).trim() === '') {
                toApply.push({ field: targetField, from: current || null, to: newValue });
            } else if (String(current).trim() === String(newValue).trim()) {
                // already matches — nothing to do
            } else {
                conflicts.push({ field: targetField, from: current, to: newValue });
            }
        });

        const skipped = [];
        if (conflicts.length > 0) {
            const lines = conflicts.map(c => `- ${c.field}: "${c.from}" → "${c.to}"`).join('\n');
            // eslint-disable-next-line no-alert
            const ok = (await appConfirm(`Replace these existing values from Brreg?\n\n${lines}`));
            if (!dialogAction.isCurrent()) return;
            if (ok) {
                conflicts.forEach(c => toApply.push(c));
            } else {
                conflicts.forEach(c => skipped.push({
                    field: c.field, existing: c.from, brreg: c.to, reason: 'user-declined',
                }));
            }
        }

        for (const entry of toApply) {
            await updateValue({ name: entry.field, value: entry.to, id });
        }

        if (id) {
            try {
                await versionStore.insert({
                    type: 'brreg-lookup',
                    documentId: id,
                    calledFrom,
                    data: {
                        orgNumber,
                        fetchedAt: new Date().toISOString(),
                        brreg: brregData,
                        applied: toApply,
                        skipped,
                    },
                });
            } catch (err) {
                console.error('brreg: failed to save version record', err);
            }
        }

        holdingStore.setFeedback(field, `Applied ${toApply.length} field${toApply.length === 1 ? '' : 's'} from Brreg`);
    } finally {
        dialogAction.finish();
    }
};

const runBrregLookup = async ({ field, obj, updateValue, holdingStore, buildTargetMap, calledFrom }) => {
    // The stored value may carry grouping and a suffix (`832 195 162 MVA`), Brreg wants the digits.
    const orgNumber = extractOrgNumber(obj[field]);
    if (!orgNumber) {
        holdingStore.setFeedback(field, '');
        holdingStore.setFeedbackButton(field, null, null);
        return;
    }
    holdingStore.setFeedback(field, 'Looking up Brreg...');
    const brregData = await brregLookup(orgNumber);
    // The form may have moved on to another record while we waited (e.g. the visitor
    // drawer opened for the next visitor). The hint and its button would then belong
    // to the previous record, so drop it instead of showing it under the wrong one.
    const isStillCurrentRecord = Object.keys(holdingStore).some(key => holdingStore[key] === obj);
    if (!isStillCurrentRecord) {
        return;
    }
    if (!brregData) {
        holdingStore.setFeedback(field, 'Brreg lookup failed. Try again later.');
        holdingStore.setFeedbackButton(field, null, null);
        return;
    }
    if (brregData.__notFound) {
        holdingStore.setFeedback(field, 'No company found for this org number');
        holdingStore.setFeedbackButton(field, null, null);
        return;
    }
    holdingStore.setFeedback(field, `Found: ${brregData.name}${brregData.place ? ', ' + brregData.place : ''}`);
    holdingStore.setFeedbackButton(field, async () => {
        holdingStore.setFeedbackButtonProgress(field, true);
        await applyBrregToForm({
            field,
            targetFieldMap: buildTargetMap(brregData),
            brregData,
            obj,
            updateValue,
            holdingStore,
            calledFrom,
        });
        holdingStore.setFeedbackButtonProgress(field, false);
        holdingStore.setFeedbackButtonDone(field, true);
    }, 'Fill from Brreg');
};

const cellphoneLookup = async (cellphone) => {
    const response = await util.fetchApi(`/api/sms/lookup`, { method: 'GET' }, {
        cellphone,
    });
    switch (response.status) {
        case 200:
            console.log('cellphoneLookup', response.data);
            return response.data;
    }
}

// NRR (Min Katt): shows a "Fetch from NRR" button under the registration-number field for chipped cats
// when the clinic has featureFlags.nrr. Clicking fills every differing field through updateValue,
// which persists for an existing animal (obj.id) and only fills the form for a new one.
const nrrRegistrationNumberHook = async (obj, updateValue, holdingStore) => {
    const field = 'nrrRegistrationNumber';
    holdingStore.setFeedback(field, '');
    holdingStore.setFeedbackError(field, '');
    holdingStore.setFeedbackButton(field, null, null);
    if (!holdingStore.nrrApproved || !obj || obj.speciesId !== NRR_CAT_SPECIES_ID || !obj.chipId) {
        return;
    }
    holdingStore.setFeedbackButton(field, async () => {
        holdingStore.setFeedbackButtonProgress(field, true);
        const response = await holdingStore.nrrGetCat(obj.chipId);
        holdingStore.setFeedbackButtonProgress(field, false);
        if (!response || response.status !== 200) {
            holdingStore.setFeedbackError(field, `NRR: ${(response && response.message) || 'NRR did not respond.'}`);
            return;
        }
        const cat = response.data?.cats?.[0];
        if (!cat) {
            holdingStore.setFeedback(field, 'Katten ble ikke funnet i NRR.');
            return;
        }
        const changes = nrrChangedFields(obj, cat);
        const names = Object.keys(changes);
        // Sequential on purpose: each updateValue may PATCH the same animal; keep the saves ordered.
        await names.reduce(
            (chain, name) => chain.then(() => updateValue({ name, value: changes[name], id: obj.id })),
            Promise.resolve(),
        );
        holdingStore.setFeedbackButtonDone(field, true);
        holdingStore.setFeedback(field, names.length > 0
            ? `Hentet fra NRR: ${names.length} felt oppdatert.`
            : 'Alt stemmer allerede med NRR.');
    }, 'Hent fra NRR');
};

function mapLetter(letter) {
    switch (letter) {
        case 'æ':
            return 'ae';
        case 'ø':
            return 'oe';
        case 'å':
            return 'aa';
        case 'Æ':
            return 'Ae';
        case 'Ø':
            return 'Oe';
        case 'Å':
            return 'Aa';
        default:
            return letter;
    }
}

// calendarEvents link to calendars by the calendarId STRING, so every calendar needs a
// non-empty calendarId. Same derivation exists server-side in backend/lib/calendar-id.js.
function deriveCalendarId(name) {
    let calendarId = name || '';
    calendarId = calendarId.replace(/[æøå]/ig, match => mapLetter(match));
    calendarId = calendarId.replace(/[^a-zA-Z0-9]/g, '_');
    calendarId = calendarId.replace(/_+/g, '_');
    calendarId = calendarId.replace(/^_/g, '');
    calendarId = calendarId.replace(/_$/g, '');
    return calendarId;
}

function lineTotal(line, format = true, inputQty) {
    const qty = inputQty || line.qty;
    if (!qty) return 0;

    let aPrice = line.price;
    if (line.medicine || line.fee || line.equipment || line.other) {
        aPrice = parseFloat(line.medicine || 0) + parseFloat(line.fee || 0) + parseFloat(line.equipment || 0) + parseFloat(line.other || 0);
    }
    let price = qty * (aPrice || 0);
    if (line.discount) {
        price -= price * (line.discount / 100);
    }
    const taxAmount = price * (line.vat / 100);
    let totalPrice = price + taxAmount;
    if (line.discountAmount) {
        totalPrice -= (line.discountAmount * qty);
    }
    if (format) {
        return util.format(totalPrice, 2, ',', ' ');
    }
    return totalPrice;
}

/**
 * Returns an array of objects describing invalid characters and their positions.
 *
 * @param {string} email - The email string to validate
 * @returns {Array<{ char: string, index: number }>}
 */

// Combine markOfOrigin + earMark into a livestock identifier using the species-specific convention.
// Cattle (Storfe, speciesId 13) writes it as markOfOrigin-earMark. Sheep (Sau, 11), pig (Gris, 12),
// and goat (Geit, 14) reverse the order to earMark-markOfOrigin per Norwegian DHP convention.
function buildLivestockId(animal) {
    const markOfOrigin = animal?.markOfOrigin || '';
    const earMark = animal?.earMark || '';
    if (!markOfOrigin && !earMark) return '';
    const reversedSpeciesIds = [11, 12, 14];
    const reversedSpeciesNames = ['Sau', 'Gris', 'Geit'];
    const isReversed = reversedSpeciesIds.includes(animal?.speciesId)
        || reversedSpeciesNames.includes(animal?.species);
    return isReversed ? `${earMark}-${markOfOrigin}` : `${markOfOrigin}-${earMark}`;
}


export const ACCOUNT_MAP = DEFAULT_ACCOUNT_MAP;

const FIELDS_BASE = {
    apiTokenBlock: [{
        key: 'apiTokenBlock',
        name: <Text id='apiKeyUsage.reason'>Reason</Text>,
        fields: [{
            name: 'reason',
            type: 'textarea',
            title: <Text id='apiKeyUsage.reason'>Reason</Text>,
            placeholder: <Text id='apiKeyUsage.reasonPlaceholder'>Explain this administrator action</Text>,
            getValue: object => object.reason || '',
        }],
    }],
    supportInternalNote: [{
        key: 'supportInternalNote',
        name: <Text id='support.staffEntry'>Staff entry</Text>,
        fields: [
            {
                title: <Text id='support.noteType'>Entry type</Text>,
                name: 'noteType',
                type: 'select',
                getValue: obj => obj.noteType || 'guidance',
                datalist: [
                    ['guidance', <Text key='guidance' id='support.internalGuidance'>Internal guidance</Text>],
                    ['third_party_request', <Text key='request' id='support.thirdPartyRequest'>
                        Request sent to third party
                    </Text>],
                    ['third_party_response', <Text key='response' id='support.thirdPartyResponse'>
                        Response received from third party
                    </Text>],
                ],
            },
            {
                title: <Text id='support.thirdParty'>Third party</Text>,
                placeholder: <Text id='support.thirdPartyPlaceholder'>Company or contact name</Text>,
                name: 'thirdParty',
                type: 'text',
                getValue: obj => obj.thirdParty || '',
            },
            {
                title: <Text id='support.correspondenceDate'>Sent / received at</Text>,
                name: 'occurredDate',
                type: 'datetime-local',
                getValue: obj => obj.occurredDate || '',
            },
            {
                title: <Text id='support.noteBody'>Content</Text>,
                name: 'body',
                type: 'textarea',
                getValue: obj => obj.body || '',
            },
        ],
    }],
    pricingSetting: [
        {
            name: <Text id='admin.pricing.section-title'>Pricing settings</Text>,
            key: 'pricingSetting',
            isAdminSection: true,
            fields: [
                {
                    title: <Text id='input.pricePerVetFirst'>Price first vet</Text>,
                    placeholder: <Text id='input.pricePerVetFirst'>Price first vet</Text>,
                    name: 'pricePerVetFirst',
                    type: 'number',
                    icon: 'fa-duotone fa-money-bill',
                    getValue: obj => obj.pricePerVetFirst || 0,
                },
                {
                    title: <Text id='input.pricePerVetAdditional'>Price additional vet</Text>,
                    placeholder: <Text id='input.pricePerVetAdditional'>Price additional vet</Text>,
                    name: 'pricePerVetAdditional',
                    type: 'number',
                    icon: 'fa-duotone fa-money-bill',
                    getValue: obj => obj.pricePerVetAdditional || 0,
                },
                {
                    title: <Text id='input.pricePerAssistant'>Price per assistant</Text>,
                    placeholder: <Text id='input.pricePerAssistant'>Price per assistant</Text>,
                    name: 'pricePerAssistant',
                    type: 'number',
                    icon: 'fa-duotone fa-money-bill',
                    getValue: obj => obj.pricePerAssistant || 0,
                },
                {
                    title: 'DyreId',
                    name: 'modulePrices.dyreid',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.dyreid || 0,
                },
                {
                    title: 'DHP',
                    name: 'modulePrices.animalia',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.animalia || 0,
                },
                {
                    title: 'Tripletex',
                    name: 'modulePrices.tripletex',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.tripletex || 0,
                },
                {
                    title: 'Fiken',
                    name: 'modulePrices.fiken',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.fiken || 0,
                },
                {
                    title: 'PowerOffice',
                    name: 'modulePrices.powerOffice',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.powerOffice || 0,
                },
                {
                    title: 'NETS',
                    name: 'modulePrices.nets',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.nets || 0,
                },
                {
                    title: 'Vipps',
                    name: 'modulePrices.vippsIntegration',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.vippsIntegration || 0,
                },
                {
                    title: 'IDEXX',
                    name: 'modulePrices.idexx',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.idexx || 0,
                },
                {
                    title: 'Breece',
                    name: 'modulePrices.breece',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.breece || 0,
                },
                {
                    title: 'Mobile Journal',
                    name: 'modulePrices.mobileJournal',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.mobileJournal || 0,
                },
                {
                    title: 'Online Booking',
                    name: 'modulePrices.onlineBooking',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.onlineBooking || 0,
                },
                {
                    title: 'Convene',
                    name: 'modulePrices.convenePayment',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.convenePayment || 0,
                },
                {
                    title: 'Veterinærfamilie (% rabatt)',
                    name: 'modulePrices.isVetFamilyMember',
                    type: 'number',
                    icon: 'fa-duotone fa-badge-percent',
                    getValue: obj => obj.modulePrices?.isVetFamilyMember || 0,
                },
                {
                    title: 'Arbeidsdeling',
                    name: 'modulePrices.workSharing',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.workSharing || 0,
                },
                {
                    title: 'Konsultasjonsmodus (mnd)',
                    name: 'modulePrices.ambientJournal',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.ambientJournal || 0,
                },
                {
                    title: 'AI-resept (mnd)',
                    name: 'modulePrices.aiPrescription',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.aiPrescription || 0,
                },
                {
                    title: 'AI-assistent (mnd)',
                    name: 'modulePrices.aiAssistant',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.aiAssistant || 0,
                },
                {
                    title: 'AI-besøk (mnd)',
                    name: 'modulePrices.aiSaleDraft',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.aiSaleDraft || 0,
                },
                {
                    title: 'AI-tilkobling (MCP)',
                    name: 'modulePrices.aiConnections',
                    type: 'number',
                    icon: 'fa-duotone fa-puzzle-piece',
                    getValue: obj => obj.modulePrices?.aiConnections || 0,
                },
                // AI usage unit prices (NOK ex VAT) — keys match AI_USAGE_UNIT_TYPES in
                // backend/lib/ai-usage.js; defaults are seeded by GET /api/pricingSettings/admin/.
                {
                    title: 'AI: Konsultasjon – lyd (kr/min)',
                    name: 'aiUsagePrices.consultationAudio',
                    type: 'number',
                    icon: 'fa-duotone fa-waveform-lines',
                    getValue: obj => obj.aiUsagePrices?.consultationAudio || 0,
                },
                {
                    title: 'AI: Konsultasjon – journalforslag (kr/kall)',
                    name: 'aiUsagePrices.consultationJournal',
                    type: 'number',
                    icon: 'fa-duotone fa-waveform-lines',
                    getValue: obj => obj.aiUsagePrices?.consultationJournal || 0,
                },
                {
                    title: 'AI: Resept – forslag (kr/kall)',
                    name: 'aiUsagePrices.prescriptionProposal',
                    type: 'number',
                    icon: 'fa-duotone fa-wand-magic-sparkles',
                    getValue: obj => obj.aiUsagePrices?.prescriptionProposal || 0,
                },
                {
                    title: 'AI: Resept – interaksjonssjekk (kr/kall)',
                    name: 'aiUsagePrices.prescriptionCheck',
                    type: 'number',
                    icon: 'fa-duotone fa-wand-magic-sparkles',
                    getValue: obj => obj.aiUsagePrices?.prescriptionCheck || 0,
                },
                {
                    title: 'AI: Assistent – Hva gjør jeg nå? (kr/kall)',
                    name: 'aiUsagePrices.criticalTasksAdvice',
                    type: 'number',
                    icon: 'fa-duotone fa-wand-magic-sparkles',
                    getValue: obj => obj.aiUsagePrices?.criticalTasksAdvice || 0,
                },
                {
                    title: 'AI: Dikter – lyd (kr/min)',
                    name: 'aiUsagePrices.dictationAudio',
                    type: 'number',
                    icon: 'fa-duotone fa-microphone',
                    getValue: obj => obj.aiUsagePrices?.dictationAudio || 0,
                },
                {
                    title: 'AI: Dikter – tekstforbedring (kr/kall)',
                    name: 'aiUsagePrices.dictationRefine',
                    type: 'number',
                    icon: 'fa-duotone fa-microphone',
                    getValue: obj => obj.aiUsagePrices?.dictationRefine || 0,
                },
                {
                    title: 'AI: Journalforslag-knapp (kr/kall)',
                    name: 'aiUsagePrices.aiJournal',
                    type: 'number',
                    icon: 'fa-duotone fa-robot',
                    getValue: obj => obj.aiUsagePrices?.aiJournal || 0,
                },
            ],
        },
    ],
    user: [
        {
            name: <Text id='input.user-most-used-admin-title'>User Most used - Admin</Text>,
            key: 'User most used - Admin',
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            isAdminSection: true,
            fields: [
                {
                    title: <Text id='input.hprNumber'>HprNumber</Text>,
                    placeholder: <Text id='input.hprNumber'>HprNumber</Text>,
                    name: 'hprNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.hprNumber || null,
                    showInList: true,
                    isAdminField: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.animaliaVeterinaryNumber'>Animalia Veterinary Number</Text>,
                    placeholder: <Text id='input.animaliaVeterinaryNumber'>Animalia Veterinary Number</Text>,
                    name: 'animaliaVeterinaryNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.animaliaVeterinaryNumber || null,
                    showInList: true,
                    isAdminField: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.code'>Code</Text>,
                    placeholder: <Text id='input.code'>Code</Text>,
                    name: 'code',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.code || null,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.firstname || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.lastname,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: true,
                    isRecommended: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphone,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.language'>Language</Text>,
                    placeholder: <Text id='input.language'>Preferred language</Text>,
                    name: 'language',
                    type: 'select',
                    icon: 'fa-solid fa-language',
                    getValue: obj => obj.language,
                    datalist: [['en', 'English'], ['no', 'Norsk']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isApproved'>IsApproved</Text>,
                    placeholder: <Text id='input.isApproved'>IsApproved</Text>,
                    name: 'isApproved',
                    type: 'toggle',
                    icon: 'fa-solid fa-thumbs-up',
                    getValue: user => user.isApproved ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isVeterinary'>IsVeterinary</Text>,
                    placeholder: <Text id='input.isVeterinary'>IsVeterinary</Text>,
                    name: 'isVeterinary',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: user => user.isVeterinary ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isCustomerAdmin'>isCustomerAdmin</Text>,
                    placeholder: <Text id='input.isCustomerAdmin'>isCustomerAdmin</Text>,
                    name: 'isCustomerAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police-tie',
                    getValue: user => user.isCustomerAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isAccountingUser'>Accounting user</Text>,
                    placeholder: <Text id='input.isAccountingUser'>Accounting user</Text>,
                    help: <Text id='input.isAccountingUser-help'>Bookkeeping/accounting user. Not counted in the per-user billing (customers do not pay for these).</Text>,
                    name: 'isAccountingUser',
                    type: 'toggle',
                    icon: 'fa-solid fa-calculator',
                    getValue: user => user.isAccountingUser ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.commissionRate'>Commission rate (%)</Text>,
                    help: <Text id='input.commissionRate-help'>Vet honorar commission percent. Blank or 0 = excluded from payout.</Text>,
                    name: 'commissionRate',
                    type: 'number',
                    icon: 'fa-duotone fa-percent',
                    getValue: user => (user.commissionRate != null ? user.commissionRate : ''),
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.commissionMva'>Commission adds MVA</Text>,
                    help: <Text id='input.commissionMva-help'>If on, 25% MVA is added to the commission (for freelance vets who invoice with MVA).</Text>,
                    name: 'commissionMva',
                    type: 'toggle',
                    icon: 'fa-duotone fa-receipt',
                    getValue: user => (user.commissionMva ? 1 : 0),
                    isAdminField: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.user-basics-title'>User basics</Text>,
            key: 'User basics',
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    help: <Text id='input.id-help'>This is the unique ID for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    help: <Text id='input.customer-help'>This is the customer.id for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.currentCustomerDepartment'>Current Customer Department</Text>,
                    placeholder: <Text id='input.currentCustomerDepartment'>Current Customer Department</Text>,
                    help: <Text id='input.currentCustomerDepartment-help'>This is the currentCustomerDepartment.id for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'currentCustomerDepartment',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.currentCustomerDepartment || null,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.foobar'>foobar</Text>,
                //     placeholder: <Text id='input.foobar'>foobar</Text>,
                //     name: 'foobar',
                //     type: 'decimal',
                //     icon: 'fa-solid fa-input-numeric',
                //     getValue: user => user.foobar || null,
                //     showInList: true,
                //     isAdminField: true,
                // },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.race || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'activities',
                    placeholder: 'activities. <14d, etc. ',
                    name: 'activities',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.activities,
                    isReadOnly: true,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'lastActivityEpoch',
                    placeholder: 'lastActivityEpoch. >1000 or <1000, etc.',
                    name: 'lastActivityEpoch',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.lastActivityEpoch ? util.isoDate(obj.lastActivityEpoch * 1000) : null,
                    isReadOnly: true,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'offline',
                    placeholder: 'offline',
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'delete',
                    placeholder: 'delete',
                    name: 'delete',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.delete,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'deleteDate',
                    placeholder: 'deleteDate',
                    name: 'deleteDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-earth-europe',
                    getValue: obj => obj.deleteDate,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.color'>Color</Text>,
                    placeholder: <Text id='input.color'>Color</Text>,
                    name: 'color',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-input-text',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.color,
                    showInList: false
                },
                {
                    title: <Text id='input.hprNumber'>HprNumber</Text>,
                    placeholder: <Text id='input.hprNumber'>HprNumber</Text>,
                    name: 'hprNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.hprNumber || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.animaliaVeterinaryNumber'>Animalia Veterinary Number</Text>,
                    placeholder: <Text id='input.animaliaVeterinaryNumber'>Animalia Veterinary Number</Text>,
                    name: 'animaliaVeterinaryNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.animaliaVeterinaryNumber || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.code'>Code</Text>,
                    placeholder: <Text id='input.code'>Code</Text>,
                    name: 'code',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.code || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.firstname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.lastname,
                    showInList: true,
                },
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    type: 'date',
                    min: '1900-01-01',
                    hideDateShortcuts: true,
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? `${util.formatDate(obj, { locale: 'nb-NO' })}` : null,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.membershipStart'>Membership start</Text>,
                    placeholder: <Text id='input.membershipStart'>Membership start</Text>,
                    name: 'membershipStart',
                    type: 'date',
                    min: '1900-01-01',
                    icon: 'fa-solid fa-id-card',
                    getValue: obj => obj.membershipStart,
                    displayValue: obj => obj ? `${util.formatDate(obj, { locale: 'nb-NO' })}` : null,
                    showInList: true,
                    hideOnCreate: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    isAdminField: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphone,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphoneShadow'>cellphoneShadow</Text>,
                    placeholder: <Text id='input.cellphoneShadow'>cellphoneShadow</Text>,
                    name: 'cellphoneShadow',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphoneShadow,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: false,
                },
                {
                    title: <Text id='input.licenseNumber'>License Number</Text>,
                    placeholder: <Text id='input.licenseNumber'>License Number</Text>,
                    name: 'licenseNumber',
                    type: 'text',
                    icon: 'fa-duotone fa-id-card',
                    getValue: obj => obj.licenseNumber,
                    adminEditAllowed: true,
                    showInList: false,
                },
                {
                    title: <Text id='input.rubicId'>Rubic Id</Text>,
                    placeholder: <Text id='input.rubicId'>Rubic Id</Text>,
                    name: 'rubicId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rubicId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.nsfId'>NSF Id</Text>,
                    placeholder: <Text id='input.nsfId'>NSF Id</Text>,
                    name: 'nsfId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.nsfId,
                    showInList: true,
                },
                {
                    title: <Text id='input.nsfClubNumber'>NSF Club Number</Text>,
                    placeholder: <Text id='input.nsfClubNumber'>NSF Club Number</Text>,
                    name: 'nsfClubNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.nsfClubNumber,
                    showInList: true,
                },
                {
                    title: <Text id='input.nsfClubId'>nsfClubId</Text>,
                    placeholder: <Text id='input.nsfClubId'>nsfClubId</Text>,
                    name: 'nsfClubId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.nsfClubId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sportsClub'>SportsClub</Text>,
                    placeholder: <Text id='input.sportsClub'>SportsClub</Text>,
                    name: 'sportsClub',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sportsClub,
                    showInList: true,
                },
                {
                    title: <Text id='input.signatureImage'>Signature</Text>,
                    placeholder: <Text id='input.signatureImage'>Signature</Text>,
                    help: <Text id='input.signatureImage-help'>Upload an image of your signature.</Text>,
                    name: 'signatureImage',
                    type: 'image',
                    icon: 'fa-duotone fa-signature',
                    getValue: obj => obj.signatureImage,
                    showInList: false,
                },
                {
                    title: <Text id='input.stampImage'>Club stamp</Text>,
                    placeholder: <Text id='input.stampImage'>Club stamp</Text>,
                    help: <Text id='input.stampImage-help'>Club officials: upload the club stamp used when signing forms.</Text>,
                    name: 'stampImage',
                    type: 'image',
                    icon: 'fa-duotone fa-stamp',
                    getValue: obj => obj.stampImage,
                    showInList: false,
                },
                {
                    title: <Text id='input.memberCardImage'>Member card image</Text>,
                    placeholder: <Text id='input.memberCardImage'>Member card image</Text>,
                    help: <Text id='input.memberCardImage-help'>Upload an image of your Rubic member card (Medlemskort).</Text>,
                    name: 'memberCardImage',
                    type: 'image',
                    icon: 'fa-duotone fa-id-card',
                    getValue: obj => obj.memberCardImage,
                    showInList: false,
                },
                {
                    title: <Text id='input.sportsAssociation'>sportsAssociation</Text>,
                    placeholder: <Text id='input.sportsAssociation'>sportsAssociation</Text>,
                    name: 'sportsAssociation',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sportsAssociation,
                    showInList: true,
                },
                {
                    title: <Text id='input.kennel'>kennel</Text>,
                    placeholder: <Text id='input.kennel'>kennel</Text>,
                    name: 'kennel',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.kennel,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-webshop-title'>User webshop</Text>,
            key: 'User webshop',
            fields: [
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.firstname || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.lastname,
                    showInList: false,
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    adminEditAllowed: true,
                    showInList: false,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphone,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: false,
                },
            ],
        },
        {
            // Minimal profile form for convention (stevne) signup — only the
            // shooting-relevant fields. Unlike 'User basics', email is editable so a
            // fresh SMS-login user can enter it themselves.
            name: <Text id='input.user-shooting-signup-title'>User shooting signup</Text>,
            key: 'User shooting signup',
            fields: [
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.firstname || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.lastname,
                    showInList: false,
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphone,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: false,
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    adminEditAllowed: true,
                    showInList: false,
                    checkValue: (obj, holdingStore) => {
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.sportsClub'>SportsClub</Text>,
                    placeholder: <Text id='input.sportsClub'>SportsClub</Text>,
                    name: 'sportsClub',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sportsClub,
                    showInList: false,
                },
                {
                    title: <Text id='input.nsfId'>NSF Id</Text>,
                    placeholder: <Text id='input.nsfId'>NSF Id</Text>,
                    name: 'nsfId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.nsfId,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-address-title'>User address</Text>,
            key: 'User address',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.address,
                    showInList: false,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalcode',
                                    value: obj.postalcode,
                                    id: obj.id,
                                });
                                updateValue({
                                    name: 'postalplace',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.postalplace'>Postalplace</Text>,
                    placeholder: <Text id='input.postalplace'>Postalplace</Text>,
                    name: 'postalplace',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalplace,
                    showInList: false,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.country,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-settings-title'>User settings</Text>,
            help: <Text id='input.user-settings-help'>Core settings for your profile.</Text>,
            key: 'User settings',
            fields: [
                {
                    title: <Text id='input.language'>Language</Text>,
                    placeholder: <Text id='input.language'>Preferred language</Text>,
                    name: 'language',
                    type: 'select',
                    icon: 'fa-solid fa-language',
                    getValue: obj => obj.language,
                    datalist: [['en', 'English'], ['no', 'Norsk']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.darkmode'>Darkmode</Text>,
                    placeholder: <Text id='input.darkmode'>Darkmode</Text>,
                    name: 'settings.darkmode',
                    type: 'toggle',
                    icon: 'fa-solid fa-moon',
                    getValue: user => user.settings?.darkmode ? 1 : 0,
                    showInList: true,
                    callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.dyrejournalContrast'>Dyrejournal contrast</Text>,
                    placeholder: <Text id='input.dyrejournalContrast'>Dyrejournal contrast</Text>,
                    help: <Text id='input.dyrejournalContrast-help'>
                        Medium strengthens text colors. High also emphasizes controls. Works with light and dark mode.
                    </Text>,
                    name: 'settings.dyrejournalContrast',
                    type: 'select',
                    icon: 'fa-solid fa-circle-half-stroke',
                    getValue: user => normalizeContrastPreference(user.settings?.dyrejournalContrast),
                    datalist: [
                        ['system', <Text key='system' id='input.contrast-system'>Follow system</Text>],
                        ['standard', <Text key='standard' id='input.contrast-standard'>Standard</Text>],
                        ['medium', <Text key='medium' id='input.contrast-medium'>Medium</Text>],
                        ['high', <Text key='high' id='input.contrast-high'>High</Text>],
                    ],
                    showInList: true,
                },
                {
                    title: <Text id='input.hue'>Hue</Text>,
                    placeholder: <Text id='input.hue'>Preferred hue</Text>,
                    name: 'settings.hue',
                    type: 'select',
                    icon: 'fa-solid fa-language',
                    getValue: user => user.settings?.hue,
                    // blue, indigo, purple, pink, red, orange, yellow, green, teal, cyan, gray
                    datalist: [['blue', 'Blue'], ['indigo', 'Indigo'], ['purple', 'Purple'], ['pink', 'Pink'], ['red', 'Red'], ['orange', 'Orange'], ['yellow', 'Yellow'], ['green', 'Green'], ['teal', 'Teal'], ['cyan', 'Cyan'], ['gray', 'Gray']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.tightmode'>tightmode</Text>,
                    placeholder: <Text id='input.tightmode'>tightmode</Text>,
                    name: 'settings.tightmode',
                    type: 'toggle',
                    icon: 'fa-solid fa-distribute-spacing-vertical',
                    getValue: user => user.settings?.tightmode ? 1 : 0,
                    showInList: true,
                    callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.imperial'>Imperial</Text>,
                    placeholder: <Text id='input.imperial'>Imperial</Text>,
                    name: 'settings.imperial',
                    type: 'toggle',
                    icon: 'fa-solid fa-earth-americas',
                    getValue: user => user.settings?.imperial ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.reduceMotion'>Reduce motion</Text>,
                    placeholder: <Text id='input.reduceMotion'>Reduce motion</Text>,
                    help: <Text id='input.reduceMotion-help'>Stops animated backgrounds, text effects and image effects in the feed.</Text>,
                    name: 'settings.reduceMotion',
                    type: 'toggle',
                    icon: 'fa-solid fa-person-walking',
                    getValue: user => user.settings?.reduceMotion ? 1 : 0,
                    showInList: true,
                    callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.twoFactorLogin'>TwoFactorLogin</Text>,
                    placeholder: <Text id='input.twoFactorLogin'>TwoFactorLogin</Text>,
                    name: 'settings.twoFactorLogin',
                    type: 'toggle',
                    icon: 'fa-solid fa-lock-a',
                    getValue: user => user.settings?.twoFactorLogin ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.supportSignature'>Support Signature</Text>,
                    placeholder: <Text id='input.supportSignature-placeholder'>Your signature for support replies</Text>,
                    help: <Text id='input.supportSignature-help'>This signature can be inserted into support ticket replies.</Text>,
                    name: 'settings.supportSignature',
                    type: 'textarea',
                    icon: 'fa-solid fa-signature',
                    getValue: user => user.settings?.supportSignature || '',
                    showInList: false,
                },
                {
                    title: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    placeholder: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    name: 'settings.backgroundColor',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-input-text',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.settings?.backgroundColor,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-race-qualifying-title'>Race qualifying</Text>,
            key: 'Race qualifying',
            fields: [
                {
                    title: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    placeholder: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    name: 'veteranRookie',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veteranRookie ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-access-title'>Simple access code</Text>,
            key: 'Simple access code',
            fields: [
                {
                    title: <Text id='input.allowSimpleAccessCode'>allowSimpleAccessCode</Text>,
                    placeholder: <Text id='input.allowSimpleAccessCode'>allowSimpleAccessCode</Text>,
                    name: 'allowSimpleAccessCode',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.allowSimpleAccessCode ? 1 : 0,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.simpleAccessCode'>simpleAccessCode</Text>,
                    placeholder: <Text id='input.simpleAccessCode'>simpleAccessCode</Text>,
                    name: 'simpleAccessCode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.simpleAccessCode,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-media-title'>Media</Text>,
            key: 'Media',
            fields: [
                {
                    title: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    placeholder: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    name: 'localNewsPaper',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.localNewsPaper,
                    showInList: false,
                },
                {
                    title: <Text id='input.okToShareInfoWithMedia'>OkToShareInfoWithMedia</Text>,
                    placeholder: <Text id='input.okToShareInfoWithMedia'>OkToShareInfoWithMedia</Text>,
                    name: 'okToShareInfoWithMedia',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.okToShareInfoWithMedia ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.rnb'>Rnb</Text>,
                    placeholder: <Text id='input.rnb'>Rnb</Text>,
                    name: 'rnb',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rnb ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.sponsors'>Sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>Sponsors</Text>,
                    name: 'sponsors',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                },
                {
                    title: <Text id='input.leadDogs'>LeadDogs</Text>,
                    placeholder: <Text id='input.leadDogs'>LeadDogs</Text>,
                    name: 'leadDogs',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.leadDogs,
                    showInList: false,
                },
                {
                    title: <Text id='input.oldMerits'>OldMerits</Text>,
                    placeholder: <Text id='input.oldMerits'>OldMerits</Text>,
                    name: 'oldMerits',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.oldMerits,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-about-title'>About yourself</Text>,
            key: 'About yourself',
            fields: [
                {
                    title: <Text id='input.occupation'>Occupation</Text>,
                    placeholder: <Text id='input.occupation'>Occupation</Text>,
                    name: 'occupation',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.occupation,
                    showInList: false,
                },

                {
                    title: <Text id='input.hobbies'>Hobbies</Text>,
                    placeholder: <Text id='input.hobbies'>Hobbies</Text>,
                    name: 'hobbies',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hobbies,
                    showInList: false,
                },
                {
                    title: <Text id='input.funFacts'>FunFacts</Text>,
                    placeholder: <Text id='input.funFacts'>FunFacts</Text>,
                    name: 'funFacts',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.funFacts,
                    showInList: false,
                },
                {
                    title: <Text id='input.ambitions'>Ambitions</Text>,
                    placeholder: <Text id='input.ambitions'>Ambitions</Text>,
                    name: 'ambitions',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ambitions,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-admin-settings-title'>User admin settings</Text>,
            help: <Text id='input.user-admin-settings-help'>Admin settings for your profile.</Text>,
            key: 'User admin settings',
            fields: [
                {
                    title: <Text id='input.isApproved'>IsApproved</Text>,
                    placeholder: <Text id='input.isApproved'>IsApproved</Text>,
                    name: 'isApproved',
                    type: 'toggle',
                    icon: 'fa-solid fa-thumbs-up',
                    getValue: user => user.isApproved ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isVeterinary'>IsVeterinary</Text>,
                    placeholder: <Text id='input.isVeterinary'>IsVeterinary</Text>,
                    name: 'isVeterinary',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: user => user.isVeterinary ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isCustomerAdmin'>isCustomerAdmin</Text>,
                    placeholder: <Text id='input.isCustomerAdmin'>isCustomerAdmin</Text>,
                    name: 'isCustomerAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police-tie',
                    getValue: user => user.isCustomerAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isAdmin'>IsAdmin</Text>,
                    placeholder: <Text id='input.isAdmin'>IsAdmin</Text>,
                    name: 'isAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isInspector'>IsInspector</Text>,
                    placeholder: <Text id='input.isInspector'>IsInspector</Text>,
                    name: 'isInspector',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isInspector ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isRaceAdmin'>isRaceAdmin</Text>,
                    placeholder: <Text id='input.isRaceAdmin'>isRaceAdmin</Text>,
                    name: 'isRaceAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isRaceAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isRaceSuperAdmin'>isRaceSuperAdmin</Text>,
                    placeholder: <Text id='input.isRaceSuperAdmin'>isRaceSuperAdmin</Text>,
                    name: 'isRaceSuperAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isRaceSuperAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isPaymentAdmin'>isPaymentAdmin</Text>,
                    placeholder: <Text id='input.isPaymentAdmin'>isPaymentAdmin</Text>,
                    name: 'isPaymentAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isPaymentAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isTicketWorker'>isTicketWorker</Text>,
                    placeholder: <Text id='input.isTicketWorker'>isTicketWorker</Text>,
                    name: 'isTicketWorker',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isTicketWorker ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isShopWorker'>isShopWorker</Text>,
                    placeholder: <Text id='input.isShopWorker'>isShopWorker</Text>,
                    name: 'isShopWorker',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isShopWorker ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isMediaWorker'>isMediaWorker</Text>,
                    placeholder: <Text id='input.isMediaWorker'>isMediaWorker</Text>,
                    name: 'isMediaWorker',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isMediaWorker ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isTimeTracker'>isTimeTracker</Text>,
                    placeholder: <Text id='input.isTimeTracker'>isTimeTracker</Text>,
                    name: 'isTimeTracker',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isTimeTracker ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isRaceLeader'>isRaceLeader</Text>,
                    placeholder: <Text id='input.isRaceLeader'>isRaceLeader</Text>,
                    name: 'isRaceLeader',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isRaceLeader ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isTdLeader'>isTdLeader</Text>,
                    placeholder: <Text id='input.isTdLeader'>isTdLeader</Text>,
                    name: 'isTdLeader',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isTdLeader ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isSoknadsguideadmin'>isSoknadsguideadmin</Text>,
                    placeholder: <Text id='input.isSoknadsguideadmin'>isSoknadsguideadmin</Text>,
                    name: 'isSoknadsguideadmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isSoknadsguideadmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isSoknadsguideSuperAdmin'>isSoknadsguideSuperAdmin</Text>,
                    placeholder: <Text id='input.isSoknadsguideSuperAdmin'>isSoknadsguideSuperAdmin</Text>,
                    name: 'isSoknadsguideSuperAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isSoknadsguideSuperAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isSupport'>isSupport</Text>,
                    placeholder: <Text id='input.isSupport'>isSupport</Text>,
                    name: 'isSupport',
                    type: 'toggle',
                    icon: 'fa-solid fa-headset',
                    getValue: user => user.isSupport ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isGpsAdmin'>isGpsAdmin</Text>,
                    placeholder: <Text id='input.isGpsAdmin'>isGpsAdmin</Text>,
                    name: 'isGpsAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-satellite-dish',
                    getValue: user => user.isGpsAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.hasValidSubscription'>hasValidSubscription</Text>,
                    placeholder: <Text id='input.hasValidSubscription'>hasValidSubscription</Text>,
                    name: 'hasValidSubscription',
                    type: 'toggle',
                    icon: 'fa-solid fa-passport',
                    getValue: user => user.hasValidSubscription ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isPremium'>isPremium</Text>,
                    placeholder: <Text id='input.isPremium'>isPremium</Text>,
                    name: 'isPremium',
                    type: 'toggle',
                    icon: 'fa-solid fa-passport',
                    getValue: user => user.isPremium ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.user-email-settings-title'>User email settings</Text>,
            help: <Text id='input.user-email-settings-help'>Email settings for your profile.</Text>,
            key: 'User email settings',
            fields: [
                {
                    title: <Text id='input.infoEmails'>InfoEmails</Text>,
                    placeholder: <Text id='input.infoEmails'>InfoEmails</Text>,
                    name: 'settings.infoEmails',
                    type: 'toggle',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.settings?.infoEmails ? 1 : 0,
                },
                {
                    title: <Text id='input.notificationEmails'>NotificationEmails</Text>,
                    placeholder: <Text id='input.notificationEmails'>NotificationEmails</Text>,
                    name: 'settings.notificationEmails',
                    type: 'toggle',
                    icon: 'fa-solid fa-envelope-dot',
                    getValue: user => user.settings?.notificationEmails ? 1 : 0,
                },
                {
                    title: <Text id='input.inboxEmails'>InboxEmails</Text>,
                    placeholder: <Text id='input.inboxEmails'>InboxEmails</Text>,
                    name: 'settings.inboxEmails',
                    type: 'toggle',
                    icon: 'fa-solid fa-envelope-circle-check',
                    getValue: user => user.settings?.inboxEmails ? 1 : 0,
                },
                {
                    title: <Text id='input.partnerEmails'>PartnerEmails</Text>,
                    placeholder: <Text id='input.partnerEmails'>PartnerEmails</Text>,
                    name: 'settings.partnerEmails',
                    type: 'toggle',
                    icon: 'fa-solid fa-envelope-open-dollar',
                    getValue: user => user.settings?.partnerEmails ? 1 : 0,
                },
                {
                    title: <Text id='input.newsletter-unsubscribe'>Unsubscribe from newsletters</Text>,
                    placeholder: <Text id='input.newsletter-unsubscribe'>Unsubscribe from newsletters</Text>,
                    help: <Text id='input.newsletter-unsubscribe-help'>When this is on, you will not receive newsletters by email.</Text>,
                    name: 'newsletterUnsubscribed',
                    type: 'toggle',
                    icon: 'fa-duotone fa-newspaper',
                    getValue: obj => (parseInt(obj.newsletterUnsubscribed, 10) === 1 ? 1 : 0),
                },
            ],
        },
        {
            name: <Text id='input.user-weapons-title'>User weapons</Text>,
            key: 'User weapons',
            fields: [
                {
                    title: <Text id='input.weapon1'>Standardpistol (25 m bane)</Text>,
                    placeholder: <Text id='input.weapon1'>Standardpistol (25 m bane)</Text>,
                    help: <Text id='input.weapon1-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 Long Rifle (5,6 mm) som tilfredsstiller kravene for standardpistol-øvelsen. Våpenspesifikasjoner: Våpenet skal være i .22 LR med maksimum 5-skudds magasin. Konkurransereglene setter bl.a. krav om minimum avtrekksvekt 1000 g, kun åpne sikter (ikke optikk), og visse begrensninger på vekt og form (f.eks. maks ca. 1400 g for pistol).</Text>,
                    name: 'weapon1',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon1 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon2'>Finpistol, 25 m (sportpistol)</Text>,
                    placeholder: <Text id='input.weapon2'>Finpistol, 25 m (sportpistol)</Text>,
                    help: <Text id='input.weapon2-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 LR (samme våpentype som i standardpistol-klassen). Våpenspesifikasjoner: Våpnene følger tilsvarende spesifikasjoner som for standardpistol (.22 LR, min. 1000 g avtrekk, kun åpne sikter, maks 5 skudd per magasin). Finpistol (også kalt sportpistol) er i praksis samme våpenkategori som standardpistol.</Text>,
                    name: 'weapon2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon2 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon3'>Silhuettpistol, 25 m (hurtigpistol)</Text>,
                    placeholder: <Text id='input.weapon3'>Silhuettpistol, 25 m (hurtigpistol)</Text>,
                    help: <Text id='input.weapon3-help'>Tillatte våpentyper: Pistol (eller evt. revolver) i kaliber .22 LR som tilfredsstiller reglementet. De fleste benytter spesialpistoler i .22 LR, da dette er en hurtigskytingsøvelse. Våpenspesifikasjoner: .22 LR pistoler med 5-skudds magasin, ofte med lav vekt og kort løp for rask skyting. Minste avtrekksvekt er 1000 g, og kun mekaniske sikter er tillatt, i tråd med ISSF-regler.</Text>,
                    name: 'weapon3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon3 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon4'>Fripistol, 50 m</Text>,
                    placeholder: <Text id='input.weapon4'>Fripistol, 50 m</Text>,
                    help: <Text id='input.weapon4-help'>Tillatte våpentyper: Enkelskudds pistol i .22 LR (5,6 mm) er typisk for fripistol. Man kan også bruke .22-våpen som i klassene over (magasinpistol/revolver), men konkurransen domineres av spesialdesignede fripistoler. Våpenspesifikasjoner: Fripistol (50 m pistol) har det mest åpne regelverket: kaliber .22 LR, maks én skudd per ladning (ingen magasin), ellers svært få begrensninger. Pistolen kan ha svært lang pipe og anatomisk grep, med svært lav avtrekksvekt (ofte 50–100 g). Det er ingen vekt- eller dimensjonsbegrensning bortsett fra at grep ikke kan støtte armen.</Text>,
                    name: 'weapon4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon4 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon5'>Grovpistol, 25 m</Text>,
                    placeholder: <Text id='input.weapon5'>Grovpistol, 25 m</Text>,
                    help: <Text id='input.weapon5-help'>Tillatte våpentyper: Pistol eller revolver i centerfire-kaliber 7,62 mm opp til 9,65 mm. Dette tilsvarer omtrent .30–.38 caliber (f.eks. .32 S&W Long, .38 Special WC, 9×19 mm). Våpenspesifikasjoner: Våpenet må følge reglementet for 25 m grovpistol (ISSF centerfire). Vanligvis brukes spesialpistoler i .32 S&W Long eller .38 wadcutter for presisjon, men 9 mm pistoler er også tillatt. Minste avtrekksvekt er 1360 g (ifølge ISSF-regler for grovpistol), og kun åpne sikter er tillatt.</Text>,
                    name: 'weapon5',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon5 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon6'>Hurtigpistol grov (25 m hurtigskyting)</Text>,
                    placeholder: <Text id='input.weapon6'>Hurtigpistol grov (25 m hurtigskyting)</Text>,
                    help: <Text id='input.weapon6-help'>Tillatte våpentyper: Pistol eller revolver i kaliber 7,62–9,65 mm (samme som for grovpistol). De fleste bruker 9×19 mm eller .38/.357 revolver i grov hurtigskyting. Våpenspesifikasjoner: Våpenkravene speiler grovpistol-klassen (typisk 5 skudds magasin/tønne, min. ~1360 g avtrekk, åpne sikter). På grunn av hurtigskytingens natur velger mange pistoler med kort løp og god balanse for rask omlading og siktebilder.</Text>,
                    name: 'weapon6',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon6 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon7'>Finfelt (feltpistol, fin)</Text>,
                    placeholder: <Text id='input.weapon7'>Finfelt (feltpistol, fin)</Text>,
                    help: <Text id='input.weapon7-help'>Tillatte våpentyper: Pistol eller revolver i .22 LR kaliber. Dette er samme våpenkategori som .22-baneøvelsene, men brukt til feltskyting. Våpenspesifikasjoner: .22 LR pistoler/revolvere i finfelt må tilfredsstille feltskytingens krav til sikkerhet og funksjon (f.eks. pålitelighet for variasjon i temperatur, robuste sikter). Det er ofte ingen strenge begrensninger på vekt/avtrekk ut over at våpenet må være trygg og håndterlig i feltløypa.</Text>,
                    name: 'weapon7',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon7 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon8'>Grovfelt</Text>,
                    placeholder: <Text id='input.weapon8'>Grovfelt</Text>,
                    help: <Text id='input.weapon8-help'>Tillatte våpentyper: Pistol eller revolver i kaliber 7,62 mm til 9,65 mm (centerfire, tilsvarende .32 til .38/9 mm). Våpenspesifikasjoner: Grovfelt-våpen er typisk robuste 9 mm pistoler eller .38/.357 revolvere som tåler feltbruk. Må oppfylle feltskytingens krav (f.eks. sikre funksjon ved rask omlading, ingen skarpe kanter som kan henge seg opp, etc.). Magasinkapasitet er vanligvis 5 skudd i feltløypa.</Text>,
                    name: 'weapon8',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon8 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon9'>Militærfelt</Text>,
                    placeholder: <Text id='input.weapon9'>Militærfelt</Text>,
                    help: <Text id='input.weapon9-help'>Tillatte våpentyper: Pistol (halvautomat) i kaliber 9 mm til 11,65 mm. Dette er grovkalibrede tjenestepistoler – typisk 9×19, .40 S&W, .45 ACP etc – som brukes i militærfelt. (Revolver hører til egen revolverklasse.) Våpenspesifikasjoner: Militærfelt-klassens våpen er ofte vanlige tjenestepistoler/krigsvåpen (f.eks. 9 mm Glock, SIG, CZ eller .45 1911). Reglementet kan kreve minst 9×19 mm kaliber, og kun åpne sikter. Våpnene må være sikre og egnet for hurtig feltløype-skyting (magasinbytte, enhåndsbruk osv.).</Text>,
                    name: 'weapon9',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon9 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon10'>Revolverfelt</Text>,
                    placeholder: <Text id='input.weapon10'>Revolverfelt</Text>,
                    help: <Text id='input.weapon10-help'>Tillatte våpentyper: Revolver i kaliber 9 mm til 11,65 mm. Dette omfatter typisk .38/.357 Magnum, .44 Special/.44 Magnum, .45 Colt etc., så lenge revolveren oppfyller reglene. Våpenspesifikasjoner: Revolverfelt-våpen er kraftige revolvere med tønnekapasitet 5–6 skudd. Det er ingen kaliberbegrensning opp til .45 (11,65 mm). Revolveren må brukes med double-action avtrekk i feltløypa (ifølge reglementet for revolverfelt). Kun åpne sikter er tillatt, og ofte brukes 4–6” løpslengde for balanse mellom presisjon og hurtighet.</Text>,
                    name: 'weapon10',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon10 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon11'>Magnum 1 (felt)</Text>,
                    placeholder: <Text id='input.weapon11'>Magnum 1 (felt)</Text>,
                    help: <Text id='input.weapon11-help'>Tillatte våpentyper: Pistol eller revolver i magnumkaliber fra .357 (9,07 mm) opp til .409” (≈10,4 mm). Dette inkluderer f.eks. .357 Magnum, .41 Magnum og .44 Special (.429”, som faller i Magnum 2 hvis ladet som Magnum). Våpenspesifikasjoner: Magnum 1-våpen er ofte revolvere kamret for .357 Magnum, eventuelt kraftige pistoler i .357 SIG, .40 S&W eller 10 mm Auto (10,2 mm). Rekyl og munningsvipp er betydelig, så våpnene bør ha robust konstruksjon. Maks kaliber .409” betyr .41 Magnum er øvre grense i denne klassen.</Text>,
                    name: 'weapon11',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon11 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon12'>Magnum 2 (felt)</Text>,
                    placeholder: <Text id='input.weapon12'>Magnum 2 (felt)</Text>,
                    help: <Text id='input.weapon12-help'>Tillatte våpentyper: Pistol eller revolver i stor magnumkaliber fra .410” (10,4 mm) opp til .505” (12,8 mm). Denne klassen dekker de virkelig kraftige håndvåpenkalibre, f.eks. .44 Magnum (.429”), .454 Casull (.45), .500 S&W (.50) og lignende. Våpenspesifikasjoner: Magnum 2-våpen er typisk tunge revolvere (f.eks. Smith & Wesson Model 29 i .44 Magnum, eller S&W .500) og enkelte store pistoler (f.eks. Desert Eagle i .50 AE). Våpnene har svært kraftig rekyl, så reglene tillater tohåndsgrep i Magnum-klassene. Ingen øvre grense på størrelse bortsett fra kaliber ≤ .505. Det presiseres at kalibre som .50 AE (12,7 mm) og .500 S&W (12,7 mm) er lovlige i Magnum 2.</Text>,
                    name: 'weapon12',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon12 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon13'>Spesialpistol (felt)</Text>,
                    placeholder: <Text id='input.weapon13'>Spesialpistol (felt)</Text>,
                    help: <Text id='input.weapon13-help'>Tillatte våpentyper: Pistol i valgfritt kaliber mellom .22 og .45 (5,6–11,5 mm), forutsatt at våpenet er lite nok til å gå i spesialpistolens målekasse. Våpenspesifikasjoner: Spesialpistol er en felt-klasse for små pistoler. Våpenet (uladd) må veie ≤ 800 gram og passe i en kasse på 185 × 125 × 35 mm. Dette tilsvarer typisk kompakte pistoler (“backup-guns”/skjulte bære-våpen). Kaliber kan være alt fra .22 LR til .45 ACP, men rekylen i de tyngre kalibrene er utfordrende i så lette våpen. Vanlige modeller i klassen er kompakte 9 mm pistoler og .380/.32 pistoler.</Text>,
                    name: 'weapon13',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon13 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon14'>Spesialrevolver (felt)</Text>,
                    placeholder: <Text id='input.weapon14'>Spesialrevolver (felt)</Text>,
                    help: <Text id='input.weapon14-help'>Tillatte våpentyper: Revolver i valgfritt kaliber .22 til .45, som får plass i spesialrevolver-målekassen. Våpenspesifikasjoner: Spesialrevolver er en klasse for små revolvere (f.eks. korte “snubnose”-revolvere). Vekten uladd kan være maks 1000 g, og maks ytre mål er 200 × 150 × 40 mm. Dette tilsvarer typisk 2–3” løpslengde revolvere i .38 Special eller .22 LR. Kaliber opp til .45 er tillatt, men en .44 Magnum i denne størrelsen vil være uvanlig pga. rekyl og størrelse. Oftest brukes 5-skudds .38/.357 revolvere i spesialrevolver-klassen.</Text>,
                    name: 'weapon14',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon14 || null,
                    showInList: false,
                },
                // {
                //     title: <Text id='input.weapon15'></Text>,
                //     placeholder: <Text id='input.weapon15'></Text>,
                //     help: <Text id='input.weapon15-help'></Text>,
                //     name: 'weapon15',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: user => user.weapon15 || null,
                //     showInList: false,
                // },



                // {
                //     title: <Text id='input.reserveWeapon1'>Reserve Standardpistol (25 m bane)</Text>,
                //     placeholder: <Text id='input.reserveWeapon1'>Reserve Standardpistol (25 m bane)</Text>,
                //     help: <Text id='input.reserveWeapon1-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 Long Rifle (5,6 mm) som tilfredsstiller kravene for standardpistol-øvelsen. Våpenspesifikasjoner: Våpenet skal være i .22 LR med maksimum 5-skudds magasin. Konkurransereglene setter bl.a. krav om minimum avtrekksvekt 1000 g, kun åpne sikter (ikke optikk), og visse begrensninger på vekt og form (f.eks. maks ca. 1400 g for pistol).</Text>,
                //     name: 'reserveWeapon1',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: user => user.reserveWeapon1 || null,
                //     showInList: false,
                // },
            ],
        },
        {
            name: <Text id='input.user-reserve-weapons-title'>User reserve weapons</Text>,
            key: 'User reserve weapons',
            isCollapsed: true,
            fields: [
                {
                    title: <Text id='input.reserveWeapon1'>Standardpistol (25 m bane)</Text>,
                    placeholder: <Text id='input.reserveWeapon1'>Standardpistol (25 m bane)</Text>,
                    help: <Text id='input.reserveWeapon1-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 Long Rifle (5,6 mm) som tilfredsstiller kravene for standardpistol-øvelsen. Våpenspesifikasjoner: Våpenet skal være i .22 LR med maksimum 5-skudds magasin. Konkurransereglene setter bl.a. krav om minimum avtrekksvekt 1000 g, kun åpne sikter (ikke optikk), og visse begrensninger på vekt og form (f.eks. maks ca. 1400 g for pistol).</Text>,
                    name: 'reserveWeapon1',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon1 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon2'>Finpistol, 25 m (sportpistol)</Text>,
                    placeholder: <Text id='input.reserveWeapon2'>Finpistol, 25 m (sportpistol)</Text>,
                    help: <Text id='input.reserveWeapon2-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 LR (samme våpentype som i standardpistol-klassen). Våpenspesifikasjoner: Våpnene følger tilsvarende spesifikasjoner som for standardpistol (.22 LR, min. 1000 g avtrekk, kun åpne sikter, maks 5 skudd per magasin). Finpistol (også kalt sportpistol) er i praksis samme våpenkategori som standardpistol.</Text>,
                    name: 'reserveWeapon2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon2 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon3'>Silhuettpistol, 25 m (hurtigpistol)</Text>,
                    placeholder: <Text id='input.reserveWeapon3'>Silhuettpistol, 25 m (hurtigpistol)</Text>,
                    help: <Text id='input.reserveWeapon3-help'>Tillatte våpentyper: Pistol (eller evt. revolver) i kaliber .22 LR som tilfredsstiller reglementet. De fleste benytter spesialpistoler i .22 LR, da dette er en hurtigskytingsøvelse. Våpenspesifikasjoner: .22 LR pistoler med 5-skudds magasin, ofte med lav vekt og kort løp for rask skyting. Minste avtrekksvekt er 1000 g, og kun mekaniske sikter er tillatt, i tråd med ISSF-regler.</Text>,
                    name: 'reserveWeapon3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon3 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon4'>Fripistol, 50 m</Text>,
                    placeholder: <Text id='input.reserveWeapon4'>Fripistol, 50 m</Text>,
                    help: <Text id='input.reserveWeapon4-help'>Tillatte våpentyper: Enkelskudds pistol i .22 LR (5,6 mm) er typisk for fripistol. Man kan også bruke .22-våpen som i klassene over (magasinpistol/revolver), men konkurransen domineres av spesialdesignede fripistoler. Våpenspesifikasjoner: Fripistol (50 m pistol) har det mest åpne regelverket: kaliber .22 LR, maks én skudd per ladning (ingen magasin), ellers svært få begrensninger. Pistolen kan ha svært lang pipe og anatomisk grep, med svært lav avtrekksvekt (ofte 50–100 g). Det er ingen vekt- eller dimensjonsbegrensning bortsett fra at grep ikke kan støtte armen.</Text>,
                    name: 'reserveWeapon4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon4 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon5'>Grovpistol, 25 m</Text>,
                    placeholder: <Text id='input.reserveWeapon5'>Grovpistol, 25 m</Text>,
                    help: <Text id='input.reserveWeapon5-help'>Tillatte våpentyper: Pistol eller revolver i centerfire-kaliber 7,62 mm opp til 9,65 mm. Dette tilsvarer omtrent .30–.38 caliber (f.eks. .32 S&W Long, .38 Special WC, 9×19 mm). Våpenspesifikasjoner: Våpenet må følge reglementet for 25 m grovpistol (ISSF centerfire). Vanligvis brukes spesialpistoler i .32 S&W Long eller .38 wadcutter for presisjon, men 9 mm pistoler er også tillatt. Minste avtrekksvekt er 1360 g (ifølge ISSF-regler for grovpistol), og kun åpne sikter er tillatt.</Text>,
                    name: 'reserveWeapon5',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon5 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon6'>Hurtigpistol grov (25 m hurtigskyting)</Text>,
                    placeholder: <Text id='input.reserveWeapon6'>Hurtigpistol grov (25 m hurtigskyting)</Text>,
                    help: <Text id='input.reserveWeapon6-help'>Tillatte våpentyper: Pistol eller revolver i kaliber 7,62–9,65 mm (samme som for grovpistol). De fleste bruker 9×19 mm eller .38/.357 revolver i grov hurtigskyting. Våpenspesifikasjoner: Våpenkravene speiler grovpistol-klassen (typisk 5 skudds magasin/tønne, min. ~1360 g avtrekk, åpne sikter). På grunn av hurtigskytingens natur velger mange pistoler med kort løp og god balanse for rask omlading og siktebilder.</Text>,
                    name: 'reserveWeapon6',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon6 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon7'>Finfelt (feltpistol, fin)</Text>,
                    placeholder: <Text id='input.reserveWeapon7'>Finfelt (feltpistol, fin)</Text>,
                    help: <Text id='input.reserveWeapon7-help'>Tillatte våpentyper: Pistol eller revolver i .22 LR kaliber. Dette er samme våpenkategori som .22-baneøvelsene, men brukt til feltskyting. Våpenspesifikasjoner: .22 LR pistoler/revolvere i finfelt må tilfredsstille feltskytingens krav til sikkerhet og funksjon (f.eks. pålitelighet for variasjon i temperatur, robuste sikter). Det er ofte ingen strenge begrensninger på vekt/avtrekk ut over at våpenet må være trygg og håndterlig i feltløypa.</Text>,
                    name: 'reserveWeapon7',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon7 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon8'>Grovfelt</Text>,
                    placeholder: <Text id='input.reserveWeapon8'>Grovfelt</Text>,
                    help: <Text id='input.reserveWeapon8-help'>Tillatte våpentyper: Pistol eller revolver i kaliber 7,62 mm til 9,65 mm (centerfire, tilsvarende .32 til .38/9 mm). Våpenspesifikasjoner: Grovfelt-våpen er typisk robuste 9 mm pistoler eller .38/.357 revolvere som tåler feltbruk. Må oppfylle feltskytingens krav (f.eks. sikre funksjon ved rask omlading, ingen skarpe kanter som kan henge seg opp, etc.). Magasinkapasitet er vanligvis 5 skudd i feltløypa.</Text>,
                    name: 'reserveWeapon8',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon8 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon9'>Militærfelt</Text>,
                    placeholder: <Text id='input.reserveWeapon9'>Militærfelt</Text>,
                    help: <Text id='input.reserveWeapon9-help'>Tillatte våpentyper: Pistol (halvautomat) i kaliber 9 mm til 11,65 mm. Dette er grovkalibrede tjenestepistoler – typisk 9×19, .40 S&W, .45 ACP etc – som brukes i militærfelt. (Revolver hører til egen revolverklasse.) Våpenspesifikasjoner: Militærfelt-klassens våpen er ofte vanlige tjenestepistoler/krigsvåpen (f.eks. 9 mm Glock, SIG, CZ eller .45 1911). Reglementet kan kreve minst 9×19 mm kaliber, og kun åpne sikter. Våpnene må være sikre og egnet for hurtig feltløype-skyting (magasinbytte, enhåndsbruk osv.).</Text>,
                    name: 'reserveWeapon9',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon9 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon10'>Revolverfelt</Text>,
                    placeholder: <Text id='input.reserveWeapon10'>Revolverfelt</Text>,
                    help: <Text id='input.reserveWeapon10-help'>Tillatte våpentyper: Revolver i kaliber 9 mm til 11,65 mm. Dette omfatter typisk .38/.357 Magnum, .44 Special/.44 Magnum, .45 Colt etc., så lenge revolveren oppfyller reglene. Våpenspesifikasjoner: Revolverfelt-våpen er kraftige revolvere med tønnekapasitet 5–6 skudd. Det er ingen kaliberbegrensning opp til .45 (11,65 mm). Revolveren må brukes med double-action avtrekk i feltløypa (ifølge reglementet for revolverfelt). Kun åpne sikter er tillatt, og ofte brukes 4–6” løpslengde for balanse mellom presisjon og hurtighet.</Text>,
                    name: 'reserveWeapon10',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon10 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon11'>Magnum 1 (felt)</Text>,
                    placeholder: <Text id='input.reserveWeapon11'>Magnum 1 (felt)</Text>,
                    help: <Text id='input.reserveWeapon11-help'>Tillatte våpentyper: Pistol eller revolver i magnumkaliber fra .357 (9,07 mm) opp til .409” (≈10,4 mm). Dette inkluderer f.eks. .357 Magnum, .41 Magnum og .44 Special (.429”, som faller i Magnum 2 hvis ladet som Magnum). Våpenspesifikasjoner: Magnum 1-våpen er ofte revolvere kamret for .357 Magnum, eventuelt kraftige pistoler i .357 SIG, .40 S&W eller 10 mm Auto (10,2 mm). Rekyl og munningsvipp er betydelig, så våpnene bør ha robust konstruksjon. Maks kaliber .409” betyr .41 Magnum er øvre grense i denne klassen.</Text>,
                    name: 'reserveWeapon11',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon11 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon12'>Magnum 2 (felt)</Text>,
                    placeholder: <Text id='input.reserveWeapon12'>Magnum 2 (felt)</Text>,
                    help: <Text id='input.reserveWeapon12-help'>Tillatte våpentyper: Pistol eller revolver i stor magnumkaliber fra .410” (10,4 mm) opp til .505” (12,8 mm). Denne klassen dekker de virkelig kraftige håndvåpenkalibre, f.eks. .44 Magnum (.429”), .454 Casull (.45), .500 S&W (.50) og lignende. Våpenspesifikasjoner: Magnum 2-våpen er typisk tunge revolvere (f.eks. Smith & Wesson Model 29 i .44 Magnum, eller S&W .500) og enkelte store pistoler (f.eks. Desert Eagle i .50 AE). Våpnene har svært kraftig rekyl, så reglene tillater tohåndsgrep i Magnum-klassene. Ingen øvre grense på størrelse bortsett fra kaliber ≤ .505. Det presiseres at kalibre som .50 AE (12,7 mm) og .500 S&W (12,7 mm) er lovlige i Magnum 2.</Text>,
                    name: 'reserveWeapon12',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon12 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon13'>Spesialpistol (felt)</Text>,
                    placeholder: <Text id='input.reserveWeapon13'>Spesialpistol (felt)</Text>,
                    help: <Text id='input.reserveWeapon13-help'>Tillatte våpentyper: Pistol i valgfritt kaliber mellom .22 og .45 (5,6–11,5 mm), forutsatt at våpenet er lite nok til å gå i spesialpistolens målekasse. Våpenspesifikasjoner: Spesialpistol er en felt-klasse for små pistoler. Våpenet (uladd) må veie ≤ 800 gram og passe i en kasse på 185 × 125 × 35 mm. Dette tilsvarer typisk kompakte pistoler (“backup-guns”/skjulte bære-våpen). Kaliber kan være alt fra .22 LR til .45 ACP, men rekylen i de tyngre kalibrene er utfordrende i så lette våpen. Vanlige modeller i klassen er kompakte 9 mm pistoler og .380/.32 pistoler.</Text>,
                    name: 'reserveWeapon13',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon13 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon14'>Spesialrevolver (felt)</Text>,
                    placeholder: <Text id='input.reserveWeapon14'>Spesialrevolver (felt)</Text>,
                    help: <Text id='input.reserveWeapon14-help'>Tillatte våpentyper: Revolver i valgfritt kaliber .22 til .45, som får plass i spesialrevolver-målekassen. Våpenspesifikasjoner: Spesialrevolver er en klasse for små revolvere (f.eks. korte “snubnose”-revolvere). Vekten uladd kan være maks 1000 g, og maks ytre mål er 200 × 150 × 40 mm. Dette tilsvarer typisk 2–3” løpslengde revolvere i .38 Special eller .22 LR. Kaliber opp til .45 er tillatt, men en .44 Magnum i denne størrelsen vil være uvanlig pga. rekyl og størrelse. Oftest brukes 5-skudds .38/.357 revolvere i spesialrevolver-klassen.</Text>,
                    name: 'reserveWeapon14',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon14 || null,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-images-file-links-title'>Images, files and links</Text>,
            key: 'Images, files and links',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
            ],
        },
    ],
    customer: [
        {
            name: <Text id='input.customer-most-used-admin-title'>Customer Most used - Admin</Text>,
            key: 'Customer most used - Admin',
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            isAdminSection: true,
            fields: [
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.name || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.orgNumber'>orgNumber</Text>,
                    placeholder: <Text id='input.orgNumber'>orgNumber</Text>,
                    name: 'orgNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.orgNumber || null,
                    showInList: true,
                    isRecommended: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.orgNumber && !extractOrgNumber(obj.orgNumber)) {
                            holdingStore.setFeedbackError('orgNumber', 'Invalid orgNumber. Must contain 9 digits. Extra text such as "MVA" is allowed.');
                        } else {
                            holdingStore.setFeedbackError('orgNumber', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        holdingStore.setFeedback('orgNumber', '');
                        holdingStore.setFeedbackButton('orgNumber', null, null);
                    },
                    loadCallback: async (obj, updateValue, holdingStore) => {
                        await runBrregLookup({
                            field: 'orgNumber',
                            obj,
                            updateValue,
                            holdingStore,
                            buildTargetMap: (data) => buildBrregMapForPrimary(data, { phoneField: 'phone' }),
                            calledFrom: 'customer:orgNumber',
                        });
                    },
                    callback: async (obj, updateValue, holdingStore) => {
                        await runBrregLookup({
                            field: 'orgNumber',
                            obj,
                            updateValue,
                            holdingStore,
                            buildTargetMap: (data) => buildBrregMapForPrimary(data, { phoneField: 'phone' }),
                            calledFrom: 'customer:orgNumber',
                        });
                    },
                },
                {
                    title: <Text id='input.billingParent'>Billing parent (customer no.)</Text>,
                    placeholder: <Text id='input.billingParent'>Billing parent (customer no.)</Text>,
                    help: <Text id='input.billingParent-help'>Customer number of the clinic that invoices this one. Income rolls up to that parent. Leave empty for none.</Text>,
                    name: 'billingParent',
                    type: 'number',
                    icon: 'fa-duotone fa-file-invoice',
                    getValue: obj => obj.billingParent || 0,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.customerNumber'>CustomerNumber</Text>,
                    placeholder: <Text id='input.customerNumber'>CustomerNumber</Text>,
                    name: 'customerNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.customerNumber || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.invoicingActive'>invoicingActive</Text>,
                    placeholder: <Text id='input.invoicingActive'>invoicingActive</Text>,
                    name: 'invoicingActive',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: customer => customer.invoicingActive ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.emailSender'>emailSender</Text>,
                    placeholder: <Text id='input.emailSender'>emailSender</Text>,
                    help: <Text id='input.emailSender-help'>Must be an approved sender in the system. If you are unsure, please do NOT change this.</Text>,
                    showDanger: true,
                    name: 'emailSender',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.emailSender,
                    isRecommended: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.emailSender && !util.validateEmail(obj.emailSender)) {
                            const invalidChars = findInvalidEmailChars(obj.emailSender);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('emailSender', `Invalid emailSender. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('emailSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('emailSender', '');
                        holdingStore.setFeedbackButton('emailSender', null, null);
                    },
                },
                {
                    title: <Text id='input.emailSenderOnlyAsReplyTo'>emailSenderOnlyAsReplyTo</Text>,
                    placeholder: <Text id='input.emailSenderOnlyAsReplyTo'>emailSenderOnlyAsReplyTo</Text>,
                    name: 'emailSenderOnlyAsReplyTo',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: customer => customer.emailSenderOnlyAsReplyTo ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.smsBookingSmsDaysBefore'>Booking SMS Days before</Text>,
                    placeholder: <Text id='input.smsBookingSmsDaysBefore'>Send Booking SMS days before booking date.</Text>,
                    help: <Text id='input.smsBookingSmsDaysBefore-help'>Default is 1 calendar day before the booking date. </Text>,
                    name: 'smsBookingSmsDaysBefore',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsBookingSmsDaysBefore || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsNotificationDaysBefore'>Recall SMS days before</Text>,
                    placeholder: <Text id='input.smsNotificationDaysBefore-placeholder'>Send recall SMS days before the recall date.</Text>,
                    help: <Text id='input.smsNotificationDaysBefore-help'>How many days before the recall date the recall SMS (innkalling) is sent. Default is 14 calendar days.</Text>,
                    name: 'smsNotificationDaysBefore',
                    type: 'number',
                    min: 1,
                    max: 365,
                    step: 1,
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsNotificationDaysBefore || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.notificationEmailEnabled'>Recall by email</Text>,
                    placeholder: <Text id='input.notificationEmailEnabled-placeholder'>Also send recalls by email</Text>,
                    help: <Text id='input.notificationEmailEnabled-help'>Also send recalls (innkallinger) by email to owners with an email address. SMS is still sent as before.</Text>,
                    name: 'notificationEmailEnabled',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope',
                    getValue: customer => customer.notificationEmailEnabled ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.notificationEmailOnly'>Recall by email only</Text>,
                    placeholder: <Text id='input.notificationEmailOnly-placeholder'>Send recalls by email only, not SMS</Text>,
                    help: <Text id='input.notificationEmailOnly-help'>Send recalls (innkallinger) by email only, not SMS. Owners without a valid email address still get the SMS.</Text>,
                    name: 'notificationEmailOnly',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope-circle-check',
                    getValue: customer => customer.notificationEmailOnly ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsSendingHourDaily'>SMS Send Booking SMS Daily at hour</Text>,
                    placeholder: <Text id='input.smsSendingHourDaily'>SMS Send Booking SMS Daily at hour</Text>,
                    help: <Text id='input.smsSendingHourDaily-help'>Whole hour of the day (0-23) when reminders and recalls are sent. Enter only the hour, e.g. 15 for 15:00 (not 1500).</Text>,
                    name: 'smsSendingHourDaily',
                    type: 'number',
                    min: 0,
                    max: 23,
                    step: 1,
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsSendingHourDaily || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsSender'>smsSender</Text>,
                    placeholder: <Text id='input.smsSender'>smsSender</Text>,
                    help: <Text id='input.smsSender-help'>Must follow these rules: Alphanumeric. Maximum of 11 characters. No spaces. No special characters. The sender ID must be the brand name of the company sending the SMS. If you are unsure, please do NOT change this.</Text>,
                    maxLength: 11,
                    showDanger: true,
                    name: 'smsSender',
                    maxLength: 11,
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.smsSender,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    isRecommended: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.smsSender && !/^[a-zA-Z0-9]+$/.test(obj.smsSender)) {
                            holdingStore.setFeedbackError('smsSender', 'Invalid smsSender. Must be 11 digits.');
                        } else {
                            holdingStore.setFeedbackError('smsSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('smsSender', '');
                        holdingStore.setFeedbackButton('smsSender', null, null);
                    },
                },
                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: customer => customer.members?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.holidayPlanner'>Holiday Planner Access</Text>,
                    placeholder: <Text id='input.holidayPlanner'>Holiday Planner Access</Text>,
                    name: 'holidayPlanner',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-umbrella-beach',
                    searchMethod: 'searchUsersAll',
                    getValue: customer => customer.holidayPlanner?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.tvRadioProducer'>TV/Radio Producer</Text>,
                    placeholder: <Text id='input.tvRadioProducer'>TV/Radio Producer</Text>,
                    name: 'tvRadioProducer',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-broadcast-tower',
                    searchMethod: 'searchUsersAll',
                    getValue: customer => customer.tvRadioProducer?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.departments'>Departments</Text>,
                    placeholder: <Text id='input.departments'>Departments</Text>,
                    name: 'departments',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminDepartments',
                    getValue: customer => customer.departments?.map(d => d),
                    datalist: [
                        'id', 'name'
                    ],
                    // displayValue: departments => <>
                    //     <div class='text-start px-2'>
                    //         {departments?.map((obj, index) => <>
                    //             {index > 0 && <br />}
                    //             <span class='font-weight-lighter'>{obj.id}:</span> {obj.name}<br />
                    //         </>)}
                    //     </div>
                    // </>,
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.id}: {obj.name}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
        {
            name: <Text id='input.customer-basics-title'>Customer basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Customer basics',
            fields: [
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                },
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.id || null,
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.hostname'>hostname</Text>,
                    placeholder: <Text id='input.hostname'>hostname</Text>,
                    name: 'hostname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.hostname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.hostname2'>hostname2</Text>,
                    placeholder: <Text id='input.hostname2'>hostname2</Text>,
                    name: 'hostname2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.hostname2 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.color'>Color</Text>,
                    placeholder: <Text id='input.color'>Color</Text>,
                    name: 'color',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-input-text',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.color,
                    showInList: false
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.name || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.orgNumber'>orgNumber</Text>,
                    placeholder: <Text id='input.orgNumber'>orgNumber</Text>,
                    name: 'orgNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.orgNumber || null,
                    showInList: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.orgNumber && !extractOrgNumber(obj.orgNumber)) {
                            holdingStore.setFeedbackError('orgNumber', 'Invalid orgNumber. Must contain 9 digits. Extra text such as "MVA" is allowed.');
                        } else {
                            holdingStore.setFeedbackError('orgNumber', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        holdingStore.setFeedback('orgNumber', '');
                        holdingStore.setFeedbackButton('orgNumber', null, null);
                    },
                    loadCallback: async (obj, updateValue, holdingStore) => {
                        await runBrregLookup({
                            field: 'orgNumber',
                            obj,
                            updateValue,
                            holdingStore,
                            buildTargetMap: (data) => buildBrregMapForPrimary(data, { phoneField: 'phone' }),
                            calledFrom: 'customer:orgNumber',
                        });
                    },
                    callback: async (obj, updateValue, holdingStore) => {
                        await runBrregLookup({
                            field: 'orgNumber',
                            obj,
                            updateValue,
                            holdingStore,
                            buildTargetMap: (data) => buildBrregMapForPrimary(data, { phoneField: 'phone' }),
                            calledFrom: 'customer:orgNumber',
                        });
                    },
                },
                {
                    title: <Text id='input.name2'>Name 2</Text>,
                    placeholder: <Text id='input.name2'>Name 2</Text>,
                    name: 'name2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.name2 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.orgNumber2'>orgNumber 2</Text>,
                    placeholder: <Text id='input.orgNumber2'>orgNumber 2</Text>,
                    name: 'orgNumber2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.orgNumber2 || null,
                    showInList: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.orgNumber2 && !extractOrgNumber(obj.orgNumber2)) {
                            holdingStore.setFeedbackError('orgNumber2', 'Invalid orgNumber2. Must contain 9 digits. Extra text such as "MVA" is allowed.');
                        } else {
                            holdingStore.setFeedbackError('orgNumber2', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        holdingStore.setFeedback('orgNumber2', '');
                        holdingStore.setFeedbackButton('orgNumber2', null, null);
                    },
                    loadCallback: async (obj, updateValue, holdingStore) => {
                        await runBrregLookup({
                            field: 'orgNumber2',
                            obj,
                            updateValue,
                            holdingStore,
                            buildTargetMap: buildBrregMapForSecondary,
                            calledFrom: 'customer:orgNumber2',
                        });
                    },
                    callback: async (obj, updateValue, holdingStore) => {
                        await runBrregLookup({
                            field: 'orgNumber2',
                            obj,
                            updateValue,
                            holdingStore,
                            buildTargetMap: buildBrregMapForSecondary,
                            calledFrom: 'customer:orgNumber2',
                        });
                    },
                },
                {
                    title: <Text id='input.customerNumber'>CustomerNumber</Text>,
                    placeholder: <Text id='input.customerNumber'>CustomerNumber</Text>,
                    name: 'customerNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.customerNumber || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.invoicingActive'>invoicingActive</Text>,
                    placeholder: <Text id='input.invoicingActive'>invoicingActive</Text>,
                    name: 'invoicingActive',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: customer => customer.invoicingActive ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: customer => customer.description,
                },
                {
                    title: <Text id='input.internalComment'>internalComment</Text>,
                    placeholder: <Text id='input.internalComment'>internalComment</Text>,
                    name: 'internalComment',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: customer => customer.internalComment,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.address,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.postalcode,
                    showInList: true,
                    isRecommended: true,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalcode',
                                    value: obj.postalcode,
                                    id: obj.id,
                                });
                                updateValue({
                                    name: 'place',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.place'>Place</Text>,
                    placeholder: <Text id='input.place'>Place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.place,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-earth-europe',
                    getValue: customer => customer.country,
                    showInList: true,
                },
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: customer => customer.url,
                },
                {
                    title: <Text id='input.email'>email</Text>,
                    placeholder: <Text id='input.email'>email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.email,
                    isRecommended: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.phone'>phone</Text>,
                    placeholder: <Text id='input.phone'>phone</Text>,
                    name: 'phone',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.phone,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.fax'>fax</Text>,
                    placeholder: <Text id='input.fax'>fax</Text>,
                    name: 'fax',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.fax,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-brands fa-facebook',
                    getValue: customer => customer.facebook,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-brands fa-instagram',
                    getValue: customer => customer.instagram,
                },
                {
                    title: <Text id='input.customer-logo'>Logo</Text>,
                    placeholder: <Text id='input.customer-logo'>Logo</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: customer => customer.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.emailSender'>emailSender</Text>,
                    placeholder: <Text id='input.emailSender'>emailSender</Text>,
                    help: <Text id='input.emailSender-help'>Must be an approved sender in the system. If you are unsure, please do NOT change this.</Text>,
                    showDanger: true,
                    name: 'emailSender',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.emailSender,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.emailSender && !util.validateEmail(obj.emailSender)) {
                            const invalidChars = findInvalidEmailChars(obj.emailSender);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('emailSender', `Invalid emailSender. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('emailSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('emailSender', '');
                        holdingStore.setFeedbackButton('emailSender', null, null);
                    },
                },
                {
                    title: <Text id='input.emailSenderOnlyAsReplyTo'>emailSenderOnlyAsReplyTo</Text>,
                    placeholder: <Text id='input.emailSenderOnlyAsReplyTo'>emailSenderOnlyAsReplyTo</Text>,
                    name: 'emailSenderOnlyAsReplyTo',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: customer => customer.emailSenderOnlyAsReplyTo ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.smsBookingSmsDaysBefore'>Booking SMS Days before</Text>,
                    placeholder: <Text id='input.smsBookingSmsDaysBefore'>Send Booking SMS days before booking date.</Text>,
                    help: <Text id='input.smsBookingSmsDaysBefore-help'>Default is 1 calendar day before the booking date. </Text>,
                    name: 'smsBookingSmsDaysBefore',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsBookingSmsDaysBefore || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsNotificationDaysBefore'>Recall SMS days before</Text>,
                    placeholder: <Text id='input.smsNotificationDaysBefore-placeholder'>Send recall SMS days before the recall date.</Text>,
                    help: <Text id='input.smsNotificationDaysBefore-help'>How many days before the recall date the recall SMS (innkalling) is sent. Default is 14 calendar days.</Text>,
                    name: 'smsNotificationDaysBefore',
                    type: 'number',
                    min: 1,
                    max: 365,
                    step: 1,
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsNotificationDaysBefore || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.notificationEmailEnabled'>Recall by email</Text>,
                    placeholder: <Text id='input.notificationEmailEnabled-placeholder'>Also send recalls by email</Text>,
                    help: <Text id='input.notificationEmailEnabled-help'>Also send recalls (innkallinger) by email to owners with an email address. SMS is still sent as before.</Text>,
                    name: 'notificationEmailEnabled',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope',
                    getValue: customer => customer.notificationEmailEnabled ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.notificationEmailOnly'>Recall by email only</Text>,
                    placeholder: <Text id='input.notificationEmailOnly-placeholder'>Send recalls by email only, not SMS</Text>,
                    help: <Text id='input.notificationEmailOnly-help'>Send recalls (innkallinger) by email only, not SMS. Owners without a valid email address still get the SMS.</Text>,
                    name: 'notificationEmailOnly',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope-circle-check',
                    getValue: customer => customer.notificationEmailOnly ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsSendingHourDaily'>SMS Send Booking SMS Daily at hour</Text>,
                    placeholder: <Text id='input.smsSendingHourDaily'>SMS Send Booking SMS Daily at hour</Text>,
                    help: <Text id='input.smsSendingHourDaily-help'>Whole hour of the day (0-23) when reminders and recalls are sent. Enter only the hour, e.g. 15 for 15:00 (not 1500).</Text>,
                    name: 'smsSendingHourDaily',
                    type: 'number',
                    min: 0,
                    max: 23,
                    step: 1,
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsSendingHourDaily || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsSender'>smsSender</Text>,
                    placeholder: <Text id='input.smsSender'>smsSender</Text>,
                    help: <Text id='input.smsSender-help'>Must follow these rules: Alphanumeric. Maximum of 11 characters. No spaces. No special characters. The sender ID must be the brand name of the company sending the SMS. If you are unsure, please do NOT change this.</Text>,
                    maxLength: 11,
                    showDanger: true,
                    name: 'smsSender',
                    maxLength: 11,
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.smsSender,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.smsSender && !/^[a-zA-Z0-9]+$/.test(obj.smsSender)) {
                            holdingStore.setFeedbackError('smsSender', 'Invalid smsSender. Must be 11 digits.');
                        } else {
                            holdingStore.setFeedbackError('smsSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('smsSender', '');
                        holdingStore.setFeedbackButton('smsSender', null, null);
                    },
                },
                {
                    title: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    placeholder: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    help: <Text id='input.smsTextBooking-help'>SMS text template for booking sms. Fields: [animal] [name] [eventDate] [clinic] [clinicPhone] [clinicEmail]</Text>,
                    name: 'smsTextBooking',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.smsTextBooking,
                },
                {
                    title: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    placeholder: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    help: <Text id='input.smsTextNotification-help'>SMS text template for notification sms. Fields: [animal] [name] [eventDate] [dueDate] [clinic] [clinicPhone] [clinicEmail]</Text>,
                    name: 'smsTextNotification',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.smsTextNotification,
                },
                {
                    title: <Text id='input.dyreIdClinicId'>dyreIdClinicId</Text>,
                    placeholder: <Text id='input.dyreIdClinicId'>dyreIdClinicId</Text>,
                    name: 'dyreIdClinicId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.dyreIdClinicId,
                },

                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: customer => customer.members?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.departments'>Departments</Text>,
                    placeholder: <Text id='input.departments'>Departments</Text>,
                    name: 'departments',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminDepartments',
                    getValue: customer => customer.departments?.map(d => d),
                    datalist: [
                        'id', 'name'
                    ],
                    // displayValue: departments => <>
                    //     <div class='text-start px-2'>
                    //         {departments?.map((obj, index) => <>
                    //             {index > 0 && <br />}
                    //             <span class='font-weight-lighter'>{obj.id}:</span> {obj.name}<br />
                    //         </>)}
                    //     </div>
                    // </>,
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.id}: {obj.name}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.workSharing'>Work Sharing Access</Text>,
                    placeholder: <Text id='input.workSharing'>Work Sharing Access</Text>,
                    name: 'workSharing',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-share-nodes',
                    searchMethod: 'searchUsersAll',
                    getValue: customer => customer.workSharing?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
        {
            name: <Text id='input.customer-invoice-title'>Invoice details</Text>,
            key: 'Invoice details',
            fields: [
                {
                    title: <Text id='input.invoiceAccount'>invoiceAccount</Text>,
                    placeholder: <Text id='input.invoiceAccount'>invoiceAccount</Text>,
                    help: <Text id='input.invoiceAccount-help'>Bank account where invoice should be paid to.</Text>,
                    name: 'invoiceAccount',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.invoiceAccount,
                },
                {
                    title: <Text id='input.invoiceBic'>BIC</Text>,
                    placeholder: <Text id='input.invoiceBic'>BIC</Text>,
                    help: <Text id='input.invoiceBic-help'>BIC code for the bank account.</Text>,
                    name: 'invoiceBic',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.invoiceBic,
                },
                {
                    title: <Text id='input.invoiceIban'>IBAN</Text>,
                    placeholder: <Text id='input.invoiceIban'>IBAN</Text>,
                    help: <Text id='input.invoiceIban-help'>IBAN code for the bank account.</Text>,
                    name: 'invoiceIban',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.invoiceIban,
                },
                {
                    title: <Text id='input.invoiceInfo'>invoiceInfo</Text>,
                    placeholder: <Text id='input.invoiceInfo'>invoiceInfo</Text>,
                    help: <Text id='input.invoiceInfo-help'>Due date info on the invoice. Ie: "Due in 10 days".</Text>,
                    name: 'invoiceInfo',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.invoiceInfo,
                },
                {
                    title: <Text id='input.invoiceHidePaymentReference'>Hide payment reference line</Text>,
                    help: <Text id='input.invoiceHidePaymentReference-help'>Hide the "Merk betaling med: Faktura nr" line on invoices.</Text>,
                    name: 'invoiceHidePaymentReference',
                    type: 'toggle',
                    icon: 'fa-solid fa-toggle-on',
                    getValue: customer => customer.invoiceHidePaymentReference,
                },
                {
                    title: <Text id='input.invoicePaymentReferenceText'>Payment reference text</Text>,
                    placeholder: 'Vennligst benytt KID ved betaling',
                    help: <Text id='input.invoicePaymentReferenceText-help'>Custom text shown on invoices instead of the default "Merk betaling med: Faktura nr" line.</Text>,
                    name: 'invoicePaymentReferenceText',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.invoicePaymentReferenceText,
                },
                {
                    title: <Text id='input.invoiceDueDays'>invoiceDueDays</Text>,
                    placeholder: <Text id='input.invoiceDueDays'>invoiceDueDays</Text>,
                    help: <Text id='input.invoiceDueDays-help'>Only a number to indicate number of days before due.</Text>,
                    name: 'invoiceDueDays',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.invoiceDueDays,
                },
                {
                    title: <Text id='input.accountingLockedUntil'>Accounting locked until</Text>,
                    placeholder: <Text id='input.accountingLockedUntil'>Accounting locked until</Text>,
                    help: <Text id='input.accountingLockedUntil-help'>Last day the accountant has filed VAT for. Sales, payments and corrections dated on or before this day are refused; use a credit note dated today. Standard two-month terms are due 10 Apr, 10 Jun, 31 Aug, 10 Oct, 10 Dec and 10 Feb.</Text>,
                    name: 'accountingLockedUntil',
                    type: 'date',
                    icon: 'fa-solid fa-lock',
                    getValue: customer => (customer.accountingLockedUntil ? util.isoDate(customer.accountingLockedUntil, false, false, true) : ''),
                    // The Klinikk page prints settings read-only through field.displayValue.
                    displayValue: value => (value ? util.isoDate(value, false, false, true) : ''),
                },
                {
                    title: <Text id='input.invoiceHeaderText'>Invoice Header Text</Text>,
                    placeholder: `Åpningstider:
Dagtid: Man til fre: 09.00-15.00
Kveldstid: Man til ons: 16.00-20.00

Ved akutte tilfeller utenom kontortid:
NVH/NMBU: 67232323`,
                    help: <>
                        <Text id='input.invoiceHeaderText-help'>Custom text displayed in invoice header (upper right corner)</Text>
                        <br /><br />
                        <strong>Eksempler:</strong><br />
                        • Åpningstider<br />
                        • Akutt-telefoner<br />
                        • Ekstra kontaktinfo<br />
                        • Annen viktig info
                    </>,
                    name: 'invoiceHeaderText',
                    type: 'textarea',
                    icon: 'fa-duotone fa-clock',
                    getValue: obj => obj.invoiceHeaderText,
                },
                {
                    title: <Text id='input.invoiceFooterText'>Invoice Footer Text</Text>,
                    placeholder: `Takk for at du handler hos oss!

Betalingsbetingelser: Netto 14 dager.
Returrett: 14 dager fra kjøpsdato.`,
                    help: <>
                        <Text id='input.invoiceFooterText-help'>Custom text displayed at bottom of invoices before company footer</Text>
                        <br /><br />
                        <strong>Eksempler:</strong><br />
                        • Betalingsbetingelser<br />
                        • Returpolicy<br />
                        • Takkemelding<br />
                        • Juridiske ansvarsfraskrivelser
                    </>,
                    name: 'invoiceFooterText',
                    type: 'textarea',
                    icon: 'fa-duotone fa-file-lines',
                    getValue: obj => obj.invoiceFooterText,
                },
                {
                    title: <Text id='input.transferCutoffDate'>Transfer cutoff date (accounting)</Text>,
                    placeholder: <Text id='input.transferCutoffDate'>Transfer cutoff date (accounting)</Text>,
                    help: <Text id='input.transferCutoffDate-help'>Only sales on/after this date are counted in the frontpage critical-tasks alerts (not transferred to accounting, voucher reporting due, sales open too long). Defaults to when the customer was created.</Text>,
                    name: 'transferCutoffDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar-day',
                    getValue: customer => customer.transferCutoffDate,
                },
                {
                    title: <Text id='input.collectiveInvoiceFeeEnabled'>collectiveInvoiceFeeEnabled</Text>,
                    help: <Text id='input.collectiveInvoiceFeeEnabled-help'>Automatically add invoice fee to collective invoices.</Text>,
                    name: 'collectiveInvoiceFeeEnabled',
                    type: 'toggle',
                    icon: 'fa-solid fa-toggle-on',
                    getValue: customer => customer.collectiveInvoiceFeeEnabled,
                },
                {
                    title: <Text id='input.collectiveInvoiceFee'>collectiveInvoiceFee</Text>,
                    placeholder: '70',
                    help: <Text id='input.collectiveInvoiceFee-help'>Invoice fee amount in NOK (max 70 NOK for consumers per Norwegian law).</Text>,
                    name: 'collectiveInvoiceFee',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.collectiveInvoiceFee,
                },
                {
                    title: <Text id='input.collectiveInvoiceFeeAccount'>collectiveInvoiceFeeAccount</Text>,
                    placeholder: '3030',
                    help: <Text id='input.collectiveInvoiceFeeAccount-help'>Account number for invoice fee.</Text>,
                    name: 'collectiveInvoiceFeeAccount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.collectiveInvoiceFeeAccount,
                },
                {
                    title: <Text id='input.collectiveInvoiceFeeName'>collectiveInvoiceFeeName</Text>,
                    placeholder: 'Fakturagebyr',
                    help: <Text id='input.collectiveInvoiceFeeName-help'>Product name for invoice fee.</Text>,
                    name: 'collectiveInvoiceFeeName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.collectiveInvoiceFeeName,
                },
                {
                    title: <Text id='input.collectiveInvoiceFeeVat'>collectiveInvoiceFeeVat</Text>,
                    placeholder: '0',
                    help: <Text id='input.collectiveInvoiceFeeVat-help'>VAT percentage for invoice fee (typically 0%).</Text>,
                    name: 'collectiveInvoiceFeeVat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.collectiveInvoiceFeeVat,
                },
                {
                    title: <Text id='input.segmentAccountsEnabled'>segmentAccountsEnabled</Text>,
                    help: <Text id='input.segmentAccountsEnabled-help'>Enable splitting price segments (fee, medicine, equipment, other) into separate accounting entries.</Text>,
                    name: 'segmentAccountsEnabled',
                    type: 'toggle',
                    icon: 'fa-solid fa-toggle-on',
                    getValue: customer => customer.segmentAccountsEnabled,
                },
                {
                    title: <Text id='input.segmentAccountFee'>segmentAccountFee</Text>,
                    placeholder: '3000',
                    help: <Text id='input.segmentAccountFee-help'>Account number for fee/service segment (default: 3000).</Text>,
                    name: 'segmentAccountFee',
                    type: 'datalist',
                    datalist: Object.keys(ACCOUNT_MAP).map(key => [parseInt(key, 10), `${key} - ${ACCOUNT_MAP[key]}`]),
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.segmentAccountFee,
                },
                {
                    title: <Text id='input.segmentAccountMedicine'>segmentAccountMedicine</Text>,
                    placeholder: '3003',
                    help: <Text id='input.segmentAccountMedicine-help'>Account number for medicine segment (default: 3003).</Text>,
                    name: 'segmentAccountMedicine',
                    type: 'datalist',
                    datalist: Object.keys(ACCOUNT_MAP).map(key => [parseInt(key, 10), `${key} - ${ACCOUNT_MAP[key]}`]),
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.segmentAccountMedicine,
                },
                {
                    title: <Text id='input.segmentAccountEquipment'>segmentAccountEquipment</Text>,
                    placeholder: '3001',
                    help: <Text id='input.segmentAccountEquipment-help'>Account number for equipment segment (default: 3001).</Text>,
                    name: 'segmentAccountEquipment',
                    type: 'datalist',
                    datalist: Object.keys(ACCOUNT_MAP).map(key => [parseInt(key, 10), `${key} - ${ACCOUNT_MAP[key]}`]),
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.segmentAccountEquipment,
                },
                {
                    title: <Text id='input.segmentAccountOther'>segmentAccountOther</Text>,
                    placeholder: '3019',
                    help: <Text id='input.segmentAccountOther-help'>Account number for other segment (default: 3019).</Text>,
                    name: 'segmentAccountOther',
                    type: 'datalist',
                    datalist: Object.keys(ACCOUNT_MAP).map(key => [parseInt(key, 10), `${key} - ${ACCOUNT_MAP[key]}`]),
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.segmentAccountOther,
                },
                // honorarServiceAccounts is edited by the bespoke
                // HonorarServiceAccountsEditor (rendered in customer/edit.js), not the
                // generic form: the 'tags' input type stores {name,md5,date} objects and
                // reads a hardcoded object.tags, so it could neither display nor save this
                // plain number array. See webapp/src/components/customer/honorarServiceAccountsEditor.js
            ],
        },
        {
            name: <Text id='input.customer-sender-settings-title'>Sender Settings</Text>,
            key: 'Sender Settings',
            fields: [
                {
                    title: <Text id='input.emailSender'>emailSender</Text>,
                    placeholder: <Text id='input.emailSender'>emailSender</Text>,
                    help: <Text id='input.emailSender-help'>Must be an approved sender in the system. If you are unsure, please do NOT change this.</Text>,
                    showDanger: true,
                    name: 'emailSender',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.emailSender,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.emailSender && !util.validateEmail(obj.emailSender)) {
                            const invalidChars = findInvalidEmailChars(obj.emailSender);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('emailSender', `Invalid emailSender. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('emailSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('emailSender', '');
                        holdingStore.setFeedbackButton('emailSender', null, null);
                    },
                },
                {
                    title: <Text id='input.supportEmailSender'>Support email sender</Text>,
                    placeholder: <Text id='input.supportEmailSender-placeholder'>post@yourclub.no</Text>,
                    help: <Text id='input.supportEmailSender-help'>Sender for support ticket replies. Must be a verified sender in the system. Empty = the system default.</Text>,
                    showDanger: true,
                    name: 'supportEmailSender',
                    type: 'text',
                    icon: 'fa-duotone fa-headset',
                    getValue: customer => customer.supportEmailSender,
                    checkValue: (obj, holdingStore) => {
                        if (obj.supportEmailSender && !util.validateEmail(obj.supportEmailSender)) {
                            const invalidChars = findInvalidEmailChars(obj.supportEmailSender);
                            const messages = invalidChars.map(c => `Character "${c.char}" at position ${c.index} is not allowed.`);
                            holdingStore.setFeedbackError('supportEmailSender', `Invalid supportEmailSender. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('supportEmailSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        holdingStore.setFeedback('supportEmailSender', '');
                        holdingStore.setFeedbackButton('supportEmailSender', null, null);
                    },
                },
                {
                    title: <Text id='input.smsBookingSmsDaysBefore'>Booking SMS Days before</Text>,
                    placeholder: <Text id='input.smsBookingSmsDaysBefore'>Send Booking SMS reminder x days before booking date.</Text>,
                    help: <Text id='input.smsBookingSmsDaysBefore-help'>Default is 1 calendar day before the booking date. </Text>,
                    name: 'smsBookingSmsDaysBefore',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsBookingSmsDaysBefore || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsNotificationDaysBefore'>Recall SMS days before</Text>,
                    placeholder: <Text id='input.smsNotificationDaysBefore-placeholder'>Send recall SMS days before the recall date.</Text>,
                    help: <Text id='input.smsNotificationDaysBefore-help'>How many days before the recall date the recall SMS (innkalling) is sent. Default is 14 calendar days.</Text>,
                    name: 'smsNotificationDaysBefore',
                    type: 'number',
                    min: 1,
                    max: 365,
                    step: 1,
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsNotificationDaysBefore || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.notificationEmailEnabled'>Recall by email</Text>,
                    placeholder: <Text id='input.notificationEmailEnabled-placeholder'>Also send recalls by email</Text>,
                    help: <Text id='input.notificationEmailEnabled-help'>Also send recalls (innkallinger) by email to owners with an email address. SMS is still sent as before.</Text>,
                    name: 'notificationEmailEnabled',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope',
                    getValue: customer => customer.notificationEmailEnabled ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.notificationEmailOnly'>Recall by email only</Text>,
                    placeholder: <Text id='input.notificationEmailOnly-placeholder'>Send recalls by email only, not SMS</Text>,
                    help: <Text id='input.notificationEmailOnly-help'>Send recalls (innkallinger) by email only, not SMS. Owners without a valid email address still get the SMS.</Text>,
                    name: 'notificationEmailOnly',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope-circle-check',
                    getValue: customer => customer.notificationEmailOnly ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsSender'>smsSender</Text>,
                    placeholder: <Text id='input.smsSender'>smsSender</Text>,
                    help: <Text id='input.smsSender-help'>Must follow these rules: Alphanumeric. Maximum of 11 characters. No spaces. No special characters. The sender ID must be the brand name of the company sending the SMS. If you are unsure, please do NOT change this.</Text>,
                    maxLength: 11,
                    showDanger: true,
                    name: 'smsSender',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.smsSender,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.smsSender && !/^[a-zA-Z0-9]+$/.test(obj.smsSender)) {
                            holdingStore.setFeedbackError('smsSender', 'Invalid smsSender. Must be 11 digits.');
                        } else {
                            holdingStore.setFeedbackError('smsSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('smsSender', '');
                        holdingStore.setFeedbackButton('smsSender', null, null);
                    },
                },
                {
                    title: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    placeholder: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    help: <Text id='input.smsTextBooking-help'>SMS text template for booking sms. Fields: [animal] [name] [eventDate] [clinic] [clinicPhone] [clinicEmail]</Text>,
                    name: 'smsTextBooking',
                    type: 'textarea',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.smsTextBooking,
                },
                {
                    title: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    placeholder: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    help: <Text id='input.smsTextNotification-help'>SMS text template for notification sms. Fields: [animal] [name] [eventDate] [dueDate] [clinic] [clinicPhone] [clinicEmail]</Text>,
                    name: 'smsTextNotification',
                    type: 'textarea',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.smsTextNotification,
                },
            ],
        },
        {
            name: <Text id='input.customer-menu-settings-title'>Menu Settings</Text>,
            key: 'menuSettings',
            fields: [
                {
                    title: <Text id='input.menuItems-mypage'>My Page</Text>,
                    placeholder: <Text id='input.menuItems-mypage'>My Page</Text>,
                    name: 'menuItems.mypage',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.mypage ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-dyrejournal'>Dyrejournal</Text>,
                    placeholder: <Text id='input.menuItems-dyrejournal'>Dyrejournal</Text>,
                    name: 'menuItems.dyrejournal',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.dyrejournal ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-liverace'>Liverace</Text>,
                    placeholder: <Text id='input.menuItems-liverace'>Liverace</Text>,
                    name: 'menuItems.liverace',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.liverace ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-marketplace'>Marketplace</Text>,
                    placeholder: <Text id='input.menuItems-marketplace'>Marketplace</Text>,
                    name: 'menuItems.marketplace',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.marketplace ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-calendar'>Calendar</Text>,
                    placeholder: <Text id='input.menuItems-calendar'>Calendar</Text>,
                    name: 'menuItems.calendar',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.calendar ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-media'>Media</Text>,
                    placeholder: <Text id='input.menuItems-media'>Media</Text>,
                    name: 'menuItems.media',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.media ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-timeTracker'>Time Tracker</Text>,
                    placeholder: <Text id='input.menuItems-timeTracker'>Time Tracker</Text>,
                    name: 'menuItems.timeTracker',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.timeTracker ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-installations'>Installations</Text>,
                    placeholder: <Text id='input.menuItems-installations'>Installations</Text>,
                    name: 'menuItems.installations',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.installations ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-parts'>Parts</Text>,
                    placeholder: <Text id='input.menuItems-parts'>Parts</Text>,
                    name: 'menuItems.parts',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.parts ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-webshop'>Webshop</Text>,
                    placeholder: <Text id='input.menuItems-webshop'>Webshop</Text>,
                    name: 'menuItems.webshop',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.webshop ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-productList'>Product List</Text>,
                    placeholder: <Text id='input.menuItems-productList'>Product List</Text>,
                    name: 'menuItems.productList',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.productList ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-shootingTools'>Shooting Tools</Text>,
                    placeholder: <Text id='input.menuItems-shootingTools'>Shooting Tools</Text>,
                    name: 'menuItems.shootingTools',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.shootingTools ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-courses'>Courses</Text>,
                    placeholder: <Text id='input.menuItems-courses'>Courses</Text>,
                    name: 'menuItems.courses',
                    type: 'toggle',
                    icon: 'fa-solid fa-graduation-cap',
                    getValue: obj => obj.menuItems?.courses ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-elearning'>E-learning</Text>,
                    placeholder: <Text id='input.menuItems-elearning'>E-learning</Text>,
                    name: 'menuItems.elearning',
                    type: 'toggle',
                    icon: 'fa-solid fa-chalkboard-user',
                    getValue: obj => obj.menuItems?.elearning ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-carpenterTools'>Carpenter Tools</Text>,
                    placeholder: <Text id='input.menuItems-carpenterTools'>Carpenter Tools</Text>,
                    name: 'menuItems.carpenterTools',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.carpenterTools ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-textTemplates'>Text Templates</Text>,
                    placeholder: <Text id='input.menuItems-textTemplates'>Text Templates</Text>,
                    name: 'menuItems.textTemplates',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.textTemplates ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-news'>News</Text>,
                    placeholder: <Text id='input.menuItems-news'>News</Text>,
                    name: 'menuItems.news',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.news ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-help'>Help</Text>,
                    placeholder: <Text id='input.menuItems-help'>Help</Text>,
                    name: 'menuItems.help',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.help ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-gdpr'>GDPR</Text>,
                    placeholder: <Text id='input.menuItems-gdpr'>GDPR</Text>,
                    name: 'menuItems.gdpr',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.gdpr ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-termsOfSale'>Terms of Sale</Text>,
                    placeholder: <Text id='input.menuItems-termsOfSale'>Terms of Sale</Text>,
                    name: 'menuItems.termsOfSale',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.menuItems?.termsOfSale ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-f1'>F1 Racing</Text>,
                    placeholder: <Text id='input.menuItems-f1'>F1 Racing</Text>,
                    name: 'menuItems.f1',
                    type: 'toggle',
                    icon: 'fa-solid fa-flag-checkered',
                    getValue: obj => obj.menuItems?.f1 ? 1 : 0,
                    isAdminField: true,
                },
                // Opt-out toggles: these entries are shown by default; enabling a hide* key removes
                // the entry (or section) from both the sidebar and the hamburger menu on this domain.
                {
                    title: <Text id='input.menuItems-hideSupport'>Hide: Support and refund request</Text>,
                    placeholder: <Text id='input.menuItems-hideSupport'>Hide: Support and refund request</Text>,
                    name: 'menuItems.hideSupport',
                    type: 'toggle',
                    icon: 'fa-duotone fa-life-ring',
                    getValue: obj => obj.menuItems?.hideSupport ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-hideNewsletter'>Hide: Newsletter</Text>,
                    placeholder: <Text id='input.menuItems-hideNewsletter'>Hide: Newsletter</Text>,
                    name: 'menuItems.hideNewsletter',
                    type: 'toggle',
                    icon: 'fa-duotone fa-newspaper',
                    getValue: obj => obj.menuItems?.hideNewsletter ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-hideSigning'>Hide: Signing section</Text>,
                    placeholder: <Text id='input.menuItems-hideSigning'>Hide: Signing section</Text>,
                    name: 'menuItems.hideSigning',
                    type: 'toggle',
                    icon: 'fa-duotone fa-signature',
                    getValue: obj => obj.menuItems?.hideSigning ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-hideAiConnections'>Hide: AI connections</Text>,
                    placeholder: <Text id='input.menuItems-hideAiConnections'>Hide: AI connections</Text>,
                    name: 'menuItems.hideAiConnections',
                    type: 'toggle',
                    icon: 'fa-solid fa-plug',
                    getValue: obj => obj.menuItems?.hideAiConnections ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-hideOfflinePrepare'>Hide: Prepare for offline</Text>,
                    placeholder: <Text id='input.menuItems-hideOfflinePrepare'>Hide: Prepare for offline</Text>,
                    name: 'menuItems.hideOfflinePrepare',
                    type: 'toggle',
                    icon: 'fa-solid fa-cloud-arrow-down',
                    getValue: obj => obj.menuItems?.hideOfflinePrepare ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-hideNotifications'>Hide: Notification settings</Text>,
                    placeholder: <Text id='input.menuItems-hideNotifications'>Hide: Notification settings</Text>,
                    name: 'menuItems.hideNotifications',
                    type: 'toggle',
                    icon: 'fa-duotone fa-bell',
                    getValue: obj => obj.menuItems?.hideNotifications ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.menuItems-hideRace'>Hide: Race section</Text>,
                    placeholder: <Text id='input.menuItems-hideRace'>Hide: Race section</Text>,
                    name: 'menuItems.hideRace',
                    type: 'toggle',
                    icon: 'fa-regular fa-flag-checkered',
                    getValue: obj => obj.menuItems?.hideRace ? 1 : 0,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.customer-frontpage-settings-title'>Frontpage Settings</Text>,
            key: 'frontpageSettings',
            fields: [
                {
                    title: <Text id='input.frontpageItems-dyrejournal'>Dyrejournal</Text>,
                    placeholder: <Text id='input.frontpageItems-dyrejournal'>Dyrejournal</Text>,
                    help: <Text id='input.frontpageItems-dyrejournal-help'>Show dyrejournal items on the frontpage</Text>,
                    name: 'frontpageItems.dyrejournal',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.dyrejournal ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-topCommercial'>Top Commercial</Text>,
                    placeholder: <Text id='input.frontpageItems-topCommercial'>Top Commercial</Text>,
                    help: <Text id='input.frontpageItems-topCommercial-help'>Show top commercial items on the frontpage</Text>,
                    name: 'frontpageItems.topCommercial',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.topCommercial ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-articleNews'>Article News</Text>,
                    placeholder: <Text id='input.frontpageItems-articleNews'>Article News</Text>,
                    help: <Text id='input.frontpageItems-articleNews-help'>Show article news items on the top of the frontpage</Text>,
                    name: 'frontpageItems.articleNews',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.articleNews ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-ticker'>Ticker</Text>,
                    placeholder: <Text id='input.frontpageItems-ticker'>Ticker</Text>,
                    help: <Text id='input.frontpageItems-ticker-help'>Show ticker items on the top of the frontpage</Text>,
                    name: 'frontpageItems.ticker',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.ticker ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-mainArticles'>Main Articles</Text>,
                    placeholder: <Text id='input.frontpageItems-mainArticles'>Main Articles</Text>,
                    help: <Text id='input.frontpageItems-mainArticles-help'>Show main articles on the top of the frontpage</Text>,
                    name: 'frontpageItems.mainArticles',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.mainArticles ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-otherArticles'>Other Articles</Text>,
                    placeholder: <Text id='input.frontpageItems-otherArticles'>Other Articles</Text>,
                    help: <Text id='input.frontpageItems-otherArticles-help'>Show other articles on the frontpage below the main content</Text>,
                    name: 'frontpageItems.otherArticles',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.otherArticles ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-timeTracker'>Time Tracker</Text>,
                    placeholder: <Text id='input.frontpageItems-timeTracker'>Time Tracker</Text>,
                    help: <Text id='input.frontpageItems-timeTracker-help'>Show time tracker tool on the frontpage</Text>,
                    name: 'frontpageItems.timeTracker',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.timeTracker ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-webshop'>Webshop</Text>,
                    placeholder: <Text id='input.frontpageItems-webshop'>Webshop</Text>,
                    help: <Text id='input.frontpageItems-webshop-help'>Show webshop items on the frontpage</Text>,
                    name: 'frontpageItems.webshop',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.webshop ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-userfeed'>User Feed</Text>,
                    placeholder: <Text id='input.frontpageItems-userfeed'>User Feed</Text>,
                    help: <Text id='input.frontpageItems-userfeed-help'>Show user feed items on the frontpage</Text>,
                    name: 'frontpageItems.userfeed',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.userfeed ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-marketplace'>Marketplace</Text>,
                    placeholder: <Text id='input.frontpageItems-marketplace'>Marketplace</Text>,
                    help: <Text id='input.frontpageItems-marketplace-help'>Show marketplace items from users on the frontpage</Text>,
                    name: 'frontpageItems.marketplace',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.marketplace ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-shootingTools'>Shooting Tools</Text>,
                    placeholder: <Text id='input.frontpageItems-shootingTools'>Shooting Tools</Text>,
                    help: <Text id='input.frontpageItems-shootingTools-help'>Show shooting tools on the frontpage</Text>,
                    name: 'frontpageItems.shootingTools',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.shootingTools ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-liverace'>LiveRace</Text>,
                    placeholder: <Text id='input.frontpageItems-liverace'>LiveRace</Text>,
                    help: <Text id='input.frontpageItems-liverace-help'>Show live race items on the frontpage</Text>,
                    name: 'frontpageItems.liverace',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.liverace ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-pastRaces'>Past races (off-season)</Text>,
                    placeholder: <Text id='input.frontpageItems-pastRaces'>Past races (off-season)</Text>,
                    help: <Text id='input.frontpageItems-pastRaces-help'>Show the "Last season" strip of finished races on the frontpage when no race is live or upcoming</Text>,
                    name: 'frontpageItems.pastRaces',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.pastRaces ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-installation'>Installation</Text>,
                    placeholder: <Text id='input.frontpageItems-installation'>Installation</Text>,
                    help: <Text id='input.frontpageItems-installation-help'>Show installation items on the frontpage</Text>,
                    name: 'frontpageItems.installation',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.installation ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-loan'>Loan</Text>,
                    placeholder: <Text id='input.frontpageItems-loan'>Loan</Text>,
                    help: <Text id='input.frontpageItems-loan-help'>Show loan items on the frontpage</Text>,
                    name: 'frontpageItems.loan',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.loan ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-inspection'>Inspection</Text>,
                    placeholder: <Text id='input.frontpageItems-inspection'>Inspection</Text>,
                    help: <Text id='input.frontpageItems-inspection-help'>Show inspection items on the frontpage</Text>,
                    name: 'frontpageItems.inspection',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.inspection ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-news'>News</Text>,
                    placeholder: <Text id='input.frontpageItems-news'>News</Text>,
                    help: <Text id='input.frontpageItems-news-help'>Show news items on the bottom of the frontpage</Text>,
                    name: 'frontpageItems.news',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.news ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-ffeFrontpage'>FFE Frontpage</Text>,
                    placeholder: <Text id='input.frontpageItems-ffeFrontpage'>FFE Frontpage</Text>,
                    help: <Text id='input.frontpageItems-ffeFrontpage-help'>Show FFE Frontpage on the frontpage</Text>,
                    name: 'frontpageItems.ffeFrontpage',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.ffeFrontpage ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-ffeProducts'>FFE Products</Text>,
                    placeholder: <Text id='input.frontpageItems-ffeProducts'>FFE Products</Text>,
                    help: <Text id='input.frontpageItems-ffeProducts-help'>Show FFE Products on the frontpage</Text>,
                    name: 'frontpageItems.ffeProducts',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.frontpageItems?.ffeProducts ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-publicCalendar'>Public Calendar</Text>,
                    placeholder: <Text id='input.frontpageItems-publicCalendar'>Public Calendar</Text>,
                    help: <Text id='input.frontpageItems-publicCalendar-help'>Show upcoming public calendar events on the frontpage</Text>,
                    name: 'frontpageItems.publicCalendar',
                    type: 'toggle',
                    icon: 'fa-solid fa-calendar-days',
                    getValue: obj => obj.frontpageItems?.publicCalendar ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.frontpageItems-publicCourses'>Public Courses</Text>,
                    placeholder: <Text id='input.frontpageItems-publicCourses'>Public Courses</Text>,
                    help: <Text id='input.frontpageItems-publicCourses-help'>Show upcoming public courses on the frontpage as cards</Text>,
                    name: 'frontpageItems.publicCourses',
                    type: 'toggle',
                    icon: 'fa-solid fa-graduation-cap',
                    getValue: obj => obj.frontpageItems?.publicCourses ? 1 : 0,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.customer-journal-settings-title'>Journal Settings</Text>,
            key: 'journalSettings',
            fields: [
                {
                    title: <Text id='input.journalItems-generalCondition'>Hide General Condition</Text>,
                    placeholder: <Text id='input.journalItems-generalCondition'>Hide General Condition</Text>,
                    help: <Text id='input.journalItems-generalCondition-help'>Hide general condition items on the frontpage</Text>,
                    name: 'journalItems.generalCondition',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.generalCondition ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-physicalExaminationFindings'>Hide Physical Examination Findings</Text>,
                    placeholder: <Text id='input.journalItems-physicalExaminationFindings'>Hide Physical Examination Findings</Text>,
                    help: <Text id='input.journalItems-physicalExaminationFindings-help'>Hide physical examination findings on the frontpage</Text>,
                    name: 'journalItems.physicalExaminationFindings',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.physicalExaminationFindings ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-diagnosticTests'>Hide Diagnostic Tests</Text>,
                    placeholder: <Text id='input.journalItems-diagnosticTests'>Hide Diagnostic Tests</Text>,
                    help: <Text id='input.journalItems-diagnosticTests-help'>Hide diagnostic tests on the frontpage</Text>,
                    name: 'journalItems.diagnosticTests',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.diagnosticTests ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-diagnosis'>Hide Diagnosis</Text>,
                    placeholder: <Text id='input.journalItems-diagnosis'>Hide Diagnosis</Text>,
                    help: <Text id='input.journalItems-diagnosis-help'>Hide diagnosis on the frontpage</Text>,
                    name: 'journalItems.diagnosis',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.diagnosis ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-treatmentPlan'>Hide Treatment Plan</Text>,
                    placeholder: <Text id='input.journalItems-treatmentPlan'>Hide Treatment Plan</Text>,
                    help: <Text id='input.journalItems-treatmentPlan-help'>Hide treatment plan on the frontpage</Text>,
                    name: 'journalItems.treatmentPlan',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.treatmentPlan ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-medicines'>Hide Medicines</Text>,
                    placeholder: <Text id='input.journalItems-medicines'>Hide Medicines</Text>,
                    help: <Text id='input.journalItems-medicines-help'>Hide medicines on the frontpage</Text>,
                    name: 'journalItems.medicines',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.medicines ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-medicine'>Hide Medicine picker</Text>,
                    placeholder: <Text id='input.journalItems-medicine'>Hide Medicine picker</Text>,
                    help: <Text id='input.journalItems-medicine-help'>Hide medicine picker on the frontpage</Text>,
                    name: 'journalItems.medicine',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.medicine ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-diagnoses'>Hide Diagnosis picker</Text>,
                    placeholder: <Text id='input.journalItems-diagnoses'>Hide Diagnosis picker</Text>,
                    help: <Text id='input.journalItems-diagnoses-help'>Hide diagnosis picker on the frontpage</Text>,
                    name: 'journalItems.diagnoses',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.diagnoses ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-description'>Hide Description</Text>,
                    placeholder: <Text id='input.journalItems-description'>Hide Description</Text>,
                    help: <Text id='input.journalItems-description-help'>Hide description on the frontpage</Text>,
                    name: 'journalItems.description',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.description ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-clientCommunication'>Hide Client Communication</Text>,
                    placeholder: <Text id='input.journalItems-clientCommunication'>Hide Client Communication</Text>,
                    help: <Text id='input.journalItems-clientCommunication-help'>Hide client communication on the frontpage</Text>,
                    name: 'journalItems.clientCommunication',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.clientCommunication ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-recommendations'>Hide Recommendations</Text>,
                    placeholder: <Text id='input.journalItems-recommendations'>Hide Recommendations</Text>,
                    help: <Text id='input.journalItems-recommendations-help'>Hide recommendations on the frontpage</Text>,
                    name: 'journalItems.recommendations',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.recommendations ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-followUp'>Hide Follow Up</Text>,
                    placeholder: <Text id='input.journalItems-followUp'>Hide Follow Up</Text>,
                    help: <Text id='input.journalItems-followUp-help'>Hide follow up on the frontpage</Text>,
                    name: 'journalItems.followUp',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.followUp ? 1 : 0,
                    // isAdminField: true,
                },

                {
                    title: <Text id='input.journalItems-puls'>Hide Puls</Text>,
                    placeholder: <Text id='input.journalItems-puls'>Hide Puls</Text>,
                    help: <Text id='input.journalItems-puls-help'>Hide puls on the frontpage</Text>,
                    name: 'journalItems.puls',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.puls ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-respiration'>Hide Respiration</Text>,
                    placeholder: <Text id='input.journalItems-respiration'>Hide Respiration</Text>,
                    help: <Text id='input.journalItems-respiration-help'>Hide respiration on the frontpage</Text>,
                    name: 'journalItems.respiration',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.respiration ? 1 : 0,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.journalItems-temperature'>Hide Temperature</Text>,
                    placeholder: <Text id='input.journalItems-temperature'>Hide Temperature</Text>,
                    help: <Text id='input.journalItems-temperature-help'>Hide temperature on the frontpage</Text>,
                    name: 'journalItems.temperature',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.journalItems?.temperature ? 1 : 0,
                    // isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.customer-settings-title'>Settings</Text>,
            key: 'Settings',
            fields: [
                {
                    title: <Text id='input.frontpageStartInFeed'>frontpageStartInFeed</Text>,
                    placeholder: <Text id='input.frontpageStartInFeed'>frontpageStartInFeed</Text>,
                    name: 'frontpageStartInFeed',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.frontpageStartInFeed ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                    isAdminField: true,
                },
                {
                    title: <Text id='input.calendarStartHour'>calendarStartHour</Text>,
                    placeholder: <Text id='input.calendarStartHour'>calendarStartHour</Text>,
                    help: <Text id='input.calendarStartHour-help'>Only a number to indicate start hour of the calendar day. Do not start with 0.</Text>,
                    showWarning: true,
                    name: 'calendarStartHour',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj.calendarStartHour,
                    showInList: false,
                },
                {
                    title: <Text id='input.calendarEndHour'>calendarEndHour</Text>,
                    placeholder: <Text id='input.calendarEndHour'>calendarEndHour</Text>,
                    help: <Text id='input.calendarEndHour-help'>Only a number to indicate end hour of the calendar day. Do not start with 0.</Text>,
                    showWarning: true,
                    name: 'calendarEndHour',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj.calendarEndHour,
                    showInList: false,
                },

                {
                    title: <Text id='input.calendarShowMonday'>Show monday</Text>,
                    placeholder: <Text id='input.calendarShowMonday'>Show monday</Text>,
                    name: 'calendarDays.monday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.monday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowTuesday'>Show tuesday</Text>,
                    placeholder: <Text id='input.calendarShowTuesday'>Show tuesday</Text>,
                    name: 'calendarDays.tuesday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.tuesday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowWednesday'>Show wednesday</Text>,
                    placeholder: <Text id='input.calendarShowWednesday'>Show wednesday</Text>,
                    name: 'calendarDays.wednesday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.wednesday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowThursday'>Show thursday</Text>,
                    placeholder: <Text id='input.calendarShowThursday'>Show thursday</Text>,
                    name: 'calendarDays.thursday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.thursday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowFriday'>Show friday</Text>,
                    placeholder: <Text id='input.calendarShowFriday'>Show friday</Text>,
                    name: 'calendarDays.friday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.friday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowSaturday'>Show saturday</Text>,
                    placeholder: <Text id='input.calendarShowSaturday'>Show saturday</Text>,
                    name: 'calendarDays.saturday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.saturday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowSunday'>Show sunday</Text>,
                    placeholder: <Text id='input.calendarShowSunday'>Show sunday</Text>,
                    name: 'calendarDays.sunday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.sunday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },

                {
                    title: <Text id='input.logoHeight'>logoHeight</Text>,
                    placeholder: <Text id='input.logoHeight'>logoHeight</Text>,
                    help: <Text id='input.logoHeight-help'>Height of the custom logo for customer domain view. Only applicaple if showCustomerLogo is enabled on domain.</Text>,
                    showDanger: true,
                    name: 'logoHeight',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.logoHeight,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.logoInvert'>Invert logo in dark mode</Text>,
                    placeholder: <Text id='input.logoInvert'>Invert logo in dark mode</Text>,
                    help: <Text id='input.logoInvert-help'>Invert the colors of the custom logo when the viewer uses dark mode, so a dark logo becomes light on the dark header. Only applicable if showCustomerLogo is enabled on domain.</Text>,
                    showDanger: true,
                    name: 'logoInvert',
                    type: 'toggle',
                    icon: 'fa-duotone fa-circle-half-stroke',
                    getValue: customer => customer.logoInvert ? 1 : 0,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    placeholder: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    help: <Text id='input.backgroundColor-help'>Background color of the page. Only applicaple if showCustomerLogo is enabled on domain.</Text>,
                    showDanger: true,
                    name: 'backgroundColor',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.backgroundColor,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.backgroundColorHeader'>backgroundColorHeader</Text>,
                    placeholder: <Text id='input.backgroundColorHeader'>backgroundColorHeader</Text>,
                    help: <Text id='input.backgroundColorHeader-help'>Background color of the header. Only applicaple if showCustomerLogo is enabled on domain.</Text>,
                    showDanger: true,
                    name: 'backgroundColorHeader',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.backgroundColorHeader,
                    isAdminField: true,
                },

                {
                    title: <Text id='input.articleContainerClasses'>articleContainerClasses</Text>,
                    placeholder: <Text id='input.articleContainerClasses'>articleContainerClasses</Text>,
                    help: <Text id='input.articleContainerClasses-help'>Default is "w-100", "w-50 px-2"...</Text>,
                    name: 'articleContainerClasses',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.articleContainerClasses?.map(yt => yt),
                    datalist: [
                        'class'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>class:</span> {obj.class}<br />
                        </div>
                    </>,
                    // hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.articleListClasses'>articleListClasses</Text>,
                    placeholder: <Text id='input.articleListClasses'>articleListClasses</Text>,
                    help: <Text id='input.articleListClasses-help'>Default is "flex-column", "flex-row", "flex-row"...</Text>,
                    name: 'articleListClasses',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.articleListClasses?.map(yt => yt),
                    datalist: [
                        'class'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>class:</span> {obj.class}<br />
                        </div>
                    </>,
                    // hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.articleListImageWidth'>articleListImageWidth</Text>,
                    placeholder: <Text id='input.articleListImageWidth'>articleListImageWidth</Text>,
                    help: <Text id='input.articleListImageWidth-help'>Default is "100%", "40%", "40%"...</Text>,
                    name: 'articleListImageWidth',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.articleListImageWidth?.map(yt => yt),
                    datalist: [
                        'width'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>width:</span> {obj.width}<br />
                        </div>
                    </>,
                    // hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.articleListImageLocations'>articleListImageLocations</Text>,
                    placeholder: <Text id='input.articleListImageLocations'>articleListImageLocations</Text>,
                    help: <Text id='input.articleListImageLocations-help'>Default is "top", "left", "right"...</Text>,
                    name: 'articleListImageLocations',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.articleListImageLocations?.map(yt => yt),
                    datalist: [
                        'location'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>location:</span> {obj.location}<br />
                        </div>
                    </>,
                    // hideOnCreate: true,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.customer-featureflags-title'>Customer Feature flags</Text>,
            help: <Text id='input.customer-featureflags-help'>Feature flags for customer. Some feature can be modules they order.</Text>,
            key: 'Customer featureFlags',
            fields: [
                {
                    title: <Text id='input.hideNativeAppPurchases'>Hide purchases in the native app</Text>,
                    placeholder: <Text id='input.hideNativeAppPurchases'>Hide purchases in the native app</Text>,
                    help: <Text id='input.hideNativeAppPurchases-help'>Inside the App Store / Google Play app, race access (tracking, results, news, streaming) can no longer be bought; the purchase screen is replaced by a notice. Access bought on the web is still shown in the app. Required by App Store guideline 3.1.1 and Google Play Billing.</Text>,
                    name: 'featureFlags.hideNativeAppPurchases',
                    type: 'toggle',
                    icon: 'fa-duotone fa-mobile-screen-button',
                    getValue: obj => parseInt(obj.featureFlags?.hideNativeAppPurchases, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.onlineBooking'>Online booking</Text>,
                    placeholder: <Text id='input.onlineBooking'>Online booking</Text>,
                    name: 'featureFlags.onlineBooking',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.onlineBooking, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.onlineBookingSelfRegister'>Self registration (without booking)</Text>,
                    placeholder: <Text id='input.onlineBookingSelfRegister'>Self registration (without booking)</Text>,
                    help: <Text id='input.onlineBookingSelfRegister-help'>Adds a "Register as customer" choice to the online booking page so clients can register their owner and animal info without booking a time.</Text>,
                    name: 'featureFlags.onlineBookingSelfRegister',
                    type: 'toggle',
                    icon: 'fa-duotone fa-user-plus',
                    getValue: obj => parseInt(obj.featureFlags?.onlineBookingSelfRegister, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.onlineBookingRequireAddress'>Require address on online booking</Text>,
                    placeholder: <Text id='input.onlineBookingRequireAddress'>Require address on online booking</Text>,
                    help: <Text id='input.onlineBookingRequireAddress-help'>Clients must fill in address, postal code and place before they can book a time or register as a customer on the online booking page.</Text>,
                    name: 'featureFlags.onlineBookingRequireAddress',
                    type: 'toggle',
                    icon: 'fa-duotone fa-map-location-dot',
                    getValue: obj => parseInt(obj.featureFlags?.onlineBookingRequireAddress, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.newsletter-unsubscribed'>Unsubscribe from Dyrejournal newsletter</Text>,
                    placeholder: <Text id='input.newsletter-unsubscribed'>Unsubscribe from Dyrejournal newsletter</Text>,
                    help: <Text id='input.newsletter-unsubscribed-help'>When enabled, this clinic will not receive newsletter emails from Dyrejournal.</Text>,
                    name: 'featureFlags.newsletterUnsubscribed',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope-open-text',
                    getValue: obj => parseInt(obj.featureFlags?.newsletterUnsubscribed, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.productionCompact'>Production compact mode</Text>,
                    placeholder: <Text id='input.productionCompact'>Production compact mode</Text>,
                    name: 'featureFlags.productionCompact',
                    type: 'toggle',
                    icon: 'fa-duotone fa-compress',
                    getValue: obj => parseInt(obj.featureFlags?.productionCompact, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.saleDateTop'>Show visit date at top of sale</Text>,
                    placeholder: <Text id='input.saleDateTop'>Show visit date at top of sale</Text>,
                    help: <Text id='input.saleDateTop-help'>Shows a prominent visit/sale date selector at the top of the sale, above customer selection, so the date is set before the journal starts. The chosen date is remembered for the next sale during the session (resets on reload) — handy when back-dating journals, e.g. for production animals.</Text>,
                    name: 'featureFlags.saleDateTop',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-day',
                    getValue: obj => parseInt(obj.featureFlags?.saleDateTop, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.enableOfflinePrefetch'>Enable offline prefetch</Text>,
                    placeholder: <Text id='input.enableOfflinePrefetch'>Enable offline prefetch</Text>,
                    help: <Text id='input.enableOfflinePrefetch-help'>Pre-downloads visitors, animals, products, drugs and diagnoses to the device so the dyrejournal-sale screen works offline. Auto-refreshes daily.</Text>,
                    name: 'featureFlags.enableOfflinePrefetch',
                    type: 'toggle',
                    icon: 'fa-duotone fa-cloud-arrow-down',
                    getValue: obj => parseInt(obj.featureFlags?.enableOfflinePrefetch, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.allowJournalWhenPaid'>Allow journal when paid</Text>,
                    placeholder: <Text id='input.allowJournalWhenPaid'>Allow journal when paid</Text>,
                    name: 'featureFlags.allowJournalWhenPaid',
                    type: 'toggle',
                    icon: 'fa-duotone fa-book-medical',
                    getValue: obj => parseInt(obj.featureFlags?.allowJournalWhenPaid, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.autoCreateJournal'>Auto-create journal from animal list</Text>,
                    placeholder: <Text id='input.autoCreateJournal'>Auto-create journal from animal list</Text>,
                    help: <Text id='input.autoCreateJournal-help'>When you click "Ny journal" on an animal in the sale, the journal is created and saved immediately (with owner, animal and date filled in) and opened for editing, so it autosaves as you type instead of being lost if you leave without pressing "Opprett journal".</Text>,
                    name: 'featureFlags.autoCreateJournal',
                    type: 'toggle',
                    icon: 'fa-duotone fa-file-circle-plus',
                    getValue: obj => parseInt(obj.featureFlags?.autoCreateJournal, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.journalFeedback'>Journal feedback</Text>,
                    placeholder: <Text id='input.journalFeedback'>Journal feedback</Text>,
                    help: <Text id='input.journalFeedback-help'>
                        Lets customer admins send journal feedback to the responsible veterinarian and save an internal
                        rating.
                    </Text>,
                    name: 'featureFlags.journalFeedback',
                    type: 'toggle',
                    icon: 'fa-duotone fa-message-pen',
                    getValue: obj => parseInt(obj.featureFlags?.journalFeedback, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.autoAddMedicineAsProduct'>Auto-add medicine as product line</Text>,
                    placeholder: <Text id='input.autoAddMedicineAsProduct'>Auto-add medicine as product line</Text>,
                    name: 'featureFlags.autoAddMedicineAsProduct',
                    type: 'toggle',
                    icon: 'fa-duotone fa-capsules',
                    getValue: obj => parseInt(obj.featureFlags?.autoAddMedicineAsProduct, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.autoAddJournalMedicineAsProducts'>Auto-add journal medicines as product lines</Text>,
                    placeholder: <Text id='input.autoAddJournalMedicineAsProducts'>Auto-add journal medicines as product lines</Text>,
                    help: <Text id='input.autoAddJournalMedicineAsProducts-help'>When a product with a linked journal is added to a visit, the journal's medicines are automatically added as price lines per animal. A product linked to the drug is used when it exists; otherwise the medicine's own price is used. Medicines without a price are skipped.</Text>,
                    name: 'featureFlags.autoAddJournalMedicineAsProducts',
                    type: 'toggle',
                    icon: 'fa-duotone fa-file-invoice-dollar',
                    getValue: obj => parseInt(obj.featureFlags?.autoAddJournalMedicineAsProducts, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.autoAddPrescriptionProduct'>Auto-add prescription product</Text>,
                    placeholder: <Text id='input.autoAddPrescriptionProduct'>Auto-add prescription product</Text>,
                    help: <Text id='input.autoAddPrescriptionProduct-help'>When a new prescription is created on a sale, every product marked "Auto-add on new prescription" is added as a sale line once per sale.</Text>,
                    name: 'featureFlags.autoAddPrescriptionProduct',
                    type: 'toggle',
                    icon: 'fa-duotone fa-prescription',
                    getValue: obj => parseInt(obj.featureFlags?.autoAddPrescriptionProduct, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.onlinePrescriptionOrders'>Online prescription orders</Text>,
                    placeholder: <Text id='input.onlinePrescriptionOrders'>Online prescription orders</Text>,
                    help: <Text id='input.onlinePrescriptionOrders-help'>Enables the public prescription order page (embed on your website) and the Reseptbestillinger inbox.</Text>,
                    name: 'featureFlags.onlinePrescriptionOrders',
                    type: 'toggle',
                    icon: 'fa-duotone fa-prescription-bottle',
                    getValue: obj => parseInt(obj.featureFlags?.onlinePrescriptionOrders, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.onlinePrescriptionOrderClinics'>Online prescription orders: sibling clinics</Text>,
                    placeholder: <Text id='input.onlinePrescriptionOrderClinics-placeholder'>e.g. "40,41"</Text>,
                    help: <Text id='input.onlinePrescriptionOrderClinics-help'>Comma-separated clinic ids. Pet owners registered at these clinics can also order through this clinic's prescription order page. The order still lands in this clinic's inbox.</Text>,
                    name: 'featureFlags.onlinePrescriptionOrderClinics',
                    type: 'text',
                    icon: 'fa-duotone fa-house-medical',
                    getValue: obj => obj.featureFlags?.onlinePrescriptionOrderClinics || '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.copyPriceLinesOnJournalCopy'>Copy price lines on journal copy</Text>,
                    placeholder: <Text id='input.copyPriceLinesOnJournalCopy'>Copy price lines on journal copy</Text>,
                    name: 'featureFlags.copyPriceLinesOnJournalCopy',
                    type: 'toggle',
                    icon: 'fa-duotone fa-copy',
                    getValue: obj => parseInt(obj.featureFlags?.copyPriceLinesOnJournalCopy, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.isVetFamilyMember'>Vet family member</Text>,
                    placeholder: <Text id='input.isVetFamilyMember'>Vet family member</Text>,
                    name: 'featureFlags.isVetFamilyMember',
                    type: 'toggle',
                    icon: 'fa-duotone fa-house-heart',
                    getValue: obj => parseInt(obj.featureFlags?.isVetFamilyMember, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.dyreid'>DyreId</Text>,
                    placeholder: <Text id='input.dyreid'>DyreId</Text>,
                    name: 'featureFlags.dyreid',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.dyreid, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.dyreIdReport'>DyreId Report</Text>,
                    placeholder: <Text id='input.dyreIdReport'>DyreId Report</Text>,
                    name: 'featureFlags.dyreIdReport',
                    type: 'toggle',
                    icon: 'fa-duotone fa-cloud-arrow-up',
                    getValue: obj => parseInt(obj.featureFlags?.dyreIdReport, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.animalia'>Animalia DHP</Text>,
                    placeholder: <Text id='input.animalia'>Animalia DHP</Text>,
                    name: 'featureFlags.animalia',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.animalia, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.mobileJournal'>Mobile Journal</Text>,
                    placeholder: <Text id='input.mobileJournal'>Mobile Journal</Text>,
                    name: 'featureFlags.mobileJournal',
                    type: 'toggle',
                    icon: 'fa-duotone fa-mobile',
                    getValue: obj => parseInt(obj.featureFlags?.mobileJournal, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.phoneConsultations'>Phone Consultations</Text>,
                    placeholder: <Text id='input.phoneConsultations'>Phone Consultations</Text>,
                    help: <Text id='input.phoneConsultations-help'>Enables the Telefonkonsultasjoner page for quickly logging phone calls without going through the full sale/journal flow.</Text>,
                    name: 'featureFlags.phoneConsultations',
                    type: 'toggle',
                    icon: 'fa-duotone fa-phone-volume',
                    getValue: obj => parseInt(obj.featureFlags?.phoneConsultations, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.fiken'>Fiken</Text>,
                    placeholder: <Text id='input.fiken'>Fiken</Text>,
                    name: 'featureFlags.fiken',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.fiken, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.tripletex'>Tripletex</Text>,
                    placeholder: <Text id='input.tripletex'>Tripletex</Text>,
                    name: 'featureFlags.tripletex',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.tripletex, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.duett'>Duett</Text>,
                    placeholder: <Text id='input.duett'>Duett</Text>,
                    name: 'featureFlags.duett',
                    type: 'toggle',
                    icon: 'fa-duotone fa-building-columns',
                    getValue: obj => parseInt(obj.featureFlags?.duett, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.duettVoucher'>Duett bilagsrapportering</Text>,
                    placeholder: <Text id='input.duettVoucher'>Duett bilagsrapportering</Text>,
                    name: 'featureFlags.duettVoucher',
                    type: 'toggle',
                    icon: 'fa-duotone fa-file-invoice',
                    getValue: obj => parseInt(obj.featureFlags?.duettVoucher, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.duettVoucherPeriod'>Duett bilagsperiode</Text>,
                    placeholder: <Text id='input.duettVoucherPeriod'>Duett bilagsperiode</Text>,
                    name: 'featureFlags.duettVoucherPeriod',
                    type: 'select',
                    datalist: [
                        ['daily', 'Daglig'],
                        ['weekly', 'Ukentlig'],
                        ['monthly', 'Månedlig'],
                    ],
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.featureFlags?.duettVoucherPeriod || 'daily',
                    showInList: false,
                },
                {
                    title: <Text id='input.duettAutoSyncPayments'>Duett auto-sync betalinger</Text>,
                    placeholder: <Text id='input.duettAutoSyncPayments'>Duett auto-sync betalinger</Text>,
                    help: <Text id='input.duettAutoSyncPayments-help'>Automatically sync payment status from Duett once a day (rolling 60-day window).</Text>,
                    name: 'featureFlags.duettAutoSyncPayments',
                    type: 'toggle',
                    icon: 'fa-duotone fa-rotate',
                    getValue: obj => parseInt(obj.featureFlags?.duettAutoSyncPayments, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.tripletexAutoSyncPayments'>Tripletex Auto-Sync Payments</Text>,
                    placeholder: <Text id='input.tripletexAutoSyncPayments'>Tripletex Auto-Sync Payments</Text>,
                    help: <Text id='input.tripletexAutoSyncPayments-help'>Automatically pull invoice payment status from Tripletex once a day (rolling 60-day window).</Text>,
                    name: 'featureFlags.tripletexAutoSyncPayments',
                    type: 'toggle',
                    icon: 'fa-duotone fa-rotate',
                    getValue: obj => parseInt(obj.featureFlags?.tripletexAutoSyncPayments, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.powerOfficeAutoSyncPayments'>PowerOffice auto-sync payments</Text>,
                    placeholder: <Text id='input.powerOfficeAutoSyncPayments'>PowerOffice auto-sync payments</Text>,
                    help: <Text id='input.powerOfficeAutoSyncPayments-help'>Automatically pull invoice payment status from PowerOffice Go once a day (rolling 60-day window).</Text>,
                    name: 'featureFlags.powerOfficeAutoSyncPayments',
                    type: 'toggle',
                    icon: 'fa-duotone fa-rotate',
                    getValue: obj => parseInt(obj.featureFlags?.powerOfficeAutoSyncPayments, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenAutoSyncPayments'>Fiken auto-sync payments</Text>,
                    placeholder: <Text id='input.fikenAutoSyncPayments'>Fiken auto-sync payments</Text>,
                    help: <Text id='input.fikenAutoSyncPayments-help'>Automatically pull invoice payment status from Fiken once a day (rolling 60-day window).</Text>,
                    name: 'featureFlags.fikenAutoSyncPayments',
                    type: 'toggle',
                    icon: 'fa-duotone fa-rotate',
                    getValue: obj => parseInt(obj.featureFlags?.fikenAutoSyncPayments, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.showVetOnInvoice'>Show vet on invoice</Text>,
                    placeholder: <Text id='input.showVetOnInvoice'>Show vet on invoice</Text>,
                    help: <Text id='input.showVetOnInvoice-help'>Print the responsible vet (the seller on each line) on invoices, receipts and the invoice lines sent to the accounting system. On a collective invoice every visit keeps its own vet, so the customer can see who did what.</Text>,
                    name: 'featureFlags.showVetOnInvoice',
                    type: 'toggle',
                    icon: 'fa-duotone fa-user-doctor',
                    getValue: obj => parseInt(obj.featureFlags?.showVetOnInvoice, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.criticalTasks'>Critical tasks card</Text>,
                    placeholder: <Text id='input.criticalTasks'>Critical tasks card</Text>,
                    help: <Text id='input.criticalTasks-help'>Show the "Critical tasks" card on the frontpage for admins (insurance follow-up, accounting transfer, overdue invoices, etc.). On by default — turn off to hide the card.</Text>,
                    name: 'featureFlags.criticalTasks',
                    type: 'toggle',
                    icon: 'fa-duotone fa-list-check',
                    // The toggle displays getValue as current state (checked when 1).
                    // Default ON: checked unless the clinic explicitly disabled it (0).
                    getValue: obj => parseInt(obj.featureFlags?.criticalTasks, 10) === 0 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.peopleAtWork'>People at work today</Text>,
                    placeholder: <Text id='input.peopleAtWork'>People at work today</Text>,
                    help: <Text id='input.peopleAtWork-help'>Show a card on the frontpage with the colleagues who have used the system today — photo or initials, name and when they were last active. Off by default.</Text>,
                    name: 'featureFlags.peopleAtWork',
                    type: 'toggle',
                    icon: 'fa-duotone fa-user-clock',
                    getValue: obj => parseInt(obj.featureFlags?.peopleAtWork, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.weeklyChecklist'>Weekly checklist (Ukens liste)</Text>,
                    placeholder: <Text id='input.weeklyChecklist'>Weekly checklist (Ukens liste)</Text>,
                    help: <Text id='input.weeklyChecklist-help'>Every Monday morning the critical tasks are frozen as the week's list with a fixed deadline Friday 16:00. Progress is shown on the frontpage, and the clinic admins get an e-mail Friday afternoon when items remain. Off by default.</Text>,
                    name: 'featureFlags.weeklyChecklist',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-check',
                    getValue: obj => parseInt(obj.featureFlags?.weeklyChecklist, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.customerCredit'>Customer credit (Kundekonto) on returns</Text>,
                    placeholder: <Text id='input.customerCredit'>Customer credit (Kundekonto) on returns</Text>,
                    help: <Text id='input.customerCredit-help'>Lets a return sale be settled onto the customer's account instead of a cash or bank refund, and lets that credit pay for a later sale. Adds the payment method «Kundekonto» (account 2900 by default). Off by default.</Text>,
                    name: 'featureFlags.customerCredit',
                    type: 'toggle',
                    icon: 'fa-duotone fa-piggy-bank',
                    getValue: obj => parseInt(obj.featureFlags?.customerCredit, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.smsOnOnlineBooking'>SMS to clinic on online booking</Text>,
                    placeholder: <Text id='input.smsOnOnlineBooking'>SMS to clinic on online booking</Text>,
                    help: <Text id='input.smsOnOnlineBooking-help'>Send an SMS to the clinic phone number whenever a customer completes a new online booking.</Text>,
                    name: 'featureFlags.smsOnOnlineBooking',
                    type: 'toggle',
                    icon: 'fa-duotone fa-message-sms',
                    getValue: obj => parseInt(obj.featureFlags?.smsOnOnlineBooking, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.smsOnSelfRegister'>SMS to clinic on self-registration</Text>,
                    placeholder: <Text id='input.smsOnSelfRegister'>SMS to clinic on self-registration</Text>,
                    help: <Text id='input.smsOnSelfRegister-help'>Send an SMS to the clinic phone number whenever a customer self-registers.</Text>,
                    name: 'featureFlags.smsOnSelfRegister',
                    type: 'toggle',
                    icon: 'fa-duotone fa-message-sms',
                    getValue: obj => parseInt(obj.featureFlags?.smsOnSelfRegister, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.emailOnSelfRegister'>Email to clinic on self-registration</Text>,
                    placeholder: <Text id='input.emailOnSelfRegister'>Email to clinic on self-registration</Text>,
                    help: <Text id='input.emailOnSelfRegister-help'>Send an email to the clinic email address whenever a customer self-registers.</Text>,
                    name: 'featureFlags.emailOnSelfRegister',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope',
                    getValue: obj => parseInt(obj.featureFlags?.emailOnSelfRegister, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.tripletexVoucher'>Tripletex Voucher Reporting</Text>,
                    placeholder: <Text id='input.tripletexVoucher'>Tripletex Voucher Reporting</Text>,
                    name: 'featureFlags.tripletexVoucher',
                    type: 'toggle',
                    icon: 'fa-duotone fa-file-invoice',
                    getValue: obj => parseInt(obj.featureFlags?.tripletexVoucher, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.tripletexVoucherPeriod'>Tripletex Voucher Default Period</Text>,
                    placeholder: <Text id='input.tripletexVoucherPeriod'>Tripletex Voucher Default Period</Text>,
                    name: 'featureFlags.tripletexVoucherPeriod',
                    type: 'select',
                    datalist: [
                        ['daily', 'Daily (Dagsoppgjor)'],
                        ['weekly', 'Weekly (Ukesoppgjor)'],
                        ['monthly', 'Monthly (Manedsoppgjor)'],
                    ],
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.featureFlags?.tripletexVoucherPeriod || 'daily',
                    showInList: false,
                },
                {
                    title: <Text id='input.tripletexVoucherRoundingAccount'>Tripletex Voucher Rounding Account</Text>,
                    placeholder: <Text id='input.tripletexVoucherRoundingAccount-placeholder'>8050</Text>,
                    name: 'featureFlags.tripletexVoucherRoundingAccount',
                    type: 'number',
                    icon: 'fa-duotone fa-calculator',
                    getValue: obj => obj.featureFlags?.tripletexVoucherRoundingAccount || 8050,
                    showInList: false,
                },
                {
                    title: <Text id='input.tripletexVoucherSendToLedger'>Tripletex Voucher - Send Directly to Ledger</Text>,
                    placeholder: <Text id='input.tripletexVoucherSendToLedger'>Tripletex Voucher - Send Directly to Ledger</Text>,
                    name: 'featureFlags.tripletexVoucherSendToLedger',
                    type: 'toggle',
                    icon: 'fa-duotone fa-paper-plane',
                    getValue: obj => parseInt(obj.featureFlags?.tripletexVoucherSendToLedger, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.tripletexVoucherAutoPost'>Tripletex Voucher - Post Automatically Every Monday</Text>,
                    placeholder: <Text id='input.tripletexVoucherAutoPost'>Tripletex Voucher - Post Automatically Every Monday</Text>,
                    help: <Text id='input.tripletexVoucherAutoPost-help'>Every Monday the dagsoppgjør vouchers for the last 35 days that are not yet posted are sent to Tripletex automatically, one voucher per period (daily/weekly/monthly as set above). Invoice sales are excluded.</Text>,
                    name: 'featureFlags.tripletexVoucherAutoPost',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-check',
                    getValue: obj => parseInt(obj.featureFlags?.tripletexVoucherAutoPost, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.powerOffice'>PowerOffice</Text>,
                    placeholder: <Text id='input.powerOffice'>PowerOffice</Text>,
                    name: 'featureFlags.powerOffice',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.powerOffice, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.convenePayment'>Convene Payments</Text>,
                    placeholder: <Text id='input.convenePayment'>Convene Payments</Text>,
                    name: 'featureFlags.convenePayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-credit-card',
                    getValue: obj => parseInt(obj.featureFlags?.convenePayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.idexx'>IDEXX</Text>,
                    placeholder: <Text id='input.idexx'>IDEXX</Text>,
                    name: 'featureFlags.idexx',
                    type: 'toggle',
                    icon: 'fa-duotone fa-flask-vial',
                    getValue: obj => parseInt(obj.featureFlags?.idexx, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.idexxPimsId'>IDEXX PIMS ID</Text>,
                    placeholder: <Text id='input.idexxPimsId-placeholder'>e.g. 29ef5b5a-5c15-4cbc-8882-ade087450136</Text>,
                    help: <Text id='input.idexxPimsId-help'>PIMS ID from IDEXX, required for results polling. Get this from IDEXX support.</Text>,
                    name: 'featureFlags.idexxPimsId',
                    type: 'text',
                    icon: 'fa-duotone fa-id-badge',
                    getValue: obj => obj.featureFlags?.idexxPimsId || '',
                    showInList: false,
                },
                {
                    title: <Text id='input.idexxEnvironment'>IDEXX Environment</Text>,
                    placeholder: <Text id='input.idexxEnvironment-placeholder'>production</Text>,
                    name: 'featureFlags.idexxEnvironment',
                    type: 'select',
                    datalist: [
                        ['production', 'Production'],
                        ['demo', 'Demo'],
                    ],
                    icon: 'fa-duotone fa-server',
                    getValue: obj => obj.featureFlags?.idexxEnvironment || 'production',
                    showInList: false,
                },
                {
                    title: <Text id='input.nets'>NETS</Text>,
                    placeholder: <Text id='input.nets'>NETS</Text>,
                    name: 'featureFlags.nets',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.nets, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.kpiDashboard'>KPI Dashboard</Text>,
                    placeholder: <Text id='input.kpiDashboard'>KPI Dashboard</Text>,
                    name: 'featureFlags.kpiDashboard',
                    type: 'toggle',
                    icon: 'fa-duotone fa-chart-line',
                    getValue: obj => parseInt(obj.featureFlags?.kpiDashboard, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.onThisDay'>Dagen i dag</Text>,
                    placeholder: <Text id='input.onThisDay'>Dagen i dag</Text>,
                    help: <Text id='input.onThisDay-help'>Vis en boks på forsiden med dagens dato, klokke, navnedag og historiske hendelser.</Text>,
                    name: 'featureFlags.onThisDay',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-star',
                    getValue: obj => parseInt(obj.featureFlags?.onThisDay, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.feedReturnFoundation'>Feed return foundation</Text>,
                    placeholder: <Text id='input.feedReturnFoundation'>Feed return foundation</Text>,
                    help: <Text id='input.feedReturnFoundation-help'>Enables new-post indicators, feed progress, and saved posts for the customer.</Text>,
                    name: 'featureFlags.feedReturnFoundation',
                    type: 'toggle',
                    icon: 'fa-duotone fa-bookmark',
                    getValue: obj => parseInt(obj.featureFlags?.feedReturnFoundation, 10) === 1 ? 0 : 1,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.articlePages'>Article pages</Text>,
                    placeholder: <Text id='input.articlePages'>Article pages</Text>,
                    help: <Text id='input.articlePages-help'>Serve every public article as a crawlable page at /article/... on this domain, list them in sitemap.xml, and open articles in the app as a page instead of a drawer. Off keeps the drawer and the app shell.</Text>,
                    name: 'featureFlags.articlePages',
                    type: 'toggle',
                    icon: 'fa-duotone fa-file-lines',
                    getValue: obj => obj.featureFlags?.articlePages ? 1 : 0,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.articlePagesCategories'>Article page categories</Text>,
                    placeholder: <Text id='input.articlePagesCategories-placeholder'>e.g. "newsletter,news"</Text>,
                    help: <Text id='input.articlePagesCategories-help'>Comma-separated article categories that get a public page when Article pages is on. Empty means every public article.</Text>,
                    name: 'featureFlags.articlePagesCategories',
                    type: 'text',
                    icon: 'fa-duotone fa-list',
                    getValue: obj => obj.featureFlags?.articlePagesCategories || '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.forceDarkmode'>Force dark mode</Text>,
                    placeholder: <Text id='input.forceDarkmode'>Force dark mode</Text>,
                    help: <Text id='input.forceDarkmode-help'>Every visitor on this domain gets dark mode, regardless of their own setting. The user's own dark mode switch has no effect here.</Text>,
                    name: 'featureFlags.forceDarkmode',
                    type: 'toggle',
                    icon: 'fa-duotone fa-moon',
                    getValue: obj => obj.featureFlags?.forceDarkmode ? 1 : 0,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.faceRecognition'>Face recognition</Text>,
                    placeholder: <Text id='input.faceRecognition'>Face recognition</Text>,
                    help: <Text id='input.faceRecognition-help'>Shows the face-recognition consent panel on the user profile. Requires the Rekognition collection and the rekognition:* IAM grants for this deployment. Off by default.</Text>,
                    name: 'featureFlags.faceRecognition',
                    type: 'toggle',
                    icon: 'fa-duotone fa-face-viewfinder',
                    getValue: obj => parseInt(obj.featureFlags?.faceRecognition, 10) === 1 ? 0 : 1,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.feedPersonalization'>Feed personalization</Text>,
                    placeholder: <Text id='input.feedPersonalization'>Feed personalization</Text>,
                    help: <Text id='input.feedPersonalization-help'>Enables explicit author and topic follows with the chronological For you feed.</Text>,
                    name: 'featureFlags.feedPersonalization',
                    type: 'toggle',
                    icon: 'fa-duotone fa-user-check',
                    getValue: obj => parseInt(obj.featureFlags?.feedPersonalization, 10) === 1 ? 0 : 1,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.breece'>Breece Cloud</Text>,
                    placeholder: <Text id='input.breece'>Breece Cloud</Text>,
                    name: 'featureFlags.breece',
                    type: 'toggle',
                    icon: 'fa-duotone fa-tags',
                    getValue: obj => parseInt(obj.featureFlags?.breece, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.vippsIntegration'>VIPPS Integration</Text>,
                    placeholder: <Text id='input.vippsIntegration'>VIPPS Integration</Text>,
                    name: 'featureFlags.vippsIntegration',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.vippsIntegration, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.vippsInClinicPayment'>VIPPS in-clinic payment (integrated)</Text>,
                    placeholder: <Text id='input.vippsInClinicPayment'>VIPPS in-clinic payment (integrated)</Text>,
                    help: <Text id='input.vippsInClinicPayment-help'>Lets the Vipps button in the sale payment drawer show a one-time Vipps QR code the customer scans to pay in the clinic. Requires the Vipps API keys under Integrations; off = the button only records the sale as paid by Vipps.</Text>,
                    name: 'featureFlags.vippsInClinicPayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-mobile-screen',
                    getValue: obj => parseInt(obj.featureFlags?.vippsInClinicPayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.dpm'>DyreID e-prescription (DPM)</Text>,
                    placeholder: <Text id='input.dpm'>DyreID e-prescription (DPM)</Text>,
                    help: <Text id='input.dpm-help'>Shows the «E-resept (DyreID)» button in the journal and lets DyreID read this clinic's launched patients and deliver prescriptions back.</Text>,
                    name: 'featureFlags.dpm',
                    type: 'toggle',
                    icon: 'fa-duotone fa-file-prescription',
                    getValue: obj => parseInt(obj.featureFlags?.dpm, 10) === 1 ? 0 : 1,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.dpmStaging'>DyreID DPM staging</Text>,
                    placeholder: <Text id='input.dpmStaging'>DyreID DPM staging</Text>,
                    help: <Text id='input.dpmStaging-help'>Test clinics only: launch DyreID's staging environment with the staging signing key.</Text>,
                    name: 'featureFlags.dpmStaging',
                    type: 'toggle',
                    icon: 'fa-duotone fa-flask',
                    getValue: obj => parseInt(obj.featureFlags?.dpmStaging, 10) === 1 ? 0 : 1,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.saleLineColors'>Sale Line Colors</Text>,
                    placeholder: <Text id='input.saleLineColors'>Sale Line Colors</Text>,
                    name: 'featureFlags.saleLineColors',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.saleLineColors, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarOffDutyColor'>Calendar Off-Duty Color</Text>,
                    placeholder: <Text id='input.calendarOffDutyColor'>Calendar Off-Duty Color</Text>,
                    name: 'featureFlags.calendarOffDutyColor',
                    type: 'select',
                    datalist: [
                        ['', 'Light (default)'],
                        ['medium', 'Medium'],
                        ['dark', 'Dark'],
                        ['veryDark', 'Very Dark'],
                    ],
                    icon: 'fa-duotone fa-palette',
                    getValue: obj => obj.featureFlags?.calendarOffDutyColor || '',
                    showInList: false,
                },
                {
                    title: <Text id='input.hideCalendarPopupPreview'>Hide Calendar Popup Preview</Text>,
                    placeholder: <Text id='input.hideCalendarPopupPreview'>Hide Calendar Popup Preview</Text>,
                    name: 'featureFlags.hideCalendarPopupPreview',
                    type: 'toggle',
                    icon: 'fa-duotone fa-eye-slash',
                    getValue: obj => parseInt(obj.featureFlags?.hideCalendarPopupPreview, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.hideCalendarInEventEditor'>Hide Calendar in Event Editor</Text>,
                    placeholder: <Text id='input.hideCalendarInEventEditor'>Hide Calendar in Event Editor</Text>,
                    name: 'featureFlags.hideCalendarInEventEditor',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-xmark',
                    getValue: obj => parseInt(obj.featureFlags?.hideCalendarInEventEditor, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.showCalendarWhenEditEvent'>Show Calendar When Editing Event</Text>,
                    placeholder: <Text id='input.showCalendarWhenEditEvent'>Show Calendar When Editing Event</Text>,
                    name: 'featureFlags.showCalendarWhenEditEvent',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-pen',
                    getValue: obj => parseInt(obj.featureFlags?.showCalendarWhenEditEvent, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.calendarHourHeight'>Calendar Hour Height (px)</Text>,
                    placeholder: <Text id='input.calendarHourHeight-placeholder'>80</Text>,
                    name: 'featureFlags.calendarHourHeight',
                    type: 'number',
                    icon: 'fa-duotone fa-arrows-up-down',
                    getValue: obj => obj.featureFlags?.calendarHourHeight || '',
                    showInList: false,
                },
                {
                    title: <Text id='input.calendarTodosWeekMonth'>Show todos in week/month view</Text>,
                    placeholder: <Text id='input.calendarTodosWeekMonth'>Show todos in week/month view</Text>,
                    help: <Text id='input.calendarTodosWeekMonth-help'>Show the calendar todo list above the week and month views (not only in day view).</Text>,
                    name: 'featureFlags.calendarTodosWeekMonth',
                    type: 'toggle',
                    icon: 'fa-duotone fa-list-check',
                    getValue: obj => (parseInt(obj.featureFlags?.calendarTodosWeekMonth, 10) === 1 ? 1 : 0),
                    showInList: false,
                },
                {
                    title: <Text id='input.workSharing'>Work Sharing</Text>,
                    placeholder: <Text id='input.workSharing'>Work Sharing</Text>,
                    name: 'featureFlags.workSharing',
                    type: 'toggle',
                    icon: 'fa-solid fa-share-nodes',
                    getValue: obj => parseInt(obj.featureFlags?.workSharing, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.holidayPlanner'>Holiday Planner</Text>,
                    placeholder: <Text id='input.holidayPlanner'>Holiday Planner</Text>,
                    name: 'featureFlags.holidayPlanner',
                    type: 'toggle',
                    icon: 'fa-solid fa-umbrella-beach',
                    getValue: obj => parseInt(obj.featureFlags?.holidayPlanner, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.courses'>Courses</Text>,
                    placeholder: <Text id='input.courses'>Courses</Text>,
                    name: 'featureFlags.courses',
                    type: 'toggle',
                    icon: 'fa-solid fa-graduation-cap',
                    getValue: obj => parseInt(obj.featureFlags?.courses, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.tvRadioProducer'>TV/Radio Producer</Text>,
                    placeholder: <Text id='input.tvRadioProducer'>TV/Radio Producer</Text>,
                    name: 'featureFlags.tvRadioProducer',
                    type: 'toggle',
                    icon: 'fa-solid fa-broadcast-tower',
                    getValue: obj => parseInt(obj.featureFlags?.tvRadioProducer, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.halloweenDecoration'>Halloween decoration</Text>,
                    name: 'featureFlags.halloweenDecoration',
                    type: 'toggle',
                    icon: 'fa-solid fa-toggle-on',
                    getValue: obj => parseInt(obj.featureFlags?.halloweenDecoration, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.christmasLights'>Christmas Lights</Text>,
                    placeholder: <Text id='input.christmasLights'>Christmas Lights</Text>,
                    name: 'featureFlags.christmasLights',
                    type: 'toggle',
                    icon: 'fa-duotone fa-lights-holiday',
                    getValue: obj => parseInt(obj.featureFlags?.christmasLights, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.christmasLightsNumLights'>Christmas Lights: Number of lights</Text>,
                    placeholder: <Text id='input.christmasLightsNumLights'>20</Text>,
                    name: 'featureFlags.christmasLightsNumLights',
                    type: 'number',
                    icon: 'fa-duotone fa-lights-holiday',
                    getValue: obj => obj.featureFlags?.christmasLightsNumLights,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.christmasLightsColorScheme'>Christmas Lights: Color scheme</Text>,
                    placeholder: <Text id='input.christmasLightsColorScheme'>classic</Text>,
                    name: 'featureFlags.christmasLightsColorScheme',
                    type: 'select',
                    datalist: [
                        ['classic', 'Classic (red, green, yellow, blue, magenta, orange)'],
                        ['warm', 'Warm (red, orange, yellow, gold)'],
                        ['cool', 'Cool (blue, cyan, white, purple)'],
                        ['rainbow', 'Rainbow'],
                        ['gold', 'Gold only'],
                        ['red', 'Red only'],
                        ['white', 'White only'],
                    ],
                    icon: 'fa-duotone fa-palette',
                    getValue: obj => obj.featureFlags?.christmasLightsColorScheme,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.christmasLightsSpeed'>Christmas Lights: Animation speed</Text>,
                    placeholder: <Text id='input.christmasLightsSpeed'>normal</Text>,
                    name: 'featureFlags.christmasLightsSpeed',
                    type: 'select',
                    datalist: [
                        ['slow', 'Slow'],
                        ['normal', 'Normal'],
                        ['fast', 'Fast'],
                    ],
                    icon: 'fa-duotone fa-gauge',
                    getValue: obj => obj.featureFlags?.christmasLightsSpeed,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.christmasLightsAnimation'>Christmas Lights: Animation style</Text>,
                    placeholder: <Text id='input.christmasLightsAnimation'>flicker</Text>,
                    name: 'featureFlags.christmasLightsAnimation',
                    type: 'select',
                    datalist: [
                        ['flicker', 'Flicker'],
                        ['chase', 'Chase'],
                        ['twinkle', 'Twinkle'],
                        ['steady', 'Steady (no animation)'],
                    ],
                    icon: 'fa-duotone fa-sparkles',
                    getValue: obj => obj.featureFlags?.christmasLightsAnimation,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.christmasLightsSize'>Christmas Lights: Light size</Text>,
                    placeholder: <Text id='input.christmasLightsSize'>medium</Text>,
                    name: 'featureFlags.christmasLightsSize',
                    type: 'select',
                    datalist: [
                        ['small', 'Small'],
                        ['medium', 'Medium'],
                        ['large', 'Large'],
                    ],
                    icon: 'fa-duotone fa-arrows-maximize',
                    getValue: obj => obj.featureFlags?.christmasLightsSize,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.christmasLightsShowWire'>Christmas Lights: Show wire</Text>,
                    placeholder: <Text id='input.christmasLightsShowWire'>Show wire</Text>,
                    name: 'featureFlags.christmasLightsShowWire',
                    type: 'toggle',
                    icon: 'fa-duotone fa-wave-sine',
                    getValue: obj => parseInt(obj.featureFlags?.christmasLightsShowWire, 10) === 1 ? 0 : 1,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.useFormFieldsCompact'>Use compact form fields (Animal/Journal)</Text>,
                    placeholder: <Text id='input.useFormFieldsCompact'>Enable compact form</Text>,
                    name: 'featureFlags.useFormFieldsCompact',
                    type: 'toggle',
                    icon: 'fa-duotone fa-table-cells',
                    getValue: obj => parseInt(obj.featureFlags?.useFormFieldsCompact, 10) === 1 ? 0 : 1,
                },
                {
                    title: <Text id='input.cardExternalTerminal'>Card external terminal</Text>,
                    placeholder: <Text id='input.cardExternalTerminal'>Card external terminal</Text>,
                    help: <Text id='input.cardExternalTerminal-help'>Adds a separate "Card (external terminal)" payment button so card payments taken on a bank terminal outside the clinic are registered separately from the clinic terminal. Keeps the terminal reconciliation correct.</Text>,
                    name: 'featureFlags.cardExternalTerminal',
                    type: 'toggle',
                    icon: 'fa-duotone fa-credit-card',
                    getValue: obj => parseInt(obj.featureFlags?.cardExternalTerminal, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentHideCard'>paymentHideCard</Text>,
                    placeholder: <Text id='input.paymentHideCard'>paymentHideCard</Text>,
                    name: 'featureFlags.paymentHideCard',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideCard, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.paymentHideCash'>paymentHideCash</Text>,
                    placeholder: <Text id='input.paymentHideCash'>paymentHideCash</Text>,
                    name: 'featureFlags.paymentHideCash',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideCash, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.paymentHideVipps'>paymentHideVipps</Text>,
                    placeholder: <Text id='input.paymentHideVipps'>paymentHideVipps</Text>,
                    name: 'featureFlags.paymentHideVipps',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideVipps, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.paymentHideInvoice'>paymentHideInvoice</Text>,
                    placeholder: <Text id='input.paymentHideInvoice'>paymentHideInvoice</Text>,
                    name: 'featureFlags.paymentHideInvoice',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideInvoice, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.paymentHideAccount'>paymentHideAccount</Text>,
                    placeholder: <Text id='input.paymentHideAccount'>paymentHideAccount</Text>,
                    name: 'featureFlags.paymentHideAccount',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideAccount, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.creditCard'>Credit Card Payment</Text>,
                    placeholder: <Text id='input.creditCard'>Credit Card Payment</Text>,
                    name: 'featureFlags.creditCard',
                    type: 'toggle',
                    icon: 'fa-regular fa-credit-card',
                    getValue: obj => parseInt(obj.featureFlags?.creditCard, 10) === 1 ? 0 : 1,
                    showInList: false,
                },

                {
                    title: <Text id='input.audioTranscribe'>Audio Transcribe (Dikter)</Text>,
                    placeholder: <Text id='input.audioTranscribe'>Audio Transcribe (Dikter)</Text>,
                    name: 'featureFlags.audioTranscribe',
                    type: 'toggle',
                    icon: 'fa-duotone fa-microphone',
                    getValue: obj => parseInt(obj.featureFlags?.audioTranscribe, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.ambientJournal'>Ambient Journal (Konsultasjonsmodus)</Text>,
                    placeholder: <Text id='input.ambientJournal'>Ambient Journal (Konsultasjonsmodus)</Text>,
                    name: 'featureFlags.ambientJournal',
                    type: 'toggle',
                    icon: 'fa-duotone fa-waveform-lines',
                    getValue: obj => parseInt(obj.featureFlags?.ambientJournal, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.aiPrescription'>AI prescription (proposal and interaction check)</Text>,
                    placeholder: <Text id='input.aiPrescription'>AI prescription (proposal and interaction check)</Text>,
                    help: <Text id='input.aiPrescription-help'>Lets the vet ask AI for a prescription proposal based on the journal and warns about drug interactions in the prescription drawer. Each call uses the Anthropic API.</Text>,
                    name: 'featureFlags.aiPrescription',
                    type: 'toggle',
                    icon: 'fa-duotone fa-wand-magic-sparkles',
                    getValue: obj => parseInt(obj.featureFlags?.aiPrescription, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.dynamicShooting'>Dynamic shooting (IPSC) module</Text>,
                    placeholder: <Text id='input.dynamicShooting'>Dynamic shooting (IPSC) module</Text>,
                    help: <Text id='input.dynamicShooting-help'>Paid module: IPSC stage scoring, squads and hit-factor results in the convention module. Switched on by KeepSpot admins only.</Text>,
                    name: 'featureFlags.dynamicShooting',
                    type: 'toggle',
                    icon: 'fa-duotone fa-person-rifle',
                    getValue: obj => parseInt(obj.featureFlags?.dynamicShooting, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.aiAssistant'>AI assistant ("What should I do now?" on the frontpage)</Text>,
                    placeholder: <Text id='input.aiAssistant'>AI assistant ("What should I do now?" on the frontpage)</Text>,
                    help: <Text id='input.aiAssistant-help'>Shows the "What should I do now?" button on the Critical tasks card. AI writes a prioritized plan from the clinic's own numbers. Each fresh answer is billed per request and logged on the user.</Text>,
                    name: 'featureFlags.aiAssistant',
                    type: 'toggle',
                    icon: 'fa-duotone fa-wand-magic-sparkles',
                    getValue: obj => parseInt(obj.featureFlags?.aiAssistant, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.aiSaleDraft'>AI visit draft (mobile: create a draft sale from text or speech)</Text>,
                    placeholder: <Text id='input.aiSaleDraft'>AI visit draft (mobile: create a draft sale from text or speech)</Text>,
                    help: <Text id='input.aiSaleDraft-help'>Shows a text field with microphone on the mobile sale page. The vet describes the visit in one sentence and AI finds the owner, animals and drug and creates a draft sale with journal and prescription for the vet to verify. Never creates owners or animals. Each turn is billed per request.</Text>,
                    name: 'featureFlags.aiSaleDraft',
                    type: 'toggle',
                    icon: 'fa-duotone fa-wand-magic-sparkles',
                    getValue: obj => parseInt(obj.featureFlags?.aiSaleDraft, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.nrr'>NRR Min Katt (cat lookup)</Text>,
                    placeholder: <Text id='input.nrr'>NRR Min Katt (cat lookup)</Text>,
                    help: <Text id='input.nrr-help'>Lets the clinic look up a cat in the Norwegian cat registry (NRR) by chip number or registration number. All clinics share one NRR API account, so only KeepSpot admins may enable it.</Text>,
                    name: 'featureFlags.nrr',
                    type: 'toggle',
                    icon: 'fa-duotone fa-cat',
                    getValue: obj => parseInt(obj.featureFlags?.nrr, 10) === 1 ? 0 : 1,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.hasVippsPayment'>hasVippsPayment</Text>,
                    placeholder: <Text id='input.hasVippsPayment'>hasVippsPayment</Text>,
                    name: 'featureFlags.hasVippsPayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.hasVippsPayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.hasPaypalPayment'>hasPaypalPayment</Text>,
                    placeholder: <Text id='input.hasPaypalPayment'>hasPaypalPayment</Text>,
                    name: 'featureFlags.hasPaypalPayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.hasPaypalPayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.hasBankPayment'>hasBankPayment</Text>,
                    placeholder: <Text id='input.hasBankPayment'>hasBankPayment</Text>,
                    name: 'featureFlags.hasBankPayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.hasBankPayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.hasInvoicePayment'>hasInvoicePayment</Text>,
                    placeholder: <Text id='input.hasInvoicePayment'>hasInvoicePayment</Text>,
                    name: 'featureFlags.hasInvoicePayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.hasInvoicePayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.hasAppPayment'>hasAppPayment</Text>,
                    placeholder: <Text id='input.hasAppPayment'>hasAppPayment</Text>,
                    name: 'featureFlags.hasAppPayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-app-store',
                    getValue: obj => parseInt(obj.featureFlags?.hasAppPayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.forceChooseSalesUser'>Force Choose Sales User</Text>,
                    placeholder: <Text id='input.forceChooseSalesUser'>Force Choose Sales User</Text>,
                    name: 'featureFlags.forceChooseSalesUser',
                    type: 'toggle',
                    icon: 'fa-duotone fa-user-check',
                    getValue: obj => parseInt(obj.featureFlags?.forceChooseSalesUser, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.skipSMSReminders'>Skip SMS Reminders</Text>,
                    placeholder: <Text id='input.skipSMSReminders'>Skip SMS Reminders</Text>,
                    name: 'featureFlags.skipSMSReminders',
                    type: 'toggle',
                    icon: 'fa-duotone fa-message-slash',
                    getValue: obj => parseInt(obj.featureFlags?.skipSMSReminders, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.skipSMSNotifications'>Skip SMS Notifications</Text>,
                    placeholder: <Text id='input.skipSMSNotifications'>Skip SMS Notifications</Text>,
                    name: 'featureFlags.skipSMSNotifications',
                    type: 'toggle',
                    icon: 'fa-duotone fa-bell-slash',
                    getValue: obj => parseInt(obj.featureFlags?.skipSMSNotifications, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.pricesExclMva'>Priser eks. MVA (B2B)</Text>,
                    placeholder: <Text id='input.pricesExclMva'>Priser eks. MVA (B2B)</Text>,
                    name: 'featureFlags.pricesExclMva',
                    type: 'toggle',
                    icon: 'fa-duotone fa-building',
                    getValue: obj => parseInt(obj.featureFlags?.pricesExclMva, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.preventEmailInvoice'>Prevent Email Invoice</Text>,
                    placeholder: <Text id='input.preventEmailInvoice'>Prevent Email Invoice</Text>,
                    help: <Text id='input.preventEmailInvoice-help'>Hide the email invoice button. Use this if you always transfer invoices to Tripletex, Fiken, PowerOffice or Duett.</Text>,
                    name: 'featureFlags.preventEmailInvoice',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope-circle-check',
                    getValue: obj => parseInt(obj.featureFlags?.preventEmailInvoice, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.invoiceAdminOnly'>Only admins can create invoices</Text>,
                    placeholder: <Text id='input.invoiceAdminOnly'>Only admins can create invoices</Text>,
                    help: <Text id='input.invoiceAdminOnly-help'>Only clinic admins can turn a visit into an invoice. Other users can still take payment or leave the visit ongoing.</Text>,
                    name: 'featureFlags.invoiceAdminOnly',
                    type: 'toggle',
                    icon: 'fa-duotone fa-user-lock',
                    getValue: obj => parseInt(obj.featureFlags?.invoiceAdminOnly, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.allowEmailInvoiceWithAccounting'>Allow email invoice with accounting system</Text>,
                    placeholder: <Text id='input.allowEmailInvoiceWithAccounting'>Allow email invoice with accounting system</Text>,
                    help: <Text id='input.allowEmailInvoiceWithAccounting-help'>Send invoices by email from Dyrejournal even though the clinic uses Tripletex, Fiken, PowerOffice or Duett. Invoices sent from here come in addition to whatever the accounting system sends, so the clinic must avoid double invoicing itself. Useful for e.g. egenandel/restfaktura in insurance cases. Has no effect while "Prevent Email Invoice" is also on (that setting hides the email button).</Text>,
                    name: 'featureFlags.allowEmailInvoiceWithAccounting',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope-open-dollar',
                    getValue: obj => parseInt(obj.featureFlags?.allowEmailInvoiceWithAccounting, 10) === 1 ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.hideOtherCustomerTickets'>Hide other users' tickets</Text>,
                    placeholder: <Text id='input.hideOtherCustomerTickets'>Hide other users' tickets</Text>,
                    help: <Text id='input.hideOtherCustomerTickets-help'>When on, regular users only see their own support tickets, not other users' tickets in the same organization. Staff and admin are unaffected.</Text>,
                    name: 'featureFlags.hideOtherCustomerTickets',
                    type: 'toggle',
                    icon: 'fa-duotone fa-eye-slash',
                    getValue: obj => obj.featureFlags?.hideOtherCustomerTickets || false,
                    showInList: false,
                },
                {
                    title: <Text id='input.sharedSales'>Shared sales across companies</Text>,
                    placeholder: <Text id='input.sharedSales'>Shared sales across companies</Text>,
                    help: <Text id='input.sharedSales-help'>When on, users who are members of several companies can toggle "All visits" to also list visits/sales from their other companies. Sales from other companies are read-only until the user switches to that company.</Text>,
                    name: 'featureFlags.sharedSales',
                    type: 'toggle',
                    icon: 'fa-duotone fa-share-nodes',
                    getValue: obj => parseInt(obj.featureFlags?.sharedSales, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.invoicePaymentInfo'>invoicePaymentInfo</Text>,
                    placeholder: <Text id='input.invoicePaymentInfo'>invoicePaymentInfo</Text>,
                    help: <Text id='input.invoicePaymentInfo-help'>Invoice payment info.</Text>,
                    name: 'invoicePaymentInfo',
                    type: 'textarea',
                    icon: 'fa-duotone fa-input-text',
                    getValue: obj => obj.invoicePaymentInfo,
                },
                {
                    title: <Text id='input.invoiceCustomCss'>Invoice Custom CSS</Text>,
                    placeholder: `.receipt, .receipt * {
    font-family: Georgia, "Times New Roman", serif !important;
}
@media print {
    .receipt, .receipt * {
        font-family: Georgia, "Times New Roman", serif !important;
    }
}`,
                    help: <>
                        <Text id='input.invoiceCustomCss-help'>Custom CSS styles for invoice emails (fonts, sizes, etc.)</Text>
                        <br /><br />
                        <strong>Web-safe fonter:</strong><br />
                        Sans-serif: <code>Arial, Helvetica, sans-serif</code><br />
                        Serif: <code>Georgia, "Times New Roman", serif</code><br />
                        Monospace: <code>"Courier New", Monaco, monospace</code><br />
                        <br />
                        <strong>Tips:</strong> Bruk <code>!important</code> og <code>@media print</code> for utskrift.<br />
                        <br />
                        <strong>CSS klasser:</strong><br />
                        <code>.receipt</code> - Hovedcontainer<br />
                        <code>.receipt-header</code> - Topptekst med logo<br />
                        <code>.receipt-header-info</code> - Høyre side av topptekst (url, telefon, egendefinert tekst)<br />
                        <code>.receipt-title</code> - Faktura/kvittering overskrift<br />
                        <code>.receipt-stamp</code> - Betalt/Kredit stempel<br />
                        <code>.receipt-customer</code> - Kundeinformasjon<br />
                        <code>.receipt-products</code> - Produkttabell<br />
                        <code>.receipt-totals</code> - Totalsum<br />
                        <code>.receipt-payment</code> - Betalingsinfo<br />
                        <code>.receipt-footer</code> - Firmainfo nederst<br />
                        <code>.invoice-footer-text</code> - Egendefinert bunntekst
                    </>,
                    name: 'invoiceCustomCss',
                    type: 'textarea',
                    icon: 'fa-duotone fa-code',
                    getValue: obj => obj.invoiceCustomCss,
                },
                {
                    title: <Text id='input.appPaymentInfo'>App Payment Info</Text>,
                    placeholder: <Text id='input.appPaymentInfo'>App Payment Info</Text>,
                    help: <Text id='input.appPaymentInfo-help'>Instructions shown when customer selects app payment method.</Text>,
                    name: 'appPaymentInfo',
                    type: 'textarea',
                    icon: 'fa-duotone fa-mobile',
                    getValue: obj => obj.appPaymentInfo,
                },
                {
                    title: <Text id='input.checkoutButtonText'>Checkout Button Text</Text>,
                    placeholder: <Text id='input.checkoutButtonText-placeholder'>e.g. "Go to payment"</Text>,
                    help: <Text id='input.checkoutButtonText-help'>Custom text for the checkout button in the cart. Leave empty for default.</Text>,
                    name: 'checkoutButtonText',
                    type: 'text',
                    icon: 'fa-duotone fa-rectangle-wide',
                    getValue: obj => obj.checkoutButtonText,
                },
                {
                    title: <Text id='input.invoiceButtonText'>Invoice Button Text</Text>,
                    placeholder: <Text id='input.invoiceButtonText-placeholder'>e.g. "Pay by invoice"</Text>,
                    help: <Text id='input.invoiceButtonText-help'>Custom text for the invoice payment button. Leave empty for default.</Text>,
                    name: 'invoiceButtonText',
                    type: 'text',
                    icon: 'fa-duotone fa-file-invoice',
                    getValue: obj => obj.invoiceButtonText,
                },
                {
                    title: <Text id='input.appButtonText'>App Button Text</Text>,
                    placeholder: <Text id='input.appButtonText-placeholder'>e.g. "Pay in app"</Text>,
                    help: <Text id='input.appButtonText-help'>Custom text for the app payment button. Leave empty for default.</Text>,
                    name: 'appButtonText',
                    type: 'text',
                    icon: 'fa-duotone fa-mobile',
                    getValue: obj => obj.appButtonText,
                },
                {
                    title: <Text id='input.vippsButtonText'>Vipps Button Text</Text>,
                    placeholder: <Text id='input.vippsButtonText-placeholder'>e.g. "Pay with Vipps"</Text>,
                    help: <Text id='input.vippsButtonText-help'>Custom text for the Vipps payment button. Leave empty for default.</Text>,
                    name: 'vippsButtonText',
                    type: 'text',
                    icon: 'fa-duotone fa-credit-card',
                    getValue: obj => obj.vippsButtonText,
                },
                {
                    title: <Text id='input.bankButtonText'>Bank Button Text</Text>,
                    placeholder: <Text id='input.bankButtonText-placeholder'>e.g. "Bank transfer"</Text>,
                    help: <Text id='input.bankButtonText-help'>Custom text for the bank payment button. Leave empty for default.</Text>,
                    name: 'bankButtonText',
                    type: 'text',
                    icon: 'fa-duotone fa-building-columns',
                    getValue: obj => obj.bankButtonText,
                },
                {
                    title: <Text id='input.paypalButtonText'>PayPal Button Text</Text>,
                    placeholder: <Text id='input.paypalButtonText-placeholder'>e.g. "Pay with PayPal"</Text>,
                    help: <Text id='input.paypalButtonText-help'>Custom text for the PayPal payment button. Leave empty for default.</Text>,
                    name: 'paypalButtonText',
                    type: 'text',
                    icon: 'fa-brands fa-paypal',
                    getValue: obj => obj.paypalButtonText,
                },

                {
                    title: <Text id='input.formShowCallingWhenReady'>Admission form: Show calling when ready</Text>,
                    placeholder: <Text id='input.formShowCallingWhenReady'>Admission form: Show calling when ready</Text>,
                    help: <Text id='input.formShowCallingWhenReady-help'>Show calling when ready section in admission form.</Text>,
                    name: 'featureFlags.formShowCallingWhenReady',
                    type: 'toggle',
                    icon: 'fa-duotone fa-eye-slash',
                    getValue: obj => parseInt(obj.featureFlags?.formShowCallingWhenReady, 10) === 1 ? 0 : 1,
                },
                {
                    title: <Text id='input.formHideBiopsy'>Admission form: Hide biopsy</Text>,
                    placeholder: <Text id='input.formHideBiopsy'>Admission form: Hide biopsy</Text>,
                    help: <Text id='input.formHideBiopsy-help'>Hide biopsy section in admission form.</Text>,
                    name: 'featureFlags.formHideBiopsy',
                    type: 'toggle',
                    icon: 'fa-duotone fa-eye-slash',
                    getValue: obj => parseInt(obj.featureFlags?.formHideBiopsy, 10) === 1 ? 0 : 1,
                },
                {
                    title: <Text id='input.formHidePantText'>Admission form: Hide lien text</Text>,
                    placeholder: <Text id='input.formHidePantText'>Admission form: Hide lien text</Text>,
                    help: <Text id='input.formHidePantText-help'>Hide the sentence about keeping the animal as lien (pant) on the admission form.</Text>,
                    name: 'featureFlags.formHidePantText',
                    type: 'toggle',
                    icon: 'fa-duotone fa-eye-slash',
                    getValue: obj => parseInt(obj.featureFlags?.formHidePantText, 10) === 1 ? 0 : 1,
                },
                {
                    title: <Text id='input.formPantText'>Admission form: Lien text</Text>,
                    placeholder: <Text id='input.formPantText-placeholder'>Leave empty for the default sentence</Text>,
                    help: <Text id='input.formPantText-help'>Replaces the default sentence about keeping the animal as lien (pant) on the admission form. Leave empty to use the default.</Text>,
                    name: 'featureFlags.formPantText',
                    type: 'textarea',
                    icon: 'fa-duotone fa-file-lines',
                    getValue: obj => obj.featureFlags?.formPantText || '',
                },
                {
                    title: <Text id='input.shareProductDatabase'>Share product database</Text>,
                    placeholder: <Text id='input.shareProductDatabase'>Share product database</Text>,
                    help: <Text id='input.shareProductDatabase-help'>When on, the product list and sale search also show products from the other clinics each user is a member of. Turn off to show only this clinic's own products and prices. Owners, animals and journals are not affected.</Text>,
                    name: 'shareProductDatabase',
                    type: 'toggle',
                    icon: 'fa-duotone fa-square-share-nodes',
                    getValue: obj => parseInt(obj.shareProductDatabase, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.helpCenterTags'>Help Center Tags</Text>,
                    placeholder: <Text id='input.helpCenterTags-placeholder'>e.g. "Journaler,Dyr,Besøk,Salg"</Text>,
                    help: <Text id='input.helpCenterTags-help'>Comma-separated list of AI topic tags to use as section headers in the help center.</Text>,
                    name: 'featureFlags.helpCenterTags',
                    type: 'text',
                    icon: 'fa-duotone fa-tags',
                    getValue: obj => obj.featureFlags?.helpCenterTags || '',
                },
                {
                    title: <Text id='input.sidebarDefaultState'>Sidebar Default State</Text>,
                    placeholder: <Text id='input.sidebarDefaultState-placeholder'>Choose default sidebar state</Text>,
                    help: <Text id='input.sidebarDefaultState-help'>Controls whether the left sidebar menu starts hidden, shows icons only, or is fully expanded when users load the page.</Text>,
                    name: 'featureFlags.sidebarConfig.defaultState',
                    type: 'select',
                    datalist: [
                        ['hidden', 'Hidden (default)'],
                        ['icons', 'Icons only'],
                        ['expanded', 'Fully expanded'],
                    ],
                    icon: 'fa-duotone fa-sidebar',
                    getValue: obj => obj.featureFlags?.sidebarConfig?.defaultState || 'hidden',
                },
                {
                    title: <Text id='input.discountType'>Discount type</Text>,
                    placeholder: <Text id='input.discountType'>Discount type</Text>,
                    help: <Text id='input.discountType-help'>Choose whether discounts are applied as a fixed amount or percentage. Select "Off" to hide the discount column in the sale screen entirely.</Text>,
                    name: 'featureFlags.discountType',
                    type: 'select',
                    datalist: [
                        ['amount', 'Amount (NOK)'],
                        ['percent', 'Percent (%)'],
                        ['both', 'Both (NOK and %)'],
                        ['none', 'Off (no discount)'],
                    ],
                    icon: 'fa-duotone fa-percent',
                    getValue: obj => obj.featureFlags?.discountType || 'amount',
                },
                {
                    title: <Text id='input.discountFeeOnly'>Discount only on fee (honorar)</Text>,
                    placeholder: <Text id='input.discountFeeOnly-placeholder'>Percent discount applies to the fee part only</Text>,
                    help: <Text id='input.discountFeeOnly-help'>When a percent discount is entered on a sale line with a split price (medicine/fee/equipment/other), it is converted to a fixed discount amount equal to that percentage of the fee (honorar) part only. Lines without a fee part are discounted as before.</Text>,
                    name: 'featureFlags.discountFeeOnly',
                    type: 'toggle',
                    icon: 'fa-duotone fa-percent',
                    getValue: obj => obj.featureFlags?.discountFeeOnly ? 1 : 0,
                },
                {
                    title: <Text id='input.discountLineAmount'>Discount in kroner applies to the whole line</Text>,
                    placeholder: <Text id='input.discountLineAmount-placeholder'>Type the discount for the whole line, not per unit</Text>,
                    help: <Text id='input.discountLineAmount-help'>The kroner discount typed on a sale line is the discount for the whole line regardless of quantity, and the receipt and invoice show that amount. Internally it is divided by the quantity, so totals, reports and accounting exports are unchanged.</Text>,
                    name: 'featureFlags.discountLineAmount',
                    type: 'toggle',
                    icon: 'fa-duotone fa-percent',
                    getValue: obj => obj.featureFlags?.discountLineAmount ? 1 : 0,
                },
                {
                    title: <Text id='input.productPackaging'>Product packaging (sell in packages)</Text>,
                    placeholder: <Text id='input.productPackaging-placeholder'>Purchase price per package, sell per unit or per package</Text>,
                    help: <Text id='input.productPackaging-help'>Lets a product carry a package size and a purchase price per package. The purchase and sale price per base unit are calculated, and the sale line can switch between base unit and package. Quantity, stock, reports and exports stay per base unit.</Text>,
                    name: 'featureFlags.productPackaging',
                    type: 'toggle',
                    icon: 'fa-duotone fa-boxes-stacked',
                    getValue: obj => obj.featureFlags?.productPackaging ? 1 : 0,
                },
                {
                    title: <Text id='input.receiptSpacing'>Receipt spacing</Text>,
                    placeholder: <Text id='input.receiptSpacing'>Receipt spacing</Text>,
                    help: <Text id='input.receiptSpacing-help'>Vertical spacing between sections on printed receipts and invoices. Compact helps most sales fit on one A4 page.</Text>,
                    name: 'featureFlags.receiptSpacing',
                    type: 'select',
                    datalist: [
                        ['loose', 'Loose'],
                        ['normal', 'Normal'],
                        ['compact', 'Compact'],
                    ],
                    icon: 'fa-duotone fa-arrows-up-down',
                    getValue: obj => obj.featureFlags?.receiptSpacing || 'loose',
                },
                {
                    title: <Text id='input.lockScreenEnabled'>Lock screen</Text>,
                    placeholder: <Text id='input.lockScreenEnabled'>Lock screen</Text>,
                    name: 'featureFlags.lockScreen.enabled',
                    type: 'toggle',
                    icon: 'fa-duotone fa-lock',
                    getValue: obj => parseInt(obj.featureFlags?.lockScreen?.enabled, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.lockScreenSoftMinutes'>Soft-lock minutes</Text>,
                    placeholder: <Text id='input.lockScreenSoftMinutes-placeholder'>15</Text>,
                    help: <Text id='input.lockScreenSoftMinutes-help'>Minutes of inactivity before the lock screen appears.</Text>,
                    name: 'featureFlags.lockScreen.softLockMinutes',
                    type: 'number',
                    icon: 'fa-duotone fa-stopwatch',
                    getValue: obj => obj.featureFlags?.lockScreen?.softLockMinutes ?? 15,
                    showInList: false,
                },
                {
                    title: <Text id='input.lockScreenHardHours'>Hard-logout hours</Text>,
                    placeholder: <Text id='input.lockScreenHardHours-placeholder'>12</Text>,
                    help: <Text id='input.lockScreenHardHours-help'>Hours after a full email + 2FA login before the device is forced to log out.</Text>,
                    name: 'featureFlags.lockScreen.hardLogoutHours',
                    type: 'number',
                    icon: 'fa-duotone fa-clock',
                    getValue: obj => obj.featureFlags?.lockScreen?.hardLogoutHours ?? 12,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.customer-vipps-title'>Vipps integration</Text>,
            key: 'Vipps integration info',
            fields: [
                {
                    title: <Text id='input.vippsName'>vippsName</Text>,
                    placeholder: <Text id='input.vippsName'>vippsName</Text>,
                    help: <Text id='input.vippsName-help'>Vippsname has to be a-z and lowercase only.</Text>,
                    name: 'vippsName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsName,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsVersion'>vippsVersion</Text>,
                    placeholder: <Text id='input.vippsVersion'>vippsVersion</Text>,
                    help: <Text id='input.vippsVersion-help'>vippsVersion has to 1.0.0</Text>,
                    name: 'vippsVersion',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsVersion,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsPluginName'>vippsPluginName</Text>,
                    placeholder: <Text id='input.vippsPluginName'>vippsPluginName</Text>,
                    help: <Text id='input.vippsPluginName-help'>vippsPluginName has to be a-z and lowercase only.</Text>,
                    name: 'vippsPluginName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsPluginName,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsPluginVersion'>vippsPluginVersion</Text>,
                    placeholder: <Text id='input.vippsPluginVersion'>vippsPluginVersion</Text>,
                    help: <Text id='input.vippsPluginVersion-help'>vippsPluginVersion has to 1.0.0</Text>,
                    name: 'vippsPluginVersion',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsPluginVersion,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsMerchantSerialNumber'>vippsMerchantSerialNumber</Text>,
                    placeholder: <Text id='input.vippsMerchantSerialNumber'>vippsMerchantSerialNumber</Text>,
                    help: <Text id='input.vippsMerchantSerialNumber-help'>Vipps MSN number.</Text>,
                    name: 'vippsMerchantSerialNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsMerchantSerialNumber,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsClientId'>vippsClientId</Text>,
                    placeholder: <Text id='input.vippsClientId'>vippsClientId</Text>,
                    name: 'vippsClientId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsClientId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    // TRUE secret (Batch 3): never echo the stored value back — write-only.
                    title: <Text id='input.vippsClientSecret'>vippsClientSecret</Text>,
                    placeholder: <Text id='input.vippsClientSecret'>vippsClientSecret</Text>,
                    name: 'vippsClientSecret',
                    type: 'password',
                    icon: 'fa-solid fa-input-text',
                    getValue: () => '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    // TRUE secret (Batch 3): never echo the stored value back — write-only.
                    title: <Text id='input.vippsSubscriptionKey1'>vippsSubscriptionKey1</Text>,
                    placeholder: <Text id='input.vippsSubscriptionKey1'>vippsSubscriptionKey1</Text>,
                    name: 'vippsSubscriptionKey1',
                    type: 'password',
                    icon: 'fa-solid fa-input-text',
                    getValue: () => '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    // TRUE secret (Batch 3): never echo the stored value back — write-only.
                    title: <Text id='input.vippsSubscriptionKey2'>vippsSubscriptionKey2</Text>,
                    placeholder: <Text id='input.vippsSubscriptionKey2'>vippsSubscriptionKey2</Text>,
                    name: 'vippsSubscriptionKey2',
                    type: 'password',
                    icon: 'fa-solid fa-input-text',
                    getValue: () => '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsUseTestMode'>vippsUseTestMode</Text>,
                    placeholder: <Text id='input.vippsUseTestMode'>vippsUseTestMode</Text>,
                    name: 'vippsUseTestMode',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsUseTestMode,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsTestPhoneNumber'>vippsTestPhoneNumber</Text>,
                    placeholder: <Text id='input.vippsTestPhoneNumber'>vippsTestPhoneNumber</Text>,
                    name: 'vippsTestPhoneNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTestPhoneNumber,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsTermsOfSale'>vippsTermsOfSale</Text>,
                    placeholder: <Text id='input.vippsTermsOfSale'>vippsTermsOfSale</Text>,
                    help: <Text id='input.vippsTermsOfSale-help'>Terms of sale in norwegian.</Text>,
                    name: 'vippsTermsOfSale',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTermsOfSale,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.vippsTermsOfSaleEn'>vippsTermsOfSaleEn</Text>,
                    placeholder: <Text id='input.vippsTermsOfSaleEn'>vippsTermsOfSaleEn</Text>,
                    help: <Text id='input.vippsTermsOfSaleEn-help'>Terms of sale in english.</Text>,
                    name: 'vippsTermsOfSaleEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTermsOfSaleEn,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.customer-paypal-title'>Paypal integration</Text>,
            key: 'Paypal integration info',
            fields: [
                {
                    title: <Text id='input.paypalHostname'>Paypal hostname</Text>,
                    placeholder: <Text id='input.paypalHostname'>Paypal hostname</Text>,
                    help: <Text id='input.paypalHostname-help'>API hostname of PayPal integrations. Ie: "api-m.sandbox.paypal.com".</Text>,
                    name: 'paypalHostname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paypalHostname,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.paypalClientId'>Paypal ClientId</Text>,
                    placeholder: <Text id='input.paypalClientId'>Paypal ClientId</Text>,
                    name: 'paypalClientId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paypalClientId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    // TRUE secret (Batch 3): never echo the stored value back — write-only.
                    title: <Text id='input.paypalClientSecret'>Paypal ClientSecret</Text>,
                    placeholder: <Text id='input.paypalClientSecret'>Paypal ClientSecret</Text>,
                    name: 'paypalClientSecret',
                    type: 'password',
                    icon: 'fa-solid fa-input-text',
                    getValue: () => '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.paypalReturnUrl'>Paypal Return URL</Text>,
                    placeholder: <Text id='input.paypalReturnUrl'>Paypal Return URL</Text>,
                    name: 'paypalReturnUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paypalReturnUrl,
                    showInList: false,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.customer-api-title'>API integration</Text>,
            key: 'API integration info',
            fields: [
                {
                    title: <Text id='input.fikenSlug'>Fiken.no - Slug</Text>,
                    placeholder: <Text id='input.fikenSlug'>Fiken.no - Slug</Text>,
                    help: <Text id='input.fikenSlug-help'>You will find this in the URL in Fiken. https://fiken.no/foretak/dyrejournal-as/forside/ = dyrejournal-as</Text>,
                    name: 'fikenSlug',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenSlug,
                    showInList: false,
                },
                {
                    title: <Text id='input.notVatRegistered'>Not VAT Registered</Text>,
                    help: <Text id='input.notVatRegistered-help'>Enable this if your business is not VAT registered (ikke MVA-registrert). Sales will be sent to Fiken without VAT.</Text>,
                    name: 'notVatRegistered',
                    type: 'toggle',
                    icon: 'fa-solid fa-toggle-on',
                    getValue: obj => obj.notVatRegistered,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodInvoice'>Fiken.no - Payment Method: Invoice</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodInvoice'>Fiken.no - Payment Method: Invoice</Text>,
                    help: <Text id='input.fikenPaymentMethodInvoice-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodInvoice',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodInvoice,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodAccount'>Fiken.no - Payment Method: Account</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodAccount'>Fiken.no - Payment Method: Account</Text>,
                    help: <Text id='input.fikenPaymentMethodAccount-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodAccount',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodAccount,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodCard'>Fiken.no - Payment Method: Card</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodCard'>Fiken.no - Payment Method: Card</Text>,
                    help: <Text id='input.fikenPaymentMethodCard-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodCard',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodCard,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodCash'>Fiken.no - Payment Method: Cash</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodCash'>Fiken.no - Payment Method: Cash</Text>,
                    help: <Text id='input.fikenPaymentMethodCash-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodCash',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodCash,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodInsurance'>Fiken.no - Payment Method: Insurance</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodInsurance'>Fiken.no - Payment Method: Insurance</Text>,
                    help: <Text id='input.fikenPaymentMethodInsurance-help'>Account for insurance direct-settlement payouts (direkteoppgjør). Check your Fiken.no installation to find the correct account. '1920:10001' might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodInsurance',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodInsurance,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodVipps'>Fiken.no - Payment Method: Vipps</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodVipps'>Fiken.no - Payment Method: Vipps</Text>,
                    help: <Text id='input.fikenPaymentMethodVipps-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodVipps',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodVipps,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodCardApp'>Fiken.no - Payment Method: Card via app</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodCardApp'>Fiken.no - Payment Method: Card via app</Text>,
                    help: <Text id='input.fikenPaymentMethodCardApp-help'>Check your Fiken.no installation to find the correct account. '1920:10001' might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodCardApp',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodCardApp,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodCreditCard'>Fiken.no - Payment Method: Credit card</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodCreditCard'>Fiken.no - Payment Method: Credit card</Text>,
                    help: <Text id='input.fikenPaymentMethodCreditCard-help'>Check your Fiken.no installation to find the correct account. '1920:10001' might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodCreditCard',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodCreditCard,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodGiftCard'>Fiken.no - Payment Method: Gift card</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodGiftCard'>Fiken.no - Payment Method: Gift card</Text>,
                    help: <Text id='input.fikenPaymentMethodGiftCard-help'>Check your Fiken.no installation to find the correct account. '1900:...' for gift card / cash equivalent.</Text>,
                    name: 'fikenPaymentMethodGiftCard',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodGiftCard,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodCustomerCredit'>Fiken.no - Payment Method: Customer credit (Kundekonto)</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodCustomerCredit'>Fiken.no - Payment Method: Customer credit (Kundekonto)</Text>,
                    help: <Text id='input.fikenPaymentMethodCustomerCredit-help'>Check your Fiken.no installation to find the correct account. '2900:...' for credit held on the customer's account.</Text>,
                    name: 'fikenPaymentMethodCustomerCredit',
                    type: 'text',
                    icon: 'fa-duotone fa-piggy-bank',
                    getValue: obj => obj.fikenPaymentMethodCustomerCredit,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodPayPal'>Fiken.no - Payment Method: PayPal</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodPayPal'>Fiken.no - Payment Method: PayPal</Text>,
                    help: <Text id='input.fikenPaymentMethodPayPal-help'>Check your Fiken.no installation to find the correct account. '1920:10001' might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodPayPal',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodPayPal,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodConvene'>Fiken.no - Payment Method: Convene</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodConvene'>Fiken.no - Payment Method: Convene</Text>,
                    help: <Text id='input.fikenPaymentMethodConvene-help'>Check your Fiken.no installation to find the correct account. '1500:...' for accounts receivable.</Text>,
                    name: 'fikenPaymentMethodConvene',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodConvene,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodCreditNote'>Fiken.no - Payment Method: Credit note</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodCreditNote'>Fiken.no - Payment Method: Credit note</Text>,
                    help: <Text id='input.fikenPaymentMethodCreditNote-help'>Check your Fiken.no installation to find the correct account. '1500:...' for accounts receivable.</Text>,
                    name: 'fikenPaymentMethodCreditNote',
                    type: 'datalist',
                    datalistName: 'fikenBankAccounts',
                    datalistFieldKey: e => e.accountCode,
                    datalistFieldVal: e => `${e.accountCode} - ${e.name}`,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodCreditNote,
                    showInList: false,
                },

                // Standard payment-method accounts (Tripletex / PowerOffice / generic).
                // Plain account number, e.g. "1920". Empty = uses Norwegian standard (NS 4102) default.
                {
                    title: <Text id='input.paymentMethodCash'>Payment Method: Cash</Text>,
                    placeholder: <Text id='input.paymentMethodCash-placeholder'>1900</Text>,
                    help: <Text id='input.paymentMethodCash-help'>Account for cash payments. Default is 1900 (Kasse).</Text>,
                    name: 'paymentMethodCash',
                    type: 'text',
                    icon: 'fa-duotone fa-money-bill',
                    getValue: obj => obj.paymentMethodCash,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodCard'>Payment Method: Card</Text>,
                    placeholder: <Text id='input.paymentMethodCard-placeholder'>1920</Text>,
                    help: <Text id='input.paymentMethodCard-help'>Account for card payments. Default is 1920 (Bankinnskudd).</Text>,
                    name: 'paymentMethodCard',
                    type: 'text',
                    icon: 'fa-duotone fa-credit-card',
                    getValue: obj => obj.paymentMethodCard,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodCardApp'>Payment Method: Card via app</Text>,
                    placeholder: <Text id='input.paymentMethodCardApp-placeholder'>1920</Text>,
                    help: <Text id='input.paymentMethodCardApp-help'>Account for card-via-app payments. Default is 1920 (Bankinnskudd).</Text>,
                    name: 'paymentMethodCardApp',
                    type: 'text',
                    icon: 'fa-duotone fa-mobile-screen',
                    getValue: obj => obj.paymentMethodCardApp,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodCreditCard'>Payment Method: Credit card</Text>,
                    placeholder: <Text id='input.paymentMethodCreditCard-placeholder'>1920</Text>,
                    help: <Text id='input.paymentMethodCreditCard-help'>Account for credit card payments. Default is 1920 (Bankinnskudd).</Text>,
                    name: 'paymentMethodCreditCard',
                    type: 'text',
                    icon: 'fa-duotone fa-credit-card',
                    getValue: obj => obj.paymentMethodCreditCard,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodVipps'>Payment Method: Vipps</Text>,
                    placeholder: <Text id='input.paymentMethodVipps-placeholder'>1920</Text>,
                    help: <Text id='input.paymentMethodVipps-help'>Account for Vipps payments. Default is 1920 (Bankinnskudd).</Text>,
                    name: 'paymentMethodVipps',
                    type: 'text',
                    icon: 'fa-duotone fa-mobile-screen',
                    getValue: obj => obj.paymentMethodVipps,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodGiftCard'>Payment Method: Gift card</Text>,
                    placeholder: <Text id='input.paymentMethodGiftCard-placeholder'>1900</Text>,
                    help: <Text id='input.paymentMethodGiftCard-help'>Account for gift card redemptions. Default is 1900 (Kasse).</Text>,
                    name: 'paymentMethodGiftCard',
                    type: 'text',
                    icon: 'fa-duotone fa-gift',
                    getValue: obj => obj.paymentMethodGiftCard,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodCustomerCredit'>Payment Method: Customer credit (Kundekonto)</Text>,
                    placeholder: <Text id='input.paymentMethodCustomerCredit-placeholder'>2900</Text>,
                    help: <Text id='input.paymentMethodCustomerCredit-help'>Account for credit held on the customer's account. Default is 2900 (Forskudd fra kunder).</Text>,
                    name: 'paymentMethodCustomerCredit',
                    type: 'text',
                    icon: 'fa-duotone fa-piggy-bank',
                    getValue: obj => obj.paymentMethodCustomerCredit,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodAccount'>Payment Method: Bank / Account</Text>,
                    placeholder: <Text id='input.paymentMethodAccount-placeholder'>1920</Text>,
                    help: <Text id='input.paymentMethodAccount-help'>Account for bank/account payments. Default is 1920 (Bankinnskudd).</Text>,
                    name: 'paymentMethodAccount',
                    type: 'text',
                    icon: 'fa-duotone fa-building-columns',
                    getValue: obj => obj.paymentMethodAccount,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodInvoice'>Payment Method: Invoice</Text>,
                    placeholder: <Text id='input.paymentMethodInvoice-placeholder'>1500</Text>,
                    help: <Text id='input.paymentMethodInvoice-help'>Account for invoice payments. Default is 1500 (Kundefordringer). With voucher reporting to Tripletex, use an account without reskontro: Tripletex rejects a voucher line on a customer ledger account («Kunde mangler»).</Text>,
                    name: 'paymentMethodInvoice',
                    type: 'text',
                    icon: 'fa-duotone fa-file-invoice',
                    getValue: obj => obj.paymentMethodInvoice,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodPayPal'>Payment Method: PayPal</Text>,
                    placeholder: <Text id='input.paymentMethodPayPal-placeholder'>1920</Text>,
                    help: <Text id='input.paymentMethodPayPal-help'>Account for PayPal payments. Default is 1920 (Bankinnskudd).</Text>,
                    name: 'paymentMethodPayPal',
                    type: 'text',
                    icon: 'fa-brands fa-paypal',
                    getValue: obj => obj.paymentMethodPayPal,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodConvene'>Payment Method: Convene</Text>,
                    placeholder: <Text id='input.paymentMethodConvene-placeholder'>1500</Text>,
                    help: <Text id='input.paymentMethodConvene-help'>Account for Convene invoices. Default is 1500 (Kundefordringer). With voucher reporting to Tripletex, use an account without reskontro: Tripletex rejects a voucher line on a customer ledger account («Kunde mangler»).</Text>,
                    name: 'paymentMethodConvene',
                    type: 'text',
                    icon: 'fa-duotone fa-file-invoice',
                    getValue: obj => obj.paymentMethodConvene,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodInsurance'>Payment Method: Insurance</Text>,
                    placeholder: <Text id='input.paymentMethodInsurance-placeholder'>1920</Text>,
                    help: <Text id='input.paymentMethodInsurance-help'>Account for insurance direct-settlement payouts (direkteoppgjør). Default is 1920 (Bankinnskudd).</Text>,
                    name: 'paymentMethodInsurance',
                    type: 'text',
                    icon: 'fa-duotone fa-umbrella',
                    getValue: obj => obj.paymentMethodInsurance,
                    showInList: false,
                },
                {
                    title: <Text id='input.agriaAgentNumber'>Agria ombudsnummer</Text>,
                    placeholder: <Text id='input.agriaAgentNumber-placeholder'>123 45678</Text>,
                    help: <Text id='input.agriaAgentNumber-help'>Ombudsnummer tildelt av Agria for direkteoppgjør (8 siffer, 3+5). Skrives på direkteoppgjørskravet og i e-posten til Agria.</Text>,
                    name: 'agriaAgentNumber',
                    type: 'text',
                    icon: 'fa-duotone fa-id-badge',
                    getValue: obj => obj.agriaAgentNumber,
                    showInList: false,
                },
                {
                    title: <Text id='input.paymentMethodCreditNote'>Payment Method: Credit note</Text>,
                    placeholder: <Text id='input.paymentMethodCreditNote-placeholder'>1500</Text>,
                    help: <Text id='input.paymentMethodCreditNote-help'>Account for credit notes. Default is 1500 (Kundefordringer).</Text>,
                    name: 'paymentMethodCreditNote',
                    type: 'text',
                    icon: 'fa-duotone fa-receipt',
                    getValue: obj => obj.paymentMethodCreditNote,
                    showInList: false,
                },
                {
                    // TRUE secret (Batch 3): never echo the stored value back — write-only.
                    title: <Text id='input.fikenAccessToken'>Fiken.no - Personal accessToken</Text>,
                    placeholder: <Text id='input.fikenAccessToken'>Fiken.no - Personal accessToken</Text>,
                    name: 'fikenAccessToken',
                    type: 'password',
                    icon: 'fa-solid fa-input-text',
                    getValue: () => '',
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenRoundingAccount'>Fiken.no - Avrundingskonto</Text>,
                    placeholder: <Text id='input.fikenRoundingAccount-placeholder'>7740</Text>,
                    help: <Text id='input.fikenRoundingAccount-help'>Konto for øreavrunding. Standard er 7740 (Tap ved avrunding). Endre kun hvis du bruker en annen konto i din kontoplan.</Text>,
                    name: 'fikenRoundingAccount',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenRoundingAccount || '7740',
                    showInList: false,
                },
                {
                    title: <Text id='input.oreRoundingCashOnly'>Øre rounding on cash only</Text>,
                    placeholder: <Text id='input.oreRoundingCashOnly'>Øre rounding on cash only</Text>,
                    help: <Text id='input.oreRoundingCashOnly-help'>When on: only cash payments are rounded to the nearest krone. Invoice, card and Vipps keep the exact amount with no øre rounding, per Norwegian tax rules.</Text>,
                    name: 'featureFlags.oreRoundingCashOnly',
                    type: 'toggle',
                    icon: 'fa-duotone fa-coins',
                    getValue: obj => parseInt(obj.featureFlags?.oreRoundingCashOnly, 10) === 1 ? 0 : 1,
                    showInList: false,
                },
                {
                    title: <Text id='input.cashCarryForward'>Carry cash forward between daily reports</Text>,
                    help: <Text id='input.cashCarryForward-help'>Use the closing cash from the previous saved daily report
                        as opening cash. Save a daily report after adding change or withdrawing cash to the bank.
                        Check the starting balance when enabling this setting.</Text>,
                    name: 'featureFlags.cashCarryForward',
                    type: 'toggle',
                    icon: 'fa-duotone fa-coins',
                    getValue: obj => Number(obj.featureFlags?.cashCarryForward) === 1 ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.insurancePaidSaleOnCompany'>Register insurance settlement on the insurance company</Text>,
                    placeholder: <Text id='input.insurancePaidSaleOnCompany'>Register insurance settlement on the insurance company</Text>,
                    help: <Text id='input.insurancePaidSaleOnCompany-help'>When on, a direkteoppgjør settlement registers the insurance-paid (covered) sale on the insurance company as customer instead of the pet owner. The owner is still invoiced their egenandel.</Text>,
                    name: 'featureFlags.insurancePaidSaleOnCompany',
                    type: 'toggle',
                    icon: 'fa-duotone fa-shield-heart',
                    getValue: obj => obj.featureFlags?.insurancePaidSaleOnCompany ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.insuranceSettlementPaymentMethod'>Insurance settlement: payment method</Text>,
                    placeholder: <Text id='input.insuranceSettlementPaymentMethod'>Insurance settlement: payment method</Text>,
                    help: <Text id='input.insuranceSettlementPaymentMethod-help'>Payment method stamped on the insurance-paid sale and its payment when a direkteoppgjør settlement is registered. "Insurance" keeps it visible as its own line in the reports; "Bank / Account" books it like any other bank transfer. Both use the bank account in accounting.</Text>,
                    name: 'featureFlags.insuranceSettlementPaymentMethod',
                    type: 'select',
                    datalist: [
                        ['insurance', 'Forsikring'],
                        ['account', 'Bank / Konto'],
                    ],
                    icon: 'fa-duotone fa-building-columns',
                    getValue: obj => obj.featureFlags?.insuranceSettlementPaymentMethod || 'insurance',
                    showInList: false,
                },
                {
                    // TRUE secret (Batch 3): never echo the stored value back — write-only.
                    title: <Text id='input.tripletexEmployeeToken'>Tripletex.no - EmployeeToken</Text>,
                    placeholder: <Text id='input.tripletexEmployeeToken'>Tripletex.no - EmployeeToken</Text>,
                    name: 'tripletexEmployeeToken',
                    type: 'password',
                    icon: 'fa-solid fa-input-text',
                    getValue: () => '',
                    showInList: false,
                },
                {
                    title: <Text id='input.duettClientId'>Duett - ClientId</Text>,
                    placeholder: <Text id='input.duettClientId'>Duett - ClientId</Text>,
                    name: 'duettClientId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.duettClientId,
                    showInList: false,
                },
                {
                    title: <Text id='input.tripletexSendInvoiceToCustomer'>Tripletex.no - Send Invoice To Customer</Text>,
                    placeholder: <Text id='input.tripletexSendInvoiceToCustomer'>Tripletex.no - Send Invoice To Customer</Text>,
                    help: <Text id='input.tripletexSendInvoiceToCustomer-help'>When enabled, invoices created in Tripletex are auto-sent to the customer using their preferred delivery type. When disabled, the invoice is created but not sent — you can complete and send it manually from Tripletex.</Text>,
                    name: 'tripletexSendInvoiceToCustomer',
                    type: 'toggle',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.tripletexSendInvoiceToCustomer,
                    showInList: false,
                },
                {
                    title: <Text id='input.tripletexAttachInvoicePdf'>Tripletex.no - Attach Invoice PDF</Text>,
                    placeholder: <Text id='input.tripletexAttachInvoicePdf'>Tripletex.no - Attach Invoice PDF</Text>,
                    help: <Text id='input.tripletexAttachInvoicePdf-help'>When enabled, the Dyrejournal invoice PDF is uploaded to the document archive of the Tripletex order. Note: Tripletex does NOT include it in the invoice sent to the customer (EHF/email) - the Tripletex API has no option to mark an attachment as "include on invoice", so the PDF is only visible on the order inside Tripletex. With auto-send enabled, the invoice is created unsent, the PDF is uploaded, and the invoice is then sent.</Text>,
                    name: 'tripletexAttachInvoicePdf',
                    type: 'toggle',
                    icon: 'fa-solid fa-paperclip',
                    getValue: obj => obj.tripletexAttachInvoicePdf,
                    showInList: false,
                },
                {
                    title: <Text id='input.powerOfficeSendInvoiceToCustomer'>PowerOffice - Send Invoice To Customer</Text>,
                    placeholder: <Text id='input.powerOfficeSendInvoiceToCustomer'>PowerOffice - Send Invoice To Customer</Text>,
                    help: <Text id='input.powerOfficeSendInvoiceToCustomer-help'>When enabled, invoices transferred to PowerOffice are auto-sent to the customer using their preferred delivery type (EHF, email, or print). When disabled, the SalesOrder is created in PowerOffice but not sent — you can complete and send it manually from PowerOffice.</Text>,
                    name: 'powerOfficeSendInvoiceToCustomer',
                    type: 'toggle',
                    icon: 'fa-solid fa-paper-plane',
                    getValue: obj => obj.powerOfficeSendInvoiceToCustomer,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenSendInvoiceToCustomer'>Fiken - Send Invoice To Customer</Text>,
                    placeholder: <Text id='input.fikenSendInvoiceToCustomer'>Fiken - Send Invoice To Customer</Text>,
                    help: <Text id='input.fikenSendInvoiceToCustomer-help'>When enabled, invoices transferred to Fiken are auto-sent to the customer using their preferred delivery type (EHF when the customer has an org number, otherwise email). When disabled, the invoice is created but not sent — you can send it manually with the "Send faktura" button or from Fiken.</Text>,
                    name: 'fikenSendInvoiceToCustomer',
                    type: 'toggle',
                    icon: 'fa-solid fa-paper-plane-top',
                    getValue: obj => obj.fikenSendInvoiceToCustomer,
                    showInList: false,
                },
                {
                    title: <Text id='input.ffeAccessToken'>FFE - AccessToken</Text>,
                    placeholder: <Text id='input.ffeAccessToken'>FFE - AccessToken</Text>,
                    name: 'ffeAccessToken',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ffeAccessToken,
                    showInList: false,
                },
                {
                    title: <Text id='input.animalia-producerNumber'>Animalia producerNumber</Text>,
                    placeholder: <Text id='input.animalia-producerNumber'>Animalia producerNumber</Text>,
                    name: 'producerNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.producerNumber,
                },


                {
                    // TRUE secret (Batch 3): never echo the stored value back — write-only.
                    title: <Text id='input.nets-username'>NETS username</Text>,
                    placeholder: <Text id='input.nets-username'>NETS username</Text>,
                    name: 'netsUsername',
                    type: 'password',
                    icon: 'fa-solid fa-input-text',
                    getValue: () => '',
                },
                {
                    // TRUE secret (Batch 3): never echo the stored value back — write-only.
                    // Was a copy-paste bug: getValue used to read obj.netsUsername instead of
                    // obj.netsPassword (harmless-looking, but meant the password field's edit
                    // form was silently pre-filled with the USERNAME value).
                    title: <Text id='input.nets-password'>NETS password</Text>,
                    placeholder: <Text id='input.nets-password'>NETS password</Text>,
                    name: 'netsPassword',
                    type: 'password',
                    icon: 'fa-solid fa-input-text',
                    getValue: () => '',
                },

                {
                    title: <Text id='input.netsTerminals'>NETS Terminals</Text>,
                    placeholder: <Text id='input.netsTerminals'>NETS Terminals</Text>,
                    name: 'netsTerminals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    getValue: customer => customer.netsTerminals?.map(m => m),
                    datalist: [
                        'name', 'terminalId',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>Terminal ID:</span> {obj.terminalId}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },

                {
                    title: <Text id='input.rubicOrganizationId'>Rubic Organization ID</Text>,
                    placeholder: <Text id='input.rubicOrganizationId'>Rubic Organization ID</Text>,
                    name: 'rubicOrganizationId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rubicOrganizationId,
                },
                {
                    title: <Text id='input.rubicOrganizationNumber'>Rubic Organization Number</Text>,
                    placeholder: <Text id='input.rubicOrganizationNumber'>Rubic Organization Number</Text>,
                    name: 'rubicOrganizationNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-number',
                    getValue: obj => obj.rubicOrganizationNumber,
                },

            ],
        },
        {
            name: <Text id='input.user-images-file-links-title'>Images, files and links</Text>,
            key: 'Images, files and links',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.icons'>icons</Text>,
                    placeholder: <Text id='input.icons'>icons</Text>,
                    name: 'icons',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.icons,
                    showInList: false,
                    isAdminField: true,
                },
            ],
        },
    ],
    usage: [
        {
            name: <Text id='input.usage-basics-title'>Usage basics</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Usage basics',
            fields: [
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.part'>Part</Text>,
                    placeholder: <Text id='input.part'>Part</Text>,
                    name: 'part',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    lookupArray: 'adminParts',
                    getValue: obj => obj.part || null,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.title,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    audioTranscribe: true,
                    getValue: obj => obj.comment,
                    showInList: false,
                },
                {
                    title: <Text id='input.date'>Date</Text>,
                    placeholder: <Text id='input.date'>Date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.hours'>Hours</Text>,
                    placeholder: <Text id='input.hours'>Hours</Text>,
                    name: 'hours',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.hours || null,
                    displayValue: val => val ? `${val} h` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.shotCount'>shotCount</Text>,
                    help: <Text id='input.shotCount-help'>Shot count in this session.</Text>,
                    placeholder: <Text id='input.shotCount'>shotCount</Text>,
                    name: 'shotCount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotCount || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    audioTranscribe: true,
                    getValue: obj => obj.comment,
                    showInList: false,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.usage-media-title'>Usage media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Usage media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
        {
            name: <Text id='input.usage-shooting-details-title'>Usage shooting details</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Usage shooting details',
            fields: [
                {
                    title: <Text id='input.shotAnalysis'>shotAnalysis</Text>,
                    placeholder: <Text id='input.shotAnalysis'>shotAnalysis</Text>,
                    name: 'shotAnalysis',
                    type: 'arrayObject',
                    drawerSize: 'drawerHeightMediumSmall',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.shotAnalysis,
                    datalist: [
                        'imageMd5', 'round', 'roundType', 'groupPlacement', 'centerScore', 'totalScore',
                    ],
                    datalistHelp: [
                        'Reference to the image.', 'Which number shot string is this?', 'Type: Prøve 150s, 1:150s, 2:150s, 1:5x3s, 2:5x3s, 20s, 10s.', '0-360 degrees', 'Score. 10.10 center target.', 'Total score for the round.',
                    ],
                    datalistValues: [
                        [], [1, 2, 3, 4], ['Prøve 150s', '1:150s', '2:150s', '1:5x3s', '2:5x3s', '20s', '10s', '8s', '6s'], [], [],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Round:</span> {obj.round}<br />
                            <span class='font-weight-lighter'>Type:</span> {obj.roundType}<br />
                            <span class='font-weight-lighter'>Placement:</span> {obj.groupPlacement}°<br />
                            <span class='font-weight-lighter'>Center group:</span> {obj.centerScore}<br />
                            <span class='font-weight-lighter'>Total Score:</span> {obj.totalScore}<br />
                        </div>
                    </>,
                    showInList: false,
                    displayHeight: '140px',
                },
                {
                    title: <Text id='input.shotStrings'>shotStrings</Text>,
                    placeholder: <Text id='input.shotStrings'>shotStrings</Text>,
                    name: 'shotStrings',
                    type: 'arrayObject',
                    drawerSize: 'drawerHeightMediumSmall',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.shotStrings,
                    datalist: [
                        'round', 'roundType', 'score', 'shotCount',
                        // 'speedHigh', 'speedLow', 'speedAvg', 'spread', 'stdDev', 'groupSize',
                    ],
                    datalistValues: [
                        [1, 2, 3, 4], ['NAIS', 'Hurtig'], [], [35, 65],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Round:</span> {obj.round}<br />
                            <span class='font-weight-lighter'>Type:</span> {obj.roundType}<br />
                            <span class='font-weight-lighter'>score:</span> {obj.score}<br />
                            <span class='font-weight-lighter'>shotCount:</span> {obj.shotCount}<br />
                            {/* <span class='font-weight-lighter'>speedHigh:</span> {obj.speedHigh}<br />
                            <span class='font-weight-lighter'>speedLow:</span> {obj.speedLow}<br />
                            <span class='font-weight-lighter'>speedAvg:</span> {obj.speedAvg}<br />
                            <span class='font-weight-lighter'>spread:</span> {obj.spread}<br />
                            <span class='font-weight-lighter'>stdDev:</span> {obj.stdDev}<br /> */}
                            {/* <span class='font-weight-lighter'>groupSize:</span> {obj.groupSize}<br /> */}
                        </div>
                    </>,
                    showInList: false,
                    displayHeight: '120px',
                },

                {
                    title: <Text id='input.distanceToTarget'>DistanceToTarget</Text>,
                    help: <Text id='input.distanceToTarget-help'>Distance to target in meters.</Text>,
                    placeholder: <Text id='input.distanceToTarget'>DistanceToTarget</Text>,
                    name: 'distanceToTarget',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.distanceToTarget || null,
                    displayValue: val => val ? `${val} m` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.totalRounds'>TotalRounds</Text>,
                    help: <Text id='input.totalRounds-help'>Total rounds in this session.</Text>,
                    placeholder: <Text id='input.totalRounds'>TotalRounds</Text>,
                    name: 'totalRounds',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.totalRounds || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.shootingPosition'>ShootingPosition</Text>,
                    help: <Text id='input.shootingPosition-help'>Shooting position.</Text>,
                    placeholder: <Text id='input.shootingPosition'>ShootingPosition</Text>,
                    name: 'shootingPosition',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.shootingPosition || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ammunition'>Ammunition</Text>,
                    help: <Text id='input.ammunition-help'>Ammunition brand and type.</Text>,
                    placeholder: <Text id='input.ammunition'>Ammunition</Text>,
                    name: 'ammunition',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ammunition || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ammunitionLot'>AmmunitionLot</Text>,
                    help: <Text id='input.ammunitionLot-help'>Ammunition lot.</Text>,
                    placeholder: <Text id='input.ammunitionLot'>AmmunitionLot</Text>,
                    name: 'ammunitionLot',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ammunitionLot || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.gunAirPressure'>GunAirPressure</Text>,
                    help: <Text id='input.gunAirPressure-help'>Air pressure inside the airtank of the gun in bars.</Text>,
                    placeholder: <Text id='input.gunAirPressure'>GunAirPressure</Text>,
                    name: 'gunAirPressure',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.gunAirPressure || null,
                    displayValue: val => val ? `${val} bar` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.ammunitionWeight'>AmmunitionWeight</Text>,
                    help: <Text id='input.ammunitionWeight-help'>Ammunition weight in milligrams.</Text>,
                    placeholder: <Text id='input.ammunitionWeight'>AmmunitionWeight</Text>,
                    name: 'ammunitionWeight',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.ammunitionWeight || null,
                    displayValue: val => val ? `${val} mg` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.distanceMeters'>DistanceMeters</Text>,
                    placeholder: <Text id='input.distanceMeters'>DistanceMeters</Text>,
                    name: 'distanceMeters',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.distanceMeters || null,
                    displayValue: val => val ? `${val} m` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.shotCount'>shotCount</Text>,
                    help: <Text id='input.shotCount-help'>Shot count in this session.</Text>,
                    placeholder: <Text id='input.shotCount'>shotCount</Text>,
                    name: 'shotCount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotCount || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotVelocityLow'>shotVelocityLow</Text>,
                    help: <Text id='input.shotVelocityLow-help'>Shot shotVelocityLow in m/s.</Text>,
                    placeholder: <Text id='input.shotVelocityLow'>shotVelocityLow</Text>,
                    name: 'shotVelocityLow',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocityLow || null,
                    displayValue: val => val ? `${val} m/s` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.shotVelocityHi'>shotVelocityHi</Text>,
                    help: <Text id='input.shotVelocityHi-help'>Shot shotVelocityHi in m/s.</Text>,
                    placeholder: <Text id='input.shotVelocityHi'>shotVelocityHi</Text>,
                    name: 'shotVelocityHi',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocityHi || null,
                    displayValue: val => val ? `${val} m/s` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.shotVelocityAvg'>shotVelocityAvg</Text>,
                    help: <Text id='input.shotVelocityAvg-help'>Shot shotVelocityAvg in m/s.</Text>,
                    placeholder: <Text id='input.shotVelocityAvg'>shotVelocityAvg</Text>,
                    name: 'shotVelocityAvg',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocityAvg || null,
                    displayValue: val => val ? `${val} m/s` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.shotVelocitySpread'>shotVelocitySpread</Text>,
                    help: <Text id='input.shotVelocitySpread-help'>Shot velocity spread.</Text>,
                    placeholder: <Text id='input.shotVelocitySpread'>shotVelocitySpread</Text>,
                    name: 'shotVelocitySpread',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocitySpread || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.shotVelocityStdDev'>shotVelocityStdDev</Text>,
                    help: <Text id='input.shotVelocityStdDev-help'>Shot velocity standard deveation.</Text>,
                    placeholder: <Text id='input.shotVelocityStdDev'>shotVelocityStdDev</Text>,
                    name: 'shotVelocityStdDev',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocityStdDev || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                    isRecommended: true,
                },

                {
                    title: <Text id='input.shotPlacementX'>ShotPlacementX</Text>,
                    help: <Text id='input.shotPlacementX-help'>Shot placement X in mm. 0 is center.</Text>,
                    placeholder: <Text id='input.shotPlacementX'>ShotPlacementX</Text>,
                    name: 'shotPlacementX',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotPlacementX || null,
                    displayValue: val => val ? `${val} cm` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.shotPlacementY'>ShotPlacementY</Text>,
                    help: <Text id='input.shotPlacementY-help'>Shot placement Y in mm. 0 is center.</Text>,
                    placeholder: <Text id='input.shotPlacementY'>ShotPlacementY</Text>,
                    name: 'shotPlacementY',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotPlacementY || null,
                    displayValue: val => val ? `${val} cm` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.shotGroup'>ShotGroup</Text>,
                    help: <Text id='input.shotGroup-help'>Shot group diameter in mm.</Text>,
                    placeholder: <Text id='input.shotGroup'>ShotGroup</Text>,
                    name: 'shotGroup',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotGroup || null,
                    showInList: true,
                    isRecommended: true,
                },

                {
                    title: <Text id='input.windDirection'>WindDirection</Text>,
                    help: <Text id='input.windDirection-help'>Wind direction N, E, S, W.</Text>,
                    placeholder: <Text id='input.windDirection'>WindDirection</Text>,
                    name: 'windDirection',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.windDirection || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.windSpeed'>WindSpeed</Text>,
                    help: <Text id='input.windSpeed-help'>Wind speed in m/s.</Text>,
                    placeholder: <Text id='input.windSpeed'>WindSpeed</Text>,
                    name: 'windSpeed',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.windSpeed || null,
                    displayValue: val => val ? `${val} m/s` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.airTemp'>AirTemp</Text>,
                    help: <Text id='input.airTemp-help'>Air temp in C.</Text>,
                    placeholder: <Text id='input.airTemp'>AirTemp</Text>,
                    name: 'airTemp',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.airTemp || null,
                    displayValue: val => val ? `${val} °C` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.airPressure'>AirPressure</Text>,
                    help: <Text id='input.airPressure-help'>Air pressure in mbar.</Text>,
                    placeholder: <Text id='input.airPressure'>AirPressure</Text>,
                    name: 'airPressure',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.airPressure || null,
                    displayValue: val => val ? `${val} mbar` : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.airHumidity'>AirHumidity</Text>,
                    help: <Text id='input.airHumidity-help'>Air humidity in %.</Text>,
                    placeholder: <Text id='input.airHumidity'>AirHumidity</Text>,
                    name: 'airHumidity',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.airHumidity || null,
                    displayValue: val => val ? `${val} %` : null,
                    isRecommended: true,
                    showInList: true,
                },
            ],
        },
    ],

    journal: [
        {
            name: <Text id='input.journal-basics-title'>Journal basics</Text>,
            key: 'Journal basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>Sale</Text>,
                    placeholder: <Text id='input.sale'>Sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.visitor'>Visitor</Text>,
                    placeholder: <Text id='input.visitor'>Visitor</Text>,
                    name: 'visitor',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitor,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.animal'>Animal</Text>,
                    placeholder: <Text id='input.animal'>Animal</Text>,
                    name: 'animal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.animal,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.journalNumber'>JournalNumber</Text>,
                    placeholder: <Text id='input.journalNumber'>JournalNumber</Text>,
                    name: 'journalNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.journalNumber,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.animalNumber'>animalNumber</Text>,
                    placeholder: <Text id='input.animalNumber'>animalNumber</Text>,
                    name: 'animalNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.animalNumber,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.saleNumber'>saleNumber</Text>,
                    placeholder: <Text id='input.saleNumber'>saleNumber</Text>,
                    name: 'saleNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.saleNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.veterinaryNumber'>veterinaryNumber</Text>,
                    placeholder: <Text id='input.veterinaryNumber'>veterinaryNumber</Text>,
                    name: 'veterinaryNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veterinaryNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.assistantNumber'>assistantNumber</Text>,
                    placeholder: <Text id='input.assistantNumber'>assistantNumber</Text>,
                    name: 'assistantNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.assistantNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.veterinary'>Veterinary</Text>,
                    placeholder: <Text id='input.veterinary'>Veterinary</Text>,
                    name: 'veterinary',
                    type: 'select',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj?.veterinary,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'veterinaries',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.veterinaryName'>Veterinary name from old system</Text>,
                    placeholder: <Text id='input.veterinaryName'>Veterinary name from old system</Text>,
                    name: 'veterinaryName',
                    type: 'text',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.veterinaryName,
                    showInList: true,
                    hideOnCreate: false,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.date'>Date</Text>,
                    placeholder: <Text id='input.date'>Date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    min: '2000-01-01',
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.endDate'>End Date</Text>,
                    placeholder: <Text id='input.endDate'>End Date</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.endDate,
                    min: '2000-01-01',
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                    focusCallback: async (obj, holdingStore, objectName) => {
                        // Set endDate to date + 30 minutes if date exists and endDate is empty
                        if (obj.date && !obj.endDate) {
                            const startDate = new Date(obj.date);
                            const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
                            holdingStore.updateObjectKeyValue(objectName, 'endDate', endDate);
                            // Save to database if object has an ID
                            if (obj.id) {
                                await holdingStore.saveField(obj.id, 'endDate', endDate);
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.closed'>Closed</Text>,
                    placeholder: <Text id='input.closed'>Closed</Text>,
                    name: 'closed',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.closed,
                    min: '2000-01-01',
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                },
                {
                    title: <Text id='input.weight'>Weight</Text>,
                    placeholder: <Text id='input.weight'>Weight</Text>,
                    name: 'weight',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.weight,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.passportNumber'>PassportNumber</Text>,
                    placeholder: <Text id='input.passportNumber'>PassportNumber</Text>,
                    name: 'passportNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.passportNumber,
                    showInList: true,
                },
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                    showInList: false
                },
                // {
                //     title: <Text id='input.description'>Description</Text>,
                //     placeholder: <Text id='input.description'>Description</Text>,
                //     name: 'description',
                //     type: 'textarea',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.description,
                //     showInList: false
                // },
            ],
        },
        {
            name: <Text id='input.journal-medical-title'>Journal medical</Text>,
            key: 'Journal medical',
            fields: [
                {
                    title: <Text id='input.chiefComplaint'>ChiefComplaint</Text>,
                    placeholder: <Text id='input.chiefComplaint'>ChiefComplaint</Text>,
                    // help: <Text id='input.chiefComplaint-help'>Chief Complaint: The primary reason for the veterinary visit, describing the main concern or symptoms observed by the owner or veterinarian.</Text>,
                    name: 'chiefComplaint',
                    type: 'textareaEditor',
                    audioTranscribe: true,
                    aiJournal: true,
                    viewHtml: false,
                    icon: 'fa-solid fa-exclamation-circle',
                    getValue: obj => obj?.chiefComplaint,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.generalCondition'>general Condition</Text>,
                    placeholder: <Text id='input.generalCondition'>general Condition</Text>,
                    // help: <Text id='input.generalCondition-help'>General condition of the animal.</Text>,
                    name: 'generalCondition',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-exclamation-circle',
                    getValue: obj => obj?.generalCondition,
                    showInList: false
                },
                {
                    title: <Text id='input.physicalExaminationFindings'>PhysicalExaminationFindings</Text>,
                    placeholder: <Text id='input.physicalExaminationFindings'>PhysicalExaminationFindings</Text>,
                    // help: <Text id='input.physicalExaminationFindings-help'>Physical Examination Findings: Document the veterinarians findings from the physical examination, including vital signs, body condition, general appearance, organ systems assessment, and any abnormalities or notable observations.</Text>,
                    name: 'physicalExaminationFindings',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-stethoscope',
                    getValue: obj => obj?.physicalExaminationFindings,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.diagnosticTests'>DiagnosticTests</Text>,
                    placeholder: <Text id='input.diagnosticTests'>DiagnosticTests</Text>,
                    // help: <Text id='input.diagnosticTests-help'>Diagnostic Tests: List any diagnostic tests performed during the visit, such as blood tests, imaging (X-rays, ultrasounds), fecal analysis, or other laboratory evaluations. Include the results if available.</Text>,
                    name: 'diagnosticTests',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-flask',
                    getValue: obj => obj?.diagnosticTests,
                    showInList: false
                },
                {
                    title: <Text id='input.diagnosis'>Diagnosis</Text>,
                    placeholder: <Text id='input.diagnosis'>Diagnosis</Text>,
                    // help: <Text id='input.diagnosis-help'>Diagnosis: Record the veterinarians assessment and diagnosis based on the physical examination, medical history, and diagnostic test results. This may involve identifying the underlying condition, disease, or injury causing the presenting symptoms.</Text>,
                    name: 'diagnosis',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-file-medical',
                    getValue: obj => obj?.diagnosis,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.treatmentPlan'>TreatmentPlan</Text>,
                    placeholder: <Text id='input.treatmentPlan'>TreatmentPlan</Text>,
                    // help: <Text id='input.treatmentPlan-help'>Treatment Plan: Outline the treatment plan recommended by the veterinarian, including medications, surgeries, therapies, or other interventions. Specify dosages, frequency, and duration of treatments as applicable.</Text>,
                    name: 'treatmentPlan',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-medkit',
                    getValue: obj => obj?.treatmentPlan,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.medicines'>medicines</Text>,
                    placeholder: <Text id='input.medicines'>medicines</Text>,
                    // help: <Text id='input.medicines-help'>Treatment Plan: Outline the treatment plan recommended by the veterinarian, including medications, surgeries, therapies, or other interventions. Specify dosages, frequency, and duration of treatments as applicable.</Text>,
                    name: 'medicines',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-medkit',
                    getValue: obj => obj?.medicines,
                    showInList: false
                },
                {
                    title: <Text id='input.medicine'>Medicine</Text>,
                    placeholder: <Text id='input.medicine'>Medicine</Text>,
                    name: 'medicine',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-prescription-bottle',
                    getValue: obj => obj?.medicine,
                    isRecommended: true,
                    datalist: [
                        // 'id', 'name', 'amountEachAnimal', 'unit', 'Note', 'Last treatment', 'Usage', 'AddedBy', 'StopDate', 'ProductNumber',
                        'id', 'name', 'navnFormStyrke', 'amountPerAnimal', 'unit',
                    ],
                    displayArrayObject: obj => <>
                        <div class='d-flex flex-column text-start flex-fill overflow-auto px-3 py-1 mt-1 small' style='line-height: 1.3;'>
                            <div class='fw-bold' style='font-size: 1.0em;'>{obj.name}</div>
                            {obj.fullname && obj.fullname !== obj.name && <div class='text-body-secondary' style='font-size: 1.0em;'>{obj.fullname}</div>}
                            <div class='d-flex flex-wrap gap-3'>
                                {(obj.amountPerAnimal || obj.unit) && <span><span class='text-body-secondary'>Mengde:</span> {obj.amountPerAnimal} {obj.unit}</span>}
                                {obj.routeOfAdministration && <span><span class='text-body-secondary'>Adm.vei:</span> {obj.routeOfAdministration}</span>}
                                {obj.no && <span><span class='text-body-secondary'>Pakning:</span> {obj.no}</span>}
                            </div>
                            {obj.productNumber && <div><span class='text-body-secondary'>Varenr:</span> {obj.productNumber}</div>}
                            {obj.lastThreatment && <div><span class='text-body-secondary'>Siste beh.:</span> {util.formatDate(obj.lastThreatment, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</div>}
                            {obj.usageReason && <div><span class='text-body-secondary'>Årsak til bruk:</span> {({ '1': 'Andre', '2': 'Forebyggende', '3': 'Sykdom' })[obj.usageReason] || obj.usageReason}</div>}
                            {obj.threatmentBy && <div><span class='text-body-secondary'>Behandlet av:</span> {({ '1': 'Dyreeier/medhjelper', '2': 'Dyrehelsepersonell' })[obj.threatmentBy] || obj.threatmentBy}</div>}
                            {obj.retentionPeriods && obj.retentionPeriods.length > 0 && <>
                                <div class='mt-1 fw-bold'>Tilbakeholdelse</div>
                                <table class='table table-sm table-borderless mb-0 small' style='line-height: 1.2;'>
                                    <thead>
                                        <tr><th class='py-0 bg-body-secondary'>Type</th><th class='py-0 bg-body-secondary'>Dyreslag</th><th class='text-end py-0 bg-body-secondary'>Dager</th></tr>
                                    </thead>
                                    <tbody>
                                        {obj.retentionPeriods.map(rp => rp.dyreslag && rp.dyreslag.map(ds => (
                                            <tr>
                                                <td class='py-0'>{rp.type}</td>
                                                <td class='py-0'>{ds.dyreslagkode}</td>
                                                <td class='text-end py-0'>{Number(ds['antall-dager']) === -1 ? <Text id='dyrejournal.retention-lifetime'>Lifetime</Text> : ds['antall-dager']}</td>
                                            </tr>
                                        )))}
                                    </tbody>
                                </table>
                            </>}
                        </div>
                    </>,
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addMedicine',
                    showInList: false,
                    displayWidth: '600px',
                    displayHeight: '200px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.diagnoses'>Diagnose</Text>,
                    placeholder: <Text id='input.diagnoses'>Diagnose</Text>,
                    name: 'diagnoses',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-stethoscope',
                    getValue: obj => obj?.diagnoses,
                    isRecommended: true,
                    datalist: [
                        // 'id', 'name', 'amountEachAnimal', 'unit', 'Note', 'Last treatment', 'Usage', 'AddedBy', 'StopDate', 'ProductNumber',
                        'id', 'name', 'diagnoseCode',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-3 py-2 mt-1'>
                            <span class='font-weight-lighter'>Code:</span> {obj.diagnoseCode}<br />
                            <span class='font-weight-lighter'>Diagnose:</span> {obj.name}
                            {!!obj.differential && <span class='badge text-bg-warning ms-1' title='Differensialdiagnose'><Text id='dyrejournal.diagnose-differential-short'>DD</Text></span>}
                            <br />
                        </div>
                    </>,
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addDiagnose',
                    showInList: false,
                    displayWidth: '300px',
                    displayHeight: '100px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    // help: <Text id='input.description-help'>Client Communication: Summarize the discussions and instructions provided by the veterinarian to the owner, including any additional information about the condition, prognosis, follow-up care, or home care instructions.</Text>,
                    name: 'description',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-comments',
                    getValue: obj => obj.description,
                    showInList: false
                },
                {
                    title: <Text id='input.clientCommunication'>ClientCommunication</Text>,
                    placeholder: <Text id='input.clientCommunication'>ClientCommunication</Text>,
                    // help: <Text id='input.clientCommunication-help'>Client Communication: Summarize the discussions and instructions provided by the veterinarian to the owner, including any additional information about the condition, prognosis, follow-up care, or home care instructions.</Text>,
                    name: 'clientCommunication',
                    type: 'textarea',
                    audioTranscribe: true,
                    icon: 'fa-solid fa-comments',
                    getValue: obj => obj.clientCommunication,
                    showInList: false
                },
                {
                    title: <Text id='input.recommendations'>Recommendations</Text>,
                    placeholder: <Text id='input.recommendations'>Recommendations</Text>,
                    // help: <Text id='input.recommendations-help'>Recommendations: Include any recommendations for preventive care, vaccinations, dietary adjustments, lifestyle changes, or future monitoring or examinations.</Text>,
                    name: 'recommendations',
                    type: 'textarea',
                    icon: 'fa-solid fa-check-square',
                    audioTranscribe: true,
                    getValue: obj => obj.recommendations,
                    showInList: false
                },
                {
                    title: <Text id='input.followUp'>FollowUp</Text>,
                    placeholder: <Text id='input.followUp'>FollowUp</Text>,
                    // help: <Text id='input.followUp-help'>Follow-up: Note any scheduled follow-up appointments, rechecks, or additional tests planned as part of the ongoing care for the patient.</Text>,
                    name: 'followUp',
                    type: 'textarea',
                    icon: 'fa-solid fa-clock',
                    audioTranscribe: true,
                    getValue: obj => obj.followUp,
                    showInList: false
                },
            ],
        },
        {
            name: <Text id='input.journal-clinical-title'>Journal clinical</Text>,
            key: 'Journal clinical',
            fields: [
                {
                    title: <Text id='input.puls'>Puls</Text>,
                    placeholder: <Text id='input.puls'>Puls</Text>,
                    name: 'puls',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.puls,
                    showInList: true,
                },
                {
                    title: <Text id='input.respiration'>respiration</Text>,
                    placeholder: <Text id='input.respiration'>respiration</Text>,
                    name: 'respiration',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.respiration,
                    showInList: true,
                },
                {
                    title: <Text id='input.temperature'>Temperature</Text>,
                    placeholder: <Text id='input.temperature'>Temperature</Text>,
                    name: 'temperature',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.temperature,
                    showInList: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.journal-passport-title'>Journal passport</Text>,
            key: 'Journal passport',
            fields: [
                {
                    title: <Text id='input.passportNumber'>PassportNumber</Text>,
                    placeholder: <Text id='input.passportNumber'>PassportNumber</Text>,
                    name: 'passportNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.passportNumber,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.journal-extra-title'>Journal extra</Text>,
            key: 'Journal extra',
            fields: [
                {
                    title: <Text id='input.isDentalCheck'>isDentalCheck</Text>,
                    placeholder: <Text id='input.isDentalCheck'>isDentalCheck</Text>,
                    name: 'isDentalCheck',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isDentalCheck ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isEmergencyResponse'>isEmergencyResponse</Text>,
                    placeholder: <Text id='input.isEmergencyResponse'>isEmergencyResponse</Text>,
                    name: 'isEmergencyResponse',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isEmergencyResponse ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isInProgress'>isInProgress</Text>,
                    placeholder: <Text id='input.isInProgress'>isInProgress</Text>,
                    name: 'isInProgress',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isInProgress ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isTemplate'>Is Template</Text>,
                    placeholder: <Text id='input.isTemplate'>Is Template</Text>,
                    name: 'isTemplate',
                    type: 'toggle',
                    icon: 'fa-solid fa-copy',
                    getValue: obj => obj.isTemplate ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.templateTitle'>Template Title</Text>,
                    placeholder: <Text id='input.templateTitle-placeholder'>E.g. Vaccination - Dog</Text>,
                    name: 'templateTitle',
                    type: 'text',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.templateTitle || '',
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIds'>Species</Text>,
                    placeholder: <Text id='input.speciesIds'>Species</Text>,
                    help: <Text id='input.speciesIds-help'>Limit this template to specific species. Leave empty to show for all species.</Text>,
                    name: 'speciesIds',
                    type: 'arrayObject',
                    icon: 'fa-duotone fa-paw',
                    searchMethod: 'searchSpecies',
                    getValue: obj => obj.speciesIds?.map(s => s),
                    datalist: [
                        'speciesId', 'name',
                    ],
                    datalistHelp: [
                        'Species ID', 'Species name',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Species:</span> {obj.name} <small class='text-body-secondary'>(#{obj.speciesId})</small>
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.diagnoseCodes'>Diagnoses</Text>,
                    placeholder: <Text id='input.diagnoseCodes'>Diagnoses</Text>,
                    help: <Text id='input.diagnoseCodes-help'>Limit this template to specific diagnoses. Leave empty to show for all diagnoses on this species.</Text>,
                    name: 'diagnoseCodes',
                    type: 'arrayObject',
                    icon: 'fa-duotone fa-stethoscope',
                    searchMethod: 'searchDiagnoses',
                    getValue: obj => obj.diagnoseCodes?.map(s => s),
                    datalist: [
                        'diagnoseCode', 'name',
                    ],
                    datalistHelp: [
                        'Diagnose code', 'Diagnose name',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Diagnose:</span> {obj.name} <small class='text-body-secondary'>({obj.diagnoseCode})</small>
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
        {
            name: <Text id='input.journal-attachments-title'>Journal attatchments</Text>,
            key: 'Journal attatchments',
            fields: [
                {
                    title: <Text id='input.prescriptions'>Prescriptions</Text>,
                    placeholder: <Text id='input.prescriptions'>Prescriptions</Text>,
                    name: 'prescriptions',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-prescription-bottle',
                    getValue: obj => obj.prescriptions,
                    isRecommended: true,
                    datalist: [
                        'id', 'name', 'description',
                    ],
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addPrescription',
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.diagnoses'>Diagnoses</Text>,
                    placeholder: <Text id='input.diagnoses'>Diagnoses</Text>,
                    name: 'diagnoses',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-file-medical',
                    getValue: obj => obj.diagnoses,
                    datalist: [
                        'id', 'name', 'description',
                    ],
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addDiagnose',
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.referrals'>Referrals</Text>,
                    placeholder: <Text id='input.referrals'>Referrals</Text>,
                    name: 'referrals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-hand-point-right',
                    getValue: obj => obj.referrals,
                    datalist: [
                        'id', 'name', 'description',
                    ],
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addReferral',
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.journal-media-title'>Journal media</Text>,
            key: 'Journal media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.emails'>emails</Text>,
                    placeholder: <Text id='input.emails'>emails</Text>,
                    name: 'emails',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.emails,
                    datalist: [
                        'subject', 'from', 'date', 'filename',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-3'>
                            <span class='font-weight-lighter'>Date:</span> {obj.date}<br />
                            <span class='font-weight-lighter'>From:</span> {obj.from}<br />
                            <span class='font-weight-lighter'>Subject:</span> {obj.subject}<br />
                            <br />
                            <span style='font-size: 1.5em;'>
                                <a href={`https://dyrejournal.app/original/${obj.fileClinic}/${obj.fileLocation}`} target='_blank' native><i class='fa-solid fa-link' /> {obj.filename}</a><br />
                            </span>
                        </div>
                    </>,
                    showInList: false,
                    displayWidth: '450px',
                    displayHeight: '250px',
                },
            ],
        },
    ],
    prescription: [
        {
            name: <Text id='input.prescription-basics-title'>Prescription basics</Text>,
            key: 'Prescription basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.journal'>Journal</Text>,
                    placeholder: <Text id='input.journal'>Journal</Text>,
                    name: 'journal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.journal,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.animal'>Animal</Text>,
                    placeholder: <Text id='input.animal'>Animal</Text>,
                    name: 'animal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.animal,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>Sale</Text>,
                    placeholder: <Text id='input.sale'>Sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.veterinary'>Veterinary</Text>,
                    placeholder: <Text id='input.veterinary'>Veterinary</Text>,
                    name: 'veterinary',
                    type: 'select',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.veterinary,
                    datalistName: 'veterinaries',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.date'>Date</Text>,
                    placeholder: <Text id='input.date'>Date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    showInList: true,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.endDate,
                    showInList: true,
                    hideOnCreate: false,
                    displayInputInfo: (obj, holdingStore, objectName) => {
                        const setEndDate = async (days) => {
                            const baseDate = obj.date ? new Date(obj.date) : new Date();
                            const endDate = new Date(baseDate);
                            endDate.setDate(endDate.getDate() + days);
                            holdingStore.updateObjectKeyValue(objectName, 'endDate', endDate);
                            if (obj.id) {
                                await holdingStore.saveField(obj.id, 'endDate', endDate);
                            }
                        };
                        return (
                            <div class='d-flex flex-row align-items-center gap-2 mt-1'>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary rounded-pill'
                                    onClick={() => setEndDate(5)}
                                >
                                    5 dager
                                </button>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary rounded-pill'
                                    onClick={() => setEndDate(30)}
                                >
                                    1 mnd
                                </button>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary rounded-pill'
                                    onClick={() => setEndDate(365)}
                                >
                                    1 år
                                </button>
                            </div>
                        );
                    },
                },
                {
                    title: <Text id='input.prescription-source'>Source</Text>,
                    placeholder: <Text id='input.prescription-source'>Source</Text>,
                    name: 'source',
                    type: 'text',
                    icon: 'fa-duotone fa-file-prescription',
                    getValue: obj => obj.source || '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.drfId'>DRF id</Text>,
                    placeholder: <Text id='input.drfId'>DRF id</Text>,
                    name: 'drfId',
                    type: 'text',
                    icon: 'fa-duotone fa-hashtag',
                    getValue: obj => obj.drfId || '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.drfReferenceNumber'>DRF reference number</Text>,
                    placeholder: <Text id='input.drfReferenceNumber'>DRF reference number</Text>,
                    name: 'drfReferenceNumber',
                    type: 'text',
                    icon: 'fa-duotone fa-hashtag',
                    getValue: obj => obj.drfReferenceNumber || '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.drfStatus'>DRF status</Text>,
                    placeholder: <Text id='input.drfStatus'>DRF status</Text>,
                    name: 'drfStatus',
                    type: 'text',
                    icon: 'fa-duotone fa-circle-info',
                    getValue: obj => obj.drfStatus || '',
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.drfStatusDate'>DRF status date</Text>,
                    placeholder: <Text id='input.drfStatusDate'>DRF status date</Text>,
                    name: 'drfStatusDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.drfStatusDate,
                    showInList: false,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.description'>Description</Text>,
                //     placeholder: <Text id='input.description'>Description</Text>,
                //     name: 'description',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.description,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.drugs'>Drugs</Text>,
                    placeholder: <Text id='input.drugs'>Drugs</Text>,
                    name: 'drugs',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.drugs,
                    datalist: [
                        'id', 'name', 'description', 'dosage', 'strength', 'sideEffects', 'units', 'reit',
                    ],
                    showInList: false,
                    displayHeight: '150px',
                    hideOnCreate: true,
                    isRecommended: true,
                },
            ],
        },
    ],
    prescriptionDrugs: [
        {
            name: <Text id='input.prescription-drugs-basics-title'>Prescription drugs basics</Text>,
            key: 'Prescription drugs basics',
            fields: [
                // {
                //     title: <Text id='input.description'>Description</Text>,
                //     placeholder: <Text id='input.description'>Description</Text>,
                //     name: 'description',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.description,
                //     showInList: true,
                // },

                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.qty,
                    showInList: true,
                },

                // AND - Andre sykdommer av vesentlig omfang/betydning
                // BEV - skjelett, ledd, bånd, sener og muskulatur
                // BFD - Behandling av friske dyr
                // BRY - Brysthuleorganer og bloddannende organer
                // FRD - fordøyelsesorganer med lever, bukspyttkjertel og endokrine organer
                // HUD - Hud og organer, inkl. klauv
                // IOT - ikke organrelaterte tilstander
                // JUR - Jur og spener
                // KOB - kjønnsorganer og obstetrikk
                // KRU - kjønnsorganer - reproduksjon - Urinveier
                // NRE - Nervesystem og sanseorganer inkl. øye og øre

                // I tillegg til disse diagnosekodene skal det registreres
                // en kode for bruksområde for legemiddelet. Dette
                // kodeverket består av tre koder/områder:
                // A: Andre
                // F: Forebyggende behandling inkl. vaksiner
                // S: Sykdom
                {
                    title: <Text id='input.diagnoseText'>diagnoseText</Text>,
                    placeholder: <Text id='input.diagnoseText'>diagnoseText</Text>,
                    name: 'diagnoseText',
                    type: 'select',
                    icon: 'fa-solid fa-language',
                    getValue: obj => obj.diagnoseText,
                    datalist: [
                        ['AND - Andre sykdommer av vesentlig omfang/betydning', 'AND - Andre sykdommer av vesentlig omfang/betydning'],
                        ['BEV - skjelett, ledd, bånd, sener og muskulatur', 'BEV - skjelett, ledd, bånd, sener og muskulatur'],
                        ['BFD - Behandling av friske dyr', 'BFD - Behandling av friske dyr'],
                        ['BRY - Brysthuleorganer og bloddannende organer', 'BRY - Brysthuleorganer og bloddannende organer'],
                        ['FRD - fordøyelsesorganer med lever, bukspyttkjertel og endokrine organer', 'FRD - fordøyelsesorganer med lever, bukspyttkjertel og endokrine organer'],
                        ['HUD - Hud og organer, inkl. klauv', 'HUD - Hud og organer, inkl. klauv'],
                        ['IOT - ikke organrelaterte tilstander', 'IOT - ikke organrelaterte tilstander'],
                        ['JUR - Jur og spener', 'JUR - Jur og spener'],
                        ['KOB - kjønnsorganer og obstetrikk', 'KOB - kjønnsorganer og obstetrikk'],
                        ['KRU - kjønnsorganer - reproduksjon - Urinveier', 'KRU - kjønnsorganer - reproduksjon - Urinveier'],
                        ['NRE - Nervesystem og sanseorganer inkl. øye og øre', 'NRE - Nervesystem og sanseorganer inkl. øye og øre'],
                    ],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.usageText'>usageText</Text>,
                    placeholder: <Text id='input.usageText'>usageText</Text>,
                    name: 'usageText',
                    type: 'select',
                    icon: 'fa-solid fa-language',
                    getValue: obj => obj.usageText,
                    datalist: [
                        ['A: Andre', 'A: Andre'],
                        ['F: Forebyggende behandling inkl. vaksiner', 'F: Forebyggende behandling inkl. vaksiner'],
                        ['S: Sykdom', 'S: Sykdom'],
                    ],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.dosage'>Dosage</Text>,
                    placeholder: <Text id='input.dosage'>Dosage</Text>,
                    help: <Text id='input.dosage-help'>Dosage: The prescribed dosage or strength of the medication.</Text>,
                    name: 'dosage',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dosage,
                    showInList: true,
                },
                // {
                //     title: <Text id='input.frequency'>Frequency</Text>,
                //     placeholder: <Text id='input.frequency'>Frequency</Text>,
                //     help: 'Frequency: How often the medication should be taken (e.g., once daily, twice daily).',
                //     name: 'frequency',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.frequency,
                //     showInList: true,
                // },
                // {
                //     title: <Text id='input.routeOfAdministration'>RouteOfAdministration</Text>,
                //     placeholder: <Text id='input.routeOfAdministration'>RouteOfAdministration</Text>,
                //     help: 'Route of Administration: How the medication should be administered (e.g., oral, topical, injection).',
                //     name: 'routeOfAdministration',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.routeOfAdministration,
                //     showInList: true,
                // },
                // {
                //     title: <Text id='input.instructionForUse'>InstructionForUse</Text>,
                //     placeholder: <Text id='input.instructionForUse'>InstructionForUse</Text>,
                //     help: 'Instructions for Use: Provide clear and specific instructions on how to take or use the medication, including any special considerations or precautions.',
                //     name: 'instructionForUse',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.instructionForUse,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.additionalNotes'>AdditionalNotes</Text>,
                    placeholder: <Text id='input.additionalNotes'>AdditionalNotes</Text>,
                    help: <Text id='input.additionalNotes-help'>Additional Notes: Any additional information or special instructions that may be relevant to the prescription, such as dietary restrictions, storage requirements, or possible side effects.</Text>,
                    name: 'additionalNotes',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.additionalNotes,
                    showInList: true,
                },

                // {
                //     title: <Text id='input.sideEffects'>SideEffects</Text>,
                //     placeholder: <Text id='input.sideEffects'>SideEffects</Text>,
                //     name: 'sideEffects',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.sideEffects,
                //     showInList: true,
                // },
                // {
                //     title: <Text id='input.units'>Units</Text>,
                //     placeholder: <Text id='input.units'>Units</Text>,
                //     name: 'units',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.units,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.reit'>Reit</Text>,
                    placeholder: <Text id='input.reit'>Reit</Text>,
                    name: 'reit',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.reit,
                    showInList: true,
                },

            ],
        },
    ],
    drug: [
        {
            name: <Text id='input.drugs-basics-title'>Drug basics</Text>,
            key: 'Drug basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.varenavn'>varenavn</Text>,
                    placeholder: <Text id='input.varenavn'>varenavn</Text>,
                    help: <Text id='input.varenavn-help'>Ie: Synulox vet</Text>,
                    name: 'varenavn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.varenavn,
                    showInList: true,
                },
                {
                    title: <Text id='input.navnFormStyrke'>NavnFormStyrke</Text>,
                    placeholder: <Text id='input.navnFormStyrke'>NavnFormStyrke</Text>,
                    help: <Text id='input.navnFormStyrke-help'>NavnFormStyrke: The name, form, and strength of the medication. Ie: Synulox vet tab hund,katt 200 mg/50 mg</Text>,
                    name: 'navnFormStyrke',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.navnFormStyrke,
                    showInList: true,
                },
                {
                    title: <Text id='input.atcDn'>atcDn</Text>,
                    placeholder: <Text id='input.atcDn'>atcDn</Text>,
                    help: <Text id='input.atcDn-help'>Ie: Amoksicillin og enzymhemmer</Text>,
                    name: 'atcDn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.atcDn,
                    showInList: true,
                },
                {
                    title: <Text id='input.administrasjonsveiDn'>administrasjonsveiDn</Text>,
                    placeholder: <Text id='input.administrasjonsveiDn'>administrasjonsveiDn</Text>,
                    help: <Text id='input.administrasjonsveiDn-help'>Ie: Oral bruk</Text>,
                    name: 'administrasjonsveiDn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.administrasjonsveiDn,
                    showInList: true,
                },
                {
                    title: <Text id='input.enhetDoseringDn'>enhetDoseringDn</Text>,
                    placeholder: <Text id='input.enhetDoseringDn'>enhetDoseringDn</Text>,
                    help: <Text id='input.enhetDoseringDn-help'>Ie: tablett</Text>,
                    name: 'enhetDoseringDn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.enhetDoseringDn,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.dosage'>Dosage</Text>,
                    placeholder: <Text id='input.dosage'>Dosage</Text>,
                    name: 'dosage',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dosage,
                    showInList: true,
                },
                {
                    title: <Text id='input.strength'>Strength</Text>,
                    placeholder: <Text id='input.strength'>Strength</Text>,
                    name: 'strength',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.strength,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sideEffects'>SideEffects</Text>,
                    placeholder: <Text id='input.sideEffects'>SideEffects</Text>,
                    name: 'sideEffects',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sideEffects,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.units'>Units</Text>,
                    placeholder: <Text id='input.units'>Units</Text>,
                    name: 'units',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.units,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                    isAdminField: true,
                },
            ],
        },
    ],
    animal: [
        {
            name: <Text id='input.animal-important-title'>Animal important</Text>,
            key: 'Animal important',
            isCollapsed: false,
            backgroundColor: 'var(--bs-info-bg-subtle) !important',
            borderColor: 'var(--bs-info-border-subtle) !important',
            fields: [
                {
                    title: <Text id='input.pedigreeName'>Pedigree Name</Text>,
                    placeholder: <Text id='input.pedigreeName'>Pedigree Name</Text>,
                    name: 'pedigreeName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.pedigreeName,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    min: '2000-01-01',
                    max: 'today',
                    type: 'date',
                    hideDateShortcuts: true,
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.gender'>Gender</Text>,
                    placeholder: <Text id='input.gender'>Gender</Text>,
                    name: 'gender',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gender,
                    datalist: [['Hann', 'Hann'], ['Hunn', 'Hunn'], ['Kastrert hann', 'Kastrert hann'], ['Kastrert hunn', 'Kastrert hunn'], ['Hoppe', 'Hoppe'], ['Hingst', 'Hingst'], ['Vallak', 'Vallak']],
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.species'>Species</Text>,
                    placeholder: <Text id='input.species'>Species</Text>,
                    name: 'speciesId',
                    type: 'selectAdvanced',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.speciesId,
                    datalistName: 'species',
                    datalistFieldKeyName: 'speciesId',
                    datalistFieldKey: e => e?.speciesId,
                    datalistFieldVal: e => `${e?.name} ${e?.isFromDyreid ? `(DyreID)` : ''}`,
                    showInList: true,
                    isRecommended: true,
                    callback: async (obj, updateValue, holdingStore) => {
                        await nrrRegistrationNumberHook(obj, updateValue, holdingStore);
                        // console.log('speciesId callback', obj);
                        if (obj?.speciesId) {
                            holdingStore.loadBreeds(obj.speciesId);
                        }
                    },
                },
                {
                    title: <Text id='input.breed'>Breed</Text>,
                    placeholder: <Text id='input.breed'>Breed</Text>,
                    name: 'breedId',
                    type: 'selectAdvanced',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.breedId,
                    datalistName: 'breeds',
                    datalistFieldKeyName: 'breedId',
                    datalistFieldKey: e => e.breedId,
                    datalistFieldVal: e => `${e?.name}  ${e?.isFromDyreid ? `(DyreID)` : ''}`,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isOkDyreId'>isOkDyreId</Text>,
                    placeholder: <Text id='input.isOkDyreId'>isOkDyreId</Text>,
                    help: <Text id='input.isOkDyreId-help'>Marks that the animal is OK in DyreID. Status flag only - it does not affect DyreID reporting.</Text>,
                    name: 'isOkDyreId',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isOkDyreId ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.chipIdDate'>chipIdDate</Text>,
                    placeholder: <Text id='input.chipIdDate'>chipIdDate</Text>,
                    name: 'chipIdDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    min: '2000-01-01',
                    max: 'today',
                    getValue: obj => obj.chipIdDate,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.birth && obj.chipIdDate && new Date(obj.birth) > new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipIdDate', 'Date can not be before birth date.');
                        } else if (new Date() < new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipIdDate', 'Date can not be in the future.');
                        } else {
                            holdingStore.setFeedback('chipIdDate', '');
                        }
                    },
                },
                {
                    title: <Text id='input.chipId'>ChipId</Text>,
                    placeholder: <Text id='input.chipId'>ChipId</Text>,
                    name: 'chipId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.chipId,
                    showInList: true,
                    isRecommended: true,
                    // dyreIdPetId
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.chipId && !/^\d{15}$/.test(obj.chipId)) {
                            holdingStore.setFeedbackError('chipId', 'Invalid chipId. Must be 15 digits.');
                        } else {
                            holdingStore.setFeedbackError('chipId', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('chipId', '');
                        holdingStore.setFeedbackButton('chipId', null, null);
                    },
                    loadCallback: async (obj, updateValue, holdingStore) => {
                        // console.log('loadCallback', obj, holdingStore.name);
                        holdingStore.setFeedback('chipId', '');
                        holdingStore.setFeedbackButton('chipId', null, null);
                        // This is beeing called when the object is loaded after component is mounted.
                        //
                        // console.log('loadCallback', obj, updateValue, holdingStore.name);
                        await nrrRegistrationNumberHook(obj, updateValue, holdingStore);
                        if (!holdingStore.dyreIdApproved) return;
                        if (!obj.id) {
                            holdingStore.setFeedback('chipId', 'You need to save the animal before you can register it with DyreID.');
                            return;
                        }
                        if (!obj.speciesId || !obj.breedId) {
                            holdingStore.setFeedback('chipId', 'You need to add species and breed before you can register this animal with DyreID.');
                        }
                        if (!obj.gender) {
                            holdingStore.setFeedback('chipId', 'You need to add gender before you can register this animal with DyreID.');
                        }
                        if (!obj.birth) {
                            holdingStore.setFeedback('chipId', 'You need to add birth date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipIdDate) {
                            holdingStore.setFeedback('chipId', 'You need to add chip date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipId) {
                            holdingStore.setFeedback('chipId', 'You need to add a chip number (15 digits) before you can register this animal with DyreID.');
                        }
                        if (obj.birth && obj.chipIdDate && new Date(obj.birth) > new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipId', 'Chip date can not be before birth date.');
                            return;
                        }
                        if (!obj.chipId || !obj.speciesId || !obj.breedId || !obj.birth || !obj.chipIdDate) return;
                        // Must be a number and 15 digits
                        if (!/^\d{15}$/.test(obj.chipId)) {
                            holdingStore.setFeedback('chipId', 'Invalid chipId. Has to be 15 digits.');
                        } else if (obj.dyreIdPetId && obj.chipId === obj.dyreIdPetChipId) {
                            console.log('obj.dyreIdPetId', obj.dyreIdPetId);
                            console.log('obj.dyreIdPetChipId', obj.dyreIdPetChipId);
                            holdingStore.setFeedback('chipId', 'DyreID: ChipId is already registered with DyreID.');
                        } else {
                            holdingStore.setFeedback('chipId', 'Checking with DyreID...');
                            const response = await holdingStore.dyreIdChipValidationStatus(obj.chipId);
                            if (response?.data?.status) {
                                switch (response.data.status) {
                                    case 1:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is already another pet. Is the number you are registering correct?');
                                        holdingStore.setFeedbackButtonInfo('chipId', 'Customer has been informed that data is shared with DyreID.')
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                            holdingStore.setFeedbackButtonInfo('chipId', '');
                                        }, 'Send to DyreId');
                                        break;
                                    case 2:
                                        holdingStore.setFeedback('chipId', 'DyreID: Valid chip & available for new registration.');
                                        holdingStore.setFeedbackButtonInfo('chipId', 'Customer has been informed that data is shared with DyreID.')
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                            holdingStore.setFeedbackButtonInfo('chipId', '');
                                        }, 'Send to DyreId');
                                        break;
                                    case 3:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is not in database, but you can still register it with DyreId');
                                        holdingStore.setFeedbackButtonInfo('chipId', 'Customer has been informed that data is shared with DyreID.')
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                            holdingStore.setFeedbackButtonInfo('chipId', '');
                                        }, 'Send to DyreId');
                                        break;
                                    default:
                                        holdingStore.setFeedback('chipId', 'DyreID: Unknown status from DyreId.');
                                        break;
                                }
                            }
                            // console.log('response', response);
                        }
                    },
                    callback: async (obj, updateValue, holdingStore) => {
                        // console.log('callback', holdingStore.name, holdingStore.dyreIdApproved, obj.speciesId, obj.breedId, obj.birth, obj.chipId, obj.dyreIdPetId);
                        await nrrRegistrationNumberHook(obj, updateValue, holdingStore);
                        if (!holdingStore.dyreIdApproved) return;
                        if (!obj.id) {
                            holdingStore.setFeedback('chipId', 'You need to save the animal before you can register it with DyreID.');
                            return;
                        }
                        if (!obj.speciesId || !obj.breedId) {
                            holdingStore.setFeedback('chipId', 'You need to add species and breed before you can register this animal with DyreID.');
                        }
                        if (!obj.gender) {
                            holdingStore.setFeedback('chipId', 'You need to add gender before you can register this animal with DyreID.');
                        }
                        if (!obj.birth) {
                            holdingStore.setFeedback('chipId', 'You need to add birth date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipIdDate) {
                            holdingStore.setFeedback('chipId', 'You need to add chip date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipId) {
                            holdingStore.setFeedback('chipId', 'You need to add a chip number (15 digits) before you can register this animal with DyreID.');
                        }
                        if (obj.birth && obj.chipIdDate && new Date(obj.birth) > new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipId', 'Chip date can not be before birth date.');
                            return;
                        }
                        if (!obj.chipId || !obj.speciesId || !obj.breedId || !obj.birth || !obj.chipIdDate) return;
                        // Must be a number and 15 digits
                        if (!/^\d{15}$/.test(obj.chipId)) {
                            holdingStore.setFeedback('chipId', 'Invalid chipId. Has to be 15 digits.');
                        } else if (obj.dyreIdPetId && obj.chipId === obj.dyreIdPetChipId) {
                            console.log('obj.dyreIdPetId', obj.dyreIdPetId);
                            console.log('obj.dyreIdPetChipId', obj.dyreIdPetChipId);
                            holdingStore.setFeedback('chipId', 'DyreID: ChipId is already registered with DyreId.');
                        } else {
                            holdingStore.setFeedback('chipId', 'Checking with DyreID...');
                            const response = await holdingStore.dyreIdChipValidationStatus(obj.chipId);
                            if (response?.data?.status) {
                                switch (response.data.status) {
                                    case 1:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is already another pet. Is the number you are registering correct?');
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                        }, 'Send to DyreId');
                                        break;
                                    case 2:
                                        holdingStore.setFeedback('chipId', 'DyreID: Valid chip & available for new registration.');
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                        }, 'Send to DyreId');
                                        break;
                                    case 3:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is not in database, but you can still register it with DyreId');
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                        }, 'Send to DyreId');
                                        break;
                                    default:
                                        holdingStore.setFeedback('chipId', 'DyreID: Unknown status from DyreId.');
                                        break;
                                }
                            }
                            // console.log('response', response);
                            // holdingStore.setFeedback('chipId', JSON.stringify(response));
                        }
                    },
                },
                {
                    title: <Text id='input.chipIdChecked'>ChipId Checked</Text>,
                    placeholder: <Text id='input.chipIdChecked'>ChipId Checked</Text>,
                    name: 'chipIdChecked',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.chipIdChecked,
                    min: '2010-01-01',
                    max: 'today',
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.passportId'>passportId</Text>,
                    placeholder: <Text id='input.passportId'>passportId</Text>,
                    name: 'passportId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.passportId,
                    showInList: true,
                    isRecommended: true,
                },

            ],
        },
        {
            name: <Text id='input.animal-basics-title'>Animal basics</Text>,
            key: 'Animal basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.veterinary'>Veterinary</Text>,
                    placeholder: <Text id='input.veterinary'>Veterinary</Text>,
                    name: 'veterinary',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.veterinary,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.owner'>Owner</Text>,
                    placeholder: <Text id='input.owner'>Owner</Text>,
                    name: 'owner',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.owner,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.animalNumber'>AnimalNumber</Text>,
                    placeholder: <Text id='input.animalNumber'>AnimalNumber</Text>,
                    name: 'animalNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.animalNumber,
                    showInList: true,
                    isAdminField: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.dyreholdsId'>Dyreholds-ID</Text>,
                    placeholder: <Text id='input.dyreholdsId-placeholder'>f.eks. 2499816-00318</Text>,
                    help: <Text id='input.dyreholdsId-help'>Enter the full animal ID from the ear tag. Format: Dyreholds-ID - Individual number. This will auto-populate the mark of origin and ear mark fields.</Text>,
                    name: 'dyreholdsId',
                    type: 'text',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => {
                        // If user has typed a value, use that
                        if (obj.dyreholdsId !== null && obj.dyreholdsId !== undefined && obj.dyreholdsId !== '') {
                            return obj.dyreholdsId;
                        }
                        // Otherwise, compute from markOfOrigin and earMark
                        const hasMarkOfOrigin = obj.markOfOrigin !== null && obj.markOfOrigin !== undefined && obj.markOfOrigin !== '';
                        const hasEarMark = obj.earMark !== null && obj.earMark !== undefined && obj.earMark !== '';
                        if (hasMarkOfOrigin || hasEarMark) {
                            return `${obj.markOfOrigin || ''}-${obj.earMark || ''}`;
                        }
                        return '';
                    },
                    showInList: false,
                    isAdminField: false,
                    isRecommended: true,
                    isVirtual: true,
                    callbackDebounce: 800,
                    callback: async (obj, updateValue) => {
                        const value = obj.dyreholdsId || '';
                        if (value && value.includes('-')) {
                            const parts = value.split('-');
                            if (parts.length === 2) {
                                const markOfOrigin = parts[0].trim();
                                const earMark = parts[1].trim();
                                // Update animal name with species-aware combined ID
                                updateValue({
                                    name: 'name',
                                    value: buildLivestockId({ ...obj, markOfOrigin, earMark }),
                                    id: obj.id,
                                });
                                if (markOfOrigin) {
                                    updateValue({
                                        name: 'markOfOrigin',
                                        value: markOfOrigin,
                                        id: obj.id,
                                    });
                                }
                                if (earMark) {
                                    updateValue({
                                        name: 'earMark',
                                        value: earMark,
                                        id: obj.id,
                                    });
                                }
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.markOfOrigin'>markOfOrigin</Text>,
                    placeholder: <Text id='input.markOfOrigin'>markOfOrigin</Text>,
                    help: <Text id='input.markOfOrigin-help'>Mark of origin is always the longest number. Usually 7-8 digits.</Text>,
                    name: 'markOfOrigin',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.markOfOrigin,
                    showInList: false,
                    isAdminField: false,
                    callback: async (obj, updateValue) => {
                        const markOfOrigin = obj.markOfOrigin || '';
                        const earMark = obj.earMark || '';
                        if (markOfOrigin || earMark) {
                            updateValue({
                                name: 'name',
                                value: buildLivestockId(obj),
                                id: obj.id,
                            });
                        }
                    },
                },
                {
                    title: <Text id='input.earMark'>earMark</Text>,
                    placeholder: <Text id='input.earMark'>earMark</Text>,
                    help: <Text id='input.earMark-help'>Ear mark is always the shortest number. Usually 4-5 digits.</Text>,
                    name: 'earMark',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.earMark,
                    showInList: false,
                    isAdminField: false,
                    callback: async (obj, updateValue) => {
                        const markOfOrigin = obj.markOfOrigin || '';
                        const earMark = obj.earMark || '';
                        if (markOfOrigin || earMark) {
                            updateValue({
                                name: 'name',
                                value: buildLivestockId(obj),
                                id: obj.id,
                            });
                        }
                    },
                },
                {
                    title: <Text id='input.visitorNumber'>visitorNumber</Text>,
                    placeholder: <Text id='input.visitorNumber'>visitorNumber</Text>,
                    name: 'visitorNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitorNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.importantInfo'>Important Info</Text>,
                    placeholder: <Text id='input.importantInfo'>Important Info</Text>,
                    name: 'importantInfo',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.importantInfo,
                    showInList: true,
                },
                {
                    title: <Text id='input.cave'>cave</Text>,
                    placeholder: <Text id='input.cave'>cave</Text>,
                    name: 'cave',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cave,
                    showInList: true,
                },
                {
                    title: <Text id='input.color'>Color</Text>,
                    placeholder: <Text id='input.color'>Color</Text>,
                    help: <Text id='input.color-animal-help'>The animal's fur color. This is reported to DyreID, so keep it as a written color.</Text>,
                    name: 'color',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.color,
                    showInList: false
                },
                {
                    title: <Text id='input.colorTag'>Marker color</Text>,
                    placeholder: <Text id='input.colorTag'>Marker color</Text>,
                    help: <Text id='input.colorTag-help'>Highlights the animal with your own color in lists and on the animal card. Does not affect DyreID.</Text>,
                    name: 'colorTag',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-palette',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.colorTag,
                    showInList: false
                },
                {
                    title: <Text id='input.isAggressive'>Animal is aggressive</Text>,
                    placeholder: <Text id='input.isAggressive'>Animal is aggressive</Text>,
                    help: <Text id='input.isAggressive-help'>Shows a red staff warning (CAVE) on the animal card in journals and bookings.</Text>,
                    name: 'isAggressive',
                    type: 'toggle',
                    icon: 'fa-duotone fa-bell-slash',
                    getValue: obj => obj?.isAggressive ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isGroup'>Group of animals</Text>,
                    placeholder: <Text id='input.isGroup'>Group of animals</Text>,
                    help: <Text id='input.isGroup-help'>The record is a flock/group of production animals, not one individual. Animalia visits are reported with flock description and count instead of an individual ID.</Text>,
                    name: 'isGroup',
                    type: 'toggle',
                    icon: 'fa-duotone fa-info-circle',
                    getValue: obj => obj?.isGroup ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.groupSize'>Group size</Text>,
                    placeholder: <Text id='input.groupSize'>Group size</Text>,
                    name: 'groupSize',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.groupSize,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.comment'>comment</Text>,
                    placeholder: <Text id='input.comment'>comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                },
                {
                    title: <Text id='input.commentInternal'>comment Internal</Text>,
                    placeholder: <Text id='input.commentInternal'>comment Internal</Text>,
                    name: 'commentInternal',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.commentInternal,
                },
                {
                    title: <Text id='input.gender'>Gender</Text>,
                    placeholder: <Text id='input.gender'>Gender</Text>,
                    name: 'gender',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gender,
                    datalist: [['Hann', 'Hann'], ['Hunn', 'Hunn'], ['Kastrert hann', 'Kastrert hann'], ['Kastrert hunn', 'Kastrert hunn'], ['Hoppe', 'Hoppe'], ['Hingst', 'Hingst'], ['Vallak', 'Vallak']],
                    showInList: true,
                },
                {
                    title: <Text id='input.species'>Species</Text>,
                    placeholder: <Text id='input.species'>Species</Text>,
                    name: 'speciesId',
                    type: 'selectAdvanced',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.speciesId,
                    datalistName: 'species',
                    datalistFieldKeyName: 'speciesId',
                    datalistFieldKey: e => e?.speciesId,
                    datalistFieldVal: e => `${e?.name} ${e?.isFromDyreid ? `(DyreID)` : ''}`,
                    showInList: true,
                    callback: async (obj, updateValue, holdingStore) => {
                        await nrrRegistrationNumberHook(obj, updateValue, holdingStore);
                        // console.log('speciesId callback', obj);
                        if (obj?.speciesId) {
                            holdingStore.loadBreeds(obj.speciesId);
                        }
                    },
                },
                {
                    title: <Text id='input.breed'>Breed</Text>,
                    placeholder: <Text id='input.breed'>Breed</Text>,
                    name: 'breedId',
                    type: 'selectAdvanced',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.breedId,
                    datalistName: 'breeds',
                    datalistFieldKeyName: 'breedId',
                    datalistFieldKey: e => e.breedId,
                    datalistFieldVal: e => `${e?.name}  ${e?.isFromDyreid ? `(DyreID)` : ''}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.species'>Species</Text>,
                    placeholder: <Text id='input.species'>Species</Text>,
                    name: 'species',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.species,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.breed'>Breed</Text>,
                    placeholder: <Text id='input.breed'>Breed</Text>,
                    name: 'breed',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.breed,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.passportId'>passportId</Text>,
                    placeholder: <Text id='input.passportId'>passportId</Text>,
                    name: 'passportId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.passportId,
                    showInList: true,
                },
                {
                    title: <Text id='input.nkkId'>nkkId</Text>,
                    placeholder: <Text id='input.nkkId'>nkkId</Text>,
                    name: 'nkkId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.nkkId,
                    showInList: true,
                },
                {
                    title: <Text id='input.nrrRegistrationNumber'>NRR registration number</Text>,
                    placeholder: <Text id='input.nrrRegistrationNumber'>NRR registration number</Text>,
                    help: <Text id='input.nrrRegistrationNumber-help'>Registration number in NRR (Min Katt). Use "Fetch from NRR" to fill it from the chip number.</Text>,
                    name: 'nrrRegistrationNumber',
                    type: 'text',
                    icon: 'fa-solid fa-cat',
                    getValue: obj => obj.nrrRegistrationNumber,
                    showInList: true,
                    callback: nrrRegistrationNumberHook,
                },
                {
                    title: <Text id='input.ems'>EMS code</Text>,
                    placeholder: <Text id='input.ems-placeholder'>NFO n 09</Text>,
                    help: <Text id='input.ems-help'>EMS code from NRR: breed code and colour.</Text>,
                    name: 'ems',
                    type: 'text',
                    icon: 'fa-solid fa-cat',
                    getValue: obj => obj.ems,
                    showInList: true,
                },
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    min: '2000-01-01',
                    max: 'today',
                    type: 'date',
                    hideDateShortcuts: true,
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.age'>Age</Text>,
                    placeholder: <Text id='input.age'>Age</Text>,
                    name: 'age',
                    type: 'text',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => {
                        if (!obj.birth) return null;
                        return animalAge(obj.birth);
                    },
                    displayValue: (val, obj) => {
                        if (!obj.birth) return null;
                        return animalAge(obj.birth);
                    },
                    showInList: true,
                    hideOnCreate: false,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.dececed'>Dececed</Text>,
                    placeholder: <Text id='input.dececed'>Dececed</Text>,
                    name: 'dececed',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.dececed,
                    showInList: true,
                    hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isOkDyreId'>isOkDyreId</Text>,
                    placeholder: <Text id='input.isOkDyreId'>isOkDyreId</Text>,
                    help: <Text id='input.isOkDyreId-help'>Marks that the animal is OK in DyreID. Status flag only - it does not affect DyreID reporting.</Text>,
                    name: 'isOkDyreId',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isOkDyreId ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.chipIdDate'>chipIdDate</Text>,
                    placeholder: <Text id='input.chipIdDate'>chipIdDate</Text>,
                    name: 'chipIdDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    min: '2000-01-01',
                    max: 'today',
                    getValue: obj => obj.chipIdDate,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.birth && obj.chipIdDate && new Date(obj.birth) > new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipIdDate', 'Date can not be before birth date.');
                        } else if (new Date() < new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipIdDate', 'Date can not be in the future.');
                        } else {
                            holdingStore.setFeedback('chipIdDate', '');
                        }
                    },
                },
                // {
                //     title: <Text id='input.chipIdBarcode'>ChipID</Text>,
                //     placeholder: <Text id='input.chipIdBarcode'>ChipID</Text>,
                //     name: 'chipIdBarcode',
                //     type: 'text',
                //     icon: 'fa-solid fa-barcode',
                //     getValue: obj => obj.chipId || null,
                //     showBarcode: true,
                //     getBarcodeValue: obj => `${obj.chipId}`,
                //     isReadOnly: true,
                //     showInList: true,
                //     hideOnCreate: true,
                // },
                {
                    title: <Text id='input.chipId'>ChipId</Text>,
                    placeholder: <Text id='input.chipId'>ChipId</Text>,
                    name: 'chipId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.chipId,
                    showInList: true,
                    // dyreIdPetId
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.chipId && !/^\d{15}$/.test(obj.chipId)) {
                            holdingStore.setFeedbackError('chipId', 'Invalid chipId. Must be 15 digits.');
                        } else {
                            holdingStore.setFeedbackError('chipId', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('chipId', '');
                        holdingStore.setFeedbackButton('chipId', null, null);
                    },
                    loadCallback: async (obj, updateValue, holdingStore) => {
                        // console.log('loadCallback', obj, holdingStore.name);
                        holdingStore.setFeedback('chipId', '');
                        holdingStore.setFeedbackButton('chipId', null, null);
                        // This is beeing called when the object is loaded after component is mounted.
                        //
                        // console.log('loadCallback', obj, updateValue, holdingStore.name);
                        await nrrRegistrationNumberHook(obj, updateValue, holdingStore);
                        if (!holdingStore.dyreIdApproved) return;
                        if (!obj.id) {
                            holdingStore.setFeedback('chipId', 'You need to save the animal before you can register it with DyreID.');
                            return;
                        }
                        if (!obj.speciesId || !obj.breedId) {
                            holdingStore.setFeedback('chipId', 'You need to add species and breed before you can register this animal with DyreID.');
                        }
                        if (!obj.gender) {
                            holdingStore.setFeedback('chipId', 'You need to add gender before you can register this animal with DyreID.');
                        }
                        if (!obj.birth) {
                            holdingStore.setFeedback('chipId', 'You need to add birth date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipIdDate) {
                            holdingStore.setFeedback('chipId', 'You need to add chip date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipId) {
                            holdingStore.setFeedback('chipId', 'You need to add a chip number (15 digits) before you can register this animal with DyreID.');
                        }
                        if (obj.birth && obj.chipIdDate && new Date(obj.birth) > new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipId', 'Chip date can not be before birth date.');
                            return;
                        }
                        if (!obj.chipId || !obj.speciesId || !obj.breedId || !obj.birth || !obj.chipIdDate) return;
                        // Must be a number and 15 digits
                        if (!/^\d{15}$/.test(obj.chipId)) {
                            holdingStore.setFeedback('chipId', 'Invalid chipId. Has to be 15 digits.');
                        } else if (obj.dyreIdPetId && obj.chipId === obj.dyreIdPetChipId) {
                            console.log('obj.dyreIdPetId', obj.dyreIdPetId);
                            console.log('obj.dyreIdPetChipId', obj.dyreIdPetChipId);
                            holdingStore.setFeedback('chipId', 'DyreID: ChipId is already registered with DyreID.');
                        } else {
                            holdingStore.setFeedback('chipId', 'Checking with DyreID...');
                            const response = await holdingStore.dyreIdChipValidationStatus(obj.chipId);
                            if (response?.data?.status) {
                                switch (response.data.status) {
                                    case 1:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is already another pet. Is the number you are registering correct?');
                                        holdingStore.setFeedbackButtonInfo('chipId', 'Customer has been informed that data is shared with DyreID.')
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                            holdingStore.setFeedbackButtonInfo('chipId', '');
                                        }, 'Send to DyreId');
                                        break;
                                    case 2:
                                        holdingStore.setFeedback('chipId', 'DyreID: Valid chip & available for new registration.');
                                        holdingStore.setFeedbackButtonInfo('chipId', 'Customer has been informed that data is shared with DyreID.')
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                            holdingStore.setFeedbackButtonInfo('chipId', '');
                                        }, 'Send to DyreId');
                                        break;
                                    case 3:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is not in database, but you can still register it with DyreId');
                                        holdingStore.setFeedbackButtonInfo('chipId', 'Customer has been informed that data is shared with DyreID.')
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                            holdingStore.setFeedbackButtonInfo('chipId', '');
                                        }, 'Send to DyreId');
                                        break;
                                    default:
                                        holdingStore.setFeedback('chipId', 'DyreID: Unknown status from DyreId.');
                                        break;
                                }
                            }
                            // console.log('response', response);
                        }
                    },
                    callback: async (obj, updateValue, holdingStore) => {
                        // console.log('callback', holdingStore.name, holdingStore.dyreIdApproved, obj.speciesId, obj.breedId, obj.birth, obj.chipId, obj.dyreIdPetId);
                        await nrrRegistrationNumberHook(obj, updateValue, holdingStore);
                        if (!holdingStore.dyreIdApproved) return;
                        if (!obj.id) {
                            holdingStore.setFeedback('chipId', 'You need to save the animal before you can register it with DyreID.');
                            return;
                        }
                        if (!obj.speciesId || !obj.breedId) {
                            holdingStore.setFeedback('chipId', 'You need to add species and breed before you can register this animal with DyreID.');
                        }
                        if (!obj.gender) {
                            holdingStore.setFeedback('chipId', 'You need to add gender before you can register this animal with DyreID.');
                        }
                        if (!obj.birth) {
                            holdingStore.setFeedback('chipId', 'You need to add birth date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipIdDate) {
                            holdingStore.setFeedback('chipId', 'You need to add chip date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipId) {
                            holdingStore.setFeedback('chipId', 'You need to add a chip number (15 digits) before you can register this animal with DyreID.');
                        }
                        if (obj.birth && obj.chipIdDate && new Date(obj.birth) > new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipId', 'Chip date can not be before birth date.');
                            return;
                        }
                        if (!obj.chipId || !obj.speciesId || !obj.breedId || !obj.birth || !obj.chipIdDate) return;
                        // Must be a number and 15 digits
                        if (!/^\d{15}$/.test(obj.chipId)) {
                            holdingStore.setFeedback('chipId', 'Invalid chipId. Has to be 15 digits.');
                        } else if (obj.dyreIdPetId && obj.chipId === obj.dyreIdPetChipId) {
                            console.log('obj.dyreIdPetId', obj.dyreIdPetId);
                            console.log('obj.dyreIdPetChipId', obj.dyreIdPetChipId);
                            holdingStore.setFeedback('chipId', 'DyreID: ChipId is already registered with DyreId.');
                        } else {
                            holdingStore.setFeedback('chipId', 'Checking with DyreID...');
                            const response = await holdingStore.dyreIdChipValidationStatus(obj.chipId);
                            if (response?.data?.status) {
                                switch (response.data.status) {
                                    case 1:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is already another pet. Is the number you are registering correct?');
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                        }, 'Send to DyreId');
                                        break;
                                    case 2:
                                        holdingStore.setFeedback('chipId', 'DyreID: Valid chip & available for new registration.');
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                        }, 'Send to DyreId');
                                        break;
                                    case 3:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is not in database, but you can still register it with DyreId');
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                        }, 'Send to DyreId');
                                        break;
                                    default:
                                        holdingStore.setFeedback('chipId', 'DyreID: Unknown status from DyreId.');
                                        break;
                                }
                            }
                            // console.log('response', response);
                            // holdingStore.setFeedback('chipId', JSON.stringify(response));
                        }
                    },
                },
                {
                    title: <Text id='input.chipIdChecked'>ChipId Checked</Text>,
                    placeholder: <Text id='input.chipIdChecked'>ChipId Checked</Text>,
                    name: 'chipIdChecked',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.chipIdChecked,
                    min: '2010-01-01',
                    max: 'today',
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.isDececed'>isDececed</Text>,
                    placeholder: <Text id='input.isDececed'>isDececed</Text>,
                    help: <Text id='input.isDececed-help'>The animal is deceased: hidden from active lists and reminders, future appointments are removed, and it is marked as dead in DyreID.</Text>,
                    name: 'isDececed',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isDececed ? 1 : 0,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isRelocated'>isRelocated</Text>,
                    placeholder: <Text id='input.isRelocated'>isRelocated</Text>,
                    help: <Text id='input.isRelocated-help'>The animal has moved to a new owner or clinic - hidden from active animal pickers, shown with a blue badge, and gets no recalls or booking reminders.</Text>,
                    name: 'isRelocated',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isRelocated ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.animal-isInactive'>Inactive</Text>,
                    placeholder: <Text id='input.animal-isInactive'>Inactive</Text>,
                    help: <Text id='input.animal-isInactive-help'>The animal is no longer a patient - hidden from the animal pickers, shown with a grey badge, and gets no recalls or booking reminders. Also set on all animals when the owner is marked inactive.</Text>,
                    name: 'isInactive',
                    type: 'toggle',
                    icon: 'fa-duotone fa-ban',
                    getValue: obj => obj.isInactive ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isLost'>Reported lost</Text>,
                    placeholder: <Text id='input.isLost'>Reported lost</Text>,
                    help: <Text id='input.isLost-help'>Animal is reported lost by its owner (e.g. registered as lost in DyreID).</Text>,
                    name: 'isLost',
                    type: 'toggle',
                    icon: 'fa-duotone fa-magnifying-glass-location',
                    getValue: obj => obj.isLost ? 1 : 0,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isOverweight'>isOverweight</Text>,
                    placeholder: <Text id='input.isOverweight'>isOverweight</Text>,
                    help: <Text id='input.isOverweight-help'>Clinical status flag: the animal is overweight. Informational only.</Text>,
                    name: 'isOverweight',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isOverweight ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isSterialized'>isSterialized</Text>,
                    placeholder: <Text id='input.isSterialized'>isSterialized</Text>,
                    help: <Text id='input.isSterialized-help'>Status flag: the animal is sterilized. Informational only.</Text>,
                    name: 'isSterialized',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSterialized ? 1 : 0,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.foodType'>food Type</Text>,
                    placeholder: <Text id='input.foodType'>food Type</Text>,
                    name: 'foodType',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.foodType,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animal-admission-title'>Animal admission</Text>,
            key: 'Animal admission',
            fields: [
                {
                    title: <Text id='input.isAdmittedToClinic'>isAdmittedToClinic</Text>,
                    placeholder: <Text id='input.isAdmittedToClinic'>isAdmittedToClinic</Text>,
                    help: <Text id='input.isAdmittedToClinic-help'>The animal is admitted to the clinic (inpatient). Shows in the admitted-animals list until it leaves the clinic.</Text>,
                    name: 'isAdmittedToClinic',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isAdmittedToClinic ? 1 : 0,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animal-insurance-title'>Animal insurance</Text>,
            key: 'Animal insurance',
            fields: [
                {
                    title: <Text id='input.insuranceId'>InsuranceId</Text>,
                    placeholder: <Text id='input.insuranceId'>InsuranceId</Text>,
                    name: 'insuranceId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.insuranceId,
                    showInList: true,
                },
                {
                    title: <Text id='input.insuranceCompany'>InsuranceCompany</Text>,
                    placeholder: <Text id='input.insuranceCompany'>InsuranceCompany</Text>,
                    name: 'insuranceCompany',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.insuranceCompany,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animal-weight-title'>Animal weight</Text>,
            key: 'Animal weight',
            fields: [
                {
                    title: <Text id='input.weight'>weight</Text>,
                    placeholder: <Text id='input.weight'>weight</Text>,
                    name: 'weight',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.weight,
                    showInList: true,
                },
                {
                    title: <Text id='input.allWeights'>allWeights</Text>,
                    placeholder: <Text id='input.allWeights'>allWeights</Text>,
                    name: 'allWeights',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.allWeights,
                    datalist: [
                        'date', 'weight',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>date:</span> {util.isoDate(obj.date)}<br />
                            <span class='font-weight-lighter'>weight:</span> {obj.weight} kg<br />
                        </div>
                    </>,
                    showInList: false,
                    displayHeight: '60px',
                    displayWidth: '200px',
                },
            ],
        },
        {
            name: <Text id='input.animal-media-title'>Animal media</Text>,
            key: 'Animal media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
        {
            name: <Text id='input.animal-advanced-title'>Animal advanced</Text>,
            key: 'Animal advanced',
            fields: [
                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: obj => obj.members,
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
    ],
    animalSpecie: [
        {
            name: <Text id='input.animalSpecie-basics-title'>animalSpecie basics</Text>,
            key: 'animalSpecie basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.speciesId'>speciesId</Text>,
                    placeholder: <Text id='input.speciesId'>speciesId</Text>,
                    name: 'speciesId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.speciesId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.speciesId'>speciesId</Text>,
                    placeholder: <Text id='input.speciesId'>speciesId</Text>,
                    name: 'speciesId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.speciesId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.shortName'>shortName</Text>,
                    placeholder: <Text id='input.shortName'>shortName</Text>,
                    name: 'shortName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.shortName,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdNkk'>speciesIdNkk</Text>,
                    placeholder: <Text id='input.speciesIdNkk'>speciesIdNkk</Text>,
                    name: 'speciesIdNkk',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdNkk,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdMats'>speciesIdMats</Text>,
                    placeholder: <Text id='input.speciesIdMats'>speciesIdMats</Text>,
                    name: 'speciesIdMats',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdMats,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdDyreid'>speciesIdDyreid</Text>,
                    placeholder: <Text id='input.speciesIdDyreid'>speciesIdDyreid</Text>,
                    name: 'speciesIdDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdDyreid,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdDhp'>speciesIdDhp</Text>,
                    placeholder: <Text id='input.speciesIdDhp'>speciesIdDhp</Text>,
                    name: 'speciesIdDhp',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdDhp,
                    showInList: true,
                },
                {
                    title: <Text id='input.idexxCode'>IDEXX code</Text>,
                    placeholder: <Text id='input.idexxCode-placeholder'>e.g. CANINE, FELINE, EQUINE</Text>,
                    help: <Text id='input.idexxCode-species-help'>Hard-linked IDEXX species code from /api/v1/ref/species. Leave blank for species not used with IDEXX.</Text>,
                    name: 'idexxCode',
                    type: 'text',
                    icon: 'fa-duotone fa-flask-vial',
                    getValue: obj => obj.idexxCode || '',
                    showInList: true,
                },
                {
                    title: <Text id='input.isFromDyreid'>isFromDyreid</Text>,
                    placeholder: <Text id='input.isFromDyreid'>isFromDyreid</Text>,
                    name: 'isFromDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isFromDyreid,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animalSpecie-media-title'>animalSpecie media</Text>,
            key: 'animalSpecie media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
    ],
    animalBreed: [
        {
            name: <Text id='input.animalBreed-basics-title'>animalBreed basics</Text>,
            key: 'animalBreed basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.speciesId'>speciesId</Text>,
                    placeholder: <Text id='input.speciesId'>speciesId</Text>,
                    name: 'speciesId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.speciesId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.breedId'>breedId</Text>,
                    placeholder: <Text id='input.breedId'>breedId</Text>,
                    name: 'breedId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.breedId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdNkk'>speciesIdNkk</Text>,
                    placeholder: <Text id='input.speciesIdNkk'>speciesIdNkk</Text>,
                    name: 'speciesIdNkk',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdNkk,
                    showInList: true,
                },
                {
                    title: <Text id='input.breedIdNkk'>breedIdNkk</Text>,
                    placeholder: <Text id='input.breedIdNkk'>breedIdNkk</Text>,
                    name: 'breedIdNkk',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.breedIdNkk,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdDyreid'>speciesIdDyreid</Text>,
                    placeholder: <Text id='input.speciesIdDyreid'>speciesIdDyreid</Text>,
                    name: 'speciesIdDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdDyreid,
                    showInList: true,
                },
                {
                    title: <Text id='input.breedIdDyreid'>breedIdDyreid</Text>,
                    placeholder: <Text id='input.breedIdDyreid'>breedIdDyreid</Text>,
                    name: 'breedIdDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.breedIdDyreid,
                    showInList: true,
                },
                {
                    title: <Text id='input.breedIdOther'>breedIdOther</Text>,
                    placeholder: <Text id='input.breedIdOther'>breedIdOther</Text>,
                    name: 'breedIdOther',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.breedIdOther,
                    showInList: true,
                },
                {
                    title: <Text id='input.idexxCode'>IDEXX code</Text>,
                    placeholder: <Text id='input.idexxCode-placeholder'>e.g. GOLDEN_RETRIEVER, MIXED_BREED_CANINE</Text>,
                    help: <Text id='input.idexxCode-breed-help'>Hard-linked IDEXX breed code from /api/v1/ref/breeds (filter by the matching speciesCode). Leave blank for breeds not used with IDEXX.</Text>,
                    name: 'idexxCode',
                    type: 'text',
                    icon: 'fa-duotone fa-flask-vial',
                    getValue: obj => obj.idexxCode || '',
                    showInList: true,
                },
                {
                    title: <Text id='input.isFromDyreid'>isFromDyreid</Text>,
                    placeholder: <Text id='input.isFromDyreid'>isFromDyreid</Text>,
                    name: 'isFromDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isFromDyreid,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animalBreed-media-title'>animalBreed media</Text>,
            key: 'animalBreed media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
    ],
    visitor: [
        {
            name: <Text id='input.visitor-search-title'>Visitor search</Text>,
            key: 'Visitor search',
            fields: [
                {
                    title: <Text id='input.cellphone-lookup'>Cellphone lookup</Text>,
                    placeholder: <Text id='input.cellphone-lookup'>Cellphone lookup</Text>,
                    help: <Text id='input.cellphone-lookup-help'>There is a SMS cost for each cellphone lookup.</Text>,
                    name: 'cellphoneLookup',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphoneLookup,
                    callback: async (obj, updateValue) => {
                        // console.log('obj.cellphone', obj?.cellphone);
                        if (!obj.id && obj.cellphoneLookup && obj.cellphoneLookup.length >= 8) {
                            const response = await cellphoneLookup(obj.cellphoneLookup);
                            console.log('response', response);
                            if (response && response?.firstName) {
                                // accountId: 0
                                // age: 52
                                // city: "Nord-Torpa"
                                // created: "2025-01-15T14:53:46.6059858+00:00"
                                // dateOfBirth: "1972-10-31"
                                // firstName: "Øistein"
                                // gender: "M"
                                // lastName: "Sørensen"
                                // msisdn: "+4791520191"
                                // streetName: "Gamlebyvegen"
                                // streetNumber: "85"
                                // success: false
                                // zipCode: "2880"
                                updateValue({ name: 'cellphone', value: response?.msisdn });
                                updateValue({ name: 'firstname', value: response?.firstName });
                                updateValue({ name: 'lastname', value: response?.lastName });
                                updateValue({ name: 'gender', value: response?.gender });
                                updateValue({ name: 'birth', value: response?.dateOfBirth });
                                // Parts of the street address are often missing (companies
                                // rarely have one), so only write it when there is one.
                                const address = formatLookupAddress(response);
                                if (address) {
                                    updateValue({ name: 'address', value: address });
                                }
                                updateValue({ name: 'postalcode', value: response?.zipCode });
                                updateValue({ name: 'place', value: response?.city });
                            }

                            // if (response) {
                            //     const place = response[0]?.poststed;
                            //     updateValue({
                            //         name: 'postalplace',
                            //         value: place,
                            //         id: obj.id,
                            //     });
                            // }
                        }
                    },
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.visitor-important-title'>Visitor important</Text>,
            key: 'Visitor important',
            isCollapsed: false,
            backgroundColor: 'var(--bs-info-bg-subtle) !important',
            borderColor: 'var(--bs-info-border-subtle) !important',
            fields: [
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.firstname,
                    showInList: true,
                    isRequired: true,
                    isRecommended: true,
                    onPaste: (e, obj, updateValue, isNew) => {
                        const pastedText = e.clipboardData.getData('text');

                        // Helper to clean whitespace: trim, replace tabs with space, collapse multiple spaces
                        const cleanText = (text) => text
                            .replace(/\t/g, ' ')
                            .replace(/\s+/g, ' ')
                            .trim();

                        // For single line paste, just clean whitespace
                        if (!pastedText.includes('\n')) {
                            const cleaned = cleanText(pastedText);
                            if (cleaned !== pastedText) {
                                e.preventDefault();
                                updateValue({ name: 'firstname', value: cleaned });
                            }
                            return;
                        }

                        // Only parse multi-line paste for new visitors
                        if (!isNew) return;

                        e.preventDefault();
                        const lines = pastedText.split('\n').map(l => cleanText(l)).filter(Boolean);
                        if (lines.length === 0) return;

                        // Parse name from first line (e.g., "Firstname Lastname")
                        const nameParts = lines[0].split(/\s+/);
                        updateValue({ name: 'firstname', value: nameParts[0] || '' });
                        if (nameParts.length > 1) {
                            updateValue({ name: 'lastname', value: nameParts.slice(1).join(' ') });
                        }

                        // Parse address (line 2)
                        if (lines[1]) {
                            updateValue({ name: 'address', value: lines[1] });
                        }

                        // Parse postalcode + place (line 3: "1234 City" or "1234, City")
                        if (lines[2]) {
                            const postalMatch = lines[2].match(/^(\d{4})\s*,?\s*(.+)$/);
                            if (postalMatch) {
                                updateValue({ name: 'postalcode', value: postalMatch[1] });
                                updateValue({ name: 'place', value: postalMatch[2].trim() });
                            } else {
                                // Maybe it's just a place or something else
                                updateValue({ name: 'place', value: lines[2] });
                            }
                        }

                        // Detect cellphone number in any line (Norwegian format: 8+ digits, optionally starting with +)
                        for (const line of lines) {
                            // Match phone patterns: +47 123 45 678, 12345678, +4712345678, etc.
                            const phoneMatch = line.match(/(\+?\d[\d\s-]{7,})/);
                            if (phoneMatch) {
                                const phone = phoneMatch[1].replace(/[\s-]/g, '');
                                if (phone.length >= 8) {
                                    updateValue({ name: 'cellphone', value: phone });
                                    break;
                                }
                            }
                        }

                        // Detect email in any line
                        for (const line of lines) {
                            const emailMatch = line.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
                            if (emailMatch) {
                                updateValue({ name: 'email', value: emailMatch[1] });
                                break;
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.lastname,
                    showInList: true,
                    isRequired: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.email,
                    showInList: true,
                    isRecommended: true,
                    checkValueOnLoad: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.email2'>Email 2</Text>,
                    placeholder: <Text id='input.email2'>Email 2</Text>,
                    name: 'email2',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.email2,
                    showInList: true,
                    checkValueOnLoad: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email2 && !util.validateEmail(obj.email2)) {
                            const invalidChars = findInvalidEmailChars(obj.email2);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email2', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email2', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email2', '');
                        holdingStore.setFeedbackButton('email2', null, null);
                    },
                },
                {
                    title: <Text id='input.invoiceEmail'>Invoice email</Text>,
                    placeholder: <Text id='input.invoiceEmail'>Invoice email</Text>,
                    help: <Text id='input.invoiceEmail-help'>Invoices and payment reminders are sent to this address when set. Otherwise the regular email is used.</Text>,
                    name: 'invoiceEmail',
                    type: 'text',
                    icon: 'fa-solid fa-file-invoice',
                    getValue: obj => obj.invoiceEmail,
                    checkValueOnLoad: true,
                    checkValue: (obj, holdingStore) => {
                        if (obj.invoiceEmail && !util.validateEmail(obj.invoiceEmail)) {
                            const invalidChars = findInvalidEmailChars(obj.invoiceEmail);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('invoiceEmail', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('invoiceEmail', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        holdingStore.setFeedback('invoiceEmail', '');
                        holdingStore.setFeedbackButton('invoiceEmail', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.address,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalcode,
                    showInList: true,
                    isRecommended: true,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalcode',
                                    value: obj.postalcode,
                                    id: obj.id,
                                });
                                updateValue({
                                    name: 'place',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.place'>Place</Text>,
                    placeholder: <Text id='input.place'>Place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.place,
                    showInList: true,
                    isRecommended: true,
                },

            ],
        },
        {
            name: <Text id='input.visitor-basics-title'>Visitor basics</Text>,
            key: 'Visitor basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    help: <Text id='input.id-help'>This is the primary ID for this object. Do not change this!</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    help: <Text id='input.uuidv4-help'>This is the uuidv4 for this object. Do not change this!</Text>,
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    help: <Text id='input.user-help'>This is the user.id for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    help: <Text id='input.customer-help'>This is the customer.id for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.visitorNumber'>VisitorNumber</Text>,
                    placeholder: <Text id='input.visitorNumber'>VisitorNumber</Text>,
                    name: 'visitorNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitorNumber,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.color'>Color</Text>,
                    placeholder: <Text id='input.color'>Color</Text>,
                    name: 'color',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-input-text',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.color,
                    showInList: false
                },
                {
                    title: <Text id='input.preferredVeterinary'>Preferred veterinarian</Text>,
                    placeholder: <Text id='input.preferredVeterinary'>Preferred veterinarian</Text>,
                    help: <Text id='input.preferredVeterinary-help'>If the customer wants a specific veterinarian, choose them here. It is shown on the customer card.</Text>,
                    name: 'preferredVeterinary',
                    type: 'select',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj?.preferredVeterinary,
                    datalistName: 'veterinaries',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: false,
                },
                {
                    title: <Text id='input.orgNumber'>orgNumber</Text>,
                    placeholder: <Text id='input.orgNumber'>orgNumber</Text>,
                    name: 'orgNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.orgNumber || null,
                    showInList: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        if (obj.orgNumber && !extractOrgNumber(obj.orgNumber)) {
                            holdingStore.setFeedbackError('orgNumber', 'Invalid orgNumber. Must contain 9 digits. Extra text such as "MVA" is allowed.');
                        } else {
                            holdingStore.setFeedbackError('orgNumber', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        holdingStore.setFeedback('orgNumber', '');
                        holdingStore.setFeedbackButton('orgNumber', null, null);
                    },
                    loadCallback: async (obj, updateValue, holdingStore) => {
                        await runBrregLookup({
                            field: 'orgNumber',
                            obj,
                            updateValue,
                            holdingStore,
                            buildTargetMap: (data) => buildBrregMapForPrimary(data, { phoneField: 'cellphone' }),
                            calledFrom: 'visitor:orgNumber',
                        });
                    },
                    callback: async (obj, updateValue, holdingStore) => {
                        await runBrregLookup({
                            field: 'orgNumber',
                            obj,
                            updateValue,
                            holdingStore,
                            buildTargetMap: (data) => buildBrregMapForPrimary(data, { phoneField: 'cellphone' }),
                            calledFrom: 'visitor:orgNumber',
                        });
                    },
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    // isAdminField: true,
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.firstname,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.lastname,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.producerNumber'>producerNumber</Text>,
                    placeholder: <Text id='input.producerNumber'>producerNumber</Text>,
                    name: 'producerNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.producerNumber,
                },
                {
                    title: <Text id='input.defaultMarkOfOrigin'>Default Mark of Origin (MT-nummer)</Text>,
                    placeholder: <Text id='input.defaultMarkOfOrigin-placeholder'>e.g. 2499816</Text>,
                    help: <Text id='input.defaultMarkOfOrigin-help'>The flock's MT-number. Auto-fills as ear number prefix when adding new animals.</Text>,
                    name: 'defaultMarkOfOrigin',
                    type: 'number',
                    icon: 'fa-duotone fa-ear',
                    getValue: obj => obj.defaultMarkOfOrigin || '',
                },
                {
                    title: <Text id='input.productionCategory'>Production Category</Text>,
                    placeholder: <Text id='input.productionCategory'>Production Category</Text>,
                    name: 'productionCategory',
                    type: 'select',
                    datalist: [['ANNEN', 'Annen'], ['KJOETT', 'Kjøtt'], ['MELK', 'Melk'], ['EGG', 'Egg']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.productionCategory,
                    showInList: true,
                },
                {
                    title: 'Prod.kategori Storfehold',
                    name: 'productionCategoryStorfehold',
                    type: 'select',
                    datalist: [['', '-'], ['ANNEN', 'Annen'], ['KJOETT', 'Kjøtt'], ['MELK', 'Melk']],
                    icon: 'fa-solid fa-cow',
                    getValue: obj => obj.productionCategoryStorfehold,
                },
                {
                    title: 'Prod.kategori Svinehold',
                    name: 'productionCategorySvinehold',
                    type: 'select',
                    datalist: [['', '-'], ['ANNEN', 'Annen'], ['KJOETT', 'Kjøtt']],
                    icon: 'fa-solid fa-pig',
                    getValue: obj => obj.productionCategorySvinehold,
                },
                {
                    title: 'Prod.kategori Fjørfe Kalkun',
                    name: 'productionCategoryFjoerfeKalkun',
                    type: 'select',
                    datalist: [['', '-'], ['ANNEN', 'Annen'], ['KJOETT', 'Kjøtt']],
                    icon: 'fa-solid fa-turkey',
                    getValue: obj => obj.productionCategoryFjoerfeKalkun,
                },
                {
                    title: 'Prod.kategori Fjørfe Høns',
                    name: 'productionCategoryFjoerfeHoens',
                    type: 'select',
                    datalist: [['', '-'], ['ANNEN', 'Annen'], ['KJOETT', 'Kjøtt'], ['EGG', 'Egg']],
                    icon: 'fa-solid fa-egg',
                    getValue: obj => obj.productionCategoryFjoerfeHoens,
                },
                {
                    title: <Text id='input.invoiceType'>invoiceType</Text>,
                    placeholder: <Text id='input.invoiceType'>invoiceType</Text>,
                    name: 'invoiceType',
                    type: 'select',
                    datalist: [['EHF', 'EHF'], ['EMAIL', 'EMAIL']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.invoiceType,
                },
            ],
        },
        {
            name: <Text id='input.visitor-about'>About</Text>,
            key: 'Visitor about',
            fields: [
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-contact-title'>Visitor contact</Text>,
            key: 'Visitor contact',
            fields: [

                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.email,
                    showInList: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphoneComment'>Cellphone name</Text>,
                    placeholder: <Text id='input.cellphoneComment'>Cellphone name</Text>,
                    name: 'cellphoneComment',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphoneComment,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphone2'>Cellphone 2</Text>,
                    placeholder: <Text id='input.cellphone2'>Cellphone 2</Text>,
                    name: 'cellphone2',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphone2,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphoneComment2'>Cellphone 2 Name</Text>,
                    placeholder: <Text id='input.cellphoneComment2'>Cellphone 2 Name</Text>,
                    name: 'cellphoneComment2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphoneComment2,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphone3'>Cellphone 3</Text>,
                    placeholder: <Text id='input.cellphone3'>Cellphone 3</Text>,
                    name: 'cellphone3',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphone3,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphoneComment3'>Cellphone 3 Name</Text>,
                    placeholder: <Text id='input.cellphoneComment3'>Cellphone 3 Name</Text>,
                    name: 'cellphoneComment3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphoneComment3,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-other-title'>Visitor other</Text>,
            key: 'Visitor other',
            fields: [
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    type: 'date',
                    min: '1900-01-01',
                    hideDateShortcuts: true,
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.socialSecurityNumber'>Social Security Number</Text>,
                    placeholder: <Text id='input.socialSecurityNumber'>Social Security Number</Text>,
                    name: 'socialSecurityNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.socialSecurityNumber,
                },
                {
                    title: <Text id='input.dyreIdOwnerId'>dyreIdOwnerId</Text>,
                    placeholder: <Text id='input.dyreIdOwnerId'>dyreIdOwnerId</Text>,
                    name: 'dyreIdOwnerId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dyreIdOwnerId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
                {
                    title: <Text id='input.visitor-hourlyRate'>Hourly rate</Text>,
                    placeholder: <Text id='input.visitor-hourlyRate-placeholder'>e.g. 1250</Text>,
                    help: <Text id='input.visitor-hourlyRate-help'>Default hourly rate used when invoicing tracked time for this client.</Text>,
                    name: 'hourlyRate',
                    type: 'number',
                    icon: 'fa-duotone fa-coins',
                    getValue: obj => obj.hourlyRate || '',
                },
                {
                    title: <Text id='input.skipBulkSms'>Reserved against SMS and e-mail broadcasts</Text>,
                    placeholder: <Text id='input.skipBulkSms'>Reserved against SMS and e-mail broadcasts</Text>,
                    help: <Text id='input.skipBulkSms-help'>Excluded from "Send SMS / e-post til alle i listen". Booking reminders and one-to-one messages are still sent.</Text>,
                    name: 'skipBulkSms',
                    type: 'toggle',
                    icon: 'fa-duotone fa-comment-sms',
                    getValue: obj => (parseInt(obj.skipBulkSms, 10) === 1 ? 1 : 0),
                    showInList: false,
                },
                {
                    title: <Text id='input.visitor-isInactive'>Inactive</Text>,
                    placeholder: <Text id='input.visitor-isInactive'>Inactive</Text>,
                    help: <Text id='input.visitor-isInactive-help'>The owner is no longer a customer. All their animals are marked inactive too: hidden from the animal pickers and they get no recalls, reminders or SMS/e-mail broadcasts. The owner stays searchable, greyed and listed last. Switching it off re-activates all the animals.</Text>,
                    name: 'isInactive',
                    type: 'toggle',
                    icon: 'fa-duotone fa-user-slash',
                    getValue: obj => (parseInt(obj.isInactive, 10) === 1 ? 1 : 0),
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.visitor-location-title'>Visitor location</Text>,
            key: 'Visitor location',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.address,
                    showInList: true,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalcode,
                    showInList: true,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalcode',
                                    value: obj.postalcode,
                                    id: obj.id,
                                });
                                updateValue({
                                    name: 'place',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.place'>Place</Text>,
                    placeholder: <Text id='input.place'>Place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.place,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.country,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-social-title'>Visitor social</Text>,
            key: 'Visitor social',
            fields: [
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.url,
                    showInList: true,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.facebook,
                    showInList: true,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.instagram,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-media-title'>Visitor media</Text>,
            key: 'Visitor media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-relations-title'>Visitor relations</Text>,
            key: 'Visitor relations',
            fields: [
                {
                    title: <Text id='input.animals'>Animals</Text>,
                    placeholder: <Text id='input.animals'>Animals</Text>,
                    name: 'animals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.animals,
                    datalist: [
                        'id', 'name', 'breed'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: obj => obj.members,
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
    ],
    phoneConsultation: [
        {
            name: <Text id='input.phone-consultation-caller'>Caller</Text>,
            key: 'Phone consultation caller',
            fields: [
                {
                    title: <Text id='phoneConsultation.callerFirstname'>Fornavn</Text>,
                    placeholder: <Text id='phoneConsultation.callerFirstname-placeholder'>Fornavn</Text>,
                    name: 'callerFirstname',
                    type: 'text',
                    icon: 'fa-duotone fa-user',
                    getValue: obj => obj.callerFirstname || '',
                },
                {
                    title: <Text id='phoneConsultation.callerLastname'>Etternavn</Text>,
                    placeholder: <Text id='phoneConsultation.callerLastname-placeholder'>Etternavn</Text>,
                    name: 'callerLastname',
                    type: 'text',
                    icon: 'fa-duotone fa-user',
                    getValue: obj => obj.callerLastname || '',
                },
                {
                    title: <Text id='phoneConsultation.callerCellphone'>Telefon</Text>,
                    placeholder: <Text id='phoneConsultation.callerCellphone-placeholder'>+47 ...</Text>,
                    name: 'callerCellphone',
                    type: 'tel',
                    icon: 'fa-duotone fa-phone',
                    getValue: obj => obj.callerCellphone || '',
                },
            ],
        },
        {
            name: <Text id='input.phone-consultation-call'>Call</Text>,
            key: 'Phone consultation call',
            fields: [
                {
                    title: <Text id='phoneConsultation.date'>Dato/tid</Text>,
                    placeholder: <Text id='phoneConsultation.date'>Dato/tid</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.date || '',
                },
                {
                    title: <Text id='phoneConsultation.chiefComplaint'>Hva gjelder det?</Text>,
                    placeholder: <Text id='phoneConsultation.chiefComplaint-placeholder'>F.eks. nedsatt allmenntilstand, kvalme, halt</Text>,
                    name: 'chiefComplaint',
                    type: 'text',
                    icon: 'fa-duotone fa-comment-question',
                    getValue: obj => obj.chiefComplaint || '',
                },
                {
                    title: <Text id='phoneConsultation.advice'>Råd og informasjon gitt</Text>,
                    placeholder: <Text id='phoneConsultation.advice-placeholder'>Hva ble formidlet til innringer</Text>,
                    name: 'advice',
                    type: 'textarea',
                    rows: 4,
                    icon: 'fa-duotone fa-comment-dots',
                    getValue: obj => obj.advice || '',
                },
                {
                    title: <Text id='phoneConsultation.outcome'>Utfall</Text>,
                    placeholder: <Text id='phoneConsultation.outcome'>Utfall</Text>,
                    name: 'outcome',
                    type: 'select',
                    datalist: [
                        ['', '—'],
                        ['Innkalt til time', 'Innkalt til time'],
                        ['Avventer', 'Avventer'],
                        ['Henvist til vakt', 'Henvist til vakt'],
                        ['Avsluttet', 'Avsluttet'],
                    ],
                    icon: 'fa-duotone fa-flag-checkered',
                    getValue: obj => obj.outcome || '',
                },
                {
                    title: <Text id='phoneConsultation.durationMinutes'>Varighet (min)</Text>,
                    placeholder: <Text id='phoneConsultation.durationMinutes'>Varighet (min)</Text>,
                    name: 'durationMinutes',
                    type: 'number',
                    icon: 'fa-duotone fa-clock',
                    getValue: obj => obj.durationMinutes || 0,
                },
            ],
        },
        {
            name: <Text id='input.phone-consultation-followup'>Follow-up</Text>,
            key: 'Phone consultation follow-up',
            fields: [
                {
                    title: <Text id='phoneConsultation.followUpNeeded'>Trenger oppfølging</Text>,
                    placeholder: <Text id='phoneConsultation.followUpNeeded'>Trenger oppfølging</Text>,
                    name: 'followUpNeeded',
                    type: 'toggle',
                    icon: 'fa-duotone fa-bell',
                    getValue: obj => !!obj.followUpNeeded,
                },
                {
                    title: <Text id='phoneConsultation.followUpDate'>Oppfølgingsdato</Text>,
                    placeholder: <Text id='phoneConsultation.followUpDate'>Oppfølgingsdato</Text>,
                    name: 'followUpDate',
                    type: 'date',
                    icon: 'fa-duotone fa-calendar-check',
                    getValue: obj => obj.followUpDate || '',
                },
                {
                    title: <Text id='phoneConsultation.followUpDone'>Oppfølging utført</Text>,
                    placeholder: <Text id='phoneConsultation.followUpDone'>Oppfølging utført</Text>,
                    name: 'followUpDone',
                    type: 'toggle',
                    icon: 'fa-duotone fa-check',
                    getValue: obj => !!obj.followUpDone,
                },
            ],
        },
        {
            name: <Text id='input.phone-consultation-notes'>Notes</Text>,
            key: 'Phone consultation notes',
            fields: [
                {
                    title: <Text id='phoneConsultation.comment'>Interne notater</Text>,
                    placeholder: <Text id='phoneConsultation.comment-placeholder'>Notat, ikke synlig for kunden</Text>,
                    name: 'comment',
                    type: 'textarea',
                    rows: 2,
                    icon: 'fa-duotone fa-note-sticky',
                    getValue: obj => obj.comment || '',
                },
            ],
        },
    ],
    sale: [
        {
            name: <Text id='input.sale-basics-title'>Sale basics</Text>,
            key: 'Sale basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.status'>Status</Text>,
                    placeholder: <Text id='input.status'>Status</Text>,
                    name: 'status',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.status,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.color'>Color</Text>,
                    placeholder: <Text id='input.color'>Color</Text>,
                    name: 'color',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-input-text',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.color,
                    showInList: false
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>user</Text>,
                    placeholder: <Text id='input.user'>user</Text>,
                    name: 'user',
                    type: 'select',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.user,
                    datalistName: 'employees',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.firstname} ${e.lastname} (${e.isApproved ? 'active' : 'inactive'})`,
                    showInList: true,
                },
                {
                    title: <Text id='dyrejournal.sale.department'>Department</Text>,
                    placeholder: <Text id='dyrejournal.sale.department'>Department</Text>,
                    name: 'customerDepartment',
                    type: 'select',
                    icon: 'fa-regular fa-building-user',
                    getValue: obj => (obj.customerDepartment ? `${obj.customerDepartment}` : ''),
                    datalistName: 'departments',
                    datalistFieldKey: d => `${d.id}`,
                    datalistFieldVal: d => `${d.id}: ${d.name}`,
                    showInList: false,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customerDepartment'>Customer Department</Text>,
                    placeholder: <Text id='input.customerDepartment'>Customer Department</Text>,
                    help: <Text id='input.customerDepartment-help'>This is the customerDepartment.id for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'customerDepartment',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customerDepartment || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.visitor'>visitor</Text>,
                    placeholder: <Text id='input.visitor'>visitor</Text>,
                    name: 'visitor',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitor,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.journal'>journal</Text>,
                    placeholder: <Text id='input.journal'>journal</Text>,
                    name: 'journal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.journal,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.calendarEvent'>calendarEvent</Text>,
                    placeholder: <Text id='input.calendarEvent'>calendarEvent</Text>,
                    name: 'calendarEvent',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.calendarEvent,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isProductionAnimal'>isProductionAnimal</Text>,
                    placeholder: <Text id='input.isProductionAnimal'>isProductionAnimal</Text>,
                    name: 'isProductionAnimal',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isProductionAnimal || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isWeborder'>isWeborder</Text>,
                    placeholder: <Text id='input.isWeborder'>isWeborder</Text>,
                    name: 'isWeborder',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isWeborder || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.invoiceNumber'>invoiceNumber</Text>,
                    placeholder: <Text id='input.invoiceNumber'>invoiceNumber</Text>,
                    name: 'invoiceNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.invoiceNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.visitorNumber'>visitorNumber</Text>,
                    placeholder: <Text id='input.visitorNumber'>visitorNumber</Text>,
                    name: 'visitorNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitorNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.animalNumber'>animalNumber</Text>,
                    placeholder: <Text id='input.animalNumber'>animalNumber</Text>,
                    name: 'animalNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.animalNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.saleNumber'>SaleNumber</Text>,
                    placeholder: <Text id='input.saleNumber'>SaleNumber</Text>,
                    name: 'saleNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.saleNumber,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.readyForPickup'>readyForPickup</Text>,
                    placeholder: <Text id='input.readyForPickup'>readyForPickup</Text>,
                    name: 'readyForPickup',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.readyForPickup,
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.saleDate'>SaleDate</Text>,
                    placeholder: <Text id='input.saleDate'>SaleDate</Text>,
                    name: 'saleDate',
                    type: 'datetime-local',
                    hideDateShortcuts: true,
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.saleDate,
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                    hideOnCreate: false,
                    isRecommended: true,
                    checkValue: (obj, holdingStore) => {
                        if (!obj.saleDate) {
                            return;
                        }
                        const saleDate = new Date(obj.saleDate);
                        const now = new Date();
                        const diffTime = now - saleDate;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        const messages = [];
                        // Check if date is more than 30 days in the past
                        if (diffDays > 30) {
                            // alert(`Advarsel: Salgsdatoen er ${diffDays} dager tilbake i tid. Er du sikker på at dette er riktig?`);
                            messages.push(`Salgsdatoen er ${diffDays} dager tilbake i tid.`);
                        }
                        // Check if date is in the future
                        if (diffDays < 0) {
                            const futureDays = Math.abs(diffDays);
                            // alert(`Advarsel: Salgsdatoen er ${futureDays} dag(er) frem i tid. Er du sikker på at dette er riktig?`);
                            messages.push(`Salgsdatoen er ${futureDays} dag(er) frem i tid.`);
                        }
                        if (messages.length > 0) {
                            holdingStore.setFeedbackError('saleDate', `Warning! ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('saleDate', '');
                        }
                    },
                },
                {
                    title: <Text id='input.paidDate'>PaidDate</Text>,
                    placeholder: <Text id='input.paidDate'>PaidDate</Text>,
                    name: 'paidDate',
                    type: 'datetime-local',
                    hideDateShortcuts: true,
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidDate,
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.dueDate'>dueDate</Text>,
                    placeholder: <Text id='input.dueDate'>dueDate</Text>,
                    name: 'dueDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.dueDate,
                    showInList: true,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.kid'>KID</Text>,
                    placeholder: <Text id='input.kid'>KID</Text>,
                    name: 'kid',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.kid,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.sale-invoice-references-title'>Invoice references</Text>,
            key: 'Sale invoice references',
            fields: [
                {
                    title: <Text id='input.invoiceReferenceNumber'>Customer reference number</Text>,
                    placeholder: (
                        <Text id='input.invoiceReferenceNumber-placeholder'>Purchase order or EHF reference</Text>
                    ),
                    name: 'invoiceReferenceNumber',
                    type: 'text',
                    icon: 'fa-solid fa-hashtag',
                    getValue: obj => obj.invoiceReferenceNumber || '',
                },
                {
                    title: <Text id='input.invoiceReferencePerson'>Customer reference person</Text>,
                    placeholder: (
                        <Text id='input.invoiceReferencePerson-placeholder'>Name at the invoice recipient</Text>
                    ),
                    name: 'invoiceReferencePerson',
                    type: 'text',
                    icon: 'fa-solid fa-user',
                    getValue: obj => obj.invoiceReferencePerson || '',
                },
            ],
        },
        {
            name: <Text id='input.sale-price-payment-title'>Sale price and payment</Text>,
            key: 'Sale price and payment',
            fields: [
                {
                    title: <Text id='input.totalPrice'>TotalPrice</Text>,
                    placeholder: <Text id='input.totalPrice'>TotalPrice</Text>,
                    name: 'totalPrice',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.totalPrice,
                    showInList: true,
                },
                {
                    title: <Text id='input.vat'>Vat</Text>,
                    placeholder: <Text id='input.vat'>Vat</Text>,
                    name: 'vat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.vat,
                    showInList: true,
                },
                {
                    title: <Text id='input.discount'>Discount</Text>,
                    placeholder: <Text id='input.discount'>Discount</Text>,
                    name: 'discount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.discount,
                    showInList: true,
                },
                {
                    title: <Text id='input.paymentMethod'>PaymentMethod</Text>,
                    placeholder: <Text id='input.paymentMethod'>PaymentMethod</Text>,
                    name: 'paymentMethod',
                    type: 'select',
                    datalist: [['cash', 'Kontant'], ['card', 'Kort'], ['invoice', 'Faktura'], ['account', 'Konto'], ['creditNote', 'Kreditnota']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentMethod,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.paymentAmount'>PaymentAmount</Text>,
                    placeholder: <Text id='input.paymentAmount'>PaymentAmount</Text>,
                    name: 'paymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.paymentAmount,
                    showInList: true,
                },
                {
                    title: <Text id='input.contactMethod'>ContactMethod</Text>,
                    placeholder: <Text id='input.contactMethod'>ContactMethod</Text>,
                    name: 'contactMethod',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contactMethod,
                    showInList: true,
                },
                {
                    title: <Text id='input.fikenLocation'>fikenLocation</Text>,
                    placeholder: <Text id='input.fikenLocation'>fikenLocation</Text>,
                    name: 'fikenLocation',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenLocation,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.animaliaId'>animaliaId</Text>,
                    placeholder: <Text id='input.animaliaId'>animaliaId</Text>,
                    name: 'animaliaId',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.animaliaId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.reminders'>reminders</Text>,
                    placeholder: <Text id='input.reminders'>reminders</Text>,
                    name: 'reminders',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-paper-plane',
                    getValue: obj => obj.reminders,
                    datalist: [
                        'date', 'type'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.comments'>comments</Text>,
                    placeholder: <Text id='input.comments'>comments</Text>,
                    name: 'comments',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-paper-plane',
                    getValue: obj => obj.comments,
                    datalist: [
                        'date', 'comment'
                    ],
                    showInList: false,
                    displayHeight: '120px',
                },
            ],
        },
        {
            name: <Text id='input.sale-content-title'>Sale content</Text>,
            key: 'Sale content',
            fields: [
                {
                    title: <Text id='input.products'>products</Text>,
                    placeholder: <Text id='input.products'>products</Text>,
                    name: 'products',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.products?.map(e => e),
                    displayArrayObject: obj => `${obj.qty} x ${obj.id} ${obj.name}, ${util.format(obj.price, 2)}`,
                    datalist: [
                        'id', 'name', 'qty', 'price',
                    ],
                    showInList: false,
                    displayHeight: '120px',
                },
                {
                    title: <Text id='input.journals'>journals</Text>,
                    placeholder: <Text id='input.journals'>journals</Text>,
                    name: 'journals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.journals?.map(e => e),
                    displayArrayObject: obj => `id: ${obj.id}, jnr: ${obj.journalNumber}, ${util.isoDate(obj.date)}`,
                    datalist: [
                        'id', 'journalNumber', 'date',
                    ],
                    showInList: false,
                    displayHeight: '120px',
                },
                {
                    title: <Text id='input.visitors'>visitors</Text>,
                    placeholder: <Text id='input.visitors'>visitors</Text>,
                    name: 'visitors',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.visitors?.map(e => e),
                    displayArrayObject: obj => `${obj.id} ${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'id', 'firstname', 'lastname', 'cellphone',
                    ],
                    showInList: false,
                    displayHeight: '120px',
                },
            ],
        },
        {
            name: <Text id='input.sale-shipping-title'>Sale shipping</Text>,
            key: 'Sale shipping',
            fields: [
                {
                    title: <Text id='input.noPackages'>noPackages</Text>,
                    placeholder: <Text id='input.noPackages'>noPackages</Text>,
                    name: 'noPackages',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.noPackages,
                    showInList: false,
                },
                {
                    title: <Text id='input.packedDate'>packedDate</Text>,
                    placeholder: <Text id='input.packedDate'>packedDate</Text>,
                    name: 'packedDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.packedDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.packedBy'>packedBy</Text>,
                    placeholder: <Text id='input.packedBy'>packedBy</Text>,
                    name: 'packedBy',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.packedBy,
                    showInList: false,
                },
                {
                    title: <Text id='input.shippingDate'>shippingDate</Text>,
                    placeholder: <Text id='input.shippingDate'>shippingDate</Text>,
                    name: 'shippingDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.shippingDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.shippedBy'>shippedBy</Text>,
                    placeholder: <Text id='input.shippedBy'>shippedBy</Text>,
                    name: 'shippedBy',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shippedBy,
                    showInList: false,
                },
                {
                    title: <Text id='input.shippingMethod'>shippingMethod</Text>,
                    placeholder: <Text id='input.shippingMethod'>shippingMethod</Text>,
                    help: <Text id='input.shippingMethod-help'>This is the shipping method used for the sale. Typical values are "Posten", "Postnord", "DHL", "FedEx", etc.</Text>,
                    name: 'shippingMethod',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.shippingMethod,
                    showInList: false,
                },
                {
                    title: <Text id='input.trackingNumber'>trackingNumber</Text>,
                    placeholder: <Text id='input.trackingNumber'>trackingNumber</Text>,
                    help: <Text id='input.trackingNumber-help'>This is the tracking number for the shipment. You can find it on the shipping label or receipt.</Text>,
                    name: 'trackingNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackingNumber,
                    showInList: false,
                },
                {
                    title: <Text id='input.shippingCode'>shippingCode</Text>,
                    placeholder: <Text id='input.shippingCode'>shippingCode</Text>,
                    help: <Text id='input.shippingCode-help'>This is the shipping code for the shipment. Typical values for Posten are: "LL1234 12345 6789"</Text>,
                    name: 'shippingCode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.shippingCode,
                    showInList: false,
                    onPaste: (e, obj, updateValue, isNew) => {
                        const pastedText = e.clipboardData.getData('text');
                        // Remove non-alphanumeric chars (except whitespace), collapse whitespace to single space
                        const cleaned = pastedText
                            .replace(/[^a-zA-Z0-9\s]/g, '')
                            .replace(/\s+/g, ' ')
                            .trim();
                        if (cleaned !== pastedText) {
                            e.preventDefault();
                            updateValue({ name: 'shippingCode', value: cleaned });
                        }
                    },
                },
            ],
        },
        {
            name: <Text id='input.sale-media-title'>Sale media</Text>,
            key: 'Sale media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
            ],
        },
    ],
    salePayment: [
        {
            name: <Text id='input.salePayment-basics-title'>salePayment basics</Text>,
            key: 'salePayment basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customerDepartment'>Customer Department</Text>,
                    placeholder: <Text id='input.customerDepartment'>Customer Department</Text>,
                    help: <Text id='input.customerDepartment-help'>This is the customerDepartment.id for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'customerDepartment',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customerDepartment || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>sale</Text>,
                    placeholder: <Text id='input.sale'>sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.paidDate'>PaidDate</Text>,
                    placeholder: <Text id='input.paidDate'>PaidDate</Text>,
                    name: 'paidDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidDate,
                    showInList: true,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.salePayment-price-payment-title'>salePayment price and payment</Text>,
            key: 'salePayment price and payment',
            fields: [
                {
                    title: <Text id='input.amount'>amount</Text>,
                    placeholder: <Text id='input.amount'>amount</Text>,
                    name: 'amount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amount,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.vat'>Vat</Text>,
                    placeholder: <Text id='input.vat'>Vat</Text>,
                    name: 'vat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.vat,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.paymentMethod'>PaymentMethod</Text>,
                    placeholder: <Text id='input.paymentMethod'>PaymentMethod</Text>,
                    name: 'paymentMethod',
                    type: 'select',
                    datalist: [['cash', 'Kontant'], ['card', 'Kort'], ['invoice', 'Faktura'], ['account', 'Konto'], ['creditNote', 'Kreditnota']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentMethod,
                    showInList: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.salePayment-media-title'>salePayment media</Text>,
            key: 'salePayment media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
            ],
        },
    ],
    saleReport: [
        {
            name: <Text id='input.saleReport-basics-title'>saleReport basics</Text>,
            key: 'saleReport basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.reportDate'>reportDate</Text>,
                    placeholder: <Text id='input.reportDate'>reportDate</Text>,
                    name: 'reportDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.reportDate,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.reportType'>reportType</Text>,
                    placeholder: <Text id='input.reportType'>reportType</Text>,
                    name: 'reportType',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.reportType,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
                {
                    title: <Text id='input.comment'>comment</Text>,
                    placeholder: <Text id='input.comment'>comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.saleReport-price-payment-title'>saleReport amounts</Text>,
            key: 'saleReport amounts',
            fields: [
                {
                    title: <Text id='input.amountCash'>amountCash</Text>,
                    placeholder: <Text id='input.amountCash'>amountCash</Text>,
                    name: 'amountCash',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountCash,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountCard'>amountCard</Text>,
                    placeholder: <Text id='input.amountCard'>amountCard</Text>,
                    name: 'amountCard',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountCard,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountInvoice'>amountInvoice</Text>,
                    placeholder: <Text id='input.amountInvoice'>amountInvoice</Text>,
                    name: 'amountInvoice',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountInvoice,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountAccount'>amountAccount</Text>,
                    placeholder: <Text id='input.amountAccount'>amountAccount</Text>,
                    name: 'amountAccount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountAccount,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountCreditNote'>amountCreditNote</Text>,
                    placeholder: <Text id='input.amountCreditNote'>amountCreditNote</Text>,
                    name: 'amountCreditNote',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountCreditNote,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountUnpaid'>amountUnpaid</Text>,
                    placeholder: <Text id='input.amountUnpaid'>amountUnpaid</Text>,
                    name: 'amountUnpaid',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountUnpaid,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountTotal'>amountTotal</Text>,
                    placeholder: <Text id='input.amountTotal'>amountTotal</Text>,
                    name: 'amountTotal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountTotal,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountTax'>amountTax</Text>,
                    placeholder: <Text id='input.amountTax'>amountTax</Text>,
                    name: 'amountTax',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountTax,
                    showInList: true,
                },

                {
                    title: <Text id='input.deviationCash'>deviationCash</Text>,
                    placeholder: <Text id='input.deviationCash'>deviationCash</Text>,
                    name: 'deviationCash',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationCash,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationCard'>deviationCard</Text>,
                    placeholder: <Text id='input.deviationCard'>deviationCard</Text>,
                    name: 'deviationCard',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationCard,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationVipps'>deviationVipps</Text>,
                    placeholder: <Text id='input.deviationVipps'>deviationVipps</Text>,
                    name: 'deviationVipps',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationVipps,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationInvoice'>deviationInvoice</Text>,
                    placeholder: <Text id='input.deviationInvoice'>deviationInvoice</Text>,
                    name: 'deviationInvoice',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationInvoice,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationAccount'>deviationAccount</Text>,
                    placeholder: <Text id='input.deviationAccount'>deviationAccount</Text>,
                    name: 'deviationAccount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationAccount,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationCreditNote'>deviationCreditNote</Text>,
                    placeholder: <Text id='input.deviationCreditNote'>deviationCreditNote</Text>,
                    name: 'deviationCreditNote',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationCreditNote,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationUnpaid'>deviationUnpaid</Text>,
                    placeholder: <Text id='input.deviationCreditNote'>deviationUnpaid</Text>,
                    name: 'deviationUnpaid',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationUnpaid,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationTotal'>deviationTotal</Text>,
                    placeholder: <Text id='input.deviationTotal'>deviationTotal</Text>,
                    name: 'deviationTotal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationTotal,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationTax'>deviationTax</Text>,
                    placeholder: <Text id='input.deviationTax'>deviationTax</Text>,
                    name: 'deviationTax',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationTax,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.saleReport-media-title'>saleReport media</Text>,
            key: 'saleReport media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
            ],
        },
    ],
    product: [
        {
            name: <Text id='input.product-important-title'>Product important</Text>,
            key: 'Product important',
            isCollapsed: false,
            backgroundColor: 'var(--bs-info-bg-subtle) !important',
            borderColor: 'var(--bs-info-border-subtle) !important',
            fields: [
                {
                    title: <Text id='input.webshop'>webshop</Text>,
                    placeholder: <Text id='input.webshop'>webshop</Text>,
                    help: <Text id='input.webshop-help'>Show this product in the public webshop list on the frontpage and products page.</Text>,
                    name: 'webshop',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj?.webshop || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.productNumber'>Product Number</Text>,
                    placeholder: <Text id='input.productNumber'>Product Number</Text>,
                    help: <Text id='input.productNumber-help'>Product number. Your own product number. THIS IS REQUIRED FOR TRIPLETEX AND FIKEN.</Text>,
                    name: 'productNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.productNumber,
                    checkValue: async (obj, holdingStore) => {
                        const productNumber = obj?.productNumber;
                        if (!productNumber) {
                            holdingStore.setFeedbackError('productNumber', '');
                            return;
                        }
                        // Check if productNumber already exists for another product
                        const response = await util.fetchApi('/api/products/', { method: 'GET' }, { productNumber, limit: 2 });
                        const existingProducts = response?.data || [];
                        console.log('checkValue existingProducts', existingProducts);
                        // Filter out the current product being edited
                        const duplicates = existingProducts.filter(p => p.id !== obj.id);
                        console.log('duplicates', duplicates);
                        if (duplicates.length > 0) {
                            const duplicate = duplicates[0];
                            holdingStore.setFeedbackError('productNumber', `Product number already exists: "${duplicate.name}" (ID: ${duplicate.id})`);
                        } else {
                            holdingStore.setFeedbackError('productNumber', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        const { latestUpdateProducts = [] } = holdingStore;
                        holdingStore.setFeedback('productNumber', `Siste: \n${latestUpdateProducts.filter(e => e.productNumber).splice(0, 5).map(e => `💡 __${e.productNumber}__: ${e.name}`).join('\n')}`);
                        console.log('before', latestUpdateProducts.map(e => e.productNumber).join(', '));
                        // holdingStore.setFeedback('productNumber', 'FOOBAR');
                        holdingStore.setFeedbackButton('productNumber', null, null);
                    },
                    showInList: true,
                    isRequired: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRequired: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.inStock'>inStock</Text>,
                    placeholder: <Text id='input.inStock'>inStock</Text>,
                    help: <Text id='input.qty-help'>Quantity in stock</Text>,
                    name: 'inStock',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.inStock,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.priceIncVat'>Price inc VAT</Text>,
                    placeholder: <Text id='input.priceIncVat'>Price inc VAT</Text>,
                    help: <Text id='input.priceIncVat-help'>Selling price with VAT</Text>,
                    name: 'priceIncVat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.priceIncVat,
                    callback: (obj, updateValue, holdingStore, value) => {
                        // console.log('callback for priceIncVat', { obj, updateValue, holdingStore, value });
                        const finalPrice = obj.priceIncVat / (1 + (obj.vat || 0) / 100);
                        updateValue({
                            id: obj.id,
                            name: 'price',
                            value: finalPrice,
                        });
                    },
                    // checkValue: (obj, holdingStore, value) => {
                    //     console.log('checkValue for priceIncVat', { obj, holdingStore, value });
                    // },
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price</Text>,
                    help: <Text id='input.price-help'>Selling price without VAT</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.price,
                    callback: (obj, updateValue, holdingStore, value) => {
                        console.log('callback for price', { obj, updateValue, holdingStore, value });
                        const finalPrice = obj.price * (1 + (obj.vat || 0) / 100);
                        updateValue({
                            id: obj.id,
                            name: 'priceIncVat',
                            value: finalPrice,
                        });
                    },
                    displayPublicValue: (price, obj) => `${obj?.currency || 'kr.'} ${lineTotal(obj, true, 1)}`,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.medicine'>Medicine</Text>,
                    placeholder: <Text id='input.medicine'>Medicine</Text>,
                    help: <Text id='input.medicine-help'>Part of price that is medicine cost.</Text>,
                    name: 'medicine',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.medicine,
                    showInList: true,
                },
                {
                    title: <Text id='input.fee'>Fee</Text>,
                    placeholder: <Text id='input.fee'>Fee</Text>,
                    help: <Text id='input.fee-help'>Part of price that is fee cost.</Text>,
                    name: 'fee',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.fee,
                    showInList: true,
                },
                {
                    title: <Text id='input.equipment'>Equipment</Text>,
                    placeholder: <Text id='input.equipment'>Equipment</Text>,
                    help: <Text id='input.equipment-help'>Part of price that is equipment.</Text>,
                    name: 'equipment',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.equipment,
                    showInList: true,
                },
                {
                    title: <Text id='input.other'>Other</Text>,
                    placeholder: <Text id='input.other'>Other</Text>,
                    help: <Text id='input.other-help'>Part of price that is other cost.</Text>,
                    name: 'other',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.other,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.product-basics-title'>Product basics</Text>,
            key: 'Product basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.webshop'>webshop</Text>,
                    placeholder: <Text id='input.webshop'>webshop</Text>,
                    help: <Text id='input.webshop-help'>Show this product in the public webshop list on the frontpage and products page.</Text>,
                    name: 'webshop',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.webshop || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isGlobalSubscription'>isGlobalSubscription</Text>,
                    placeholder: <Text id='input.webshop'>isGlobalSubscription</Text>,
                    help: <Text id='input.isGlobalSubscription-help'>Subscription product: when paid, it activates a subscription for the linked race on the buyer's account.</Text>,
                    name: 'isGlobalSubscription',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isGlobalSubscription || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.isMemberProduct'>Member Product</Text>,
                    placeholder: <Text id='input.isMemberProduct'>Member Product</Text>,
                    help: <Text id='input.isMemberProduct-help'>Offered as a member add-on product in the club membership signup and in the cart.</Text>,
                    name: 'isMemberProduct',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-check',
                    getValue: obj => obj.isMemberProduct || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.afterPurchaseUrl'>After purchase URL</Text>,
                    placeholder: <Text id='input.afterPurchaseUrl-placeholder'>/race/[raceId]/tracking</Text>,
                    help: <Text id='input.afterPurchaseUrl-help'>URL to navigate to after purchase. Use [raceId] or [productId] as placeholders.</Text>,
                    name: 'afterPurchaseUrl',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.afterPurchaseUrl || '',
                    showInList: false,
                },
                {
                    title: <Text id='input.afterPurchaseButtonText'>After purchase button text</Text>,
                    placeholder: <Text id='input.afterPurchaseButtonText-placeholder'>Go to Live Tracking</Text>,
                    help: <Text id='input.afterPurchaseButtonText-help'>Button text shown after successful purchase.</Text>,
                    name: 'afterPurchaseButtonText',
                    type: 'text',
                    icon: 'fa-solid fa-text',
                    getValue: obj => obj.afterPurchaseButtonText || '',
                    showInList: false,
                },
                {
                    title: <Text id='input.account'>account</Text>,
                    placeholder: <Text id='input.account'>account</Text>,
                    help: <Text id='input.account-help'>Accounting account number. Use real series.</Text>,
                    name: 'account',
                    type: 'datalist',
                    icon: 'fa-solid fa-input-numeric',
                    datalist: Object.keys(ACCOUNT_MAP).map(key => [parseInt(key, 10), `${key} - ${ACCOUNT_MAP[key]}`]),
                    getValue: obj => obj.account,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.productNumber'>Product Number</Text>,
                    placeholder: <Text id='input.productNumber'>Product Number</Text>,
                    help: <Text id='input.productNumber-help'>Product number. Your own product number. THIS IS REQUIRED FOR TRIPLETEX AND FIKEN.</Text>,
                    name: 'productNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.productNumber,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.category'>category</Text>,
                    placeholder: <Text id='input.category'>category</Text>,
                    help: <Text id='input.category-help'>Category of product</Text>,
                    name: 'category',
                    type: 'select',
                    icon: 'fa-solid fa-folder',
                    getValue: obj => obj.category,
                    datalist: [
                        ['produkt', 'Produkt'],
                        ['honorar', 'Honorar'],
                        ['medisin', 'Medisin'],
                        ['annet', 'Annet'],
                    ],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.dealer'>Dealer / Supplier</Text>,
                    placeholder: <Text id='input.dealer'>Dealer / Supplier</Text>,
                    help: <Text id='input.dealer-help'>The dealer or supplier of this product.</Text>,
                    name: 'dealer',
                    type: 'select',
                    icon: 'fa-solid fa-building',
                    getValue: obj => obj.dealer,
                    datalistName: 'dealers',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => e.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.type'>type</Text>,
                    placeholder: <Text id='input.type'>type</Text>,
                    help: <Text id='input.type-help'>Type of product. Ie. "shipping", "invoiceFee"</Text>,
                    name: 'type',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.type,
                    showInList: true,
                },
                {
                    title: <Text id='input.saleType'>Sale Type</Text>,
                    placeholder: <Text id='input.saleType-placeholder'>e.g. Black Friday</Text>,
                    help: <Text id='input.saleType-help'>Sale label displayed on product image</Text>,
                    name: 'saleType',
                    type: 'text',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.saleType,
                    showInList: true,
                },
                {
                    title: <Text id='input.gtin'>gtin</Text>,
                    placeholder: <Text id='input.gtin'>gtin</Text>,
                    help: <Text id='input.gtin-help'>Global Trade Item Number for product. This is used for barcodes.</Text>,
                    name: 'gtin',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gtin,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    help: <Text id='input.description-help'>Description of product</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.technicalInfo'>Technical Info</Text>,
                    placeholder: <Text id='input.technicalInfo'>Technical Info</Text>,
                    help: <Text id='input.technicalInfo-help'>Technical Info of product</Text>,
                    name: 'technicalInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.technicalInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.properties'>Properties</Text>,
                    placeholder: <Text id='input.properties'>Properties</Text>,
                    help: <Text id='input.properties-help'>Properties of product</Text>,
                    name: 'properties',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.properties,
                    showInList: false,
                },
                {
                    title: <Text id='input.colors'>Colors</Text>,
                    placeholder: <Text id='input.colors'>Colors</Text>,
                    help: <Text id='input.colors-help'>Color variants of this product.</Text>,
                    name: 'colors',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.colors,
                    datalist: [
                        'productNumer', 'qty', 'name', 'description'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>productNumer:</span> {obj.productNumer}<br />
                            <span class='font-weight-lighter'>qty:</span> {obj.qty}<br />
                            <span class='font-weight-lighter'>name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>description:</span> {obj.description}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '250px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.sizes'>Sizes</Text>,
                    placeholder: <Text id='input.sizes'>Sizes</Text>,
                    help: <Text id='input.sizes-help'>Sizes variants of this product.</Text>,
                    name: 'sizes',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.sizes,
                    datalist: [
                        'productNumer', 'qty', 'name', 'description'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>productNumer:</span> {obj.productNumer}<br />
                            <span class='font-weight-lighter'>qty:</span> {obj.qty}<br />
                            <span class='font-weight-lighter'>name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>description:</span> {obj.description}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '250px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.journal'>journal</Text>,
                    placeholder: <Text id='input.journal'>journal</Text>,
                    name: 'journal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.journal,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.drug'>Drug</Text>,
                    placeholder: <Text id='input.drug'>Drug</Text>,
                    name: 'drug',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.drug,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.idexxTestCode'>IDEXX test code</Text>,
                    placeholder: <Text id='input.idexxTestCode-placeholder'>e.g. IHD_CBC, IHD_Chem17, FECG</Text>,
                    help: <Text id='input.idexxTestCode-help'>Links this product to an IDEXX test code. When the linked test is ordered via IDEXX, this product is auto-added as a sale line.</Text>,
                    name: 'idexxTestCode',
                    type: 'text',
                    icon: 'fa-duotone fa-flask-vial',
                    getValue: obj => obj.idexxTestCode || '',
                    showInList: true,
                },
                {
                    title: <Text id='input.addOnPrescription'>Auto-add on new prescription</Text>,
                    placeholder: <Text id='input.addOnPrescription'>Auto-add on new prescription</Text>,
                    help: <Text id='input.addOnPrescription-help'>When the clinic has "Auto-add prescription product" enabled, this product is added as a sale line the first time a prescription is created on a sale.</Text>,
                    name: 'addOnPrescription',
                    type: 'toggle',
                    icon: 'fa-duotone fa-prescription',
                    getValue: obj => parseInt(obj.addOnPrescription, 10) === 1 ? 0 : 1,
                    showInList: true,
                },
                {
                    title: <Text id='input.reminderDays'>reminderDays</Text>,
                    placeholder: <Text id='input.reminderDays'>reminderDays</Text>,
                    help: <Text id='input.reminderDays-help'>Days in the future you want to schedule an reminder sms to the animal owner.</Text>,
                    name: 'reminderDays',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.reminderDays,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.reminderSmsText'>reminderSmsText</Text>,
                    placeholder: <Text id='input.reminderSmsText'>reminderSmsText</Text>,
                    help: <Text id='input.reminderSmsText-help'>SMS text template for reminder sms. Fields: [animal] [name] [clinic] [clinicPhone]</Text>,
                    name: 'reminderSmsText',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.reminderSmsText,
                },
            ],
        },
        {
            name: <Text id='input.product-online-booking-title'>Products for online booking</Text>,
            key: 'Products for online booking',
            fields: [
                {
                    title: <Text id='input.isOnlineBooking'>isOnlineBooking</Text>,
                    placeholder: <Text id='input.isOnlineBooking'>isOnlineBooking</Text>,
                    help: <Text id='input.isOnlineBooking-help'>Selectable as a bookable service in the visitor online booking flow.</Text>,
                    name: 'isOnlineBooking',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isOnlineBooking || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isBookingExclusive'>Booking is exclusive</Text>,
                    placeholder: <Text id='input.isBookingExclusive'>Booking is exclusive</Text>,
                    help: <Text id='input.isBookingExclusive-help'>Booking is exclusive for this customer.</Text>,
                    name: 'isBookingExclusive',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isBookingExclusive || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.estimatedTime'>Estimated Time</Text>,
                    placeholder: <Text id='input.estimatedTime'>Estimated Time</Text>,
                    help: <Text id='input.estimatedTime-help'>Estimated Time in minutes</Text>,
                    name: 'estimatedTime',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.estimatedTime,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.minutesBeforeEndOfDay'>Minutes Before End Of Day</Text>,
                    placeholder: <Text id='input.minutesBeforeEndOfDay'>Minutes Before End Of Day</Text>,
                    help: <Text id='input.minutesBeforeEndOfDay-help'>Not later than x minutes before end of day.</Text>,
                    name: 'minutesBeforeEndOfDay',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.minutesBeforeEndOfDay,
                    showInList: true,
                },
                {
                    title: <Text id='input.veterinaries'>Staff</Text>,
                    placeholder: <Text id='input.veterinaries'>Staff</Text>,
                    help: <Text id='input.veterinaries-help'>Limit online booking of this product to these staff members (veterinarians, nurses or others). Leave empty to offer it on every calendar open for online booking.</Text>,
                    name: 'veterinaries',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'veterinaries',
                    getValue: obj => obj.veterinaries?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of staff member.', 'Lastname of staff member.',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.speciesIds'>Species</Text>,
                    placeholder: <Text id='input.speciesIds'>Species</Text>,
                    help: <Text id='input.speciesIds-help'>Limit this to specific species. Leave empty to show for all species.</Text>,
                    name: 'speciesIds',
                    type: 'arrayObject',
                    icon: 'fa-duotone fa-paw',
                    searchMethod: 'searchSpecies',
                    getValue: obj => obj.speciesIds?.map(s => s),
                    datalist: [
                        'speciesId', 'name',
                    ],
                    datalistHelp: [
                        'Species ID', 'Species name',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-start px-2'>
                            <span class='font-weight-lighter'>Species:</span> {obj.name} <small class='text-body-secondary'>(#{obj.speciesId})</small>
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
        {
            name: <Text id='input.product-price-stock-title'>Product price and stock</Text>,
            key: 'Product price and stock',
            fields: [
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    help: <Text id='input.qty-help'>Quantity added when you add product to sale.</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.qty,
                    showInList: true,
                },
                {
                    title: <Text id='input.idealStock'>idealStock</Text>,
                    placeholder: <Text id='input.idealStock'>idealStock</Text>,
                    help: <Text id='input.idealStock-help'>Quantity should always be in stock.</Text>,
                    name: 'idealStock',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.idealStock,
                    showInList: true,
                },
                {
                    title: <Text id='input.inStock'>inStock</Text>,
                    placeholder: <Text id='input.inStock'>inStock</Text>,
                    help: <Text id='input.qty-help'>Quantity in stock</Text>,
                    name: 'inStock',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.inStock,
                    showInList: true,
                },
                {
                    title: <Text id='input.inPrice'>inPrice</Text>,
                    placeholder: <Text id='input.inPrice'>inPrice</Text>,
                    help: <Text id='input.inPrice-help'>In price without VAT</Text>,
                    name: 'inPrice',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.inPrice,
                    showInList: true,
                },
                {
                    title: <Text id='input.inPriceDiscount'>inPriceDiscount</Text>,
                    placeholder: <Text id='input.inPriceDiscount'>inPriceDiscount</Text>,
                    help: <Text id='input.inPriceDiscount-help'>In price discount in %</Text>,
                    name: 'inPriceDiscount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.inPriceDiscount,
                    showInList: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const priceWithDiscount = object.inPrice * (1 - object.inPriceDiscount / 100);
                        return (<>
                            <span class='font-weight-lighter'><Text id='input.inPrice-width-discount'>inPrice w/discount</Text>:</span> {util.format(priceWithDiscount, 2, ',', ' ')}
                        </>);
                    },
                    // checkValue: (obj, holdingStore) => {
                    //     // console.log('checkValue', obj, holdingStore.name);
                    //     if (obj.talentApplication.length > 500) {
                    //         holdingStore.setFeedbackError('talentApplication', 'Too many characters');
                    //     } else {
                    //         holdingStore.setFeedbackError('talentApplication', '');
                    //     }
                    // },
                },
                {
                    title: <Text id='input.desiredMargin'>desiredMargin</Text>,
                    placeholder: <Text id='input.desiredMargin'>desiredMargin</Text>,
                    help: <Text id='input.desiredMargin-help'>Desired margin in percent</Text>,
                    name: 'desiredMargin',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.desiredMargin,
                    callback: (obj, updateValue) => {
                        const inPrice = obj.inPrice;
                        const desiredMargin = obj.desiredMargin;
                        const price = Math.round(inPrice * (1 + desiredMargin / 100));
                        updateValue({
                            id: obj.id,
                            name: 'price',
                            value: price,
                        });
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.packageSize'>Package size</Text>,
                    placeholder: <Text id='input.packageSize-placeholder'>100</Text>,
                    help: <Text id='input.packageSize-help'>How many base units one package contains, e.g. 100 ml per bottle or 12 cans per carton. Prices, quantity and stock stay per base unit.</Text>,
                    name: 'packageSize',
                    type: 'number',
                    icon: 'fa-duotone fa-box',
                    getValue: obj => obj.packageSize,
                    callback: (obj, updateValue) => packagingCallback(obj, updateValue),
                },
                {
                    title: <Text id='input.packageUnit'>Package unit</Text>,
                    placeholder: <Text id='input.packageUnit-placeholder'>flaske</Text>,
                    help: <Text id='input.packageUnit-help'>Name of the package, e.g. flaske or kartong. Shown as a choice on the sale line.</Text>,
                    name: 'packageUnit',
                    type: 'text',
                    icon: 'fa-duotone fa-box-open',
                    getValue: obj => obj.packageUnit || '',
                },
                {
                    title: <Text id='input.baseUnit'>Base unit</Text>,
                    placeholder: <Text id='input.baseUnit-placeholder'>ml</Text>,
                    help: <Text id='input.baseUnit-help'>Name of the unit you price and sell in, e.g. ml or stk. Display only.</Text>,
                    name: 'baseUnit',
                    type: 'text',
                    icon: 'fa-duotone fa-ruler',
                    getValue: obj => obj.baseUnit || '',
                },
                {
                    title: <Text id='input.packageInPrice'>Purchase price per package</Text>,
                    placeholder: <Text id='input.packageInPrice-placeholder'>850</Text>,
                    help: <Text id='input.packageInPrice-help'>Purchase price ex VAT for one package. The purchase price per base unit (inPrice) is calculated as package price divided by package size, and the sale price from the desired margin.</Text>,
                    name: 'packageInPrice',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.packageInPrice,
                    callback: (obj, updateValue) => packagingCallback(obj, updateValue),
                },
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price</Text>,
                    help: <Text id='input.price-help'>Selling price without VAT</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.price,
                    displayPublicValue: (price, obj) => `${obj?.currency || 'kr.'} ${lineTotal(obj, true, 1)}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.medicine'>Medicine</Text>,
                    placeholder: <Text id='input.medicine'>Medicine</Text>,
                    help: <Text id='input.medicine-help'>Part of price that is medicine cost.</Text>,
                    name: 'medicine',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.medicine,
                    showInList: true,
                },
                {
                    title: <Text id='input.fee'>Fee</Text>,
                    placeholder: <Text id='input.fee'>Fee</Text>,
                    help: <Text id='input.fee-help'>Part of price that is fee cost.</Text>,
                    name: 'fee',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.fee,
                    showInList: true,
                },
                {
                    title: <Text id='input.equipment'>Equipment</Text>,
                    placeholder: <Text id='input.equipment'>Equipment</Text>,
                    help: <Text id='input.equipment-help'>Part of price that is equipment.</Text>,
                    name: 'equipment',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.equipment,
                    showInList: true,
                },
                {
                    title: <Text id='input.other'>Other</Text>,
                    placeholder: <Text id='input.other'>Other</Text>,
                    help: <Text id='input.other-help'>Part of price that is other cost.</Text>,
                    name: 'other',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.other,
                    showInList: true,
                },
                {
                    title: <Text id='input.vat'>Vat</Text>,
                    placeholder: <Text id='input.vat'>Vat</Text>,
                    help: <Text id='input.vat-help'>VAT in percent. Normally 25%.</Text>,
                    name: 'vat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.vat,
                    showInList: true,
                    defaultValue: 25,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.discount'>Discount</Text>,
                    placeholder: <Text id='input.discount'>Discount</Text>,
                    help: <Text id='input.discount-help'>Discount in percent</Text>,
                    name: 'discount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.discount,
                    showInList: true,
                },
                {
                    title: <Text id='input.discountAmount'>Discount Amount</Text>,
                    placeholder: <Text id='input.discountAmount'>Discount Amount</Text>,
                    help: <Text id='input.discountAmount-help'>Amount of discount applied inc VAT</Text>,
                    name: 'discountAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.discountAmount,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.product-sub-title'>Sub Products</Text>,
            key: 'Sub Products',
            fields: [
                {
                    title: <Text id='input.subProducts'>subProducts</Text>,
                    placeholder: <Text id='input.subProducts'>subProducts</Text>,
                    help: <Text id='input.subProducts-help'>Sub products of this product. Here you can attach other products that will be added automatically when you choose this product.</Text>,
                    name: 'subProducts',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',

                    lookupArray: 'adminAllProducts',
                    searchMethod: 'searchProducts',
                    getValue: obj => obj.subProducts?.map(e => e),
                    displayArrayObject: obj => `${obj.qty} stk. ${obj.name} (${obj.externalId}/${obj.id})`,

                    datalist: [
                        'md5', 'qty', 'name', 'id', 'externalId',
                    ],
                    drawerName: 'addSubProducts',
                    drawerSize: 'drawerHeightMediumLarge',
                    showInList: false,
                    displayHeight: '150px',
                    isAdminField: true,
                },
                {
                    title: <Text id='input.subProducts'>subProducts</Text>,
                    placeholder: <Text id='input.subProducts'>subProducts</Text>,
                    help: <Text id='input.subProducts-help'>Sub products of this product. Here you can attach other products that will be added automatically when you choose this product.</Text>,
                    name: 'subProducts',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',

                    lookupArray: 'allProducts',
                    searchMethod: 'searchProducts',
                    getValue: obj => obj.subProducts?.map(e => e),
                    displayArrayObject: obj => obj ? `${obj.qty} stk. ${obj.name} (${obj.externalId}/${obj.id})` : '',

                    datalist: [
                        'qty', 'name', 'id', 'externalId',
                    ],
                    drawerName: 'addSubProducts',
                    drawerSize: 'drawerHeightMediumLarge',
                    showInList: false,
                    displayHeight: '150px',
                },
            ],
        },
        {
            name: <Text id='input.product-media-title'>Product media</Text>,
            key: 'Product media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.product-images-help'>Images of product</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.product-models'>3D model (STL)</Text>,
                    placeholder: <Text id='input.product-models'>3D model (STL)</Text>,
                    help: <Text id='input.product-models-help'>Upload a 3D-print STL file to show an interactive 3D preview. A preview image is generated automatically.</Text>,
                    name: 'models',
                    type: 'file',
                    icon: 'fa-duotone fa-cube',
                    getValue: obj => obj.models,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    help: <Text id='input.files-help'>Files related to this product. Can be product sheets, usage manuals and such.</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    help: <Text id='input.links-help'>Links to product sheets, product pages or such.</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    help: <Text id='input.tags-help'>Tags are used to find similar news. Add as many as you like.</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
    ],
    diagnose: [
        {
            name: <Text id='input.diagnose-basics-title'>Diagnose basics</Text>,
            key: 'Diagnose basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.diagnoseCode'>DiagnoseCode</Text>,
                    placeholder: <Text id='input.diagnoseCode'>DiagnoseCode</Text>,
                    name: 'diagnoseCode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.diagnoseCode,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.drugs'>Drugs</Text>,
                    placeholder: <Text id='input.drugs'>Drugs</Text>,
                    name: 'drugs',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.drugs,
                    datalist: [
                        'drug', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
            ],
        },
    ],
    file: [
        {
            name: <Text id='input.file-basics-title'>File basics</Text>,
            key: 'File basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.date'>date</Text>,
                    placeholder: <Text id='input.date'>date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    showInList: true,
                    hideOnCreate: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isPublic'>isPublic</Text>,
                    placeholder: <Text id='input.isPublic'>isPublic</Text>,
                    name: 'isPublic',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isPublic || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.showOnFrontpage'>showOnFrontpage</Text>,
                    placeholder: <Text id='input.showOnFrontpage'>showOnFrontpage</Text>,
                    name: 'showOnFrontpage',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.showOnFrontpage || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.isFavorite'>isFavorite</Text>,
                    placeholder: <Text id='input.isFavorite'>isFavorite</Text>,
                    name: 'isFavorite',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.isFavorite || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.fileCode'>FileCode</Text>,
                    placeholder: <Text id='input.fileCode'>FileCode</Text>,
                    name: 'fileCode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fileCode,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    hideOnCreate: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
    ],
    media: [
        {
            name: <Text id='input.media-content-title'>Media content</Text>,
            key: 'Media content',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    isRecommended: true,
                    callback: async (obj, updateValue) => {
                        // console.log('fields.callback', { obj, updateValue });
                        if (obj.images?.length > 0) {
                            const img = obj.images[0];
                            if (img?.exif?.image?.Artist) {
                                updateValue({
                                    name: 'attribution',
                                    value: img?.exif?.image?.Artist,
                                });
                            }
                            if (img?.exif?.exif?.DateTimeOriginal) {
                                updateValue({
                                    name: 'whenIsTheMediaRecorded',
                                    value: img?.exif?.exif?.DateTimeOriginal,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                    isRecommended: true,
                },
                // {
                //     title: <Text id='input.links'>Links</Text>,
                //     placeholder: <Text id='input.links'>Links</Text>,
                //     name: 'links',
                //     type: 'arrayObject',
                //     icon: 'fa-solid fa-tag',
                //     getValue: obj => obj.links,
                //     datalist: [
                //         'url', 'name', 'description'
                //     ],
                //     showInList: false,
                //     displayHeight: '60px',
                //     hideOnCreate: false,
                // },
            ],
        },
        {
            name: <Text id='input.media-basics-title'>Media basics</Text>,
            key: 'Media basics',
            fields: [
                {
                    lang: { en: 'Id', no: 'Id' },
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    lang: { en: 'Offline', no: 'Avpublisert' },
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    help: <Text id='input.offline-help'>Set to 1 to delete the media</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                },
                {
                    title: <Text id='input.sort'>Sort</Text>,
                    placeholder: <Text id='input.sort'>Sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sort,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.race'>Race</Text>,
                //     placeholder: <Text id='input.race'>Race</Text>,
                //     name: 'race',
                //     type: 'number',
                //     icon: 'fa-solid fa-input-numeric',
                //     getValue: obj => obj.race,
                //     showInList: true,
                //     isAdminField: true,
                // },

                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'select',
                    icon: 'fa-solid fa-list',
                    getValue: obj => obj.race,
                    datalistName: 'races',
                    // datalist: [['birthday', 'Birthday'], ['buying', 'Buying'], ['christmas', 'Christmas'], ['dreaming', 'Dreaming about'], ['wedding', 'Wedding'], ['other', 'Other']],
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}`,
                    showInList: false,
                    isRecommended: true,
                },

                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    isRecommended: true,
                },


                {
                    title: <Text id='input.attribution'>attribution</Text>,
                    placeholder: <Text id='input.attribution'>attribution</Text>,
                    help: <Text id='input.attribution-help'>Include an optional field for users to enter how they want the image to be attributed, especially important for Creative Commons licenses that require it.</Text>,
                    name: 'attribution',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.attribution,
                    showInList: true,
                    isRecommended: true,
                },


                // Copyright Licenses

                // - All Rights Reserved: The creator retains all the rights provided by copyright law. Others can only use the work in ways that are directly allowed by law.
                // - Royalty-Free: A one-time fee is paid for non-exclusive, multiple usages of the asset. Note that "free" here doesn't mean no cost, but rather that you are free from paying royalties every time the asset is used.
                // - Rights Managed: A license where the user pays for the use of a copyrighted asset based on factors like duration, geographical location, type of media, etc.

                // Open Source Licenses (mostly for software)
                // - MIT License: A permissive license that only requires including the original copyright and license.
                // - GNU General Public License (GPL): Requires any derivative work to also be open-source under the same license.
                // - Apache License: A permissive free software license written by the Apache Software Foundation.

                // Creative Commons Licenses
                // CC BY (Attribution): Others can distribute, remix, adapt, and build upon your work, even commercially, as long as they credit you for the original creation.
                // CC BY-SA (Attribution-ShareAlike): Same as CC BY, but derivative works must be licensed under identical terms.
                // CC BY-ND (Attribution-NoDerivs): Allows for redistribution, commercial and non-commercial, as long as the work is passed along unchanged and in whole, with credit to you.
                // CC BY-NC (Attribution-NonCommercial): Others can remix, adapt, and build upon your work non-commercially, and although their new works must also acknowledge you and be non-commercial, they don’t have to license their derivative works on the same terms.
                // CC BY-NC-SA (Attribution-NonCommercial-ShareAlike): Others can remix, adapt, and build upon your work non-commercially, as long as they credit you and license their new creations under the identical terms.
                // CC BY-NC-ND (Attribution-NonCommercial-NoDerivs): Others can download your works and share them with others as long as they credit you, but they can’t change them in any way or use them commercially.

                // Public Domain
                // Public Domain (CC0): The creator waives all their copyright and related rights, effectively placing their work in the public domain.

                // These licenses provide various levels of permissions, requirements, and limitations. Make sure to consult with a legal advisor to ensure that you choose the license that fits your specific needs.
                {
                    title: <Text id='input.licenseType'>licenseType</Text>,
                    placeholder: <Text id='input.licenseType'>licenseType</Text>,
                    name: 'licenseType',
                    type: 'select',
                    icon: 'fa-solid fa-file-certificate',
                    getValue: obj => obj.licenseType,
                    datalist: [
                        ['All Rights Reserved', 'All Rights Reserved'],
                        ['Royalty-Free', 'Royalty-Free'],
                        ['Rights Managed', 'Rights Managed'],
                        ['Creative Commons Licenses CC BY-NC', 'Creative Commons Licenses CC BY-NC'],
                        ['Public Domain (CC0)', 'Public Domain (CC0)'],
                    ],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.userAgreement'>userAgreement</Text>,
                    placeholder: <Text id='input.userAgreement'>userAgreement</Text>,
                    help: <Text id='input.userAgreement-help'>I confirm that I own or have the right to use these images, and I grant permission to use them as described in the license above.</Text>,
                    name: 'userAgreement',
                    type: 'toggle',
                    icon: 'fa-solid fa-handshake',
                    getValue: obj => obj.userAgreement ? 1 : 0,
                    showInList: true,
                },


            ],
        },
        {
            name: <Text id='input.media-meta-title'>Media meta</Text>,
            key: 'Media meta',
            fields: [
                // {
                //     title: <Text id='input.location'>location</Text>,
                //     placeholder: <Text id='input.location'>location</Text>,
                //     name: 'location',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.location,
                //     showInList: true
                // },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.bib'>bib</Text>,
                    placeholder: <Text id='input.bib'>bib</Text>,
                    name: 'bib',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.bib || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.whoIsInTheMedia'>WhoIsInTheMedia</Text>,
                    placeholder: <Text id='input.whoIsInTheMedia'>Who is in the media</Text>,
                    name: 'whoIsInTheMedia',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whoIsInTheMedia,
                    showInList: true,
                },
                {
                    title: <Text id='input.whatIsInTheMedia'>WhatIsInTheMedia</Text>,
                    placeholder: <Text id='input.whatIsInTheMedia'>What is in the media</Text>,
                    name: 'whatIsInTheMedia',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whatIsInTheMedia,
                    showInList: true,
                },
                {
                    title: <Text id='input.whatIsTheStory'>WhatIsTheStory</Text>,
                    placeholder: <Text id='input.whatIsTheStory'>What is the story</Text>,
                    name: 'whatIsTheStory',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whatIsTheStory,
                    showInList: true,
                },
                {
                    title: <Text id='input.whereIsTheMediaRecorded'>WhereIsTheMediaRecorded</Text>,
                    placeholder: <Text id='input.whereIsTheMediaRecorded'>Where is the media recorded</Text>,
                    name: 'whereIsTheMediaRecorded',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whereIsTheMediaRecorded,
                    showInList: true,
                },
                {
                    title: <Text id='input.whenIsTheMediaRecorded'>WhenIsTheMediaRecorded</Text>,
                    placeholder: <Text id='input.whenIsTheMediaRecorded'>When is the media recorded</Text>,
                    name: 'whenIsTheMediaRecorded',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whenIsTheMediaRecorded,
                    showInList: true,
                },
                {
                    title: <Text id='input.preferredUse'>PreferredUse</Text>,
                    placeholder: <Text id='input.preferredUse'>Preferred use</Text>,
                    name: 'preferredUse',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.preferredUse,
                    datalist: [['web', 'Web'], ['instagram', 'Instagram'], ['facebook', 'Facebook'], ['magazine', 'Magazine']],
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.media-text-title'>Media text</Text>,
            key: 'Media text',
            fields: [
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Media text</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.body,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.media-publish-info-title'>Media publish info</Text>,
            key: 'Media publish info',
            fields: [
                {
                    title: <Text id='input.publishedToWeb'>publishedToWeb</Text>,
                    placeholder: <Text id='input.publishedToWeb'>publishedToWeb</Text>,
                    name: 'publishedToWeb',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToWeb || null,
                    showInList: true,
                    isAdminField: false,
                    // isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.publishedToNews'>publishedToNews</Text>,
                    placeholder: <Text id='input.publishedToNews'>publishedToNews</Text>,
                    name: 'publishedToNews',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToNews || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.publishedToFacebook'>publishedToFacebook</Text>,
                    placeholder: <Text id='input.publishedToFacebook'>publishedToFacebook</Text>,
                    name: 'publishedToFacebook',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToFacebook || null,
                    showInList: true,
                    isAdminField: false,
                    // isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.publishedToInstagram'>publishedToInstagram</Text>,
                    placeholder: <Text id='input.publishedToInstagram'>publishedToInstagram</Text>,
                    name: 'publishedToInstagram',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToInstagram || null,
                    showInList: true,
                    isAdminField: false,
                    // isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.publishedToOther'>publishedToOther</Text>,
                    placeholder: <Text id='input.publishedToOther'>publishedToOther</Text>,
                    name: 'publishedToOther',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToOther || null,
                    showInList: true,
                    isAdminField: false,
                    // isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.done'>done</Text>,
                    placeholder: <Text id='input.done'>done</Text>,
                    name: 'done',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.done || null,
                    showInList: true,
                    isAdminField: false,
                    isReadOnly: true,
                    // hideOnCreate: true,
                },


            ],
        },
    ],
    comment: [
        {
            name: <Text id='input.comment-basics-title'>Comment basics</Text>,
            key: 'Comment basics',
            fields: [
                {
                    lang: { en: 'Id', no: 'Id' },
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.objectId'>objectId</Text>,
                    placeholder: <Text id='input.objectId'>objectId</Text>,
                    name: 'objectId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.objectId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Article text</Text>,
                    name: 'body',
                    type: 'textareaEditor',
                    audioTranscribe: true,
                    viewHtml: false,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.body,
                    displayValue: obj => obj.body,
                    showInList: false,
                    aiPost: true,
                },
                {
                    title: <Text id='input.type'>type</Text>,
                    placeholder: <Text id='input.type'>type</Text>,
                    name: 'type',
                    type: 'select',
                    icon: 'fa-duotone fa-comment',
                    getValue: obj => obj.type,
                    datalist: [['article', 'Article'], ['product', 'Product'], ['comment', 'Comment']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: false,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.comment-basics-title'>Comment basics</Text>,
            key: 'Comment image',
            showSectionName: false,
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-upload-help'>Add one or more images. Images takes some seconds to prepare for web view. Please wait until it is ready.</Text>,
                    name: 'images',
                    type: 'image',
                    displayLargeImages: true,
                    displayWidth: '180px',
                    displayHeight: 'auto',
                    // icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        // console.log('fields.callback', { obj, updateValue });
                        if (obj.images?.length > 0) {
                            const img = obj.images[0];
                            if (img?.exif?.image?.Artist) {
                                updateValue({
                                    name: 'attribution',
                                    value: img?.exif?.image?.Artist,
                                });
                            }
                            if (img?.exif?.exif?.DateTimeOriginal) {
                                updateValue({
                                    name: 'whenIsTheMediaRecorded',
                                    value: img?.exif?.exif?.DateTimeOriginal,
                                });
                            }
                        }
                    },
                    isSimpleEdit: true,
                    showFieldInfo: false,
                },
            ],
        },
    ],
    pricelist: [
        {
            name: <Text id='input.pricelist-basics-title'>Pricelist</Text>,
            key: 'pricelist basics',
            fields: [
                {
                    title: <Text id='input.pricelist-name'>Name</Text>,
                    placeholder: <Text id='input.pricelist-name'>Name</Text>,
                    help: <Text id='input.pricelist-name-help'>Name of this pricelist, e.g. "Store forhandlere".</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-duotone fa-tags',
                    getValue: obj => obj.name || '',
                    showInList: true,
                },
                {
                    title: <Text id='input.pricelist-description'>Description</Text>,
                    placeholder: <Text id='input.pricelist-description'>Description</Text>,
                    help: <Text id='input.pricelist-description-help'>Internal description of this pricelist.</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-duotone fa-text',
                    getValue: obj => obj.description || '',
                },
            ],
        },
    ],
    oauthClient: [
        {
            name: <Text id='input.oauthClient-basics-title'>OAuth client</Text>,
            key: 'oauthClient basics',
            fields: [
                {
                    title: <Text id='input.oauthClient-clientName'>Name</Text>,
                    placeholder: <Text id='input.oauthClient-clientName'>Name</Text>,
                    help: <Text id='input.oauthClient-clientName-help'>Shown to the veterinarian on the consent page and on the journal button.</Text>,
                    name: 'clientName',
                    type: 'text',
                    icon: 'fa-duotone fa-plug',
                    getValue: obj => obj.clientName || '',
                    showInList: true,
                },
                {
                    title: <Text id='input.oauthClient-description'>Description</Text>,
                    placeholder: <Text id='input.oauthClient-description'>Description</Text>,
                    help: <Text id='input.oauthClient-description-help'>One or two sentences shown under the name on the consent page.</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-duotone fa-text',
                    getValue: obj => obj.description || '',
                },
                {
                    title: <Text id='input.oauthClient-contactEmail'>Contact e-mail</Text>,
                    placeholder: <Text id='input.oauthClient-contactEmail'>Contact e-mail</Text>,
                    help: <Text id='input.oauthClient-contactEmail-help'>Technical contact at the vendor.</Text>,
                    name: 'contactEmail',
                    type: 'text',
                    icon: 'fa-duotone fa-envelope',
                    getValue: obj => obj.contactEmail || '',
                },
                {
                    title: <Text id='input.oauthClient-redirectUris'>Redirect URIs</Text>,
                    placeholder: <Text id='input.oauthClient-redirectUris-placeholder'>One https URL per line</Text>,
                    help: <Text id='input.oauthClient-redirectUris-help'>Exact-match OAuth redirect URIs, one per line. https only.</Text>,
                    name: 'redirectUrisText',
                    type: 'textarea',
                    icon: 'fa-duotone fa-link',
                    getValue: obj => (obj.redirectUris || []).join('\n'),
                },
                {
                    title: <Text id='input.oauthClient-allowedScopes'>Allowed scopes</Text>,
                    placeholder: <Text id='input.oauthClient-allowedScopes-placeholder'>visits:read visits:suggest</Text>,
                    help: <Text id='input.oauthClient-allowedScopes-help'>Space separated. Valid: installations:read, parts:read, visits:read, visits:suggest.</Text>,
                    name: 'allowedScopesText',
                    type: 'text',
                    icon: 'fa-duotone fa-key',
                    getValue: obj => (obj.allowedScopes || []).join(' '),
                },
                {
                    title: <Text id='input.oauthClient-launchUrl'>Launch URL</Text>,
                    placeholder: <Text id='input.oauthClient-launchUrl-placeholder'>https://vendor.example/launch</Text>,
                    help: <Text id='input.oauthClient-launchUrl-help'>Opened by the "Open in vendor" button in the journal, with visitId, animalId, clinicId and userId as query parameters. https only. Leave empty for no button.</Text>,
                    name: 'launchUrl',
                    type: 'text',
                    icon: 'fa-duotone fa-arrow-up-right-from-square',
                    getValue: obj => obj.launchUrl || '',
                },
                {
                    title: <Text id='input.oauthClient-logoUrl'>Logo URL</Text>,
                    placeholder: <Text id='input.oauthClient-logoUrl-placeholder'>https://vendor.example/logo.png</Text>,
                    help: <Text id='input.oauthClient-logoUrl-help'>Square logo shown on the consent and connections pages.</Text>,
                    name: 'logoUrl',
                    type: 'text',
                    icon: 'fa-duotone fa-image',
                    getValue: obj => obj.logoUrl || '',
                },
                {
                    title: <Text id='input.oauthClient-approved'>Approved</Text>,
                    help: <Text id='input.oauthClient-approved-help'>Only approved clients may request visit scopes or show a launch button.</Text>,
                    name: 'approved',
                    type: 'toggle',
                    icon: 'fa-duotone fa-badge-check',
                    getValue: obj => !!obj.approved,
                },
            ],
        },
    ],
    recipientList: [
        {
            name: <Text id='input.recipientlist-basics-title'>Recipient list</Text>,
            key: 'recipientlist basics',
            fields: [
                {
                    title: <Text id='input.recipientlist-name'>Name</Text>,
                    placeholder: <Text id='input.recipientlist-name'>Name</Text>,
                    help: <Text id='input.recipientlist-name-help'>Name of this list, e.g. "Alle LiveRace-brukere".</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-duotone fa-users',
                    getValue: obj => obj.name || '',
                    showInList: true,
                },
                {
                    title: <Text id='input.recipientlist-description'>Description</Text>,
                    placeholder: <Text id='input.recipientlist-description'>Description</Text>,
                    help: <Text id='input.recipientlist-description-help'>Internal description of this list.</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-duotone fa-text',
                    getValue: obj => obj.description || '',
                },
                {
                    title: <Text id='input.recipientlist-all-users'>All users</Text>,
                    placeholder: <Text id='input.recipientlist-all-users'>All users</Text>,
                    help: <Text id='input.recipientlist-all-users-help'>Include every active user in the system.</Text>,
                    name: 'includeAllUsers',
                    type: 'toggle',
                    icon: 'fa-duotone fa-globe',
                    getValue: obj => (parseInt(obj.includeAllUsers, 10) === 1 ? 1 : 0),
                },
            ],
        },
    ],
    contact: [
        {
            name: <Text id='input.contact-basics-title'>contact basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'contact basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'offline',
                    placeholder: 'offline',
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.name || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.firstname || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.lastname,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    showInList: true,
                    isRecommended: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphone,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-text',
                    getValue: user => user.description,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.contact-address-title'>contact address</Text>,
            key: 'contact address',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.address,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                    isRecommended: true,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalcode',
                                    value: obj.postalcode,
                                    id: obj.id,
                                });
                                updateValue({
                                    name: 'postalplace',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.postalplace'>Postalplace</Text>,
                    placeholder: <Text id='input.postalplace'>Postalplace</Text>,
                    name: 'postalplace',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalplace,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.country,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.contact-media-title'>contact media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'contact media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
    ],

    calendar: [
        {
            name: <Text id='input.calendar-basics-title'>Calendar basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Calendar basics',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj?.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sort'>sort</Text>,
                    placeholder: <Text id='input.sort'>sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sort,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.calendarId'>calendarId</Text>,
                    placeholder: <Text id='input.calendarId'>calendarId</Text>,
                    name: 'calendarId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj?.calendarId,
                    isReadOnly: true,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.type'>type</Text>,
                    placeholder: <Text id='input.type'>type</Text>,
                    name: 'type',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.type,
                    datalist: [['booking', 'Booking'], ['notification', 'Innkallinger']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                    isRecommended: true,
                },
                // {
                //     title: <Text id='input.type'>type</Text>,
                //     placeholder: <Text id='input.type'>type</Text>,
                //     name: 'type',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj?.type,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.useCalendarAvailability'>useCalendarAvailability</Text>,
                    placeholder: <Text id='input.useCalendarAvailability'>useCalendarAvailability</Text>,
                    name: 'useCalendarAvailability',
                    type: 'toggle',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj?.useCalendarAvailability ? 1 : 0,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isPublic'>Public calendar</Text>,
                    placeholder: <Text id='input.isPublic'>Public calendar</Text>,
                    help: <Text id='input.isPublic-help'>Events from this calendar will be visible to unauthenticated users on the public calendar page.</Text>,
                    name: 'isPublic',
                    type: 'toggle',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj?.isPublic ? 1 : 0,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isMain'>Main calendar</Text>,
                    placeholder: <Text id='input.isMain'>Main calendar</Text>,
                    help: <Text id='input.isMain-help'>Events from this calendar will be shown on the frontpage widget. Other calendars are only shown in the full calendar view.</Text>,
                    name: 'isMain',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj?.isMain ? 1 : 0,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRecommended: true,
                    callback: (obj, updateValue) => {
                        if (obj.id) {
                            // Existing calendar: calendarEvents link to it by the calendarId
                            // string, so never regenerate it on rename. If it is empty, the
                            // backend derives it from the name when the PATCH is saved.
                            return;
                        }
                        updateValue({
                            name: 'calendarId',
                            value: deriveCalendarId(obj.name),
                        });
                    },
                    onPaste: (e, obj, updateValue, isNew) => {
                        const pastedText = e.clipboardData.getData('text');
                        // Clean the pasted text:
                        // 1. Replace tabs with spaces
                        // 2. Replace multiple spaces/whitespace with single space
                        // 3. Trim leading/trailing whitespace
                        let cleanedText = pastedText
                            .replace(/\t/g, ' ')           // Replace tabs with space
                            .replace(/\s+/g, ' ')          // Replace multiple whitespace with single space
                            .trim();                        // Remove leading/trailing whitespace

                        e.preventDefault();
                        updateValue({ name: 'name', value: cleanedText });
                        if (!obj.id) {
                            // Pasting bypasses the callback above - derive calendarId here too.
                            updateValue({ name: 'calendarId', value: deriveCalendarId(cleanedText) });
                        }
                    },
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.isOnlineBooking'>isOnlineBooking</Text>,
                    placeholder: <Text id='input.isOnlineBooking'>isOnlineBooking</Text>,
                    name: 'isOnlineBooking',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isOnlineBooking || null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.displayCustomerInfo'>displayCustomerInfo</Text>,
                    placeholder: <Text id='input.displayCustomerInfo'>displayCustomerInfo</Text>,
                    name: 'displayCustomerInfo',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.displayCustomerInfo || null,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.calendar-color-title'>Calendar color</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'calendar color',
            fields: [
                {
                    title: <Text id='input.color'>color</Text>,
                    placeholder: <Text id='input.color'>color</Text>,
                    name: 'color',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    datalist: [
                        ['', 'Default'],
                        ['#ffffff', 'White'],
                        ['#000000', 'Black'],
                        ['#808080', 'Gray'],
                        ['#ff0000', 'Red'],
                        ['#00ff00', 'Green'],
                        ['#0000ff', 'blue'],
                    ],
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.color,
                    showInList: false,
                },
                {
                    title: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    placeholder: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    name: 'backgroundColor',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-input-text',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.backgroundColor,
                    showInList: false
                },
                {
                    title: <Text id='input.dragBackgroundColor'>dragBackgroundColor</Text>,
                    placeholder: <Text id='input.dragBackgroundColor'>dragBackgroundColor</Text>,
                    name: 'dragBackgroundColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dragBackgroundColor,
                    showInList: true,
                },
                {
                    title: <Text id='input.borderColor'>borderColor</Text>,
                    placeholder: <Text id='input.borderColor'>borderColor</Text>,
                    name: 'borderColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.borderColor,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.calendar-texttemplate-title'>Calendar text template</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'calendar text template',
            fields: [
                {
                    title: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    placeholder: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    help: <Text id='input.smsTextBooking-help'>This is the text that will be sent to the customer when a booking is made. Use these text replacements: [animal], [name], [eventDate], [clinic], [clinicPhone], [clinicEmail]</Text>,
                    name: 'smsTextBooking',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.smsTextBooking,
                    showInList: false,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                            {length > 160 ? <> <i class='fa-duotone fa-exclamation-triangle text-warning ms-2' /> <Text id='input.smsTextMoreThanOne'>More than 1 message.</Text></> : ''}
                        </>);
                    },
                },
                {
                    title: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    placeholder: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    name: 'smsTextNotification',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.smsTextNotification,
                    showInList: false,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                            {length > 160 ? <> <i class='fa-duotone fa-exclamation-triangle text-warning ms-2' /> <Text id='input.smsTextMoreThanOne'>More than 1 message.</Text></> : ''}
                        </>);
                    },
                },
            ],
        },
        {
            name: <Text id='input.calendar-media-title'>Calendar media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'calendar media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    calendarEvent: [
        {
            name: <Text id='input.calendarevent-basics-title'>Calendar event</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>sale</Text>,
                    placeholder: <Text id='input.sale'>sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.calendarId'>calendarId</Text>,
                //     placeholder: <Text id='input.calendarId'>calendarId</Text>,
                //     name: 'calendarId',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.calendarId,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.calendarId'>calendarId</Text>,
                    placeholder: <Text id='input.calendarId'>calendarId</Text>,
                    name: 'calendarId',
                    type: 'select',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj?.calendarId,
                    datalistName: 'calendars',
                    addToDatalist: ['__all__', 'All Calendars'],
                    datalistFieldKey: e => e.calendarId,
                    datalistFieldVal: e => `${e.calendarId}`,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                    isRecommended: true,
                },

                {
                    title: <Text id='input.isAllday'>isAllday</Text>,
                    placeholder: <Text id='input.isAllday'>isAllday</Text>,
                    name: 'isAllday',
                    type: 'text',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.isAllday,
                    isAdminField: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevent-sms-title'>Calendar Event SMS</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'calendarevent sms',
            isCollapsed: true,
            fields: [
                {
                    title: <Text id='input.smsText'>smsText</Text>,
                    placeholder: <Text id='input.smsText'>smsText</Text>,
                    help: <Text id='input.smsTextBooking-help'>SMS text template for booking sms. Fields: [animal] [name] [eventDate] [clinic] [clinicPhone] [clinicEmail]</Text>,
                    name: 'smsText',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.smsText,
                },
                {
                    title: <Text id='input.notificationDueDate'>Due date (siste frist)</Text>,
                    placeholder: <Text id='input.notificationDueDate-placeholder'>Last valid date</Text>,
                    help: <Text id='input.notificationDueDate-help'>Optional deadline shown by the [dueDate] placeholder in the notification SMS text, e.g. the last valid vaccination date. If empty, [dueDate] shows the event date.</Text>,
                    name: 'dueDate',
                    type: 'date',
                    icon: 'fa-duotone fa-calendar-exclamation',
                    getValue: obj => obj.dueDate,
                    displayValue: obj => obj ? `${util.formatDate(obj, { locale: 'nb-NO' })}` : null,
                },
                {
                    title: <Text id='input.referringProduct'>Referring Product</Text>,
                    placeholder: <Text id='input.referringProduct'>Referring Product</Text>,
                    name: 'referringProduct',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.referringProduct,
                    showInList: false,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-dates-title'>Calendar event dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event dates',
            fields: [
                {
                    title: <Text id='input.start'>Start</Text>,
                    placeholder: <Text id='input.start'>Start</Text>,
                    name: 'start',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.start,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.start);
                        const end = new Date(obj.end);
                        if (!obj.end || end < start) {
                            const end = new Date(obj.start);
                            end.setMinutes(end.getMinutes() + 15);
                            updateValue({
                                name: 'end',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.end'>End</Text>,
                    placeholder: <Text id='input.end'>End</Text>,
                    name: 'end',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.end,
                    minFunc: obj => obj.start ? util.isoDate(obj.start, { dateTimeSep: 'T' }) : null,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.goingDuration'>goingDuration</Text>,
                    placeholder: <Text id='input.goingDuration'>goingDuration</Text>,
                    name: 'goingDuration',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.goingDuration,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.comingDuration'>comingDuration</Text>,
                    placeholder: <Text id='input.comingDuration'>comingDuration</Text>,
                    name: 'comingDuration',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.comingDuration,
                    showInList: true,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-recurring-title'>Calendar event recurring</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event recurring',
            isCollapsed: true,
            fields: [
                {
                    title: <Text id='input.isRecurring'>isRecurring</Text>,
                    placeholder: <Text id='input.isRecurring'>isRecurring</Text>,
                    name: 'isRecurring',
                    type: 'toggle',
                    icon: 'fa-solid fa-warning',
                    getValue: obj => obj?.isRecurring ? 1 : 0,
                    callback: (obj, updateValue) => {
                        // Only set defaults when toggled ON and fields are not already set
                        if (obj.isRecurring) {
                            // Set recurringStart to the event's start date if not already set
                            if (!obj.recurringStart && obj.start) {
                                updateValue({
                                    name: 'recurringStart',
                                    value: obj.start,
                                });
                            }
                            // Set recurringEnd to 365 days after recurringStart if not already set
                            if (!obj.recurringEnd) {
                                const startDate = new Date(obj.recurringStart || obj.start || new Date());
                                startDate.setDate(startDate.getDate() + 365);
                                updateValue({
                                    name: 'recurringEnd',
                                    value: util.isoDate(startDate),
                                });
                            }
                            // Set recurringFrequency to 'weekly' if not already set
                            if (!obj.recurringFrequency) {
                                updateValue({
                                    name: 'recurringFrequency',
                                    value: 'weekly',
                                });
                            }
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.recurringStart'>recurringStart</Text>,
                    placeholder: <Text id='input.recurringStart'>recurringStart</Text>,
                    name: 'recurringStart',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.recurringStart,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.recurringStart);
                        const end = new Date(obj.recurringEnd);
                        if (!obj.recurringEnd || end < start) {
                            const end = new Date(obj.recurringStart);
                            end.setDate(end.getDate() + 365);
                            updateValue({
                                name: 'recurringEnd',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.recurringEnd'>recurringEnd</Text>,
                    placeholder: <Text id='input.recurringEnd'>recurringEnd</Text>,
                    name: 'recurringEnd',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.recurringEnd,
                    showInList: true,
                },
                {
                    title: <Text id='input.recurringFrequency'>recurringFrequency</Text>,
                    placeholder: <Text id='input.recurringFrequency'>recurringFrequency</Text>,
                    name: 'recurringFrequency',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    datalist: [
                        ['daily', 'Daily'],
                        ['weekly', 'Weekly'],
                        ['biweekly', 'Bi-weekly'],
                        ['every6weeks', 'Every 6 weeks'],
                        ['every7weeks', 'Every 7 weeks'],
                        ['every8weeks', 'Every 8 weeks'],
                        ['monthly', 'Monthly'],
                        ['bimonthly', 'Bi-monthly'],
                        ['yearly', 'Yearly'],
                    ],
                    getValue: obj => obj.recurringFrequency,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.calendarevent-color-title'>Calendar Event color</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'calendarevent color',
            isCollapsed: false,
            fields: [
                {
                    title: <Text id='input.color'>color</Text>,
                    placeholder: <Text id='input.color'>color</Text>,
                    name: 'color',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    datalist: [
                        ['', 'Default'],
                        ['#ffffff', 'White'],
                        ['#000000', 'Black'],
                        ['#808080', 'Gray'],
                        ['#ff0000', 'Red'],
                        ['#00ff00', 'Green'],
                        ['#0000ff', 'blue'],
                    ],
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.color,
                    showInList: false,
                },
                {
                    title: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    placeholder: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    name: 'backgroundColor',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-input-text',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.backgroundColor,
                    showInList: false,
                },
                {
                    title: <Text id='input.dragBackgroundColor'>dragBackgroundColor</Text>,
                    placeholder: <Text id='input.dragBackgroundColor'>dragBackgroundColor</Text>,
                    name: 'dragBackgroundColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dragBackgroundColor,
                    showInList: true,
                },
                {
                    title: <Text id='input.borderColor'>borderColor</Text>,
                    placeholder: <Text id='input.borderColor'>borderColor</Text>,
                    name: 'borderColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.borderColor,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-meta-title'>Calendar event meta</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event meta',
            isCollapsed: false,
            fields: [
                {
                    title: <Text id='input.didNotShow'>didNotShow</Text>,
                    placeholder: <Text id='input.didNotShow'>didNotShow</Text>,
                    name: 'didNotShow',
                    hideOnCreate: true,
                    type: 'toggle',
                    icon: 'fa-solid fa-warning',
                    getValue: obj => obj?.didNotShow ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.didNotShowReason'>No-show reason</Text>,
                    placeholder: <Text id='input.didNotShowReason'>No-show reason</Text>,
                    help: <Text id='input.didNotShowReason-help'>Why the owner did not show up. Used in the no-show overview.</Text>,
                    name: 'didNotShowReason',
                    hideOnCreate: true,
                    type: 'select',
                    datalist: NO_SHOW_REASON_DATALIST,
                    icon: 'fa-duotone fa-person-circle-question',
                    getValue: obj => obj?.didNotShowReason || '',
                },
                {
                    title: <Text id='input.didNotShowNote'>No-show note</Text>,
                    placeholder: <Text id='input.didNotShowNote-placeholder'>Internal note about the no-show</Text>,
                    name: 'didNotShowNote',
                    hideOnCreate: true,
                    type: 'textarea',
                    icon: 'fa-duotone fa-note-sticky',
                    getValue: obj => obj?.didNotShowNote || '',
                },
                {
                    title: <Text id='input.skipReminders'>Skip SMS Reminders</Text>,
                    placeholder: <Text id='input.skipReminders'>Skip SMS Reminders</Text>,
                    name: 'skipReminders',
                    type: 'toggle',
                    icon: 'fa-duotone fa-bell-slash',
                    getValue: obj => obj?.skipReminders ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.hasArrived'>Customer has arrived</Text>,
                    placeholder: <Text id='input.hasArrived'>Customer has arrived</Text>,
                    name: 'hasArrived',
                    type: 'toggle',
                    icon: 'fa-duotone fa-check',
                    getValue: obj => obj?.hasArrived ? 1 : 0,
                    showInList: true,
                },

                {
                    title: <Text id='input.arrivedTime'>arrivedTime</Text>,
                    placeholder: <Text id='input.arrivedTime'>arrivedTime</Text>,
                    name: 'arrivedTime',
                    type: 'datetime-local',
                    step: 60,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.arrivedTime,
                    showInList: true,
                },
                {
                    title: <Text id='input.receivedTime'>receivedTime</Text>,
                    placeholder: <Text id='input.receivedTime'>receivedTime</Text>,
                    name: 'receivedTime',
                    type: 'datetime-local',
                    step: 60,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.receivedTime,
                    showInList: true,
                },
                {
                    title: <Text id='input.doneTime'>doneTime</Text>,
                    placeholder: <Text id='input.doneTime'>doneTime</Text>,
                    name: 'doneTime',
                    type: 'datetime-local',
                    step: 60,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.doneTime,
                    showInList: true,
                },

                {
                    title: <Text id='input.location'>location</Text>,
                    placeholder: <Text id='input.location'>location</Text>,
                    name: 'location',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.location,
                    showInList: true,
                },
                {
                    title: <Text id='input.calendar-category'>category</Text>,
                    placeholder: <Text id='input.calendar-category'>category</Text>,
                    help: <Text id='input.calendar-category-help'>Category of the event. Available categories are 'milestone', 'task', 'time' and 'allday'.</Text>,
                    name: 'category',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.category,
                    showInList: true,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.animals'>animals</Text>,
                    placeholder: <Text id='input.animals'>animals</Text>,
                    name: 'animals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminAnimals',
                    getValue: obj => {
                        if (obj.animals) {
                            return obj.animals?.map(m => m);
                        }
                        return [];
                    },
                    displayArrayObject: obj => `${obj.name} ${obj.species}, ${obj.breed}`,
                    datalist: [
                        'id', 'name', 'birth', 'breed', 'species',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
    ],
    calendarAvailability: [
        {
            name: <Text id='input.calendarevent-basics-title'>Calendar event</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Calendar availability',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.calendar'>calendar</Text>,
                    placeholder: <Text id='input.calendar'>calendar</Text>,
                    name: 'calendar',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.calendar,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.calendarId'>calendarId</Text>,
                //     placeholder: <Text id='input.calendarId'>calendarId</Text>,
                //     name: 'calendarId',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.calendarId,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                },
                {
                    title: <Text id='input.isAllday'>isAllday</Text>,
                    placeholder: <Text id='input.isAllday'>isAllday</Text>,
                    name: 'isAllday',
                    type: 'text',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.isAllday,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-dates-title'>Calendar event dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event dates',
            fields: [
                {
                    title: <Text id='input.start'>Start</Text>,
                    placeholder: <Text id='input.start'>Start</Text>,
                    name: 'start',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.start,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.start);
                        const end = new Date(obj.end);
                        if (!obj.end || end < start) {
                            const end = new Date(obj.start);
                            end.setMinutes(end.getMinutes() + 15);
                            updateValue({
                                name: 'end',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.end'>End</Text>,
                    placeholder: <Text id='input.end'>End</Text>,
                    name: 'end',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.end,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-meta-title'>Calendar event meta</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event meta',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
    ],
    textTemplate: [
        {
            name: <Text id='input.texttemplate-basics-title'>Text template basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'text template',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sort'>sort</Text>,
                    placeholder: <Text id='input.sort'>sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sort,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.section'>section</Text>,
                    placeholder: <Text id='input.section'>section</Text>,
                    name: 'section',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.section,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.field'>field</Text>,
                    placeholder: <Text id='input.field'>field</Text>,
                    name: 'field',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.field,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.type'>type</Text>,
                    placeholder: <Text id='input.type'>type</Text>,
                    name: 'type',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.type,
                    showInList: true,
                    isAdminField: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.title'>title</Text>,
                    placeholder: <Text id='input.title'>title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    isAdminField: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                    showInList: true,
                    isRecommended: true,
                },
            ],
        },
        {
            name: <Text id='input.texttemplate-meta-title'>Text template meta</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'text template meta',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
    ],
    calendarTemplate: [
        {
            name: <Text id='input.calendartemplate-basics-title'>Calendar Template Basics</Text>,
            key: 'calendar template',
            fields: [
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name-placeholder'>Template name (displayed in selection)</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRecommended: true,
                    isRequired: true,
                    callback: (obj, updateValue) => {
                        if (obj.name && !obj.title) {
                            updateValue({
                                id: obj.id,
                                name: 'title',
                                value: obj.name,
                            });
                        }
                    },
                },
                {
                    title: <Text id='input.title'>Event Title</Text>,
                    placeholder: <Text id='input.title-placeholder'>Pre-filled event title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    isRecommended: true,
                    callback: (obj, updateValue) => {
                        if (obj.title && !obj.name) {
                            updateValue({
                                id: obj.id,
                                name: 'name',
                                value: obj.title,
                            });
                        }
                    },
                },
                {
                    title: <Text id='input.body'>Description</Text>,
                    placeholder: <Text id='input.body-placeholder'>Pre-filled event description</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.durationMinutes'>Duration (minutes)</Text>,
                    placeholder: <Text id='input.durationMinutes-placeholder'>30</Text>,
                    name: 'durationMinutes',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj.durationMinutes || 30,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.backgroundColor'>Background Color</Text>,
                    placeholder: <Text id='input.backgroundColor-placeholder'>Event background color</Text>,
                    name: 'backgroundColor',
                    type: 'colorPicker',
                    icon: 'fa-solid fa-palette',
                    datalist: COLOR_PICKER_LIST,
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.backgroundColor,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.smsText'>SMS Template</Text>,
                    placeholder: <Text id='input.smsText-placeholder'>Pre-filled SMS reminder text</Text>,
                    name: 'smsText',
                    type: 'textarea',
                    icon: 'fa-solid fa-comment-sms',
                    getValue: obj => obj.smsText,
                    help: <Text id='input.smsText-help'>Use [animal], [name], [clinic], [clinicPhone] for dynamic values</Text>,
                    showInList: false,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.sort'>Sort Order</Text>,
                    placeholder: <Text id='input.sort-placeholder'>Sort order (lower = first)</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-sort',
                    getValue: obj => obj.sort,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.category'>Category</Text>,
                    placeholder: <Text id='input.category-placeholder'>Template category</Text>,
                    name: 'category',
                    type: 'text',
                    icon: 'fa-solid fa-folder',
                    getValue: obj => obj.category,
                    showInList: false,
                    isAdminField: false,
                },
            ],
        },
    ],
    sms: [
        {
            name: <Text id='input.sms-basics-title'>sms basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'sms',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'initiator',
                    placeholder: 'initiator',
                    name: 'initiator',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.initiator,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'sender',
                    placeholder: 'sender',
                    name: 'sender',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sender,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'cellphone',
                    placeholder: 'cellphone',
                    name: 'cellphone',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'message',
                    placeholder: 'message',
                    name: 'message',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.message,
                    showInList: true,
                    isAdminField: true,
                },
            ],
        },
    ],
    payment: [
        {
            name: <Text id='input.payment-basics-title'>payment basics</Text>,
            key: 'payment basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    placeholder: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    name: 'raceClassContestant',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClassContestant,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>sale</Text>,
                    placeholder: <Text id='input.sale'>sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.paidDate'>PaidDate</Text>,
                    placeholder: <Text id='input.paidDate'>PaidDate</Text>,
                    name: 'paidDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidDate,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.capturedDate'>capturedDate</Text>,
                    placeholder: <Text id='input.capturedDate'>capturedDate</Text>,
                    name: 'capturedDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.capturedDate,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.title'>title</Text>,
                    placeholder: <Text id='input.title'>title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.type'>type</Text>,
                    placeholder: <Text id='input.type'>type</Text>,
                    name: 'type',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.type,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
                {
                    title: <Text id='input.wantedPage'>wantedPage</Text>,
                    placeholder: <Text id='input.wantedPage'>wantedPage</Text>,
                    name: 'wantedPage',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.wantedPage,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.payment-price-payment-title'>payment price and payment</Text>,
            key: 'payment price and payment',
            fields: [
                {
                    title: <Text id='input.amount'>amount</Text>,
                    placeholder: <Text id='input.amount'>amount</Text>,
                    name: 'amount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amount,
                    showInList: true,
                },
                {
                    title: <Text id='input.vat'>Vat</Text>,
                    placeholder: <Text id='input.vat'>Vat</Text>,
                    name: 'vat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.vat,
                    showInList: true,
                },
                {
                    title: <Text id='input.currency'>currency</Text>,
                    placeholder: <Text id='input.currency'>currency</Text>,
                    name: 'currency',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.currency,
                    showInList: true,
                },
                {
                    title: <Text id='input.paymentMethod'>paymentMethod</Text>,
                    placeholder: <Text id='input.paymentMethod'>paymentMethod</Text>,
                    name: 'paymentMethod',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentMethod,
                    showInList: true,
                },
                {
                    title: <Text id='input.paymentStatus'>paymentStatus</Text>,
                    placeholder: <Text id='input.paymentStatus'>paymentStatus</Text>,
                    name: 'paymentStatus',
                    type: 'select',
                    /*
                        0: 'Not started',
                        1: 'Payment started',
                        2: 'Payment authorized',
                        3: 'Payment captured',
                        90: 'Payment canceled',
                        91: 'Payment aborted',
                        99: 'Payment failed',
                    */
                    datalist: [[0, 'Not started'], [1, 'Payment started'], [2, 'Payment authorized'], [3, 'Payment captured'], [90, 'Payment canceled'], [91, 'Payment aborted'], [99, 'Payment failed']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentStatus,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.payment-media-title'>payment media</Text>,
            key: 'payment media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    displayArrayObject: obj => <>{obj.name} (<a href={obj.url} target='_blank' rel='noopener noreferrer'>{obj.url}</a>)</>,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
            ],
        },
    ],

    // Coffee collection fields
    document: [
        {
            name: <Text id='input.document-info-title'>Document info</Text>,
            key: 'Document info',
            fields: [
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-heading',
                    getValue: obj => obj?.title,
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.documentType'>Document Type</Text>,
                    placeholder: <Text id='input.documentType'>Document Type</Text>,
                    name: 'documentType',
                    type: 'select',
                    icon: 'fa-solid fa-file-lines',
                    getValue: obj => obj?.documentType,
                    datalist: [
                        ['driving-license', 'Driving License'],
                        ['fishing-license', 'Fishing License'],
                        ['hunting-license', 'Hunting License'],
                        ['boat-license', 'Boat License'],
                        ['weapons-license', 'Weapons License'],
                        ['hunting-permit', 'Hunting Permit'],
                        ['weapons-permit', 'Weapons Permit'],
                        ['travel-permit', 'Travel Permit'],
                        ['event-ticket', 'Event Ticket'],
                        ['travel-ticket', 'Travel Ticket'],
                        ['membership-card', 'Membership Card'],
                        ['certificate', 'Certificate'],
                        ['id-card', 'ID Card'],
                        ['passport', 'Passport'],
                        ['other', 'Other'],
                    ],
                    showInList: true,
                    isRecommended: true,
                },
                {
                    title: <Text id='input.documentNumber'>Document Number</Text>,
                    placeholder: <Text id='input.documentNumber'>Document Number</Text>,
                    name: 'documentNumber',
                    type: 'text',
                    icon: 'fa-solid fa-hashtag',
                    getValue: obj => obj?.documentNumber,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-align-left',
                    getValue: obj => obj?.description,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.document-validity-title'>Document Validity</Text>,
            key: 'Document validity',
            fields: [
                {
                    title: <Text id='input.issueDate'>Issue Date</Text>,
                    placeholder: <Text id='input.issueDate'>Issue Date</Text>,
                    name: 'issueDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar-plus',
                    getValue: obj => obj?.issueDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.expiryDate'>Expiry Date</Text>,
                    placeholder: <Text id='input.expiryDate'>Expiry Date</Text>,
                    name: 'expiryDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar-xmark',
                    getValue: obj => obj?.expiryDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.issuedBy'>Issued By</Text>,
                    placeholder: <Text id='input.issuedBy'>Issued By</Text>,
                    name: 'issuedBy',
                    type: 'text',
                    icon: 'fa-solid fa-building',
                    getValue: obj => obj?.issuedBy,
                    showInList: false,
                },
                {
                    title: <Text id='input.issuedCountry'>Issued Country</Text>,
                    placeholder: <Text id='input.issuedCountry'>Issued Country</Text>,
                    name: 'issuedCountry',
                    type: 'text',
                    icon: 'fa-solid fa-flag',
                    getValue: obj => obj?.issuedCountry,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.document-weapon-license-title'>Weapon License Details</Text>,
            key: 'Document weapon license',
            showIf: obj => obj?.documentType === 'weapons-license' || obj?.documentType === 'weapons-permit',
            fields: [
                {
                    title: <Text id='input.weaponType'>Weapon Type</Text>,
                    placeholder: <Text id='input.weaponType'>Weapon Type</Text>,
                    name: 'weaponType',
                    type: 'select',
                    icon: 'fa-solid fa-gun',
                    getValue: obj => obj?.weaponType,
                    datalist: [
                        ['pistol', 'Pistol'],
                        ['rifle', 'Rifle'],
                        ['shotgun', 'Shotgun'],
                        ['revolver', 'Revolver'],
                        ['combination', 'Combination Weapon'],
                        ['other', 'Other'],
                    ],
                    showInList: false,
                },
                {
                    title: <Text id='input.weaponCategory'>License Category</Text>,
                    placeholder: <Text id='input.weaponCategory'>License Category</Text>,
                    name: 'weaponCategory',
                    type: 'text',
                    icon: 'fa-solid fa-layer-group',
                    getValue: obj => obj?.weaponCategory,
                    showInList: false,
                },
                {
                    title: <Text id='input.weaponRestrictions'>Restrictions</Text>,
                    placeholder: <Text id='input.weaponRestrictions'>Restrictions</Text>,
                    name: 'weaponRestrictions',
                    type: 'textarea',
                    icon: 'fa-solid fa-ban',
                    getValue: obj => obj?.weaponRestrictions,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.document-ticket-title'>Event/Ticket Details</Text>,
            key: 'Document ticket',
            showIf: obj => obj?.documentType === 'event-ticket' || obj?.documentType === 'travel-ticket',
            fields: [
                {
                    title: <Text id='input.eventName'>Event Name</Text>,
                    placeholder: <Text id='input.eventName'>Event Name</Text>,
                    name: 'eventName',
                    type: 'text',
                    icon: 'fa-solid fa-ticket',
                    getValue: obj => obj?.eventName,
                    showInList: false,
                },
                {
                    title: <Text id='input.eventDate'>Event Date</Text>,
                    placeholder: <Text id='input.eventDate'>Event Date</Text>,
                    name: 'eventDate',
                    type: 'datetime',
                    icon: 'fa-solid fa-calendar-day',
                    getValue: obj => obj?.eventDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.eventVenue'>Venue</Text>,
                    placeholder: <Text id='input.eventVenue'>Venue</Text>,
                    name: 'eventVenue',
                    type: 'text',
                    icon: 'fa-solid fa-location-dot',
                    getValue: obj => obj?.eventVenue,
                    showInList: false,
                },
                {
                    title: <Text id='input.eventSeat'>Seat/Section</Text>,
                    placeholder: <Text id='input.eventSeat'>Seat/Section</Text>,
                    name: 'eventSeat',
                    type: 'text',
                    icon: 'fa-solid fa-chair',
                    getValue: obj => obj?.eventSeat,
                    showInList: false,
                },
                {
                    title: <Text id='input.ticketBarcode'>Barcode/QR Code</Text>,
                    placeholder: <Text id='input.ticketBarcode'>Barcode/QR Code</Text>,
                    name: 'ticketBarcode',
                    type: 'text',
                    icon: 'fa-solid fa-qrcode',
                    getValue: obj => obj?.ticketBarcode,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.document-attachments-title'>Attachments</Text>,
            key: 'Document attachments',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-upload-help'>Add one or more images. Images takes some seconds to prepare for web view. Please wait until it is ready.</Text>,
                    name: 'images',
                    type: 'image',
                    displayLargeImages: true,
                    displayWidth: '180px',
                    displayHeight: 'auto',
                    getValue: obj => obj?.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    getValue: obj => obj?.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tags',
                    getValue: obj => obj?.tags,
                    showInList: false,
                },
                {
                    title: <Text id='input.notes'>Notes</Text>,
                    placeholder: <Text id='input.notes'>Notes</Text>,
                    name: 'notes',
                    type: 'textarea',
                    icon: 'fa-solid fa-sticky-note',
                    getValue: obj => obj?.notes,
                    showInList: false,
                },
            ],
        },
    ],
    notificationTopics: [
        {
            name: <Text id='push.settings.topicsHeading'>Topics</Text>,
            key: 'notificationTopics',
            fields: [
                {
                    title: <Text id='push.topic.allStories'>News & updates</Text>,
                    name: 'notificationTopics.allStories',
                    type: 'toggle',
                    icon: 'fa-duotone fa-newspaper',
                    getValue: obj => (obj?.notificationTopics?.allStories === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.topic.allArticles'>Feed articles</Text>,
                    name: 'notificationTopics.allArticles',
                    type: 'toggle',
                    icon: 'fa-duotone fa-rss',
                    getValue: obj => (obj?.notificationTopics?.allArticles === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.topic.allNews'>News</Text>,
                    name: 'notificationTopics.allNews',
                    type: 'toggle',
                    icon: 'fa-duotone fa-newspaper',
                    getValue: obj => (obj?.notificationTopics?.allNews === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.topic.allRaces'>Races</Text>,
                    name: 'notificationTopics.allRaces',
                    type: 'toggle',
                    icon: 'fa-duotone fa-flag-checkered',
                    getValue: obj => (obj?.notificationTopics?.allRaces === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.topic.allCompetitions'>Competitions</Text>,
                    name: 'notificationTopics.allCompetitions',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trophy',
                    getValue: obj => (obj?.notificationTopics?.allCompetitions === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.topic.allOffers'>Offers</Text>,
                    name: 'notificationTopics.allOffers',
                    type: 'toggle',
                    icon: 'fa-duotone fa-tag',
                    getValue: obj => (obj?.notificationTopics?.allOffers === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.topic.allTips'>Tips</Text>,
                    name: 'notificationTopics.allTips',
                    type: 'toggle',
                    icon: 'fa-duotone fa-lightbulb',
                    getValue: obj => (obj?.notificationTopics?.allTips === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.topic.allConventions'>Conventions</Text>,
                    name: 'notificationTopics.allConventions',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar-star',
                    getValue: obj => (obj?.notificationTopics?.allConventions === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.allCourses'>Courses</Text>,
                    name: 'notificationTopics.allCourses',
                    type: 'toggle',
                    icon: 'fa-duotone fa-graduation-cap',
                    getValue: obj => (obj?.notificationTopics?.allCourses === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.allExams'>Exams</Text>,
                    name: 'notificationTopics.allExams',
                    type: 'toggle',
                    icon: 'fa-duotone fa-clipboard-check',
                    getValue: obj => (obj?.notificationTopics?.allExams === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.allPolls'>Polls</Text>,
                    name: 'notificationTopics.allPolls',
                    type: 'toggle',
                    icon: 'fa-duotone fa-square-poll-vertical',
                    getValue: obj => (obj?.notificationTopics?.allPolls === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.allStreams'>Streams</Text>,
                    name: 'notificationTopics.allStreams',
                    type: 'toggle',
                    icon: 'fa-duotone fa-tower-broadcast',
                    getValue: obj => (obj?.notificationTopics?.allStreams === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.allBroadcastPrograms'>Broadcast programs</Text>,
                    name: 'notificationTopics.allBroadcastPrograms',
                    type: 'toggle',
                    icon: 'fa-duotone fa-radio',
                    getValue: obj => (obj?.notificationTopics?.allBroadcastPrograms === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.allProducts'>Products</Text>,
                    name: 'notificationTopics.allProducts',
                    type: 'toggle',
                    icon: 'fa-duotone fa-tag',
                    getValue: obj => (obj?.notificationTopics?.allProducts === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.racesSignupOpens'>Race signup opens</Text>,
                    name: 'notificationTopics.racesSignupOpens',
                    type: 'toggle',
                    icon: 'fa-duotone fa-arrow-right-to-line',
                    getValue: obj => (obj?.notificationTopics?.racesSignupOpens === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.racesSignupClosesWarn'>Race signup closing soon</Text>,
                    name: 'notificationTopics.racesSignupClosesWarn',
                    type: 'toggle',
                    icon: 'fa-duotone fa-hourglass-half',
                    getValue: obj => (obj?.notificationTopics?.racesSignupClosesWarn === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.racesStarts'>Race starts</Text>,
                    name: 'notificationTopics.racesStarts',
                    type: 'toggle',
                    icon: 'fa-duotone fa-flag',
                    getValue: obj => (obj?.notificationTopics?.racesStarts === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.racesEnds'>Race ends</Text>,
                    name: 'notificationTopics.racesEnds',
                    type: 'toggle',
                    icon: 'fa-duotone fa-flag-checkered',
                    getValue: obj => (obj?.notificationTopics?.racesEnds === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.racesResultsPublished'>Race results published</Text>,
                    name: 'notificationTopics.racesResultsPublished',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trophy',
                    getValue: obj => (obj?.notificationTopics?.racesResultsPublished === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.conventionsStarts'>Convention starts</Text>,
                    name: 'notificationTopics.conventionsStarts',
                    type: 'toggle',
                    icon: 'fa-duotone fa-flag',
                    getValue: obj => (obj?.notificationTopics?.conventionsStarts === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.conventionsResultsPublished'>Convention results published</Text>,
                    name: 'notificationTopics.conventionsResultsPublished',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trophy',
                    getValue: obj => (obj?.notificationTopics?.conventionsResultsPublished === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.coursesSignupOpens'>Course signup opens</Text>,
                    name: 'notificationTopics.coursesSignupOpens',
                    type: 'toggle',
                    icon: 'fa-duotone fa-arrow-right-to-line',
                    getValue: obj => (obj?.notificationTopics?.coursesSignupOpens === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.coursesStarts'>Course starts</Text>,
                    name: 'notificationTopics.coursesStarts',
                    type: 'toggle',
                    icon: 'fa-duotone fa-flag',
                    getValue: obj => (obj?.notificationTopics?.coursesStarts === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.examsDeadlineWarn'>Exam deadline approaching</Text>,
                    name: 'notificationTopics.examsDeadlineWarn',
                    type: 'toggle',
                    icon: 'fa-duotone fa-hourglass-half',
                    getValue: obj => (obj?.notificationTopics?.examsDeadlineWarn === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.pollsClosesWarn'>Poll closing soon</Text>,
                    name: 'notificationTopics.pollsClosesWarn',
                    type: 'toggle',
                    icon: 'fa-duotone fa-hourglass-half',
                    getValue: obj => (obj?.notificationTopics?.pollsClosesWarn === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.pollsResultsPublished'>Poll results published</Text>,
                    name: 'notificationTopics.pollsResultsPublished',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trophy',
                    getValue: obj => (obj?.notificationTopics?.pollsResultsPublished === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.streamsGoesLive'>Stream goes live</Text>,
                    name: 'notificationTopics.streamsGoesLive',
                    type: 'toggle',
                    icon: 'fa-duotone fa-tower-broadcast',
                    getValue: obj => (obj?.notificationTopics?.streamsGoesLive === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.topic.broadcastProgramsStartsWarn'>Broadcast program starting soon</Text>,
                    name: 'notificationTopics.broadcastProgramsStartsWarn',
                    type: 'toggle',
                    icon: 'fa-duotone fa-hourglass-half',
                    getValue: obj => (obj?.notificationTopics?.broadcastProgramsStartsWarn === 0 ? 0 : 1),
                },
            ],
        },
    ],
    notifications: [
        {
            name: <Text id='push.personal.heading'>Notifications about your content</Text>,
            key: 'notifications',
            fields: [
                {
                    title: <Text id='push.personal.articleLike'>Likes on your feed posts</Text>,
                    name: 'notifications.articleLike',
                    type: 'toggle',
                    icon: 'fa-duotone fa-thumbs-up',
                    getValue: obj => (obj?.notifications?.articleLike === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.personal.articleComment'>Comments on your feed posts</Text>,
                    name: 'notifications.articleComment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-comment',
                    getValue: obj => (obj?.notifications?.articleComment === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.personal.storyLike'>Likes on your stories</Text>,
                    name: 'notifications.storyLike',
                    type: 'toggle',
                    icon: 'fa-duotone fa-thumbs-up',
                    getValue: obj => (obj?.notifications?.storyLike === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.personal.storyComment'>Comments on your stories</Text>,
                    name: 'notifications.storyComment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-comment',
                    getValue: obj => (obj?.notifications?.storyComment === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.personal.workoutLike'>Likes on your workouts</Text>,
                    name: 'notifications.workoutLike',
                    type: 'toggle',
                    icon: 'fa-duotone fa-thumbs-up',
                    getValue: obj => (obj?.notifications?.workoutLike === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.personal.workoutComment'>Comments on your workouts</Text>,
                    name: 'notifications.workoutComment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-comment',
                    getValue: obj => (obj?.notifications?.workoutComment === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.personal.message'>Messages to you</Text>,
                    name: 'notifications.message',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope',
                    getValue: obj => (obj?.notifications?.message === 1 ? 1 : 0),
                },
                {
                    title: <Text id='push.personal.supportTicketReply'>Support ticket reply</Text>,
                    name: 'notifications.supportTicketReply',
                    type: 'toggle',
                    icon: 'fa-duotone fa-message-question',
                    getValue: obj => (obj?.notifications?.supportTicketReply === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.personal.trophyEarned'>Trophy earned</Text>,
                    name: 'notifications.trophyEarned',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trophy',
                    getValue: obj => (obj?.notifications?.trophyEarned === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.personal.inboxMessageReceived'>Inbox message received</Text>,
                    name: 'notifications.inboxMessageReceived',
                    type: 'toggle',
                    icon: 'fa-duotone fa-envelope',
                    getValue: obj => (obj?.notifications?.inboxMessageReceived === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.personal.yourExamGraded'>Your exam graded</Text>,
                    name: 'notifications.yourExamGraded',
                    type: 'toggle',
                    icon: 'fa-duotone fa-clipboard-check',
                    getValue: obj => (obj?.notifications?.yourExamGraded === 0 ? 0 : 1),
                },
                {
                    title: <Text id='push.personal.newCheckout'>New checkout</Text>,
                    name: 'notifications.newCheckout',
                    type: 'toggle',
                    icon: 'fa-duotone fa-cart-shopping',
                    getValue: obj => (obj?.notifications?.newCheckout === 0 ? 0 : 1),
                },
            ],
        },
    ],
    aiFeedback: [
        {
            name: <Text id='support.aiFeedback.review-title'>AI Feedback Review</Text>,
            key: 'ai feedback',
            fields: [
                {
                    title: <Text id='support.aiFeedback.category'>Category</Text>,
                    placeholder: <Text id='support.aiFeedback.category'>Category</Text>,
                    name: 'category',
                    type: 'select',
                    icon: 'fa-duotone fa-flag',
                    datalist: [
                        ['hallucination', <Text id='support.aiFeedback.category-hallucination'>Hallucination — made-up info</Text>],
                        ['wrong_info', <Text id='support.aiFeedback.category-wrong_info'>Wrong information</Text>],
                        ['missing_article', <Text id='support.aiFeedback.category-missing_article'>Missing help article</Text>],
                        ['wrong_language', <Text id='support.aiFeedback.category-wrong_language'>Wrong language</Text>],
                        ['tone', <Text id='support.aiFeedback.category-tone'>Tone</Text>],
                        ['other', <Text id='support.aiFeedback.category-other'>Other</Text>],
                    ],
                    getValue: obj => obj.category || 'hallucination',
                },
                {
                    title: <Text id='support.aiFeedback.comment'>Comment</Text>,
                    placeholder: <Text id='support.aiFeedback.comment-placeholder'>e.g. tells user to click a button that does not exist</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-duotone fa-comment',
                    getValue: obj => obj.comment || '',
                },
                {
                    title: <Text id='support.aiFeedback.resolution-notes'>Resolution notes</Text>,
                    placeholder: <Text id='support.aiFeedback.resolution-placeholder'>What was changed (article, prompt, etc.)</Text>,
                    name: 'resolutionNotes',
                    type: 'textarea',
                    icon: 'fa-duotone fa-pen',
                    getValue: obj => obj.resolutionNotes || '',
                },
                {
                    title: <Text id='support.aiFeedback.filter-status'>Status</Text>,
                    placeholder: <Text id='support.aiFeedback.filter-status'>Status</Text>,
                    name: 'status',
                    type: 'select',
                    icon: 'fa-duotone fa-circle-check',
                    datalist: [
                        ['open', <Text id='support.aiFeedback.status-open'>Open</Text>],
                        ['reviewed', <Text id='support.aiFeedback.status-reviewed'>Reviewed</Text>],
                        ['fixed', <Text id='support.aiFeedback.status-fixed'>Fixed</Text>],
                        ['wontfix', <Text id='support.aiFeedback.status-wontfix'>Won't fix</Text>],
                    ],
                    getValue: obj => obj.status || 'open',
                },
            ],
        },
    ],

    ...fieldsOtherEntities,
};

const FIELDS = applyFieldExtensions(FIELDS_BASE, fieldExtensions);

// --- Global date shortcuts (I dag / I morgen / +1 uke / Nå) ---------------
// Every datetime-local field without its own showFieldInfo gets one that
// renders the shortcut buttons. Injection works on memoized copies keyed by
// the stable global definitions — FIELDS itself is never mutated, and
// repeated get() calls return the identical instances, so callers may keep
// relying on reference equality of unfiltered results.
const injectedFieldCache = new WeakMap();
const injectDateShortcutField = f => {
    // hideDateShortcuts is the per-field opt-out (birth dates, sale dates, ...) —
    // components/calendar/datePicker.js honours the same flag for date fields.
    if (f.type !== 'datetime-local' || f.showFieldInfo || f.hideDateShortcuts) {
        return f;
    }
    if (!injectedFieldCache.has(f)) {
        injectedFieldCache.set(f, {
            ...f,
            showFieldInfo: ({ obj, field }) => renderDateTimeShortcuts({ obj, field, fieldDef: f }),
        });
    }
    return injectedFieldCache.get(f);
};

const injectedSectionCache = new WeakMap();
const injectDateShortcutSection = s => {
    if (!injectedSectionCache.has(s)) {
        const mappedFields = (s.fields || []).map(injectDateShortcutField);
        const changed = mappedFields.some((f, idx) => f !== s.fields[idx]);
        injectedSectionCache.set(s, changed ? { ...s, fields: mappedFields } : s);
    }
    return injectedSectionCache.get(s);
};

const injectedTypeCache = new Map();
const getInjectedTypeSections = type => {
    if (!injectedTypeCache.has(type)) {
        const mapped = FIELDS[type].map(injectDateShortcutSection);
        const changed = mapped.some((s, idx) => s !== FIELDS[type][idx]);
        injectedTypeCache.set(type, changed ? mapped : FIELDS[type]);
    }
    return injectedTypeCache.get(type);
};

class Fields {
    static get(type, section, skipFields = [], onlyFields = []) {
        if (FIELDS[type]) {
            let sections = FIELDS[type];
            if (Array.isArray(section)) {
                // Check if s.name or s.key exists in array section:
                sections = FIELDS[type].filter(s => section.includes(s.name) || section.includes(s.key));
            } else if (section) {
                sections = FIELDS[type].filter(s => s.name === section || s.key === section);
            }
            // Filtering by skip/only must NOT mutate the shared global FIELDS definition.
            // `sections` still references the global section objects here (when `section` is
            // falsy it IS FIELDS[type] itself), so reassigning `s.fields` in place would
            // permanently shrink the field list for every other consumer this session.
            // Return shallow copies with their own filtered `fields` array instead.
            if (skipFields.length > 0 || onlyFields.length > 0) {
                sections = sections.map(s => ({
                    ...s,
                    fields: (s.fields || []).filter(f => {
                        if (skipFields.length > 0 && skipFields.includes(f.name)) {
                            return false;
                        }
                        if (onlyFields.length > 0 && !onlyFields.includes(f.name)) {
                            return false;
                        }
                        return true;
                    }).map(injectDateShortcutField),
                }));
            } else if (section) {
                // Filtered array is new anyway; keep the section instances stable.
                sections = sections.map(injectDateShortcutSection);
            } else {
                // Unfiltered reads must keep returning the same array instance.
                sections = getInjectedTypeSections(type);
            }
            return sections;
        }
        return [];
    }

    static getSection(type, section) {
        if (FIELDS[type]) {
            for (let i = 0, l = FIELDS[type].length; i < l; i += 1) {
                const sec = FIELDS[type][i];
                if (sec.key === section) {
                    return sec;
                }
            }
        }
        return null;
    }

    static getSections(type) {
        if (FIELDS[type]) {
            return FIELDS[type];
        }
        return null;
    }

    static getField(type, field) {
        if (FIELDS[type]) {
            for (let i = 0, l = FIELDS[type].length; i < l; i += 1) {
                const section = FIELDS[type][i];
                for (let j = 0, l2 = section.fields.length; j < l2; j += 1) {
                    const f = section.fields[j];
                    if (f.name === field) {
                        return f;
                    }
                }
            }
        }
        return null;
    }

    static getNumberFixedWidth(value, width = 4) {
        if (value === null || value === undefined) {
            return null;
        }
        const numberAsString = value.toString();
        const paddingNeeded = Math.max(width - numberAsString.length, 0);
        const paddedNumber = '0'.repeat(paddingNeeded) + numberAsString;
        return paddedNumber;
    }
}
export default Fields;
